export interface BlogPostTranslation {
  title: string;
  metaTitle: string;
  metaDescription: string;
  excerpt: string;
  content: string;
}

export interface BlogPost {
  slug: string;
  translations: {
    en: BlogPostTranslation;
    fr: BlogPostTranslation;
  };
  author: string;
  publishDate: string;
  updatedDate: string;
  category: string;
  tags: string[];
  readTime: number;
  featured: boolean;
}

// Helper type for backwards compatibility
export interface LocalizedBlogPost {
  slug: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  excerpt: string;
  content: string;
  author: string;
  publishDate: string;
  updatedDate: string;
  category: string;
  tags: string[];
  readTime: number;
  featured: boolean;
}

export const blogPosts: BlogPost[] = [
  {
    slug: "how-to-conduct-iso-27001-risk-assessment",
    translations: {
      en: {
        title: "How to Conduct an ISO 27001 Risk Assessment (2022 Step-by-Step Guide)",
        metaTitle: "How to Conduct an ISO 27001 Risk Assessment - Step-by-Step",
        metaDescription: "A practical step-by-step guide to the ISO 27001:2022 risk assessment process under clause 6.1.2: identify, analyze, evaluate, and treat risks.",
        excerpt: "ISO 27001:2022 clause 6.1.2 requires a documented, repeatable risk assessment process. This guide walks you through each step, from defining criteria to producing an audit-ready risk register.",
        content: `
## What ISO 27001:2022 Requires for Risk Assessment

Clause 6.1.2 of ISO 27001:2022 requires every ISMS to define and apply an information security risk assessment process that produces consistent, valid, and comparable results. In plain terms, two people doing the same assessment must reach similar conclusions, and the same risk must score the same way every time you re-evaluate it.

The standard does not prescribe a specific methodology. You can use qualitative, quantitative, or hybrid approaches as long as you document the criteria, apply them consistently, and keep evidence of every decision. Most organizations choose a qualitative 5x5 likelihood-by-impact matrix because it is fast to operate and easy for auditors to follow.

## Step 1 - Define Your Risk Criteria

Before you score a single risk, document two things: the criteria for accepting risk, and the criteria for performing assessments. Auditors will ask for both.

- **Risk acceptance criteria.** Which scores can be accepted as-is, and who is allowed to accept them. A common pattern: low risks accepted by the risk owner, medium risks by the ISMS manager, high and critical risks by top management.
- **Likelihood scale.** Define what 1-5 mean (e.g. 1 = once every 5+ years, 5 = monthly or more).
- **Impact scale.** Define what 1-5 mean across the categories that matter to your business: confidentiality, integrity, availability, financial loss, regulatory, reputation.
- **Risk matrix.** Multiply likelihood by impact to get an inherent risk score, then map score bands to severity (low/medium/high/critical).

Write these down once in a "Risk Assessment Methodology" document. Every assessment from now on references it.

## Step 2 - Identify Risks

Clause 6.1.2 c) requires you to identify risks associated with the loss of confidentiality, integrity, and availability of information within the scope of your ISMS. The cleanest way to do this is the asset-threat-vulnerability model:

1. **List your information assets** - databases, source code repositories, customer data, employee laptops, SaaS tenants, physical sites.
2. **For each asset, identify relevant threats** - ransomware, insider misuse, supplier outage, lost device, misconfiguration.
3. **For each threat, identify the vulnerability that would let it materialize** - missing [MFA](/controls/a-8-5-secure-authentication), unpatched software, no [offsite backup](/controls/a-8-13-information-backup), no [access review](/controls/a-5-18-access-rights).
4. **Combine into a risk statement** - "Customer PII in the production database is exposed because of SQL injection in the web app."

Aim for 30-80 risks for a small organization. Fewer than 20 is usually too thin; more than 150 is hard to maintain.

## Step 3 - Assign Risk Owners

Every risk needs a single named owner who is accountable for the treatment decision. This is a hard requirement of clause 6.1.2 c) 2). The owner is usually the manager of the function most affected - the CTO owns engineering risks, the Head of People owns HR risks, the CFO owns financial-process risks.

Avoid assigning all risks to the CISO or "IT". Auditors will challenge that pattern, and it puts decision-making on someone who cannot actually implement the fix.

## Step 4 - Analyze Risks (Inherent Score)

For each risk, score likelihood and impact using the scales you defined in Step 1, before considering any existing controls. The product is the **inherent risk score**.

Document the rationale for each score in one sentence. "Likelihood 4 because we publish endpoints publicly and have seen scanning attempts in logs." That sentence is what an auditor reads to test whether your process is consistent.

## Step 5 - Evaluate Against Acceptance Criteria

Compare each inherent score to the acceptance criteria from Step 1. Anything above the acceptance threshold needs treatment. Anything at or below can be formally accepted, but the acceptance still needs to be recorded with the owner's name and the date.

This is also where you decide priority. Critical risks get treated this quarter, high risks within six months, medium risks within the year.

## Step 6 - Plan Risk Treatment

Clause 6.1.3 gives you four treatment options:

- **Modify** - apply controls to reduce likelihood or impact (most common).
- **Avoid** - stop doing the activity that creates the risk.
- **Share** - transfer via insurance, contract, or outsourcing.
- **Accept** - acknowledge and live with it, with formal sign-off.

For "modify" treatments, map each chosen control back to Annex A. This is what feeds your Statement of Applicability. After treatment is implemented, re-score likelihood and impact to get the **residual risk score**. The residual score is what you carry forward into ongoing monitoring.

## Step 7 - Document Everything in a Risk Register

Your risk register is the single source of truth. For each risk, capture: ID, statement, asset, threat, vulnerability, owner, inherent likelihood/impact/score, treatment option, mapped Annex A controls, treatment plan, target date, residual likelihood/impact/score, acceptance status, last reviewed date.

If you are still using a spreadsheet, the [free ISO 27001 risk register template](/iso-27001-risk-register-template-excel) on this site mirrors this structure. For a maintained register with automated scoring, audit trail, and reports, the [Risk Assessment tool](/tools/risk-assessment) does the same job without the spreadsheet drift.

## Step 8 - Review on a Schedule

Clause 8.2 requires risk assessments to be performed at planned intervals and when significant changes occur. The pragmatic rhythm:

- **Quarterly** - review high and critical risks, update residual scores if controls changed.
- **Annually** - full re-assessment of every risk in the register.
- **Event-driven** - any new system, new supplier, new regulation, or security incident triggers a targeted re-assessment.

Every review must be recorded. "Reviewed by Jane on 2026-06-15, no change" is enough.

## Common Audit Findings to Avoid

- **No documented methodology.** You jumped straight to scoring risks without writing down the scales.
- **Risks without owners**, or every risk owned by the same person.
- **Inherent and residual scores identical** across the register - a sign you did not actually evaluate the effect of controls.
- **Stale review dates** - if every risk was last reviewed 14 months ago, your ISMS is not operating.
- **No link from risks to Annex A controls**, which breaks the Statement of Applicability.

## Next Steps

The fastest way to operationalize this is to start with a structured template, then move to a tool once your register exceeds 30-40 risks. Try the [free ISO 27001 risk register template](/iso-27001-risk-register-template-excel), or jump straight into the [Risk Assessment tool](/tools/risk-assessment) to build an audit-ready register with the methodology above already baked in.
`
      },
      fr: {
        title: "Comment realiser une evaluation des risques ISO 27001",
        metaTitle: "Comment realiser une evaluation des risques ISO 27001",
        metaDescription: "Guide etape par etape du processus d'evaluation des risques selon ISO 27001:2022 clause 6.1.2.",
        excerpt: "Guide pratique pour realiser une evaluation des risques conforme a ISO 27001:2022.",
        content: `Ce contenu sera bientot disponible en francais.`
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-06-29",
    updatedDate: "2026-06-29",
    category: "Risk Management",
    tags: ["iso 27001 risk assessment", "clause 6.1.2", "risk assessment methodology", "iso 27001:2022", "risk register"],
    readTime: 10,
    featured: false
  },
  {
    slug: "best-isms-risk-management-software",
    translations: {
      en: {
        title: "Best ISMS Risk Management Software 2026 (Free & Paid Compared)",
        metaTitle: "Best ISMS Risk Management Software 2026 - Free & Paid Compared",
        metaDescription: "We compared 10+ ISMS risk management tools for ISO 27001. See pricing, features, and the best free option to replace your risk register spreadsheet.",
        excerpt: "Choosing the right ISMS risk management software can make or break your ISO 27001 implementation. This guide compares the best tools for 2026, explains what features matter most, and shows why purpose-built solutions outperform generic spreadsheets.",
        content: `
## What Is ISMS Risk Management Software?

ISMS risk management software is a specialized tool designed to help organizations identify, assess, treat, and monitor information security risks within the framework of an Information Security Management System. Unlike generic project management or GRC platforms, ISMS risk management software is built around the specific requirements of standards like ISO 27001:2022.

At its core, this type of software replaces the manual, error-prone process of managing risks in spreadsheets. It provides structured workflows for risk identification, quantitative and qualitative risk assessment, treatment planning, control mapping, and audit-ready reporting. The goal is to make risk management a living, continuous process rather than a one-time compliance exercise.

For organizations pursuing ISO 27001 certification, the right ISMS risk management tool directly supports Clause 6.1 (Actions to Address Risks and Opportunities), Clause 8.2 (Information Security Risk Assessment), and Clause 8.3 (Information Security Risk Treatment). It also helps you maintain the risk register, Statement of Applicability, and risk treatment plans that auditors expect to see.

## Why Spreadsheets Fall Short for ISMS Risk Management

Many organizations start their ISO 27001 journey using Excel or Google Sheets for risk management. While spreadsheets are flexible and familiar, they create serious problems as your ISMS matures.

**Version control chaos.** When multiple people edit a shared spreadsheet, you lose track of who changed what and when. ISO 27001 requires an audit trail for risk decisions, and spreadsheets simply cannot provide this reliably.

**No automated scoring.** Calculating inherent and residual risk scores, mapping controls to risks, and updating treatment status all require manual work in a spreadsheet. One formula error can cascade across your entire risk register.

**Disconnected from controls.** In a spreadsheet, the link between a risk, its treatment plan, and the controls that mitigate it exists only in your head or in a separate tab. Purpose-built software maintains these relationships automatically.

**Reporting is painful.** Generating a risk heatmap, treatment progress report, or management review summary from a spreadsheet means hours of manual formatting every time. ISMS software generates these reports in seconds.

**Scalability issues.** A spreadsheet with 20 risks is manageable. A spreadsheet with 200 risks across multiple business processes, linked to assets and threats, becomes unworkable. Real ISMS software is built to scale.

The bottom line: spreadsheets work for getting started, but they become a liability once your ISMS is operational. The cost of maintaining them exceeds the cost of proper tooling within months.

## What to Look for in ISMS Risk Management Software

Not all risk management tools are created equal. When evaluating ISMS risk management software for ISO 27001, prioritize these capabilities:

### Risk Register with ISO 27001 Structure

Your tool should support a risk register that captures risk ID, title, description, threat, vulnerability, affected assets, likelihood, impact, inherent risk score, existing controls, residual risk score, risk owner, and treatment status. This structure maps directly to what auditors expect.

### Configurable Risk Assessment Methodology

ISO 27001 does not mandate a specific risk assessment methodology, so your tool should support your chosen approach. Look for configurable likelihood and impact scales (3x3, 4x4, or 5x5 matrices), customizable scoring formulas, and the ability to define your own risk appetite thresholds.

### Control Mapping to Annex A

The ability to link controls to specific risks and map them back to ISO 27001:2022 Annex A controls is essential. This linkage feeds directly into your Statement of Applicability and demonstrates to auditors that your control selection is risk-based.

### Treatment Planning and Tracking

Each risk needs a treatment plan with clear actions, deadlines, owners, and completion tracking. Your tool should support the four ISO 27001 treatment options: mitigate, accept, transfer, and avoid.

### Audit Trail and History

Every change to a risk, control, or treatment should be logged automatically. This audit trail is critical during certification audits and surveillance audits. Auditors want to see that risk decisions are documented and traceable.

### Asset and Threat Libraries

Built-in asset registers and threat libraries accelerate risk identification. Instead of starting from scratch, you can select from common information asset types and threat categories relevant to your industry.

### Reporting and Dashboards

Management review requires clear, concise risk reports. Look for tools that generate risk heatmaps, treatment progress dashboards, trend analysis, and exportable reports in PDF, Excel, or Word format.

## Comparing ISMS Risk Management Tools for 2026

Here is an honest comparison of the main options available for ISMS risk management in 2026:

### Spreadsheets (Excel or Google Sheets)

**Cost:** Free to low.
**Pros:** Familiar, flexible, no learning curve.
**Cons:** No audit trail, no automated scoring, no control mapping, no real-time collaboration safeguards, reporting is manual and painful. Not suitable for organizations with more than 50 risks or multiple stakeholders.
**Best for:** Very early-stage exploration or organizations with minimal risk scope.

### Generic GRC Platforms

**Cost:** $500 to $5,000+ per month.
**Pros:** Comprehensive compliance coverage across multiple frameworks, enterprise-grade features, integrations with IT infrastructure.
**Cons:** Expensive, complex to set up, overkill for organizations focused primarily on ISO 27001. Often require consultants to configure. Long implementation timelines measured in months. For organizations looking for [compliance management software](https://copla.com/) that can centralize requirements and streamline compliance workflows, Copla is another option worth considering alongside broader GRC platforms
**Best for:** Large enterprises managing multiple compliance frameworks simultaneously (SOC 2, PCI DSS, HIPAA alongside ISO 27001).

### Standalone Risk Assessment Tools

**Cost:** $50 to $300 per month.
**Pros:** Focused on risk assessment, usually simpler than full GRC platforms.
**Cons:** Often lack ISO 27001-specific features like Annex A control mapping, SoA generation, or treatment tracking. May not include audit trail functionality. Risk methodology may be fixed rather than configurable.
**Best for:** Organizations that need basic risk assessment without full ISMS workflow support.

### ISO27001KIT Risk Copilot

**Cost:** Free demo, paid plans from $29 per month.
**Pros:** Purpose-built for ISO 27001:2022. Includes full risk register with configurable 5x5 risk matrix, Annex A control mapping, treatment planning and tracking, asset register with CIA classification, threat and vulnerability libraries, business process mapping, complete audit trail, and exportable reports. AI-assisted risk identification and control suggestions. No setup required - start immediately.
**Cons:** Focused specifically on ISO 27001 (not a multi-framework GRC platform). Best suited for small to mid-sized organizations.
**Best for:** Organizations implementing ISO 27001 who want a dedicated, affordable, and ready-to-use risk management solution without enterprise complexity.

## Key Features That Set Risk Copilot Apart

Risk Copilot was designed from the ground up for ISO 27001 practitioners, not adapted from a generic compliance tool. Here is what makes it different:

### Built-In Risk Framework Configuration

Risk Copilot lets you configure your risk assessment framework to match your methodology. Define your own likelihood and impact scales, set scoring formulas, customize risk appetite thresholds with color coding, and create a risk matrix that reflects your organization's context. This flexibility means you are not forced into a one-size-fits-all approach.

### Asset-Based Risk Identification

Start with your information assets and work outward to identify risks. The built-in asset register supports classification by confidentiality, integrity, and availability (CIA triad), and links assets to business processes. When you create a risk, you can associate it with specific assets and threats from the built-in threat library.

### End-to-End Treatment Workflow

Every risk in the register can have one or more treatment plans. Each treatment includes an action plan, assigned owner, due date, priority level, and completion percentage. The dashboard shows treatment progress at a glance, so management reviews become straightforward.

### Automatic Audit Trail

Every action in Risk Copilot is logged automatically. Risk creation, score changes, treatment updates, control assignments - everything is timestamped and attributed to a user. During your certification audit, you can pull up the complete history of any risk decision in seconds.

### Export Everything

Generate professional reports in Excel, PDF, or Word format. The risk register export includes all fields, scores, and linked controls. Dashboard reports provide the visual summaries that management and auditors expect. No manual formatting required.

### AI-Powered Risk Identification

Risk Copilot integrates AI to help identify risks you might miss. Based on your [asset inventory](/controls/a-5-9-inventory-of-information-and-other-associated-assets) and industry context, the AI suggests relevant threats, vulnerabilities, and potential risk scenarios. You review and accept the suggestions that apply to your organization.

## How to Get Started with ISMS Risk Management

Implementing ISMS risk management software does not need to be complicated. Here is a practical path:

1. **Define your methodology.** Before selecting a tool, decide on your risk assessment approach: qualitative, semi-quantitative, or quantitative. Define your likelihood and impact scales. Most organizations start with a 5x5 matrix.

2. **Build your asset inventory.** List your information assets, classify them by CIA, and assign owners. This is the foundation of risk-based thinking in ISO 27001.

3. **Identify risks.** For each asset, consider what threats and vulnerabilities could compromise its confidentiality, integrity, or availability. Use threat libraries and AI suggestions to ensure coverage.

4. **Assess and score risks.** Rate each risk for likelihood and impact. Calculate inherent risk scores. This gives you a prioritized view of your risk landscape.

5. **Plan treatments.** For risks above your appetite threshold, define treatment plans. Assign owners, set deadlines, and track progress.

6. **Map controls.** Link your controls to the risks they address and map them to Annex A. This feeds your Statement of Applicability.

7. **Review and report.** Generate dashboard reports for management review. Use the audit trail to demonstrate continuous improvement.

## Try Risk Copilot Free

If you are looking for ISMS risk management software that is purpose-built for ISO 27001, affordable, and ready to use today, [try Risk Copilot free](/risk-management). No credit card required. You will have a working risk register, configured risk framework, and your first risks assessed within 30 minutes.

For a complete ISO 27001 implementation toolkit including 46+ document templates, 11 free tools, and the Risk Copilot platform, explore the [ISO 27001 Document Pack](/documents).

[Start your free risk assessment now](/risk-management)
`
      },
      fr: {
        title: "Meilleur logiciel de gestion des risques SMSI - Outils gratuits et payants pour 2026",
        metaTitle: "Meilleur logiciel de gestion des risques SMSI pour 2026",
        metaDescription: "Comparez les meilleurs logiciels de gestion des risques SMSI pour ISO 27001. Outils gratuits et payants avec registre des risques et rapports.",
        excerpt: "Choisir le bon logiciel de gestion des risques SMSI peut determiner le succes de votre implementation ISO 27001. Ce guide compare les meilleurs outils pour 2026.",
        content: `Ce contenu sera bientot disponible en francais.`
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-04-15",
    updatedDate: "2026-04-15",
    category: "Risk Management",
    tags: ["isms risk management software", "isms risk register tool", "isms risk assessment tool", "iso 27001 risk management software", "information security risk management tool", "risk copilot"],
    readTime: 12,
    featured: true
  },
  {
    slug: "iso-27001-rto-rpo-business-impact",
    translations: {
      en: {
        title: "ISO 27001 RTO and RPO: How to Calculate and Document Business Impact",
        metaTitle: "ISO 27001 RTO and RPO: How to Calculate Recovery Objectives + BIA Guide",
        metaDescription: "Learn how to calculate RTO and RPO for ISO 27001 compliance. Step-by-step guide to business impact analysis with practical examples and documentation tips.",
        excerpt: "Recovery Time Objective (RTO) and Recovery Point Objective (RPO) are critical inputs to your ISO 27001 business continuity planning. This guide explains how to calculate them properly, document your business impact analysis, and align everything with Annex A requirements.",
        content: `
## What Are RTO and RPO in ISO 27001?

Recovery Time Objective (RTO) and Recovery Point Objective (RPO) are two foundational metrics in [business continuity](/controls/a-5-30-ict-readiness-for-business-continuity) planning under ISO 27001. They define how quickly you need to recover from a disruption and how much data loss is acceptable.

**RTO** answers: "How long can this process be down before the impact becomes unacceptable?"

**RPO** answers: "How much data can we afford to lose before the impact becomes unacceptable?"

Under ISO 27001:2022, these metrics directly support the requirements in Clause 8.1 (Operational Planning and Control) and Annex A control [A.5.29](/controls/a-5-29-information-security-during-disruption) (Information Security During Disruption) and A.5.30 (ICT Readiness for Business Continuity).

Getting RTO and RPO wrong means your disaster recovery and [backup](/controls/a-8-13-information-backup) strategies will be misaligned with actual business needs - either over-engineered (wasting money) or under-engineered (creating unacceptable risk).

## Why RTO and RPO Matter for ISO 27001 Certification

Auditors will specifically look for evidence that you have:

1. **Identified critical business processes** and their dependencies on information systems
2. **Assessed the impact** of disruption over time for each process
3. **Defined measurable recovery objectives** (RTO and RPO) based on that impact analysis
4. **Aligned your technical recovery capabilities** with those objectives
5. **Tested and validated** that you can actually meet those objectives

This is not a theoretical exercise. If your RTO for email is 4 hours but your actual recovery capability is 24 hours, you have a documented gap that needs treatment in your risk register.

## Step 1: Identify Critical Business Processes

Start by listing every business process that depends on information systems. Common examples include:

| Business Process | Supporting Systems | Process Owner |
|---|---|---|
| Customer order processing | ERP, payment gateway, CRM | Operations Director |
| Employee payroll | HRIS, banking portal | HR Manager |
| Client service delivery | Project management, email, file storage | Service Delivery Lead |
| Financial reporting | Accounting software, data warehouse | Finance Director |
| Customer support | Ticketing system, knowledge base, phone | Support Manager |

For each process, identify:
- What information systems it depends on
- What data it creates or consumes
- Who owns the process
- What upstream and downstream processes it affects

The [ISMS Scope Builder](/tools/isms-scope-builder) on iso27001kit.com can help you map these processes and their boundaries systematically.

## Step 2: Conduct the Business Impact Analysis (BIA)

For each critical process, assess the impact of disruption across multiple time horizons. Impact categories typically include:

### Financial Impact

Estimate direct revenue loss, penalty costs, and recovery costs at each time interval:

| Downtime Duration | Order Processing | Payroll | Client Delivery | Financial Reporting |
|---|---|---|---|---|
| 1 hour | $5,000 | Minimal | $2,000 | Minimal |
| 4 hours | $20,000 | Minimal | $10,000 | Minimal |
| 1 day | $100,000 | Moderate | $50,000 | $10,000 |
| 3 days | $300,000 | High | $200,000 | $50,000 |
| 1 week | $700,000 | Critical | $500,000 | $200,000 |

### Operational Impact

Consider the cascading effects: if order processing is down, does shipping stop? Does customer service get overwhelmed?

### Reputational Impact

How quickly does a disruption become visible to customers, partners, or regulators? Social media amplifies incidents rapidly.

### Legal and Regulatory Impact

Are there contractual SLAs? Regulatory reporting deadlines? Data protection notification requirements under GDPR (72 hours)?

## Step 3: Calculate RTO for Each Process

Your RTO is the maximum tolerable downtime before impact crosses from "manageable" to "unacceptable." This threshold is a business decision, not a technical one.

**Practical approach:**

1. Look at your BIA impact table
2. Identify where impact moves from "moderate" to "high" or "critical"
3. Set your RTO at or below that threshold
4. Validate with the process owner

**Example:** If order processing losses become unacceptable at the 4-hour mark, your RTO is 4 hours. This means your recovery solution must be able to restore that system within 4 hours.

### Common RTO Ranges

| Process Criticality | Typical RTO Range |
|---|---|
| Mission-critical (revenue-generating) | 1-4 hours |
| Important (internal operations) | 4-24 hours |
| Normal (supporting functions) | 1-3 days |
| Low priority (non-essential) | 3-7 days |

## Step 4: Calculate RPO for Each Process

Your RPO determines how frequently you need to back up data. If your RPO is 1 hour, you cannot rely on daily backups - you need near-real-time replication or hourly snapshots.

**Key question:** "If this system failed right now, how far back could we roll the data without causing unacceptable harm?"

**Example:** For a transactional database processing customer orders, losing 24 hours of data might mean re-entering hundreds of orders manually. An RPO of 1 hour is more realistic, requiring at minimum hourly backups or database replication.

### Common RPO Ranges

| Data Criticality | Typical RPO | Backup Method Required |
|---|---|---|
| Zero tolerance | 0 (real-time) | Synchronous replication |
| Near-zero | 15-60 minutes | Asynchronous replication, frequent snapshots |
| Standard | 4-24 hours | Scheduled backups (multiple daily) |
| Relaxed | 24-72 hours | Daily backups |

## Step 5: Document Your Recovery Objectives

Your documentation should include:

1. **Business Impact Analysis report** - summarizing the analysis above
2. **Recovery objectives table** - listing RTO and RPO for each critical process
3. **Gap analysis** - comparing objectives against current capabilities
4. **Treatment plan** - addressing any gaps identified

### Recovery Objectives Summary Table

| Process | RTO | RPO | Current Recovery Capability | Gap? |
|---|---|---|---|---|
| Order Processing | 4 hours | 1 hour | 8 hours / daily backup | Yes |
| Payroll | 24 hours | 24 hours | 48 hours / daily backup | Yes |
| Client Delivery | 8 hours | 4 hours | 4 hours / 4-hour snapshots | No |
| Customer Support | 4 hours | 1 hour | 2 hours / real-time replication | No |

Any gap between your defined objective and your actual capability is a risk that must be recorded in your risk register and treated.

## Step 6: Align with ISO 27001 Annex A Controls

Several Annex A controls directly relate to RTO/RPO:

- **A.5.29 - Information Security During Disruption**: Requires maintaining information security during adverse situations
- **A.5.30 - ICT Readiness for Business Continuity**: Requires ICT continuity planning based on BIA results
- **A.8.13 - Information Backup**: Requires backup policies aligned with recovery objectives
- **[A.8.14](/controls/a-8-14-redundancy-of-information-processing-facilities) - Redundancy of Information Processing Facilities**: Requires redundancy to meet availability requirements

Your Statement of Applicability should reference these controls and document how your RTO/RPO analysis supports their implementation.

## Step 7: Test and Validate

Defining RTO and RPO means nothing if you cannot meet them. ISO 27001 requires testing:

1. **Tabletop exercises** - walk through recovery scenarios with stakeholders
2. **Technical recovery tests** - actually restore systems from backups and measure time
3. **Full failover tests** - switch to backup systems and validate functionality

Document test results and compare against your defined objectives. Any shortfall needs a corrective action plan.

### Testing Schedule

| Test Type | Frequency | Scope |
|---|---|---|
| Tabletop exercise | Quarterly | All critical processes |
| Backup restoration test | Monthly | Rotating systems |
| Full failover test | Annually | Mission-critical systems |

## Common Mistakes to Avoid

**Setting RTO/RPO without business input.** These are business decisions, not IT decisions. The CTO should not define acceptable downtime for sales - the VP of Sales should.

**Using the same RTO/RPO for everything.** Different processes have different criticality. Treating everything as mission-critical wastes resources; treating everything as low-priority creates unacceptable risk.

**Confusing RTO with actual recovery time.** Your RTO is the maximum acceptable time. Your actual capability should be lower to provide a safety margin.

**Not testing recovery.** An untested recovery plan is not a plan - it is a hope.

**Ignoring dependencies.** If your CRM depends on your email server, your CRM's RTO cannot be shorter than your email server's RTO.

## Start Your Business Impact Analysis Today

Calculating RTO and RPO does not need to be complicated if you follow a structured approach. Use the [RTO/RPO and BIA Helper](/tools/rto-rpo-helper) on iso27001kit.com to calculate recovery objectives for your critical processes, assess business impact at different time intervals, and generate a documented BIA summary ready for your auditor.

Combined with the [ISO 27001 Document Pack](/documents), which includes a complete Business Continuity Policy and Disaster Recovery Plan template, you will have everything you need to satisfy Clause 8.1 and the relevant Annex A controls.

[Start your BIA analysis now - free](/tools/rto-rpo-helper)
`
      },
      fr: {
        title: "ISO 27001 RTO et RPO : Comment calculer et documenter l'impact sur l'activite",
        metaTitle: "ISO 27001 RTO et RPO : Guide de calcul des objectifs de reprise",
        metaDescription: "Apprenez a calculer le RTO et le RPO pour la conformite ISO 27001. Guide etape par etape pour l'analyse d'impact sur l'activite.",
        excerpt: "Le RTO et le RPO sont des metriques fondamentales pour la continuite d'activite ISO 27001. Ce guide explique comment les calculer et documenter votre analyse d'impact.",
        content: `Ce contenu sera bientot disponible en francais.`
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-04-08",
    updatedDate: "2026-04-08",
    category: "Compliance",
    tags: ["iso 27001", "rto", "rpo", "business continuity", "business impact analysis", "disaster recovery"],
    readTime: 14,
    featured: false
  },
  {
    slug: "iso-27001-business-continuity-policy-template",
    translations: {
      en: {
        title: "ISO 27001 Business Continuity Policy Template: Free Download + Guide",
        metaTitle: "ISO 27001 Business Continuity Policy Template: Free Download + Writing Guide",
        metaDescription: "Download a free ISO 27001 business continuity policy template. Learn what to include, how to align with Annex A controls, and avoid common audit findings.",
        excerpt: "A business continuity policy is a mandatory component of your ISO 27001 ISMS. This guide covers exactly what your policy needs to include, provides a professional template structure, and explains how to align with Annex A.5.29 and A.5.30.",
        content: `
## Why You Need a Business Continuity Policy for ISO 27001

ISO 27001:2022 requires organizations to maintain information security during adverse situations and ensure [ICT readiness for business continuity](/controls/a-5-30-ict-readiness-for-business-continuity). Annex A controls [A.5.29](/controls/a-5-29-information-security-during-disruption) and A.5.30 make this explicit, and Clause 8.1 requires operational planning that covers continuity scenarios.

Without a documented business continuity policy, you will fail your certification audit. But more importantly, without a tested continuity plan, a single ransomware attack or infrastructure failure could shut down your business for days or weeks.

This guide walks you through building a business continuity policy that satisfies auditors and actually protects your organization.

## What ISO 27001:2022 Requires

### Clause References

- **Clause 6.1** - Actions to address risks and opportunities, including continuity risks
- **Clause 8.1** - Operational planning and control for continuity processes
- **Clause 8.2** - Information security risk assessment covering availability
- **Clause 10.1** - Continual improvement of continuity arrangements

### Annex A Controls

- **A.5.29 - Information Security During Disruption**: The organization shall plan how to maintain information security at an appropriate level during disruption
- **A.5.30 - ICT Readiness for Business Continuity**: ICT readiness shall be planned, implemented, maintained, and tested based on business continuity objectives
- **[A.5.2](/controls/a-5-2-information-security-roles-and-responsibilities) - Information Security Roles and Responsibilities**: Must include continuity responsibilities
- **[A.8.13](/controls/a-8-13-information-backup) - Information Backup**: Backup copies shall be maintained and regularly tested
- **[A.8.14](/controls/a-8-14-redundancy-of-information-processing-facilities) - Redundancy of Information Processing Facilities**: Sufficient redundancy to meet availability requirements

## Business Continuity Policy Structure

A compliant business continuity policy typically includes the following sections:

### 1. Purpose and Scope

Define what the policy covers:
- Which business processes are in scope
- Which locations and systems are included
- The relationship between this policy and the overall ISMS

**Example scope statement:** "This policy applies to all information systems, business processes, and personnel within the scope of the ISMS as defined in the ISMS Scope Statement. It covers prevention, detection, response, and recovery from events that could disrupt business operations."

### 2. Roles and Responsibilities

| Role | Responsibility |
|---|---|
| Top Management | Approve the policy, allocate resources, review annually |
| ISMS Manager | Maintain the policy, coordinate testing, report results |
| Business Process Owners | Define RTO/RPO for their processes, participate in BIA |
| IT Operations | Implement and test technical recovery solutions |
| All Employees | Follow continuity procedures, report incidents |
| Crisis Management Team | Coordinate response during actual disruptions |

### 3. Business Impact Analysis Requirements

Your policy should mandate:
- Annual business impact analysis for all critical processes
- Defined RTO and RPO for each critical process
- Impact assessment across financial, operational, reputational, and legal dimensions
- Regular review and update when processes change

Use the [RTO/RPO and BIA Helper](/tools/rto-rpo-helper) to systematically calculate recovery objectives and generate BIA documentation.

### 4. Continuity Strategy

Document your approach to maintaining operations:

**Prevention strategies:**
- Redundant infrastructure for critical systems
- Geographically distributed data centers
- Regular security patching and [vulnerability management](/controls/a-8-8-management-of-technical-vulnerabilities)

**Detection strategies:**
- Monitoring and alerting for system failures
- Automated health checks for critical services
- Incident detection and escalation procedures

**Response strategies:**
- Incident response activation criteria
- Communication protocols (internal and external)
- Decision authority for failover activation

**Recovery strategies:**
- Backup and restoration procedures
- Failover to secondary systems
- Manual workaround procedures for critical processes

### 5. Testing and Exercise Requirements

Your policy must specify testing frequency and scope:

| Test Type | Minimum Frequency | Scope |
|---|---|---|
| Business continuity plan review | Every 6 months | All plans |
| Tabletop exercise | Quarterly | Critical processes |
| Backup restoration test | Monthly | Rotating systems |
| Full failover test | Annually | Primary data center |
| Communication cascade test | Semi-annually | All key contacts |

### 6. Maintenance and Review

- Policy review frequency (at least annual)
- Triggers for unscheduled review (major incidents, organizational changes, new systems)
- Version control and change history
- Distribution and awareness requirements

## Common Audit Findings

### Finding 1: No Business Impact Analysis

Many organizations write a business continuity policy but never conduct the underlying BIA. Without a BIA, your RTO/RPO values are arbitrary, and your recovery strategies may be misaligned with actual business needs.

### Finding 2: Untested Recovery Procedures

A policy that says "backups are taken daily" is worthless if nobody has verified that those backups can be restored within the required timeframe.

### Finding 3: Missing Dependencies

Your continuity plan accounts for your primary application server failing, but not for the authentication server it depends on, the VPN that remote staff need, or the DNS service that routes traffic.

### Finding 4: Outdated Contact Information

Crisis communication lists with former employees, old phone numbers, or personal email addresses that are never checked during emergencies.

### Finding 5: No Integration with Risk Register

Business continuity risks should be tracked in your risk register with defined treatments. If your BIA identifies a gap between RTO and actual recovery capability, that gap is a risk that needs formal treatment.

## How to Write Your Policy Step by Step

### Step 1: Gather Requirements

Review ISO 27001 Clause 8.1, Annex A controls A.5.29 and A.5.30, and any regulatory or contractual requirements that apply to your organization.

### Step 2: Conduct or Update Your BIA

Use structured analysis to identify critical processes, assess impacts, and define recovery objectives. The [RTO/RPO and BIA Helper](/tools/rto-rpo-helper) provides a guided workflow for this.

### Step 3: Draft the Policy

Follow the structure outlined above. Keep the policy concise - detailed procedures belong in separate documents (Disaster Recovery Plan, Incident Response Plan, Crisis Communication Plan).

### Step 4: Define Supporting Procedures

Your policy references but does not contain the detailed "how-to" procedures:
- Disaster Recovery Plan (technical recovery steps)
- Crisis Communication Plan (who contacts whom)
- Business Recovery Plan (manual workaround procedures)
- IT Recovery Procedures (system-specific runbooks)

### Step 5: Get Approval and Communicate

Top management must approve the policy (Clause 5.1). Distribute it to all relevant personnel and include it in your security awareness program.

### Step 6: Test and Improve

Schedule your first tabletop exercise within 30 days of policy approval. Document results and feed lessons learned back into the policy and procedures.

## Download a Professional Template

Writing a business continuity policy from scratch is time-consuming and error-prone. The [ISO 27001 Document Pack](/documents) includes a complete, auditor-approved Business Continuity Policy template along with supporting templates for Disaster Recovery Plans and Incident Response procedures.

All templates are in editable Word format with no watermarks, ready to customize for your organization.

[Get the Document Pack - $99](/documents)
`
      },
      fr: {
        title: "ISO 27001 Politique de continuite d'activite : Modele gratuit + Guide",
        metaTitle: "ISO 27001 Politique de continuite d'activite : Modele + Guide",
        metaDescription: "Telechargez un modele de politique de continuite d'activite ISO 27001. Apprenez quoi inclure et comment s'aligner avec l'Annexe A.",
        excerpt: "Une politique de continuite d'activite est un composant obligatoire de votre SMSI ISO 27001.",
        content: `Ce contenu sera bientot disponible en francais.`
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-04-08",
    updatedDate: "2026-04-08",
    category: "Compliance",
    tags: ["iso 27001", "business continuity", "policy template", "annex a", "disaster recovery"],
    readTime: 13,
    featured: false
  },
  {
    slug: "iso-27001-incident-response-policy-template",
    translations: {
      en: {
        title: "ISO 27001 Incident Response Policy Template: Free Download + Guide",
        metaTitle: "ISO 27001 Incident Response Policy Template (2026) - Free",
        metaDescription: "Free ISO 27001 incident response policy template with severity levels, escalation flow, and Annex A.5.24-A.5.27 mapping. Download and customize in minutes.",
        excerpt: "An incident response policy is essential for ISO 27001 compliance. This guide covers how to structure your incident management process, define escalation procedures, classify severity levels, and document everything auditors expect to see.",
        content: `
## Why Incident Response Is Critical for ISO 27001

ISO 27001:2022 dedicates specific Annex A controls to information security [incident management](/controls/a-5-24-information-security-incident-management-planning-and-preparation). Without a documented [incident response](/controls/a-5-26-response-to-information-security-incidents) process, you cannot demonstrate that your organization can detect, respond to, and learn from security events.

Annex A control A.5.24 (Information Security Incident Management Planning and Preparation) requires you to establish and communicate incident response procedures. Controls [A.5.25](/controls/a-5-25-assessment-and-decision-on-information-security-events) (Assessment and Decision on Information Security Events), A.5.26 (Response to Information Security Incidents), and [A.5.27](/controls/a-5-27-learning-from-information-security-incidents) (Learning from Information Security Incidents) cover the complete incident lifecycle.

This guide walks you through building an incident response policy that satisfies auditors and genuinely protects your organization when incidents occur.

## What Your Incident Response Policy Must Cover

### 1. Purpose and Scope

Define what constitutes a security event versus a security incident:

- **Security event**: Any observable occurrence in a system or network that could indicate a security issue (e.g., failed login attempt, unexpected system restart)
- **Security incident**: A security event that has been assessed and confirmed to compromise confidentiality, integrity, or availability of information

Your policy should cover all information assets within the ISMS scope.

### 2. Incident Classification

Define severity levels with clear criteria:

| Severity | Impact | Examples | Response Time |
|---|---|---|---|
| Critical (P1) | Business operations severely impacted, data breach confirmed | Ransomware, confirmed data exfiltration, complete system compromise | Immediate (within 15 minutes) |
| High (P2) | Significant impact to operations or data security | Successful phishing attack, unauthorized access detected, malware on multiple systems | Within 1 hour |
| Medium (P3) | Limited impact, contained to specific systems | Single system malware, policy violation detected, unsuccessful targeted attack | Within 4 hours |
| Low (P4) | Minimal impact, no data compromise | Spam campaign, port scan detected, single failed login attempt | Within 24 hours |

### 3. Incident Response Phases

#### Phase 1: Detection and Reporting

Document how incidents are detected:
- Automated monitoring and alerting (SIEM, IDS/IPS, endpoint detection)
- User reports through a defined channel (email, ticketing system, phone)
- Third-party notifications (customers, vendors, security researchers)

**Every employee must know how to report a suspected incident.** Your policy should specify a single, clear reporting channel.

#### Phase 2: Triage and Assessment

When an event is reported:
1. Log the initial report with timestamp, reporter, and description
2. Assess whether the event is a confirmed incident
3. Classify severity using the defined criteria
4. Assign an incident handler based on severity and type
5. Notify relevant stakeholders per the escalation matrix

#### Phase 3: Containment

Immediate actions to prevent the incident from spreading:
- **Short-term containment**: Isolate affected systems, block malicious IPs, disable compromised accounts
- **Evidence preservation**: Capture logs, memory dumps, disk images before remediation destroys evidence
- **Communication**: Notify affected parties as required by the escalation matrix

#### Phase 4: Eradication and Recovery

Remove the root cause and restore normal operations:
- Remove malware, patch vulnerabilities, reset compromised credentials
- Restore systems from verified clean [backups](/controls/a-8-13-information-backup)
- Validate system integrity before returning to production
- Monitor closely for signs of re-compromise

#### Phase 5: Post-Incident Review

ISO 27001 specifically requires learning from incidents (A.5.27):
- Conduct a post-incident review within 5 business days of resolution
- Document root cause analysis
- Identify corrective actions to prevent recurrence
- Update risk register if new risks are identified
- Share lessons learned with relevant teams

### 4. Escalation Matrix

| Severity | Incident Handler | Notify | Escalate To (if unresolved) |
|---|---|---|---|
| Critical (P1) | Security Lead | CISO, CEO, Legal, PR | External incident response team |
| High (P2) | Senior Security Analyst | CISO, affected department heads | Security Lead |
| Medium (P3) | Security Analyst | Security Lead | Senior Security Analyst |
| Low (P4) | IT Support | Security team (weekly report) | Security Analyst |

### 5. Communication Procedures

Define communication templates and protocols for:
- Internal stakeholder notifications
- Customer notifications (if data is compromised)
- Regulatory notifications (GDPR requires 72-hour notification for personal data breaches)
- Law enforcement engagement criteria
- Media and public communication (for significant incidents)

### 6. Evidence Handling

Document requirements for:
- Chain of custody procedures
- Log retention requirements (minimum retention period)
- Forensic image creation procedures
- Secure storage of incident evidence
- Destruction procedures after retention period expires

## Incident Response Metrics

Track and report these metrics to demonstrate continual improvement:

| Metric | Target | Measurement |
|---|---|---|
| Mean time to detect (MTTD) | < 24 hours | Time from incident start to detection |
| Mean time to respond (MTTR) | Per severity SLA | Time from detection to containment |
| Mean time to resolve | Per severity SLA | Time from detection to full recovery |
| Incidents by category | Trending down | Monthly count by type |
| Repeat incidents | Zero | Same root cause recurring |
| Post-incident reviews completed | 100% | Reviews conducted within 5 days |

## Common Audit Findings

**No documented process.** Having a policy is not enough - you need evidence that the process is followed consistently. Maintain an incident log with proper records.

**No testing.** Run tabletop exercises at least quarterly to test your response procedures. Document the scenarios, participants, findings, and improvements.

**Incomplete post-incident reviews.** Every incident (not just critical ones) should have a documented review. This is how you demonstrate learning (A.5.27).

**Missing legal and regulatory requirements.** Your policy must reference applicable breach notification requirements (GDPR, HIPAA, contractual obligations).

## Build Your Incident Response Capability

The [ISO 27001 Document Pack](/documents) includes a complete Incident Response Policy template with pre-built severity classifications, escalation matrices, and communication templates. All documents are in editable Word format with no watermarks.

For organizations using the [Risk Management platform](/risk-manager), security incidents can be tracked directly alongside your risk register, ensuring complete traceability from incident to risk to treatment.

[Get the Document Pack - $99](/documents)
`
      },
      fr: {
        title: "ISO 27001 Politique de reponse aux incidents : Modele gratuit + Guide",
        metaTitle: "ISO 27001 Politique de reponse aux incidents : Modele + Guide",
        metaDescription: "Telechargez un modele de politique de reponse aux incidents ISO 27001.",
        excerpt: "Une politique de reponse aux incidents est essentielle pour la conformite ISO 27001.",
        content: `Ce contenu sera bientot disponible en francais.`
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-04-08",
    updatedDate: "2026-04-08",
    category: "Compliance",
    tags: ["iso 27001", "incident response", "policy template", "security incidents", "annex a"],
    readTime: 14,
    featured: false
  },
  {
    slug: "iso-27001-access-control-policy-template",
    translations: {
      en: {
        title: "ISO 27001 Access Control Policy Template: Free Download + Guide",
        metaTitle: "Access Control Policy Template - ISO 27001 Free Download",
        metaDescription: "Free access control policy template aligned to ISO 27001 Annex A.5.15-A.8.5. Includes RBAC, MFA, joiner-mover-leaver, and review cadence sections.",
        excerpt: "Access control is one of the most heavily audited areas in ISO 27001. This guide covers how to write an access control policy that satisfies Annex A controls A.5.15 through A.8.5, with practical examples and a professional template structure.",
        content: `
## Why Access Control Is Central to ISO 27001

[Access control](/controls/a-5-15-access-control) determines who can access what information and systems, under what conditions. It is arguably the most operationally impactful area of your ISMS because it touches every user, every system, and every piece of data in your organization.

ISO 27001:2022 dedicates numerous Annex A controls to access management:

- **A.5.15** - Access Control
- **[A.5.16](/controls/a-5-16-identity-management)** - Identity Management
- **[A.5.17](/controls/a-5-17-authentication-information)** - Authentication Information
- **[A.5.18](/controls/a-5-18-access-rights)** - Access Rights
- **[A.8.2](/controls/a-8-2-privileged-access-rights)** - Privileged Access Rights
- **[A.8.3](/controls/a-8-3-information-access-restriction)** - Information Access Restriction
- **A.8.5** - Secure Authentication

Auditors pay particular attention to access control because failures here directly enable data breaches, privilege escalation, and unauthorized data modification.

## Access Control Policy Structure

### 1. Access Control Principles

Your policy should establish core principles:

**Least Privilege:** Users receive only the minimum access rights necessary to perform their job functions. No more, no less.

**Need-to-Know:** Access to information is granted only when there is a demonstrated business need.

**Separation of Duties:** Critical functions are divided among different people to prevent fraud and error. No single person should be able to initiate, approve, and execute a sensitive transaction.

**Default Deny:** Access is denied by default. All access must be explicitly granted through the defined provisioning process.

### 2. User Access Management

#### Access Provisioning

Document the process for granting access:

1. Manager submits access request specifying role and required systems
2. System owner approves based on business justification
3. IT provisions access according to the role-based access matrix
4. User receives credentials through secure channel
5. Access is logged in the access management system

#### Access Modification

When users change roles:
1. Manager submits modification request
2. Previous access rights are reviewed - unnecessary access is removed
3. New access rights are provisioned per the new role
4. Changes are logged and auditable

#### Access Revocation

When users leave the organization or no longer need access:
1. HR notifies IT immediately upon termination decision
2. Access is disabled within 4 hours of last working day (or immediately for involuntary termination)
3. All credentials are revoked, including VPN, email, cloud services
4. Shared credentials are rotated
5. Physical access (badges, keys) is collected

### 3. Authentication Requirements

| System Category | Minimum Authentication | MFA Required? | Password Policy |
|---|---|---|---|
| Critical systems (production, financial) | Username + password + MFA | Yes | 14+ characters, complexity, 90-day rotation |
| Business systems (email, CRM, ERP) | Username + password + MFA | Yes | 12+ characters, complexity |
| Internal tools (wiki, project management) | Username + password | Recommended | 10+ characters |
| External-facing services | Username + password + MFA | Yes | 14+ characters, complexity |

#### Password Requirements

- Minimum length: 12 characters (14 for privileged accounts)
- Complexity: combination of uppercase, lowercase, numbers, and special characters
- No reuse of previous 12 passwords
- Account lockout after 5 failed attempts
- Automatic session timeout after 15 minutes of inactivity

### 4. Privileged Access Management

Privileged accounts (administrators, root, service accounts) require additional controls:

- **Separate accounts:** Administrators must use separate privileged accounts for administrative tasks (not their daily user account)
- **Just-in-time access:** Where possible, provide privileged access only when needed and automatically revoke it after use
- **Logging:** All privileged actions must be logged and regularly reviewed
- **Break-glass procedures:** Document emergency access procedures for when normal channels are unavailable
- **Regular review:** Privileged access is reviewed quarterly by system owners

### 5. Access Reviews

Regular access reviews are essential for maintaining the principle of least privilege over time:

| Review Type | Frequency | Scope | Reviewer |
|---|---|---|---|
| User access review | Quarterly | All user accounts and their access rights | System owners |
| Privileged access review | Monthly | All administrative and service accounts | Security team |
| Dormant account review | Monthly | Accounts with no login for 30+ days | IT operations |
| Service account review | Quarterly | All non-human accounts | Application owners |

Document review results, including any access that was modified or revoked, and maintain records for audit evidence.

### 6. Remote Access

With hybrid and remote work, your policy must cover:
- VPN requirements for accessing internal resources
- Approved remote access tools (no shadow IT)
- Device requirements for remote access (company-managed or BYOD with MDM)
- Network segmentation for remote connections
- Geographic restrictions if applicable

### 7. Third-Party Access

Vendor and partner access requires additional scrutiny:
- Business justification for all third-party access
- Time-limited access with defined expiry dates
- Monitoring and logging of all third-party sessions
- NDA and security requirements in contracts
- Separate accounts (never share employee credentials)
- Access review when contracts end

## Implementation Checklist

Use this checklist to verify your access control implementation:

- [ ] Access control policy approved by management
- [ ] Role-based access matrix defined for all systems
- [ ] Provisioning and deprovisioning procedures documented
- [ ] MFA implemented for all critical and external-facing systems
- [ ] Password policy enforced technically (not just documented)
- [ ] Privileged access management procedures in place
- [ ] Quarterly access reviews scheduled and assigned
- [ ] Remote access policy defined and communicated
- [ ] Third-party access procedures documented
- [ ] Access logs retained for audit requirements

## Get a Professional Template

Writing an access control policy from scratch takes significant effort. The [ISO 27001 Document Pack](/documents) includes a complete Access Control Policy template covering all Annex A requirements, plus supporting templates for user access request forms, access review checklists, and privileged access procedures.

All templates are in editable Word format with no watermarks.

[Get the Document Pack - $99](/iso-27001-templates)
`
      },
      fr: {
        title: "ISO 27001 Politique de controle d'acces : Modele gratuit + Guide",
        metaTitle: "ISO 27001 Politique de controle d'acces : Modele + Guide",
        metaDescription: "Telechargez un modele de politique de controle d'acces ISO 27001.",
        excerpt: "Le controle d'acces est l'un des domaines les plus audites en ISO 27001.",
        content: `Ce contenu sera bientot disponible en francais.`
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-04-08",
    updatedDate: "2026-04-08",
    category: "Controls",
    tags: ["iso 27001", "access control", "policy template", "authentication", "privileged access"],
    readTime: 14,
    featured: false
  },
  {
    slug: "iso-27001-acceptable-use-policy-template",
    translations: {
      en: {
        title: "ISO 27001 Acceptable Use Policy Template: Free Download + Writing Guide",
        metaTitle: "ISO 27001 Acceptable Use Policy Template: Free Download + Writing Guide",
        metaDescription: "Download a free ISO 27001 acceptable use policy template. Learn what to include, how to enforce it, and common mistakes that lead to audit findings.",
        excerpt: "An acceptable use policy defines how employees can use organizational IT resources. This guide covers what your AUP must include for ISO 27001 compliance, how to make it enforceable, and provides a complete template structure you can customize.",
        content: `
## What Is an Acceptable Use Policy?

An [Acceptable Use](/controls/a-5-10-acceptable-use-of-information-and-other-associated-assets) Policy (AUP) defines the rules and guidelines for how employees, contractors, and third parties may use your organization's information systems, networks, and data. It is one of the foundational policies required for ISO 27001 compliance.

Under ISO 27001:2022, the AUP supports several Annex A controls:

- **A.5.10** - Acceptable Use of Information and Other Associated Assets
- **[A.5.9](/controls/a-5-9-inventory-of-information-and-other-associated-assets)** - Inventory of Information and Other Associated Assets (by defining how assets may be used)
- **[A.8.1](/controls/a-8-1-user-endpoint-devices)** - User Endpoint Devices
- **[A.8.20](/controls/a-8-20-networks-security)** - Networks Security
- **[A.8.22](/controls/a-8-22-segregation-of-networks)** - [Web Filtering](/controls/a-8-23-web-filtering) (related to acceptable internet use)

Without a clear AUP, employees make their own decisions about what is acceptable - and those decisions often introduce significant security risks.

## Why Your AUP Matters Beyond Compliance

An AUP is not just an audit checkbox. It serves critical operational purposes:

**Legal protection.** If an employee misuses company systems, your ability to take disciplinary action depends on having a clear, communicated policy that they acknowledged.

**Risk reduction.** Shadow IT, personal device usage, unauthorized cloud storage, and risky browsing behavior are all addressed by a well-written AUP.

**Consistent expectations.** New employees need to know the rules from day one. An AUP sets clear boundaries without relying on tribal knowledge.

**Incident response support.** When investigating a security incident, your AUP defines what constitutes normal versus abnormal behavior.

## What to Include in Your AUP

### 1. Scope and Applicability

Define who the policy applies to and what assets it covers:

- All employees (full-time, part-time, temporary)
- Contractors and consultants with access to organizational systems
- Third-party vendors working on-site or remotely
- All company-owned devices, networks, and information systems
- Personal devices used for work purposes (BYOD)

### 2. General Use Principles

Establish overarching principles:

- Company IT resources are provided primarily for business purposes
- Limited personal use may be permitted provided it does not interfere with work, consume excessive resources, or create security risks
- All use is subject to monitoring in accordance with the organization's monitoring policy
- Users are responsible for the security of their credentials and the data they access

### 3. Email and Communication

| Allowed | Not Allowed |
|---|---|
| Business communications via approved email | Sending sensitive data via personal email |
| Using approved messaging platforms | Installing unauthorized communication tools |
| Reasonable personal email during breaks | Mass emailing or chain letters |
| Encrypted email for sensitive data | Sharing credentials via email |

### 4. Internet and Web Usage

- Internet access is provided for business purposes
- Reasonable personal browsing is permitted during breaks
- Prohibited categories: malware distribution sites, hacking tools, illegal content, gambling, excessive streaming
- Downloads must be from approved and trusted sources
- Web-based cloud storage is restricted to approved services only

### 5. Software and Application Usage

- Only authorized and licensed software may be installed
- All software must be approved by IT before installation
- Browser extensions and plugins require IT approval
- SaaS applications must be vetted and approved through the procurement process
- Shadow IT (unauthorized tools) is prohibited

### 6. Data Handling

- Classify data according to the organization's data classification scheme
- Store sensitive data only in approved locations
- Do not copy sensitive data to personal devices or personal cloud storage
- Follow data retention and destruction procedures
- Report suspected data breaches immediately

### 7. Device Usage

**Company-owned devices:**
- Keep devices physically secure (locked when unattended)
- Do not disable security software (antivirus, encryption, MDM)
- Report lost or stolen devices immediately
- Do not connect to untrusted networks without VPN

**Personal devices (BYOD):**
- Must be enrolled in the organization's MDM solution
- Must meet minimum security requirements (encryption, screen lock, current OS)
- Organization may remotely wipe company data from personal devices
- Separate personal and business data using containerization

### 8. Password and Authentication

- Follow the organization's password policy
- Never share passwords or authentication tokens
- Use multi-factor authentication where available
- Report suspected credential compromise immediately
- Do not write passwords on sticky notes or store them in plain text

### 9. Social Media

- Do not disclose confidential company information on social media
- Do not represent personal opinions as company positions
- Do not use company email addresses for personal social media accounts
- Follow brand guidelines when posting about the company
- Report social engineering attempts via social media

### 10. Consequences of Violation

Your AUP must clearly state consequences:
- First violation: Verbal warning and mandatory security awareness refresher
- Second violation: Written warning placed in personnel file
- Serious violation: Suspension of access privileges pending investigation
- Severe violation: Disciplinary action up to and including termination

For violations involving illegal activity, the organization may report to law enforcement.

## Making Your AUP Enforceable

### Get Written Acknowledgment

Every user must sign (physically or digitally) that they have read, understood, and agree to comply with the AUP. This acknowledgment should be:
- Part of the onboarding process for new employees
- Renewed annually for all users
- Updated and re-signed whenever the policy changes materially

### Keep It Readable

A 30-page policy full of legal jargon will not be read by anyone. Keep your AUP:
- Under 10 pages
- Written in clear, non-technical language
- Organized with clear headings and bullet points
- Available in the employee handbook and on the intranet

### Include in Security Awareness Training

Reference the AUP in your security awareness program. Use real examples to illustrate why each rule exists.

### Enforce Consistently

The fastest way to make your AUP worthless is to enforce it selectively. If executives are exempt from the rules, the entire policy loses credibility and legal standing.

## Common Audit Findings

**No evidence of acknowledgment.** Auditors will ask to see signed acknowledgments. Digital signatures with timestamps are preferred over paper records.

**Policy not reviewed annually.** Your AUP should have a review date and version history. Update it when technology or working practices change.

**No coverage of remote/hybrid work.** Post-2020, auditors expect your AUP to address home working, personal device usage, and remote access.

**No consequences defined.** A policy without enforcement mechanisms is a suggestion, not a policy.

## Download a Professional Template

The [ISO 27001 Document Pack](/documents) includes a complete Acceptable Use Policy template that covers all the sections above, formatted for immediate customization and auditor review. No watermarks, fully editable Word format.

[Get the Document Pack - $99](/documents)
`
      },
      fr: {
        title: "ISO 27001 Politique d'utilisation acceptable : Modele gratuit + Guide",
        metaTitle: "ISO 27001 Politique d'utilisation acceptable : Modele + Guide",
        metaDescription: "Telechargez un modele de politique d'utilisation acceptable ISO 27001.",
        excerpt: "Une politique d'utilisation acceptable definit comment les employes peuvent utiliser les ressources informatiques.",
        content: `Ce contenu sera bientot disponible en francais.`
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-04-08",
    updatedDate: "2026-04-08",
    category: "Compliance",
    tags: ["iso 27001", "acceptable use policy", "policy template", "employee security", "annex a"],
    readTime: 13,
    featured: false
  },
  {
    slug: "iso-27001-supplier-risk-management",
    translations: {
      en: {
        title: "ISO 27001 Supplier Risk Management: How to Assess and Control Third-Party Risk",
        metaTitle: "ISO 27001 Supplier Risk Management: Assess & Control Third-Party Risk",
        metaDescription: "Learn how to assess and manage supplier risk for ISO 27001 compliance. Covers vendor evaluation criteria, contractual requirements, and ongoing monitoring.",
        excerpt: "Third-party risk is one of the fastest-growing threat vectors in information security. This guide covers how to build an ISO 27001-compliant supplier risk management process, from initial assessment through ongoing monitoring.",
        content: `
## Why Supplier Risk Management Is Essential for ISO 27001

Your information security is only as strong as your weakest supplier. When you share data with a vendor, give a contractor access to your systems, or rely on a cloud provider for critical infrastructure, their security posture directly affects yours.

ISO 27001:2022 addresses supplier relationships through several Annex A controls:

- **[A.5.19](/controls/a-5-19-information-security-in-supplier-relationships)** - Information Security in Supplier Relationships
- **[A.5.20](/controls/a-5-20-addressing-information-security-within-supplier-agreements)** - Addressing Information Security Within Supplier Agreements
- **[A.5.21](/controls/a-5-21-managing-information-security-in-the-ict-supply-chain)** - Managing Information Security in the ICT Supply Chain
- **[A.5.22](/controls/a-5-22-monitoring-review-and-change-management-of-supplier-services)** - Monitoring, Review and [Change Management](/controls/a-8-32-change-management) of Supplier Services
- **[A.5.23](/controls/a-5-23-information-security-for-use-of-cloud-services)** - Information Security for Use of Cloud Services

These controls require you to identify supplier risks, establish contractual security requirements, and monitor supplier compliance over time.

## Building Your Supplier Risk Assessment Framework

### Step 1: Inventory Your Suppliers

Start by listing every supplier that has access to your information or provides services that affect your information security:

| Supplier | Service | Data Shared | Access Level | Criticality |
|---|---|---|---|---|
| Cloud provider | Infrastructure hosting | All application data | Full infrastructure | Critical |
| CRM vendor | Customer management | Customer PII | Application-level | High |
| Payroll processor | Salary processing | Employee financial data | Data transfer | High |
| Office cleaning | Facility maintenance | Physical access | Physical only | Low |
| IT support | Technical support | System access | Admin access | High |

### Step 2: Classify Suppliers by Risk

Not all suppliers need the same level of scrutiny. Classify them based on:

**Data sensitivity:** What type of data do they access or process?
- Personal data (PII/GDPR-relevant)
- Financial data
- Intellectual property
- General business data
- No data access

**Access level:** What systems or facilities can they access?
- Infrastructure/admin access
- Application-level access
- Data transfer only
- Physical access only
- No access

**Service criticality:** What happens if this supplier fails?
- Business stops (critical dependency)
- Significant degradation
- Minor inconvenience
- No operational impact

### Step 3: Conduct Risk Assessment

For each supplier classified as medium, high, or critical risk, conduct a structured assessment:

**Security questionnaire:** Send a standardized questionnaire covering:
- Information security management system and certifications
- Data protection and privacy practices
- Access control and authentication
- Incident response capabilities
- Business continuity and disaster recovery
- Subcontractor management

**Evidence review:** Request supporting evidence:
- ISO 27001 or SOC 2 certification
- Penetration test results (summary)
- Insurance certificates
- Data processing agreements
- Recent audit reports

**Risk scoring:** Use consistent criteria to score each supplier:

| Factor | Weight | Scoring (1-5) |
|---|---|---|
| Data sensitivity | 30% | 1=none, 5=highly sensitive PII |
| Access level | 25% | 1=none, 5=admin access |
| Service criticality | 25% | 1=replaceable, 5=business-critical |
| Security maturity | 20% | 1=certified, 5=no formal security |

Use the [Supplier Risk Manager](/tools/supplier-risk-manager) on iso27001kit.com to score and track your supplier risk assessments systematically.

### Step 4: Define Contractual Requirements

Every supplier agreement should include information security clauses appropriate to the risk level:

**For all suppliers:**
- Confidentiality obligations
- Data protection requirements
- Right to audit
- Incident notification requirements (within 24-48 hours)
- Termination and data return/destruction clauses

**For high-risk suppliers:**
- Specific security controls required
- Regular security reporting
- Compliance with your information security policy
- Subcontractor approval requirements
- Liability and indemnification for security breaches

**For critical suppliers:**
- Service level agreements with availability targets
- Business continuity and disaster recovery requirements
- Regular penetration testing
- On-site audit rights
- Insurance requirements

### Step 5: Monitor Ongoing Compliance

Supplier risk assessment is not a one-time activity. Establish ongoing monitoring:

| Activity | Frequency | Applies To |
|---|---|---|
| Security questionnaire | Annually | All medium+ risk suppliers |
| Certification verification | Annually | Certified suppliers |
| Service review meeting | Quarterly | Critical suppliers |
| Performance monitoring | Ongoing | All suppliers with SLAs |
| Incident review | Per incident | Any supplier with a security event |
| Full reassessment | Every 2 years | All suppliers |

### Step 6: Manage Supplier Incidents

When a supplier experiences a security incident:

1. Assess the impact on your organization's data and operations
2. Document the incident in your own incident register
3. Evaluate the supplier's response and communication
4. Determine if the incident represents a change in the supplier's risk profile
5. Update your risk register and treatment plans accordingly
6. Consider whether the supplier relationship should continue

## Cloud Service Provider Considerations

Cloud services require special attention under A.5.23. Key considerations:

**Shared responsibility model:** Clearly define which security controls are the provider's responsibility and which are yours. Document this in your Statement of Applicability.

**Data location:** Know where your data is stored and processed. This affects regulatory compliance (GDPR, data sovereignty).

**Exit strategy:** Document how you would migrate away from the cloud provider. What format will your data be exported in? What is the timeline?

**Subprocessors:** Cloud providers use subprocessors. Ensure your contract gives you visibility and approval rights over significant subprocessor changes.

## Common Audit Findings

**No supplier inventory.** Auditors expect a complete list of suppliers with risk classifications. If you cannot show this, you cannot demonstrate compliance with A.5.19.

**Missing contractual clauses.** Contracts signed before your ISMS was established often lack security clauses. Review and update them.

**No ongoing monitoring.** Assessing suppliers once and never revisiting is a common finding. Show evidence of regular reviews.

**Ignoring the supply chain.** Your supplier uses subcontractors who handle your data. A.5.21 requires you to consider the entire chain, not just your direct suppliers.

## Start Managing Supplier Risk Today

Use the [Supplier Risk Manager](/tools/supplier-risk-manager) on iso27001kit.com to assess, score, and monitor your supplier risk. The tool provides structured evaluation criteria, automatic risk scoring, and exportable reports for your audit evidence.

[Assess your supplier risk now - free](/tools/supplier-risk-manager)
`
      },
      fr: {
        title: "ISO 27001 Gestion des risques fournisseurs : Evaluer et controler les risques tiers",
        metaTitle: "ISO 27001 Gestion des risques fournisseurs",
        metaDescription: "Apprenez a evaluer et gerer les risques fournisseurs pour la conformite ISO 27001.",
        excerpt: "Le risque tiers est l'un des vecteurs de menace a la croissance la plus rapide en securite de l'information.",
        content: `Ce contenu sera bientot disponible en francais.`
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-04-08",
    updatedDate: "2026-04-08",
    category: "Risk Management",
    tags: ["iso 27001", "supplier risk", "third-party risk", "vendor management", "supply chain"],
    readTime: 14,
    featured: false
  },
  {
    slug: "iso-27001-scope-statement-examples",
    translations: {
      en: {
        title: "ISO 27001 Scope Statement: 7 Real Examples + Free Template",
        metaTitle: "ISO 27001 Scope Statement Examples 2026 (Free Template)",
        metaDescription: "7 real ISO 27001 scope statement examples auditors accept, plus a free template. Avoid the common mistakes that fail Stage 1 audits.",
        excerpt: "Your ISMS scope statement defines the boundaries of your certification. Get it wrong and you will either over-scope (wasting time and money) or under-scope (failing the audit). This guide provides real examples and a step-by-step approach to defining scope correctly.",
        content: `
## What Is an ISO 27001 Scope Statement?

The ISMS scope statement defines the boundaries and applicability of your Information Security Management System. It specifies which parts of your organization, which locations, which processes, and which information assets are covered by your ISO 27001 certification.

Clause 4.3 of ISO 27001:2022 requires you to determine the scope of the ISMS by considering:
- External and internal issues (Clause 4.1)
- Requirements of interested parties (Clause 4.2)
- Interfaces and dependencies between activities performed by the organization and those performed by other organizations

Your scope statement is one of the first documents an auditor reviews. If it is unclear, too broad, or excludes critical dependencies, your certification audit will have problems before it even starts.

## Why Scope Matters More Than You Think

### Cost Impact

Every additional location, department, or system in scope increases:
- The number of controls you need to implement
- The documentation you need to create
- The number of people who need [security awareness](/controls/a-6-3-information-security-awareness-education-and-training) training
- The duration and cost of your certification audit

A well-defined scope can reduce your certification cost by 40-60% compared to an overly broad scope.

### Audit Implications

Auditors will test whether your scope makes sense. Specifically, they will challenge:
- **Exclusions:** If you exclude a department that processes sensitive data, the auditor will question why
- **Boundaries:** If your scope says "London office" but your London staff access systems hosted in a data center you excluded, the auditor will flag this
- **Interfaces:** If you rely on a shared IT team that is out of scope, the auditor will want to understand how you manage that interface

## What to Include in Your Scope Statement

### 1. Organizational Context

Identify the organization or business unit being certified:
- Legal entity name
- Business activities relevant to the ISMS
- Industry and regulatory environment

### 2. Locations

List every physical and virtual location in scope:
- Office locations
- Data centers (owned or third-party)
- [Remote work](/controls/a-6-7-remote-working) arrangements
- Cloud infrastructure

### 3. Processes

Define which business processes are included:
- Core business processes (those that deliver your products/services)
- Supporting processes (HR, finance, IT operations)
- Management processes (governance, risk management, audit)

### 4. Information Assets

Describe the types of information covered:
- Customer data
- Employee data
- Intellectual property
- Financial records
- System configurations and credentials

### 5. Technologies

List the key technology platforms and systems:
- [Cloud services](/controls/a-5-23-information-security-for-use-of-cloud-services) (AWS, Azure, Google Cloud)
- Business applications (CRM, ERP, email)
- Network infrastructure
- Endpoint devices

### 6. Exclusions and Justifications

Document anything explicitly excluded and why:
- Departments or business units not in scope
- Legacy systems being decommissioned
- Joint ventures or subsidiaries with separate certification

**Every exclusion needs a justification.** "We decided not to include it" is not acceptable. Legitimate reasons include: separate legal entity with its own ISMS, system being decommissioned within 6 months, no processing of sensitive information.

## Scope Statement Examples

### Example 1: SaaS Company

"The scope of the ISMS covers the design, development, deployment, and operation of the [Product Name] SaaS platform, including all supporting processes, infrastructure, and personnel. This includes:

- **Locations:** Head office at [address]; remote workers in [countries]; AWS eu-west-1 and us-east-1 regions
- **Processes:** Software development, platform operations, customer support, sales, HR, and finance
- **Technologies:** AWS infrastructure, GitHub, Jira, Slack, Google Workspace, [Product Name] application
- **People:** All employees (45 FTE), contractors with system access (8)

**Exclusions:** Marketing agency [Name] is excluded as they do not access customer data or internal systems. Marketing content is reviewed before publication by in-scope personnel."

### Example 2: Professional Services Firm

"The scope of the ISMS covers the delivery of consulting services and the management of client information at [Company Name], including:

- **Locations:** [City] office at [address]; client sites where consulting engagements are delivered
- **Processes:** Client engagement delivery, proposal management, knowledge management, HR, IT operations, and finance
- **Technologies:** Microsoft 365, SharePoint, client VPN connections, company-issued laptops
- **People:** All employees (120 FTE), subcontractors with access to client data (15)

**Exclusions:** The [City 2] office is excluded as it operates as a separate legal entity with its own information security program. Interfaces between the two entities are managed through a formal information sharing agreement."

### Example 3: Manufacturing Company

"The scope of the ISMS covers the information systems supporting manufacturing operations, supply chain management, and corporate functions at [Company Name], including:

- **Locations:** Corporate headquarters at [address]; manufacturing facility at [address]; Azure West Europe data center
- **Processes:** Production planning, supply chain management, quality management, ERP operations, HR, finance, and IT
- **Technologies:** SAP ERP, MES system, Azure cloud infrastructure, office network, OT/SCADA systems
- **People:** All corporate and factory staff with access to information systems (350 FTE)

**Exclusions:** The retail distribution network (12 owned stores) is excluded as stores operate standalone POS systems with no connectivity to corporate information systems. Store data is transferred via encrypted batch upload to the ERP system, which is in scope."

## Common Scope Mistakes

**Too broad.** Scoping your entire global organization when certification is only needed for one product line. Start focused and expand later.

**Too narrow.** Excluding shared services (IT, HR) that directly support in-scope processes. If your in-scope department uses the company email system, that email system is in scope.

**Ignoring cloud.** If your data is in AWS, your cloud infrastructure is in scope even though the data center is not yours. You need to demonstrate controls over your cloud configuration.

**Forgetting remote workers.** Post-pandemic, remote and hybrid workers must be explicitly included in scope if they access in-scope systems and data.

**No justification for exclusions.** Every exclusion needs a documented rationale that an auditor will accept.

## Build Your Scope Statement with a Guided Tool

The [ISMS Scope Builder](/tools/isms-scope-builder) on iso27001kit.com guides you through defining your scope systematically - selecting locations, systems, processes, and legal requirements, and generating a formatted scope statement ready for your ISMS documentation.

[Build your scope statement now - free](/tools/isms-scope-builder)
`
      },
      fr: {
        title: "ISO 27001 Declaration de perimetre : Exemples, modeles et guide de redaction",
        metaTitle: "ISO 27001 Declaration de perimetre : Exemples et guide",
        metaDescription: "Apprenez a rediger une declaration de perimetre ISO 27001 avec des exemples reels.",
        excerpt: "Votre declaration de perimetre SMSI definit les limites de votre certification.",
        content: `Ce contenu sera bientot disponible en francais.`
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-04-08",
    updatedDate: "2026-04-08",
    category: "Implementation",
    tags: ["iso 27001", "scope statement", "isms scope", "certification", "implementation"],
    readTime: 14,
    featured: false
  },
  {
    slug: "iso-27001-risk-register-template-excel",
    translations: {
      en: {
        title: "ISO 27001 Risk Register Template: Free Excel Download + Guide",
        metaTitle: "ISO 27001 Risk Register Template: Free Excel Download + Complete Guide",
        metaDescription: "Download a free ISO 27001 risk register template in Excel format. Learn how to structure your risk register, score risks, and track treatment plans.",
        excerpt: "Your risk register is the central record of all identified information security risks, their scores, owners, and treatment plans. This guide covers how to structure it properly, what columns you need, and how to keep it useful throughout your certification journey.",
        content: `
## What Is a Risk Register in ISO 27001?

A risk register is a structured document that records all identified information security risks, their assessment scores, assigned owners, treatment decisions, and current status. It is the operational output of your risk assessment process and one of the most scrutinized documents during certification audits.

Under ISO 27001:2022, Clause 6.1.2 requires you to apply an information security risk assessment process that identifies risks, analyzes them, and evaluates them. Clause 6.1.3 requires you to determine appropriate risk treatment options. Your risk register is where all of this is recorded and maintained.

## Essential Risk Register Columns

A well-structured risk register needs these columns at minimum:

### Identification

| Column | Description | Example |
|---|---|---|
| Risk ID | Unique identifier | RISK-001 |
| Title | Short descriptive name | Unauthorized access to customer database |
| Description | Detailed risk description | An attacker gains unauthorized access to the production customer database through compromised credentials or unpatched vulnerabilities |
| Category | Risk category | [Access Control](/controls/a-5-15-access-control) |
| Asset | Affected asset | Customer database (PostgreSQL) |
| Threat | What could go wrong | External attacker, malicious insider |
| Vulnerability | What weakness enables the threat | Weak password policy, missing [MFA](/controls/a-8-5-secure-authentication), unpatched database |

### Assessment

| Column | Description | Example |
|---|---|---|
| Inherent Likelihood | Likelihood before controls (1-5) | 4 (Likely) |
| Inherent Impact | Impact before controls (1-5) | 5 (Critical) |
| Inherent Risk Score | Likelihood x Impact | 20 (Critical) |
| Existing Controls | Controls already in place | Firewall, basic authentication, quarterly patching |
| Control Effectiveness | How effective are existing controls (1-5) | 2 (Partially effective) |
| Residual Likelihood | Likelihood after controls (1-5) | 3 (Possible) |
| Residual Impact | Impact after controls (1-5) | 5 (Critical) |
| Residual Risk Score | Residual L x Residual I | 15 (High) |

### Treatment

| Column | Description | Example |
|---|---|---|
| Treatment Decision | Accept, mitigate, transfer, avoid | Mitigate |
| Treatment Plan | What actions will reduce the risk | Implement MFA, deploy database activity monitoring, increase patching to monthly |
| Risk Owner | Person accountable for the risk | CTO |
| Treatment Owner | Person responsible for treatment actions | Security Engineer |
| Due Date | When treatment must be completed | 2026-06-30 |
| Status | Current status | In Progress |

### Review

| Column | Description | Example |
|---|---|---|
| Last Review Date | When the risk was last reviewed | 2026-03-15 |
| Next Review Date | When the risk should be reviewed again | 2026-06-15 |
| Notes | Additional context or updates | MFA implementation 60% complete, on track for deadline |

## How to Score Risks

### Likelihood Scale

| Level | Label | Description |
|---|---|---|
| 1 | Rare | Could occur in exceptional circumstances (less than once in 5 years) |
| 2 | Unlikely | Could occur but not expected (once in 2-5 years) |
| 3 | Possible | Might occur at some point (once per year) |
| 4 | Likely | Will probably occur in most circumstances (multiple times per year) |
| 5 | Almost Certain | Expected to occur frequently (monthly or more) |

### Impact Scale

| Level | Label | Financial | Operational | Reputational |
|---|---|---|---|---|
| 1 | Negligible | < $1,000 | No disruption | No external awareness |
| 2 | Minor | $1,000 - $10,000 | Hours of disruption | Limited local awareness |
| 3 | Moderate | $10,000 - $100,000 | Days of disruption | Industry awareness |
| 4 | Major | $100,000 - $1M | Weeks of disruption | National media coverage |
| 5 | Critical | > $1M | Extended outage | International media, regulatory action |

### Risk Matrix

|  | Impact 1 | Impact 2 | Impact 3 | Impact 4 | Impact 5 |
|---|---|---|---|---|---|
| Likelihood 5 | 5 Medium | 10 High | 15 High | 20 Critical | 25 Critical |
| Likelihood 4 | 4 Low | 8 Medium | 12 High | 16 Critical | 20 Critical |
| Likelihood 3 | 3 Low | 6 Medium | 9 Medium | 12 High | 15 High |
| Likelihood 2 | 2 Low | 4 Low | 6 Medium | 8 Medium | 10 High |
| Likelihood 1 | 1 Low | 2 Low | 3 Low | 4 Low | 5 Medium |

## Risk Treatment Options

For each risk above your risk appetite threshold, choose a treatment option:

**Mitigate (Modify):** Implement additional controls to reduce likelihood or impact. This is the most common treatment option.

**Transfer (Share):** Transfer the risk to a third party, typically through insurance or outsourcing to a specialist provider. Note: you transfer the financial impact, not the accountability.

**Avoid (Terminate):** Eliminate the risk by stopping the activity that creates it. Example: if processing a certain type of sensitive data creates unacceptable risk, stop collecting that data.

**Accept (Retain):** Accept the risk without additional treatment because it falls within your risk appetite. Document the rationale and get formal approval from the risk owner.

## Maintaining Your Risk Register

Your risk register is a living document. Schedule regular reviews:

- **Monthly:** Review risks rated Critical and High
- **Quarterly:** Review all risks and treatment progress
- **Annually:** Full risk reassessment
- **Event-driven:** After security incidents, major changes, or new threats

Each review should be documented in your audit trail.

## Use a Professional Risk Management Tool

Managing a risk register in Excel works for small organizations, but it quickly becomes unwieldy as your risk count grows. The [Risk Assessment tool](/tools/risk-assessment) on iso27001kit.com provides a structured risk assessment workflow with automated scoring and a built-in risk matrix.

For organizations ready for a full risk management platform, the [Risk Manager](/risk-manager) offers a complete risk register with treatment tracking, control mapping, and audit trail capabilities.

The [ISO 27001 Document Pack](/documents) includes a professionally formatted Risk Register Template in Excel format, ready to customize for your organization.

[Start your risk assessment now - free](/tools/risk-assessment)
`
      },
      fr: {
        title: "ISO 27001 Registre des risques : Modele Excel gratuit + Guide",
        metaTitle: "ISO 27001 Registre des risques : Modele Excel gratuit + Guide",
        metaDescription: "Telechargez un modele de registre des risques ISO 27001 en format Excel.",
        excerpt: "Votre registre des risques est le document central de tous les risques identifies.",
        content: `Ce contenu sera bientot disponible en francais.`
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-04-08",
    updatedDate: "2026-04-08",
    category: "Risk Management",
    tags: ["iso 27001", "risk register", "risk assessment", "excel template", "risk matrix"],
    readTime: 14,
    featured: false
  },
  {
    slug: "iso-27001-asset-inventory-template",
    translations: {
      en: {
        title: "ISO 27001 Asset Inventory Template: How to Build Your Asset Register",
        metaTitle: "Asset Inventory ISO 27001 Template - Free Register Builder",
        metaDescription: "Free ISO 27001 asset inventory template with CIA ratings, ownership fields, and Annex A.5.9 mapping. Build your asset register in under an hour.",
        excerpt: "An asset inventory is a prerequisite for effective risk assessment under ISO 27001. This guide covers how to identify, classify, and document your information assets with a structured template approach.",
        content: `
## Why You Need an Asset Inventory for ISO 27001

You cannot protect what you do not know you have. An [asset inventory](/controls/a-5-9-inventory-of-information-and-other-associated-assets) is the foundation of your risk assessment process under ISO 27001:2022. Annex A control A.5.9 (Inventory of Information and Other Associated Assets) requires you to identify and maintain an inventory of information assets and their owners.

Without a comprehensive asset inventory:
- Your risk assessment will miss critical assets
- You cannot assign ownership (required by A.5.9)
- You have no basis for classification (required by [A.5.12](/controls/a-5-12-classification-of-information))
- Your controls may not cover all assets in scope

## What Counts as an Information Asset?

Information assets under ISO 27001 extend far beyond hardware and software. You need to inventory:

### Primary Information Assets

| Category | Examples |
|---|---|
| Data and databases | Customer database, employee records, financial data, intellectual property |
| Documents | Policies, procedures, contracts, design documents |
| Software and applications | ERP, CRM, email, custom applications, operating systems |
| Communication systems | Email servers, VoIP, messaging platforms |

### Supporting Assets

| Category | Examples |
|---|---|
| Hardware | Servers, workstations, laptops, mobile devices, network equipment |
| Network infrastructure | Firewalls, switches, routers, load balancers, VPN concentrators |
| [Cloud services](/controls/a-5-23-information-security-for-use-of-cloud-services) | IaaS (AWS, Azure), SaaS (Salesforce, Office 365), PaaS |
| Physical facilities | Data centers, server rooms, offices, archives |

### Human Assets

| Category | Examples |
|---|---|
| Key personnel | System administrators, security staff, key developers |
| Specialized knowledge | Undocumented processes, tribal knowledge, vendor relationships |

## Asset Inventory Template Structure

### Essential Fields

| Field | Description | Example |
|---|---|---|
| Asset ID | Unique identifier | ASSET-001 |
| Asset Name | Descriptive name | Customer CRM Database |
| Asset Type | Category classification | Data/Database |
| Description | Detailed description | PostgreSQL database containing customer contact information, order history, and support tickets |
| Owner | Person accountable for the asset | Sales Director |
| Custodian | Person responsible for day-to-day management | Database Administrator |
| Location | Physical or logical location | AWS RDS eu-west-1 |
| Status | Current status | Active |

### Classification Fields (CIA Ratings)

Rate each asset's sensitivity for Confidentiality, Integrity, and Availability on a scale of 1-5:

| Rating | Confidentiality | Integrity | Availability |
|---|---|---|---|
| 1 - Public | Information is publicly available | Corruption would have no impact | Hours of downtime acceptable |
| 2 - Internal | For internal use only | Corruption would cause minor inconvenience | Same-day restoration acceptable |
| 3 - Confidential | Limited to specific departments | Corruption would cause moderate business impact | 4-hour RTO |
| 4 - Restricted | Need-to-know basis only | Corruption would cause significant business impact | 1-hour RTO |
| 5 - Top Secret | Highly sensitive, regulated data | Any corruption is unacceptable | Near-zero downtime required |

The combined CIA score helps prioritize which assets need the most protection and feeds directly into your risk assessment.

### Lifecycle Fields

| Field | Description |
|---|---|
| Acquisition Date | When the asset was acquired or created |
| Review Date | When the asset was last reviewed |
| End of Life | Planned decommission date |
| Disposal Method | How the asset will be securely disposed of |

## Step-by-Step Process

### Step 1: Define Asset Categories

Create a taxonomy that makes sense for your organization. The categories above are a starting point - adjust them to match your environment.

### Step 2: Identify Assets Systematically

Do not rely on a single person's knowledge. Use multiple sources:

- **IT systems management:** [Configuration management](/controls/a-8-9-configuration-management) databases, cloud console inventories
- **Procurement records:** What was purchased and when
- **Network scanning:** Discover devices and services on your network
- **Interviews:** Talk to department heads about the systems and data they use
- **Process mapping:** Follow business processes and identify the assets they depend on

### Step 3: Assign Ownership

Every asset must have an owner (A.5.9). The owner is accountable for:
- Ensuring the asset is properly classified
- Defining [access controls](/controls/a-5-15-access-control) for the asset
- Ensuring the asset is included in risk assessments
- Approving changes to the asset's security controls

**Common mistake:** Assigning IT as the owner of everything. The business owner of the data should own the asset. IT is the custodian.

### Step 4: Classify Assets

Apply your classification scheme (A.5.12 - Classification of Information) to each asset. This determines the baseline security controls required.

### Step 5: Rate CIA Values

For each asset, rate confidentiality, integrity, and availability requirements. This feeds directly into your risk assessment - higher-value assets face greater consequences from security incidents.

### Step 6: Establish Review Cadence

Your asset inventory must be kept current. Establish a review cycle:
- Full inventory review: Annually
- New asset additions: Ongoing (integrate with procurement and [change management](/controls/a-8-32-change-management))
- Departing asset removal: Triggered by decommission process

## Common Audit Findings

**Incomplete inventory.** Auditors will cross-reference your asset inventory against your network scan, cloud console, and procurement records. Missing assets are a common finding.

**No ownership assigned.** Every asset needs a named owner, not just "IT department."

**Stale entries.** Assets that were decommissioned months ago but still appear as active in the inventory.

**Missing CIA ratings.** Without classification, you cannot demonstrate that controls are appropriate to the asset's sensitivity.

**No cloud assets.** SaaS applications and cloud infrastructure are often missing from asset inventories despite being critical to operations.

## Build Your Asset Inventory Today

The [Asset Inventory tool](/tools/asset-inventory) on iso27001kit.com provides a structured interface for documenting your information assets, assigning owners, rating CIA values, and exporting a formatted asset register for your ISMS documentation.

[Start your asset inventory now - free](/tools/asset-inventory)
`
      },
      fr: {
        title: "ISO 27001 Inventaire des actifs : Modele et guide pour construire votre registre",
        metaTitle: "ISO 27001 Inventaire des actifs : Modele + Guide",
        metaDescription: "Apprenez a construire un inventaire des actifs ISO 27001 avec un modele gratuit.",
        excerpt: "Un inventaire des actifs est un prerequis pour une evaluation efficace des risques sous ISO 27001.",
        content: `Ce contenu sera bientot disponible en francais.`
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-04-08",
    updatedDate: "2026-04-08",
    category: "Implementation",
    tags: ["iso 27001", "asset inventory", "asset register", "asset classification", "cia triad"],
    readTime: 13,
    featured: false
  },
  {
    slug: "iso-27001-implementation-steps-2026",
    translations: {
      en: {
        title: "ISO 27001 Implementation Steps 2026: 12-Phase Checklist",
        metaTitle: "ISO 27001 Implementation Steps 2026 (Free 12-Phase Checklist)",
        metaDescription: "The exact 12 phases to implement ISO 27001 in 2026, with realistic timelines (3-12 months), deliverables per phase, and a free checklist.",
        excerpt: "Implementing ISO 27001 can take 3-12 months depending on your organization's size and maturity. This guide breaks the process into clear phases with specific deliverables, timelines, and practical advice for each step.",
        content: `
## How Long Does ISO 27001 Implementation Take?

The typical implementation timeline depends on your organization:

| Organization Size | Typical Timeline | Key Variables |
|---|---|---|
| Startup (10-50 people) | 3-4 months | Less complexity, fewer stakeholders, faster decisions |
| SMB (50-200 people) | 4-8 months | Multiple departments, more assets, more processes |
| Enterprise (200+ people) | 6-12 months | Multiple locations, complex IT, governance requirements |

These timelines assume dedicated effort. If ISO 27001 is a part-time project competing with other priorities, double the estimates.

## Phase 1: Planning and Preparation (Weeks 1-3)

### Get Management Commitment

ISO 27001 Clause 5.1 requires top management to demonstrate leadership and commitment. This is not optional or cosmetic - without executive support, your implementation will stall at the first obstacle requiring budget, policy enforcement, or organizational change.

Deliverables:
- Management commitment statement
- Budget approval
- Project sponsor identified
- Project team assembled

### Define the Project Scope

Before defining your ISMS scope (that comes later), define what the implementation project will cover and its boundaries.

- Which parts of the organization will be certified?
- What is the target certification date?
- What resources are available (people, budget, tools)?
- Who are the key stakeholders?

### Conduct Initial Gap Assessment

Before building anything, assess your current state against ISO 27001 requirements:

- Review existing policies and procedures
- Assess current security controls against Annex A
- Identify major gaps that need attention
- Prioritize effort based on gap severity

Use the [Gap Analysis tool](/tools/gap-analysis) on iso27001kit.com to systematically assess your compliance against all ISO 27001 requirements and Annex A controls.

## Phase 2: ISMS Foundation (Weeks 3-6)

### Define the ISMS Scope (Clause 4.3)

Document what your ISMS covers:
- Organizational boundaries
- Locations (physical and virtual)
- Processes and services
- Information assets and technologies
- Exclusions with justification

The [ISMS Scope Builder](/tools/isms-scope-builder) can guide you through this process systematically.

### Establish the Information Security Policy (Clause 5.2)

Your top-level [information security policy](/controls/a-5-1-policies-for-information-security) must:
- Be appropriate to the purpose of the organization
- Include a commitment to continual improvement
- Provide a framework for setting information security objectives
- Be communicated to all employees
- Be available as documented information

### Define Roles and Responsibilities (A.5.2)

Assign clear accountability for information security:
- ISMS Manager (or equivalent role)
- Risk owners for each business area
- System owners for key information assets
- Internal auditor (must be independent of what they audit)
- Management representative

### Build the Document Framework

Establish your documentation structure:
- Policy hierarchy (top-level policy, topic-specific policies, procedures, work instructions)
- Document control procedures (versioning, approval, distribution)
- Record management requirements

The [ISO 27001 Document Pack](/documents) provides 40+ professionally written policy and procedure templates that you can customize, saving weeks of documentation effort.

## Phase 3: Risk Assessment (Weeks 6-10)

### Define Your Risk Methodology (Clause 6.1.2)

Document your approach to risk assessment:
- Risk identification methods
- Likelihood and impact scales
- Risk scoring formula
- Risk acceptance criteria (risk appetite)

### Build the Asset Inventory (A.5.9)

Identify and document all information assets in scope. Use the [Asset Inventory tool](/tools/asset-inventory) for a structured approach.

### Identify and Assess Risks

For each significant asset or process:
1. Identify threats (what could go wrong)
2. Identify vulnerabilities (what weaknesses could be exploited)
3. Assess inherent likelihood and impact
4. Document existing controls
5. Calculate residual risk

Use the [Risk Assessment tool](/tools/risk-assessment) to work through this systematically with a built-in 5x5 risk matrix and automated scoring.

### Determine Risk Treatment

For each risk above your acceptance threshold:
- Select a treatment option (mitigate, transfer, avoid, accept)
- Define specific treatment actions
- Assign owners and deadlines
- Document the risk treatment plan

## Phase 4: Control Implementation (Weeks 8-16)

### Develop the Statement of Applicability (Clause 6.1.3d)

The SoA lists all 93 Annex A controls with:
- Whether each control is applicable
- Justification for inclusion or exclusion
- Implementation status
- Reference to implementing policy or procedure

Use the [SoA Generator](/soa-generator) to build your Statement of Applicability systematically.

### Implement Required Controls

Based on your risk treatment plan, implement the necessary controls. Common areas requiring significant effort:

- **[Access control](/controls/a-5-15-access-control)** - provisioning, deprovisioning, reviews, [MFA](/controls/a-8-5-secure-authentication)
- **Asset management** - inventory, classification, handling
- **Cryptography** - [encryption](/controls/a-8-24-use-of-cryptography) at rest and in transit
- **[Physical security](/controls/a-7-1-physical-security-perimeters)** - access controls, environmental protection
- **Operations security** - [change management](/controls/a-8-32-change-management), monitoring, backup
- **Supplier management** - assessment, contracts, monitoring

### Write Supporting Policies and Procedures

Each implemented control needs documented procedures. Key policies include:
- Access Control Policy
- Acceptable Use Policy
- Incident Response Policy
- Business Continuity Policy
- Data Classification Policy
- Backup and Recovery Policy
- Change Management Policy

## Phase 5: Awareness and Training (Weeks 12-16)

### Security Awareness Program (A.6.3)

All employees must receive information security awareness training:
- General security awareness for all staff
- Role-specific training for IT, developers, managers
- Phishing awareness and social engineering defense
- Policy acknowledgment (especially the AUP)

### Document Competence

Maintain training records showing who received what training and when. Auditors will verify these records.

## Phase 6: Internal Audit and Management Review (Weeks 16-20)

### Conduct Internal Audit (Clause 9.2)

Your internal audit must:
- Cover all ISO 27001 clauses and applicable Annex A controls
- Be conducted by auditors independent of the areas being audited
- Document findings (nonconformities and observations)
- Generate a formal audit report

Use the [Internal Audit Checklist](/tools/internal-audit) to systematically verify compliance across all requirements.

### Conduct Management Review (Clause 9.3)

Top management must review the ISMS at planned intervals, considering:
- Status of actions from previous reviews
- Changes affecting the ISMS
- Nonconformities and corrective actions
- Monitoring and measurement results
- Audit results
- Opportunities for improvement

Document the management review minutes and action items.

## Phase 7: Certification Audit (Weeks 20-24)

### Stage 1 Audit (Document Review)

The certification body reviews your ISMS documentation to verify:
- Scope is properly defined
- Policies and procedures exist
- Risk assessment has been conducted
- Statement of Applicability is complete
- Internal audit and management review have been performed

### Stage 2 Audit (Implementation Verification)

The certification body verifies that your ISMS is implemented and effective:
- Interview employees about security practices
- Review evidence of control implementation
- Test selected controls for effectiveness
- Verify that the ISMS operates as documented

### Address Nonconformities

If the auditor identifies nonconformities:
- **Major:** Must be resolved before certification is granted
- **Minor:** Must be addressed within an agreed timeframe (typically 90 days)
- **Observations:** Recommendations for improvement, no corrective action required

## Plan Your Implementation with a Structured Roadmap

The [Implementation Roadmap](/tools/implementation-roadmap) on iso27001kit.com generates a customized project plan based on your organization size, maturity level, and target certification date, with specific milestones and deliverables for each phase.

[Build your implementation roadmap now - free](/tools/implementation-roadmap)
`
      },
      fr: {
        title: "ISO 27001 Etapes de mise en oeuvre : Guide complet pour 2026",
        metaTitle: "ISO 27001 Etapes de mise en oeuvre 2026 : Guide complet",
        metaDescription: "Guide complet etape par etape pour la mise en oeuvre de l'ISO 27001 en 2026.",
        excerpt: "La mise en oeuvre de l'ISO 27001 peut prendre de 3 a 12 mois selon la taille de votre organisation.",
        content: `Ce contenu sera bientot disponible en francais.`
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-04-08",
    updatedDate: "2026-04-08",
    category: "Implementation",
    tags: ["iso 27001", "implementation", "certification", "roadmap", "2026", "step by step"],
    readTime: 15,
    featured: false
  },
  {
    slug: "iso-27001-gap-analysis-template-free",
    translations: {
      en: {
        title: "ISO 27001 Gap Analysis Template: Free Download + Step-by-Step Guide",
        metaTitle: "ISO 27001 Gap Analysis Template: Free Download + Step-by-Step Guide",
        metaDescription: "Download a free ISO 27001 gap analysis template. Learn how to assess your current compliance status, identify gaps, and build a prioritized action plan.",
        excerpt: "A gap analysis is the essential first step in any ISO 27001 implementation. This guide explains how to systematically assess your organization against every ISO 27001 requirement and Annex A control, identify gaps, and prioritize your remediation efforts.",
        content: `
## What Is an ISO 27001 Gap Analysis?

A gap analysis compares your organization's current information security practices against the requirements of ISO 27001:2022. It identifies where you already comply, where you partially comply, and where you have significant gaps that need to be addressed before certification.

Think of it as a diagnostic - you need to know your starting point before you can plan the journey to certification.

## When Should You Conduct a Gap Analysis?

**Before starting implementation.** The gap analysis informs your project plan, resource allocation, and timeline. Without it, you are estimating effort blindly.

**After significant organizational changes.** Mergers, new products, new locations, or major technology changes can create new gaps.

**Before your certification audit.** A pre-audit gap analysis helps you identify and fix issues before the external auditor finds them.

**Annually as part of continual improvement.** Regular gap analysis ensures you maintain compliance and identify emerging gaps.

## Gap Analysis Methodology

### Step 1: Define Assessment Scope

Your gap analysis should cover the same scope as your planned ISMS:
- ISO 27001 Clauses 4-10 (management system requirements)
- All 93 Annex A controls (to identify applicable controls and their implementation status)

### Step 2: Establish Assessment Criteria

For each requirement, assess using a consistent maturity scale:

| Level | Status | Description |
|---|---|---|
| 0 | Not Started | No awareness or activity |
| 1 | Initial | Ad hoc, undocumented practices |
| 2 | Developing | Partially documented, inconsistently applied |
| 3 | Defined | Documented, communicated, generally followed |
| 4 | Managed | Monitored, measured, and consistently applied |
| 5 | Optimized | Continually improved based on metrics |

For certification, most requirements need to be at Level 3 or above.

### Step 3: Assess Clause Requirements

Work through each ISO 27001 clause systematically:

**Clause 4 - Context of the Organization**
- Have you identified external and internal issues affecting the ISMS?
- Have you identified interested parties and their requirements?
- Is the ISMS scope documented?

**Clause 5 - Leadership**
- Has top management demonstrated commitment?
- Is there an [information security policy](/controls/a-5-1-policies-for-information-security)?
- Are roles and responsibilities assigned?

**Clause 6 - Planning**
- Is there a risk assessment methodology?
- Has a risk assessment been conducted?
- Are information security objectives defined?

**Clause 7 - Support**
- Are adequate resources allocated?
- Is competence documented?
- Is there a [security awareness](/controls/a-6-3-information-security-awareness-education-and-training) program?
- Is documented information controlled?

**Clause 8 - Operation**
- Are operational planning and control processes in place?
- Have risk assessments been performed?
- Have risk treatment plans been implemented?

**Clause 9 - Performance Evaluation**
- Is monitoring and measurement in place?
- Has an internal audit been conducted?
- Has a management review been conducted?

**Clause 10 - Improvement**
- Is there a process for handling nonconformities?
- Are corrective actions tracked?
- Is there evidence of continual improvement?

### Step 4: Assess Annex A Controls

For each of the 93 Annex A controls, assess:
- **Applicability:** Is this control relevant to your organization?
- **Implementation status:** Using the maturity scale above
- **Evidence:** What evidence supports your assessment?
- **Gap description:** If not fully implemented, what specifically is missing?
- **Priority:** How urgently does this gap need to be addressed?

### Step 5: Prioritize Gaps

Not all gaps are equally important. Prioritize based on:

**Risk-based priority:**
- Gaps that expose the organization to high or critical risks - address first
- Gaps in controls that protect the most sensitive assets - address first
- Gaps that affect multiple areas or processes - address first

**Audit-based priority:**
- Mandatory clause requirements (Clauses 4-10) - must be addressed
- Controls marked as applicable in your SoA - must be addressed
- Areas auditors commonly focus on ([access control](/controls/a-5-15-access-control), [incident management](/controls/a-5-24-information-security-incident-management-planning-and-preparation), risk assessment) - address early

**Effort-based priority:**
- Quick wins (low effort, high impact) - do immediately
- Major projects (high effort, high impact) - plan and resource properly
- Nice-to-haves (low effort, low impact) - schedule when convenient

### Step 6: Build the Action Plan

For each identified gap, document:

| Field | Description |
|---|---|
| Gap ID | Unique reference |
| Requirement | ISO 27001 clause or Annex A control |
| Current Status | Maturity level assessment |
| Target Status | Required maturity level |
| Action Required | Specific steps to close the gap |
| Owner | Person responsible for the action |
| Priority | Critical, High, Medium, Low |
| Target Date | When the gap should be closed |
| Resources Needed | Budget, tools, external support |

## What Auditors Look For

When reviewing your gap analysis (or conducting their own Stage 1 assessment), auditors specifically check:

1. **Completeness:** Have you assessed all clauses and applicable Annex A controls?
2. **Objectivity:** Are your assessments realistic, or have you overstated compliance?
3. **Evidence:** Can you demonstrate the status you have assessed?
4. **Action planning:** For identified gaps, do you have a realistic remediation plan?
5. **Progress tracking:** Are you monitoring progress against the plan?

## Common Mistakes

**Being too optimistic.** Rating a control as "Managed" when you have a policy document but no evidence it is followed. Be honest - it is better to identify gaps now than during the certification audit.

**Assessing in isolation.** The gap analysis should involve department heads and process owners, not just the security team. They know the reality of how processes actually work.

**Not documenting evidence.** For each assessment, note what evidence supports your rating. This prepares you for the auditor's evidence requests.

**Skipping the action plan.** A gap analysis without an action plan is just a list of problems. The value is in the prioritized remediation plan.

## Conduct Your Gap Analysis Today

The [Gap Analysis tool](/tools/gap-analysis) on iso27001kit.com guides you through every ISO 27001 clause and Annex A control with a structured assessment workflow. It generates a prioritized gap report with recommendations and an exportable action plan.

Combined with the [Implementation Roadmap](/tools/implementation-roadmap) tool, you can turn your gap analysis results into a phased project plan with realistic timelines and milestones.

[Start your gap analysis now - free](/tools/gap-analysis)
`
      },
      fr: {
        title: "ISO 27001 Analyse des ecarts : Modele gratuit + Guide etape par etape",
        metaTitle: "ISO 27001 Analyse des ecarts : Modele gratuit + Guide",
        metaDescription: "Telechargez un modele d'analyse des ecarts ISO 27001 gratuit.",
        excerpt: "Une analyse des ecarts est la premiere etape essentielle de toute mise en oeuvre ISO 27001.",
        content: `Ce contenu sera bientot disponible en francais.`
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-04-08",
    updatedDate: "2026-04-08",
    category: "Implementation",
    tags: ["iso 27001", "gap analysis", "compliance assessment", "audit preparation", "template"],
    readTime: 14,
    featured: false
  },
  {
    slug: "iso-27001-soa-template-guide",
    translations: {
      en: {
        title: "ISO 27001 Statement of Applicability (SoA) Template: Free Example + Guide",
        metaTitle: "ISO 27001 SoA Template: Free Statement of Applicability Example + Guide",
        metaDescription: "Download a free ISO 27001 Statement of Applicability template. Learn what the SoA must contain, how to justify control inclusion/exclusion, and avoid common audit findings.",
        excerpt: "The Statement of Applicability is one of the most important documents in your ISMS - and one of the most common sources of audit findings. This guide explains exactly what your SoA must contain, how to justify control decisions, and provides a complete template structure.",
        content: `
## What Is a Statement of Applicability?

The Statement of Applicability (SoA) is a mandatory document required by ISO 27001:2022 Clause 6.1.3d. It lists all 93 Annex A controls and, for each one, states whether it is applicable to your organization and provides justification for its inclusion or exclusion.

The SoA serves three critical purposes:

1. **Demonstrates completeness:** It shows that you have considered every control in the standard and made a deliberate decision about each one
2. **Links risk assessment to controls:** It connects your identified risks to the controls that treat them
3. **Defines your control baseline:** It tells auditors exactly which controls they should expect to find implemented

The SoA is typically one of the first documents an auditor requests during a Stage 1 audit. Errors, omissions, or weak justifications in the SoA will generate nonconformities.

## What the SoA Must Contain

### Required Information per ISO 27001

For each of the 93 Annex A controls, your SoA must document:

1. **Control reference and title** (e.g., [A.5.1](/controls/a-5-1-policies-for-information-security) - Policies for Information Security)
2. **Applicability status** (Applicable or Not Applicable)
3. **Justification for the decision** (why included or excluded)
4. **Implementation status** (Implemented, Partially Implemented, Planned, Not Implemented)

### Recommended Additional Information

Most organizations include additional columns for practical value:

| Column | Purpose |
|---|---|
| Implementation method | How the control is implemented (policy, technical, procedural) |
| Responsible person | Who owns the control |
| Evidence reference | Link to policy, procedure, or system that implements the control |
| Risk reference | Which risks this control addresses |
| Notes | Additional context for auditors |

## SoA Template Structure

### Example Entries

**Applicable control:**

| Field | Value |
|---|---|
| Control | [A.5.15](/controls/a-5-15-access-control) - Access Control |
| Applicable | Yes |
| Justification | Access to information systems must be controlled to prevent unauthorized access to customer data and internal systems. Addresses risks RISK-003, RISK-007, RISK-015. |
| Status | Implemented |
| Method | Access Control Policy (POL-007), RBAC in all production systems, quarterly [access reviews](/controls/a-5-18-access-rights) |
| Owner | IT Security Manager |

**Not applicable control:**

| Field | Value |
|---|---|
| Control | [A.7.3](/controls/a-7-3-securing-offices-rooms-and-facilities) - Securing Offices, Rooms and Facilities |
| Applicable | No |
| Justification | The organization operates as a fully remote company with no physical offices or facilities. All infrastructure is cloud-hosted. Employee devices are managed via MDM. |
| Status | N/A |
| Owner | N/A |

### Justification Guidelines

**For applicable controls:** Explain the business need or risk that makes this control necessary. Reference specific risks from your risk register where possible.

**For not applicable controls:** Provide a clear, specific reason why the control does not apply. Generic statements like "not relevant" are insufficient.

Acceptable exclusion reasons:
- The technology or system the control addresses does not exist in your environment
- The risk the control addresses has been treated through alternative means (specify what)
- The activity the control relates to is out of scope (must align with your scope statement)
- Regulatory or contractual requirements supersede the control

**Not acceptable:** "We decided not to implement this" or "Not applicable" without explanation.

## Organizing the 93 Controls

ISO 27001:2022 organizes Annex A controls into four themes:

### Organizational Controls (A.5) - 37 Controls

Covers policies, roles, asset management, access control, supplier relationships, [incident management](/controls/a-5-24-information-security-incident-management-planning-and-preparation), [business continuity](/controls/a-5-30-ict-readiness-for-business-continuity), and compliance. These are the governance and management controls.

### People Controls (A.6) - 8 Controls

Covers screening, employment terms, security awareness, disciplinary process, post-employment responsibilities, remote working, and event reporting.

### Physical Controls (A.7) - 14 Controls

Covers physical security perimeters, entry controls, securing offices, physical security monitoring, protection against environmental threats, working in secure areas, clear desk policy, equipment siting, asset management off-site, storage media, supporting utilities, cabling security, and equipment maintenance.

### Technological Controls (A.8) - 34 Controls

Covers endpoint devices, privileged access, access restriction, secure authentication, capacity management, malware protection, vulnerability management, configuration management, information deletion, data masking, data leakage prevention, monitoring, web filtering, secure coding, and more.

## Common SoA Mistakes

### Mistake 1: Excluding Controls Without Valid Justification

Every exclusion will be questioned by auditors. "We do not do this" is not a justification. You need to explain why the underlying risk does not exist or is addressed another way.

### Mistake 2: Not Linking to Risk Assessment

Your SoA should trace back to your risk assessment. If you identified a risk related to unauthorized access, your SoA should show applicable access control controls with references to that risk.

### Mistake 3: Stale Implementation Status

If your SoA says a control is "Implemented" but the actual implementation is incomplete or broken, the auditor will find a nonconformity. Keep implementation status accurate and up to date.

### Mistake 4: Missing Controls

ISO 27001:2022 has 93 controls. If your SoA shows 80, the auditor will immediately ask about the missing 13. Every control must be listed, even if excluded.

### Mistake 5: Copy-Paste Justifications

Using identical justification text for multiple controls suggests you have not actually thought about each one. Tailor justifications to the specific control and your organization's context.

## Building Your SoA Efficiently

Working through 93 controls manually is tedious and error-prone. The [SoA Generator](/soa-generator) on iso27001kit.com guides you through every Annex A control with:

- Clear descriptions of what each control requires
- Guided applicability assessment
- Implementation status tracking
- Exportable SoA document formatted for auditor review

The tool ensures you do not miss any controls and helps you write appropriate justifications for each decision.

[Generate your Statement of Applicability now - free](/soa-generator)
`
      },
      fr: {
        title: "ISO 27001 Declaration d'applicabilite (DdA) : Modele gratuit + Guide",
        metaTitle: "ISO 27001 Declaration d'applicabilite : Modele gratuit + Guide",
        metaDescription: "Telechargez un modele de declaration d'applicabilite ISO 27001 gratuit.",
        excerpt: "La declaration d'applicabilite est l'un des documents les plus importants de votre SMSI.",
        content: `Ce contenu sera bientot disponible en francais.`
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-04-08",
    updatedDate: "2026-04-08",
    category: "Compliance",
    tags: ["iso 27001", "statement of applicability", "soa", "annex a", "controls"],
    readTime: 14,
    featured: false
  },
  {
    slug: "iso-27001-certification-cost-2026",
    translations: {
      en: {
        title: "ISO 27001 Certification Cost: Complete 2026 Breakdown",
        metaTitle: "ISO 27001 Certification Cost 2026: Complete Breakdown of Fees & Hidden Expenses",
        metaDescription: "How much does ISO 27001 certification really cost in 2026? Complete breakdown of consultant fees, audit costs, tooling, training, and hidden expenses with tips to reduce total spend.",
        excerpt: "ISO 27001 certification costs vary wildly depending on company size, scope, and approach. This complete 2026 breakdown covers every expense category - from consultants to audit bodies to tooling - and shows you where the hidden costs are.",
        content: `
## How Much Does ISO 27001 Certification Actually Cost?

The honest answer: it depends. ISO 27001 certification costs range from under $10,000 for a small startup doing most of the work in-house, to over $200,000 for a large enterprise with complex scope and full consultant engagement.

What makes budgeting difficult is that the certification audit itself is often the smallest line item. The real costs hide in preparation, documentation, tooling, and the ongoing maintenance most organizations underestimate.

This guide breaks down every cost category for 2026, gives you realistic ranges based on company size, and shows you exactly where you can save money without cutting corners on compliance.

## The Five Major Cost Categories

### 1. Consultant Fees

Most organizations hire an ISO 27001 consultant to guide implementation. Costs vary based on the consultant's involvement level:

| Engagement Type | Typical Cost | What You Get |
|---|---|---|
| Full implementation support | $30,000 - $100,000+ | End-to-end guidance, document creation, audit prep |
| Advisory / coaching | $10,000 - $30,000 | Periodic guidance, document review, gap assessment |
| Gap assessment only | $3,000 - $10,000 | Initial assessment with recommendations |
| Document review | $2,000 - $5,000 | Review of existing documentation for compliance |

**Key factors affecting consultant costs:**
- Organization size (number of employees in scope)
- Complexity of IT environment
- Geographic location of the consultant
- Whether they create documents or review yours
- Industry-specific requirements (healthcare, finance, government)

**How to save:** Do the heavy lifting yourself using structured [implementation tools](/tools) and [professionally written templates](/documents), and bring in a consultant only for review and guidance. This can reduce consultant costs by 50-70%.

### 2. Certification Audit Fees

The certification audit is conducted by an accredited certification body (CB) and has two stages:

| Audit Stage | Purpose | Typical Cost |
|---|---|---|
| Stage 1 (Documentation Review) | Review ISMS documentation, confirm readiness | $3,000 - $8,000 |
| Stage 2 (Implementation Audit) | On-site/remote verification of implementation | $5,000 - $15,000 |
| **Total Initial Certification** | | **$8,000 - $23,000** |

After initial certification, you also pay for:

| Ongoing Audit | Frequency | Typical Cost |
|---|---|---|
| Surveillance Audit | Annually (Year 1 and 2) | $3,000 - $8,000 per year |
| Recertification Audit | Every 3 years | $6,000 - $18,000 |

**Key factors affecting audit costs:**
- Number of employees in scope
- Number of physical locations
- Complexity of the ISMS scope
- Whether audits are on-site or remote
- The certification body you choose (prices vary significantly)

**How to save:** Get quotes from at least three accredited certification bodies. Prices can vary by 30-50% for the same scope. Also, a well-prepared organization requires fewer audit days, which directly reduces the fee.

### 3. Tooling and Software Costs

You need tools to build, manage, and maintain your ISMS. Here's what the market looks like in 2026:

| Tool Category | Enterprise GRC Platforms | Mid-Market Solutions | ISO27001KIT |
|---|---|---|---|
| Risk Management | $15,000 - $50,000/yr | $3,000 - $10,000/yr | Included |
| Policy Management | $5,000 - $20,000/yr | $1,000 - $5,000/yr | $99 one-time |
| Gap Analysis | Included in platform | $500 - $2,000 | Included |
| Audit Management | $5,000 - $15,000/yr | $1,000 - $5,000/yr | Included |
| **Total Annual Cost** | **$25,000 - $85,000** | **$5,000 - $22,000** | **$99 + Pro plan** |

Enterprise GRC platforms like OneTrust, Vanta, or Drata charge annual subscriptions that can exceed $50,000 per year for larger organizations. These platforms are powerful but often overkill for companies going through their first certification.

**How to save:** ISO27001KIT provides all the implementation tools you need - [gap analysis](/tools/gap-analysis), [risk assessment](/tools/risk-assessment), [SoA generator](/soa-generator), [internal audit checklists](/tools/internal-audit), and [implementation roadmaps](/tools/implementation-roadmap) - plus a complete [document pack](/documents) with 40+ editable templates for a fraction of the cost of traditional GRC platforms. View our [pricing](/pricing) for details.

### 4. Training and Awareness Costs

ISO 27001 Clause 7.2 and 7.3 require competence and awareness programs. Budget for:

| Training Type | Cost Range | Who |
|---|---|---|
| ISO 27001 Lead Implementer course | $2,000 - $4,000 per person | ISMS manager / project lead |
| ISO 27001 Internal Auditor course | $1,000 - $2,500 per person | Internal audit team (2-3 people) |
| [Security awareness](/controls/a-6-3-information-security-awareness-education-and-training) training platform | $3 - $10 per employee per year | All staff in scope |
| Specialized technical training | $500 - $3,000 per person | IT and security team |

**Minimum realistic budget:** $5,000 - $15,000 for a small to mid-size organization.

**How to save:** Consider online training options, which are typically 40-60% cheaper than in-person courses. For awareness training, many affordable platforms offer ISO 27001-aligned content.

### 5. Internal Resource Costs

The most frequently underestimated cost category. Someone in your organization needs to lead the implementation project:

| Activity | Estimated Hours | Cost at $75/hr |
|---|---|---|
| Gap analysis and planning | 40 - 80 hours | $3,000 - $6,000 |
| Policy and procedure writing | 80 - 200 hours | $6,000 - $15,000 |
| Risk assessment | 40 - 100 hours | $3,000 - $7,500 |
| Control implementation | 80 - 200 hours | $6,000 - $15,000 |
| Internal audit | 20 - 60 hours | $1,500 - $4,500 |
| Audit preparation | 40 - 80 hours | $3,000 - $6,000 |
| **Total** | **300 - 720 hours** | **$22,500 - $54,000** |

These hours represent the opportunity cost of pulling staff away from their regular responsibilities.

**How to save:** Using structured tools and professional templates can reduce documentation time by 60-70%. The [ISO 27001 Document Pack](/documents) alone eliminates 80-200 hours of policy writing.

## Total Cost Summary by Company Size

| Cost Category | Small (10-50 employees) | Medium (50-250 employees) | Large (250+ employees) |
|---|---|---|---|
| Consultant | $5,000 - $20,000 | $15,000 - $50,000 | $40,000 - $100,000+ |
| Certification Audit | $8,000 - $12,000 | $10,000 - $18,000 | $15,000 - $25,000+ |
| Tooling | $99 - $5,000 | $2,000 - $20,000 | $10,000 - $50,000 |
| Training | $3,000 - $8,000 | $8,000 - $20,000 | $15,000 - $40,000 |
| Internal Resources | $10,000 - $25,000 | $25,000 - $50,000 | $40,000 - $80,000 |
| **Total Year 1** | **$26,000 - $70,000** | **$60,000 - $158,000** | **$120,000 - $295,000** |

## Hidden Costs Most Organizations Miss

### Scope Creep

Starting with a broad scope is the fastest way to blow your budget. Define a tight, defensible [ISMS scope](/tools/isms-scope) from the beginning and expand later if needed.

### Failed Surveillance Audits

If you receive nonconformities during a surveillance audit, you'll need to remediate and potentially schedule a follow-up audit at additional cost. Ongoing maintenance is not optional.

### Technology Changes

Major infrastructure changes (cloud migration, new SaaS tools, M&A activity) trigger risk reassessments and potential control changes that add unplanned work.

### Staff Turnover

When your ISMS manager or key security staff leave, you lose institutional knowledge. Documentation quality and process maturity often degrade, requiring recovery effort.

### Integration Costs

If customers or partners require evidence of specific controls beyond the standard ISO 27001 certificate, you may need additional tooling or processes to provide ongoing compliance reporting.

## How ISO27001KIT Reduces Your Total Cost

ISO27001KIT is designed to minimize certification costs without sacrificing compliance quality:

- **[Document Pack](/documents) ($99):** 40+ professionally written, editable Word templates replace $10,000-$30,000 in consultant document creation fees
- **[Implementation Tools](/tools) (included):** Gap analysis, risk assessment, SoA generator, internal audit checklists, and implementation roadmap - all at no cost
- **[Risk Management Platform](/risk-management):** Purpose-built risk register with audit trails, replacing $5,000-$50,000/year GRC subscriptions
- **Self-service approach:** Reduce consultant dependency by 50-70% using guided tools and pre-built templates

For a small organization, you can realistically achieve certification readiness for under $15,000 in total preparation costs (excluding the audit fee) by using ISO27001KIT tools and templates combined with targeted consultant review.

## Getting Started

1. **Run a [Gap Analysis](/tools/gap-analysis)** to understand your current position
2. **Build your [Implementation Roadmap](/tools/implementation-roadmap)** to plan the project
3. **Download the [Document Pack](/documents)** to accelerate documentation
4. **Complete your [Risk Assessment](/tools/risk-assessment)** using the guided tool
5. **Check our [Pricing page](/pricing)** for the plan that fits your needs

The sooner you start, the sooner you certify. Most organizations achieve certification in 3-6 months with focused effort and the right tools.
`
      },
      fr: {
        title: "Cout de la certification ISO 27001 : repartition complete 2026",
        metaTitle: "Cout de la certification ISO 27001 en 2026 : repartition complete",
        metaDescription: "Combien coute reellement la certification ISO 27001 en 2026 ? Repartition complete des honoraires de consultants, couts d'audit, outillage et depenses cachees.",
        excerpt: "Les couts de certification ISO 27001 varient enormement. Cette repartition complete couvre chaque categorie de depense et montre ou se cachent les couts.",
        content: `
## Combien coute reellement la certification ISO 27001 ?

Ce contenu est disponible en anglais uniquement. Veuillez consulter la version anglaise pour le guide complet.
`
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-04-08",
    updatedDate: "2026-04-08",
    category: "Certification",
    tags: ["iso 27001", "certification cost", "budget", "audit fees", "roi", "2026"],
    readTime: 15,
    featured: false
  },
  {
    slug: "iso-27001-risk-assessment-template-guide",
    translations: {
      en: {
        title: "ISO 27001 Risk Assessment Template: Download + Complete Guide",
        metaTitle: "ISO 27001 Risk Assessment Template: Download + Complete Guide",
        metaDescription: "Learn how to perform an ISO 27001 risk assessment with a step-by-step methodology, risk scoring framework, and a professional template you can use today.",
        excerpt: "A risk assessment is the backbone of your ISO 27001 ISMS. This guide walks you through the complete methodology - from asset identification to risk scoring to treatment planning - with a professional template to get started.",
        content: `
## Why the Risk Assessment Is the Most Important Part of ISO 27001

If there's one thing that can make or break your ISO 27001 certification, it's the risk assessment. Clauses 6.1.2 and 8.2 of the standard explicitly require organizations to define and apply a risk assessment process, and auditors spend more time reviewing this area than almost any other.

A weak risk assessment leads to poorly justified controls, gaps in your Statement of Applicability, and treatment plans that don't address real threats. A strong one creates a defensible, evidence-based foundation for your entire ISMS.

This guide walks you through the complete risk assessment process, explains how to score and prioritize risks, and shows you how to document everything in a format auditors expect to see.

## What ISO 27001 Requires for Risk Assessment

### Clause 6.1.2 - Information Security Risk Assessment

The standard requires your organization to:

1. **Establish and maintain risk assessment criteria** - including risk acceptance criteria and criteria for performing assessments
2. **Ensure repeated assessments produce consistent, valid, comparable results**
3. **Identify risks** - identify the risks associated with the loss of confidentiality, integrity, and availability of information
4. **Analyze risks** - assess the realistic likelihood and potential impact of each risk
5. **Evaluate risks** - compare risk levels against your criteria and prioritize for treatment

### Clause 8.2 - Risk Assessment Execution

You must perform risk assessments at planned intervals or when significant changes occur, and retain documented evidence of the results.

## Step-by-Step Risk Assessment Methodology

### Step 1: Define Your Risk Criteria

Before identifying any risks, establish your scoring framework. This includes:

**Likelihood Scale (1-5):**

| Level | Label | Description |
|---|---|---|
| 1 | Rare | Less than 5% probability in the next 12 months |
| 2 | Unlikely | 5-20% probability |
| 3 | Possible | 20-50% probability |
| 4 | Likely | 50-80% probability |
| 5 | Almost Certain | Greater than 80% probability |

**Impact Scale (1-5):**

| Level | Label | Financial Impact | Operational Impact |
|---|---|---|---|
| 1 | Negligible | Under $10,000 | Minor disruption, recovered in hours |
| 2 | Minor | $10,000 - $50,000 | Some process disruption, recovered in days |
| 3 | Moderate | $50,000 - $250,000 | Significant disruption, weeks to recover |
| 4 | Major | $250,000 - $1M | Severe disruption, months to recover |
| 5 | Catastrophic | Over $1M | Business-threatening, may not recover |

**Risk Score Calculation:**
Risk Score = Likelihood x Impact (range: 1-25)

**Risk Appetite Thresholds:**
- **Low (1-4):** Accept - monitor during periodic reviews
- **Medium (5-9):** Treat - implement controls within 6 months
- **High (10-15):** Treat urgently - implement controls within 3 months
- **Critical (16-25):** Escalate - immediate management attention required

### Step 2: Identify Your Information Assets

You cannot assess risks without knowing what you're protecting. Create an inventory of information assets including:

- **Digital assets:** databases, file servers, cloud storage, SaaS applications
- **Physical assets:** servers, network equipment, laptops, mobile devices
- **People:** key personnel, contractors, third-party administrators
- **Processes:** business processes that handle sensitive information
- **Intangible assets:** intellectual property, reputation, customer trust

Use the [Asset Inventory Tool](/tools/asset-inventory) to build a structured inventory with confidentiality, integrity, and availability ratings for each asset.

### Step 3: Identify Threats and Vulnerabilities

For each asset (or group of related assets), identify:

- **Threats:** what could go wrong? (ransomware, data breach, insider threat, natural disaster, supply chain compromise)
- **Vulnerabilities:** what weaknesses could be exploited? (unpatched systems, weak passwords, lack of [encryption](/controls/a-8-24-use-of-cryptography), no [backup](/controls/a-8-13-information-backup) testing, inadequate training)

A useful framework is the threat-vulnerability-asset triad: each risk should link a specific threat exploiting a specific vulnerability affecting a specific asset.

### Step 4: Assess Inherent Risk

For each identified risk, score the **inherent risk** - the risk level *before* any controls are applied:

- Assign a likelihood score (1-5)
- Assign an impact score (1-5)
- Calculate the inherent risk score (likelihood x impact)

Be realistic and evidence-based. Common mistakes include:
- Scoring every risk as "unlikely" to minimize the register
- Inflating impact scores to justify existing controls
- Using inconsistent criteria across different risk assessors

### Step 5: Evaluate Existing Controls

Document what controls are already in place for each risk and assess their effectiveness:

- **Fully effective:** Control consistently prevents or detects the risk
- **Partially effective:** Control reduces but doesn't fully address the risk
- **Ineffective:** Control exists but provides minimal real protection

### Step 6: Calculate Residual Risk

After accounting for existing controls, calculate the **residual risk** - the remaining risk level:

Residual Risk = Inherent Risk x (1 - Control Effectiveness %)

For example, an inherent risk of 20 with a control that is 60% effective gives a residual risk of 8.

### Step 7: Decide on Risk Treatment

For each risk above your acceptance threshold, choose a treatment option:

- **Mitigate:** Implement additional controls to reduce likelihood or impact
- **Transfer:** Share the risk through insurance or outsourcing
- **Avoid:** Change business processes to eliminate the risk entirely
- **Accept:** Formally accept the residual risk with management approval

Document your treatment decision, the planned actions, the responsible owner, and the target completion date.

## What Your Risk Assessment Template Should Include

A professional risk assessment template needs these fields for each risk entry:

| Field | Purpose |
|---|---|
| Risk ID | Unique identifier for tracking |
| Risk Title | Clear, concise description |
| Asset | The information asset affected |
| Threat | The threat scenario |
| Vulnerability | The exploitable weakness |
| Inherent Likelihood | Score before controls (1-5) |
| Inherent Impact | Score before controls (1-5) |
| Inherent Risk Score | Calculated (L x I) |
| Existing Controls | What's already in place |
| Control Effectiveness | How well controls work |
| Residual Likelihood | Score after controls |
| Residual Impact | Score after controls |
| Residual Risk Score | Calculated (L x I) |
| Treatment Option | Mitigate / Transfer / Avoid / Accept |
| Risk Owner | Person accountable |
| Status | Open / In Treatment / Closed |

## Common Risk Assessment Mistakes

### Mistake 1: Treating It as a One-Time Exercise

ISO 27001 requires periodic reassessment (Clause 8.2). Your risk assessment is a living document that should be reviewed at least annually and whenever significant changes occur.

### Mistake 2: Using Generic Risk Lists

Copying a generic list of IT risks from the internet does not constitute a risk assessment. Your risks must be specific to your organization's context, assets, and threat landscape.

### Mistake 3: No Connection Between Risks and Controls

Every applicable control in your SoA should trace back to one or more risks in your register. If you can't explain *why* a control is implemented, your SoA justification is weak.

### Mistake 4: Inconsistent Scoring

If different team members score the same risk differently, your methodology needs calibration. Run a scoring workshop to align on criteria before the full assessment.

## Using a Dedicated Risk Assessment Tool

While a spreadsheet template gets you started, a dedicated tool provides significant advantages for ongoing risk management:

- **Automatic risk scoring** based on your defined criteria
- **Asset-threat-vulnerability linking** with referential integrity
- **Control mapping** to connect risks with your SoA
- **Treatment plan tracking** with progress monitoring
- **Audit trail** documenting every change for certification evidence

The [Risk Assessment Tool](/tools/risk-assessment) on ISO27001KIT lets you build a complete risk register with automatic scoring, and the [Risk Management platform](/risk-management) provides ongoing risk lifecycle management with dashboards, audit trails, and reporting.

## Download the Risk Assessment Template

The [ISO 27001 Document Pack](/documents) includes a professionally formatted Risk Register Template in Excel format with:

- Pre-built likelihood and impact scales
- Automatic risk score calculation
- Conditional formatting for risk levels
- Treatment tracking columns
- Dashboard summary sheet

You can also [preview the template](/documents) as a watermarked PDF before purchasing.

## Next Steps

1. **Start with your [asset inventory](/controls/a-5-9-inventory-of-information-and-other-associated-assets)** using the [Asset Inventory tool](/tools/asset-inventory)
2. **Run your risk assessment** with the [Risk Assessment tool](/tools/risk-assessment)
3. **Generate your Statement of Applicability** with the [SoA Generator](/soa-generator)
4. **Download the full document pack** from the [Documents page](/documents) for your complete ISMS foundation
`
      },
      fr: {
        title: "Modele d'evaluation des risques ISO 27001 : telechargement + guide complet",
        metaTitle: "Modele d'evaluation des risques ISO 27001 : guide complet",
        metaDescription: "Apprenez a realiser une evaluation des risques ISO 27001 avec une methodologie etape par etape et un modele professionnel.",
        excerpt: "L'evaluation des risques est l'epine dorsale de votre SMSI ISO 27001. Ce guide vous accompagne dans la methodologie complete.",
        content: `
## Pourquoi l'evaluation des risques est la partie la plus importante de l'ISO 27001

Ce contenu est disponible en anglais uniquement. Veuillez consulter la version anglaise pour le guide complet.
`
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-04-08",
    updatedDate: "2026-04-08",
    category: "Risk Management",
    tags: ["iso 27001", "risk assessment", "risk register", "template", "methodology"],
    readTime: 14,
    featured: false
  },
  {
    slug: "iso-27001-policy-templates-guide",
    translations: {
      en: {
        title: "ISO 27001 Policy Templates: The Complete Guide to Downloads and What You Actually Need",
        metaTitle: "ISO 27001 Policy Templates - PDF Previews & Editable Word Pack",
        metaDescription: "Explore ISO 27001 policy templates with free watermarked PDF previews. Get 40+ editable Word documents and 2 Excel registers in the $99 one-time pack.",
        excerpt: "Not all ISO 27001 policy templates are created equal. This guide breaks down exactly which policies the standard requires, what separates a usable template from a compliance liability, and where to get professionally written documents you can actually use.",
        content: `
## Why ISO 27001 Policy Templates Matter

Building an Information Security Management System (ISMS) from scratch is one of the most resource-intensive parts of ISO 27001 certification. At the core of every ISMS sits a collection of mandatory policies and procedures that define how your organization manages information security.

Writing these documents from a blank page is not only time-consuming - it's risky. Miss a required clause reference, use inconsistent terminology, or skip a mandatory section, and your auditor will flag it. That's why most organizations turn to policy templates as a starting point.

But not all templates are equal. Some are generic boilerplate that creates more work than it saves. Others are professionally structured, clause-aligned, and ready to customize for your specific environment.

This guide covers exactly what you need, what to look for, and where to get templates that actually pass audit scrutiny.

## Which Policies Does ISO 27001 Actually Require?

ISO 27001:2022 mandates specific documented information across several clauses. Here are the core policies and documents you must have:

### Mandatory Policies (Clause-Required)

| Document | ISO 27001 Reference |
|---|---|
| [Information Security Policy](/controls/a-5-1-policies-for-information-security) | Clause 5.2 |
| Risk Assessment Methodology | Clause 6.1.2 |
| Risk Treatment Plan | Clause 6.1.3 |
| Statement of Applicability (SoA) | Clause 6.1.3 d) |
| Information Security Objectives | Clause 6.2 |
| Competence Evidence | Clause 7.2 |
| Documented Information Control | Clause 7.5 |
| Operational Planning and Control | Clause 8.1 |
| Risk Assessment Results | Clause 8.2 |
| Risk Treatment Results | Clause 8.3 |
| Monitoring and Measurement Results | Clause 9.1 |
| Internal Audit Program and Results | Clause 9.2 |
| Management Review Results | Clause 9.3 |
| Nonconformities and Corrective Actions | Clause 10.1 |

### Annex A Supporting Policies

Beyond the mandatory documents, Annex A controls require additional supporting policies depending on which controls you declare applicable in your SoA. Common examples include:

- **[Acceptable Use](/controls/a-5-10-acceptable-use-of-information-and-other-associated-assets) Policy** (A.5.10)
- **[Access Control](/controls/a-5-15-access-control) Policy** (A.5.15)
- **Asset Management Policy** ([A.5.9](/controls/a-5-9-inventory-of-information-and-other-associated-assets))
- **[Backup](/controls/a-8-13-information-backup) Policy** (A.8.13)
- **[Business Continuity](/controls/a-5-30-ict-readiness-for-business-continuity) Plan** (A.5.29, A.5.30)
- **Change Management Policy** (A.8.32)
- **Clear Desk and Clear Screen Policy** (A.7.7)
- **Cryptography Policy** (A.8.24)
- **Data Classification Policy** (A.5.12, A.5.13)
- **Incident Response Procedure** (A.5.24, A.5.25, A.5.26)
- **Mobile Device and Teleworking Policy** (A.6.7, A.8.1)
- **Password Policy** (A.5.17)
- **Physical Security Policy** (A.7.1 - A.7.14)
- **Supplier Security Policy** (A.5.19 - A.5.22)

The exact number of policies you need depends on your [Statement of Applicability](/soa-generator). Most organizations end up with 25-45 documents across their ISMS.

## What Makes a Good ISO 27001 Template?

Not every template you find online will save you time. Here's what separates a genuinely useful template from one that creates more problems than it solves:

### 1. Clause Alignment

Every policy should clearly reference the ISO 27001 clauses and Annex A controls it addresses. This makes it easy for auditors to trace requirements to documented procedures and saves you hours during audit preparation.

### 2. Consistent Structure

Professional templates follow a consistent document structure across all policies:
- **Document metadata** (version, owner, approval date, review date)
- **Purpose and scope**
- **Roles and responsibilities**
- **Policy statements**
- **Compliance and enforcement**
- **Review and update schedule**

This consistency demonstrates maturity to auditors and makes your ISMS easier to navigate.

### 3. Editable Format

PDF templates are essentially useless. You need documents in an editable format - ideally Word (.docx) - so you can customize them for your organization's specific context, terminology, and processes.

### 4. Customization Placeholders

Good templates include clear placeholders (like [Organization Name], [ISMS Scope], [Review Frequency]) that guide you through what needs to be customized. Templates without these leave you guessing about what to change.

### 5. Plain Language

Overly legalistic or jargon-heavy templates are harder to implement and less likely to be followed by staff. The best templates use clear, professional language that balances compliance requirements with readability.

## Common Mistakes When Using Policy Templates

### Mistake 1: Using Templates Without Customization

The biggest mistake organizations make is deploying templates as-is without tailoring them to their environment. Auditors will immediately spot generic policies that don't reflect your actual processes, scope, or organizational structure.

### Mistake 2: Ignoring Version Control

Every policy needs a version history, approval record, and scheduled review date. Templates without document control metadata will fail the Clause 7.5 requirements for documented information.

### Mistake 3: Downloading Random Templates from Search Results

Many templates available through generic searches are outdated (still referencing ISO 27001:2013), incomplete, or poorly structured. Using these as your foundation means rebuilding them later when issues surface during the audit.

### Mistake 4: Having Too Many or Too Few Policies

Some organizations create a separate document for every conceivable topic, leading to an unmanageable document library. Others try to cram everything into a single policy. The right approach is to align your document structure with your SoA and organizational complexity.

## How to Customize Templates for Your Organization

Follow this process to turn templates into audit-ready documents:

### Step 1: Run a Gap Analysis

Use a [gap analysis tool](/tools/gap-analysis) to identify which clauses and controls you need to address. This tells you exactly which policies and procedures are required for your scope.

### Step 2: Define Your ISMS Scope

Before customizing any policy, clearly define your [ISMS scope](/tools/isms-scope). Every policy should reference and align with this scope statement.

### Step 3: Replace All Placeholders

Go through each template systematically and replace every placeholder with your organization's specific information: company name, department names, responsible roles, review frequencies, and technical details.

### Step 4: Align with Existing Processes

Where your organization already has processes in place (HR onboarding, change management, incident response), make sure the policy templates reflect those existing processes rather than introducing contradictory procedures.

### Step 5: Get Formal Approval

ISO 27001 Clause 5.2 requires top management to establish and approve the information security policy. Make sure all policies go through your formal approval process and that this approval is documented.

### Step 6: Schedule Reviews

Set review dates for every policy. Most organizations review policies annually, but some high-impact policies (like incident response) may warrant more frequent reviews.

## Where to Get Professional ISO 27001 Templates

The [editable ISO 27001 template pack](/iso-27001-templates) from ISO27001KIT includes 40+ professionally written policies, procedures, and templates in clean, editable Word format. Every document is:

- Aligned to ISO 27001:2022 clauses and Annex A controls
- Written in clear, professional English
- Structured with consistent formatting and document control metadata
- Designed to be customized for any organization size or industry
- Available for instant download with no watermarks

You can [preview every document](/documents) as a watermarked PDF before purchasing, so you know exactly what you're getting.

## Pairing Templates with Implementation Tools

Policy templates are just one piece of the puzzle. To build a complete ISMS efficiently, pair your document pack with:

- **[Gap Analysis Tool](/tools/gap-analysis)** - identify which documents you need
- **[SoA Generator](/soa-generator)** - define your control applicability
- **[Risk Assessment Tool](/tools/risk-assessment)** - build your risk methodology
- **[Internal Audit Checklist](/tools/internal-audit)** - verify your implementation
- **[Implementation Roadmap](/tools/implementation-roadmap)** - plan your project timeline

## Start Building Your ISMS Today

The fastest path to ISO 27001 certification starts with a solid document foundation. Browse the complete [Document Pack](/documents) to preview every template, or start with the [Gap Analysis Tool](/tools/gap-analysis) to identify exactly which policies your organization needs.
`
      },
      fr: {
        title: "Modeles de politiques ISO 27001 : guide complet des telechargements et de ce dont vous avez reellement besoin",
        metaTitle: "Modeles de politiques ISO 27001 : guide complet",
        metaDescription: "Decouvrez quels modeles de politiques ISO 27001 sont necessaires, ce qui fait un bon modele et ou telecharger des documents professionnels editables pour votre SMSI.",
        excerpt: "Ce guide detaille les politiques requises par la norme ISO 27001, ce qui distingue un modele utilisable d'un risque de conformite, et ou obtenir des documents professionnels.",
        content: `
## Pourquoi les modeles de politiques ISO 27001 sont importants

Ce contenu est disponible en anglais uniquement. Veuillez consulter la version anglaise pour le guide complet.
`
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-04-07",
    updatedDate: "2026-04-07",
    category: "Implementation",
    tags: ["iso 27001", "policy templates", "isms", "documentation", "compliance"],
    readTime: 12,
    featured: false
  },
  {
    slug: "iso-27001-risk-management-without-spreadsheets",
    translations: {
      en: {
        title: "How to Manage ISO 27001 Risks Without Spreadsheets",
        metaTitle: "ISO 27001 Risk Management Without Spreadsheets | Better Alternatives in 2025",
        metaDescription: "Discover why spreadsheets fail for ISO 27001 risk management and how purpose-built tools like Risk Copilot streamline risk registers, treatment plans, and audit trails.",
        excerpt: "Spreadsheets were never designed for ISO 27001 risk management. Learn the hidden costs of using Excel for your risk register and discover a smarter, audit-ready alternative.",
        content: `
## Why Most Companies Still Use Spreadsheets for ISO 27001 Risks

Let's be honest - when most organizations start their ISO 27001 journey, the risk register lands in a spreadsheet. It's familiar, free, and flexible. But what starts as a quick solution quickly becomes one of the biggest bottlenecks to maintaining a compliant ISMS.

If you've ever spent hours reconciling version conflicts in a shared Excel file, or scrambled to produce an audit trail before a certification audit, you already know the pain.

This guide explores exactly where spreadsheets break down for ISO 27001 risk management - and what to use instead.

## The 7 Critical Problems with Spreadsheet-Based Risk Management

### 1. No Audit Trail

ISO 27001 Clause 7.5 requires documented information to be controlled. Spreadsheets offer no built-in change history, no user-level tracking, and no timestamped modification logs. During a certification audit, you'll struggle to prove *who* changed *what* and *when*.

A purpose-built [risk management tool](/risk-management) automatically logs every action - every risk created, updated, reviewed, or closed - with timestamps and user attribution.

### 2. Version Control Chaos

Multiple people editing the same spreadsheet leads to conflicting versions, overwritten data, and lost work. Even cloud-based spreadsheets suffer from merge conflicts when used across teams.

### 3. No Relationship Mapping

ISO 27001 requires you to link risks to:
- **Assets** (Clause A.8)
- **Threats and vulnerabilities** (Clause 6.1.2)
- **Controls** (Annex A / Statement of Applicability)
- **Treatment plans** (Clause 6.1.3)

In a spreadsheet, these relationships are just text in a cell. There's no referential integrity, no automatic updates when a linked item changes, and no way to visualize the connections.

With [Risk Copilot](/risk-management), every risk is linked to assets, threats, vulnerabilities, controls, and treatments as structured data - ensuring nothing falls through the cracks.

### 4. Manual Risk Scoring

Calculating inherent risk, residual risk, and control effectiveness manually is tedious and error-prone. Formula errors in spreadsheets can silently corrupt your entire risk picture.

A dedicated tool applies your [risk framework methodology](/risk-management) consistently - including customizable likelihood/impact scales and automatic residual risk calculations based on control effectiveness.

### 5. No Built-In Review Workflow

ISO 27001 Clause 8.2 requires periodic risk reassessment. In a spreadsheet, there's no way to enforce review cycles, flag overdue reviews, or track which risks have been reassessed.

### 6. Reporting is Manual and Time-Consuming

Management reviews (Clause 9.3) require summarized risk data: heat maps, trend analysis, treatment progress, and KPIs. Building these from a spreadsheet means hours of manual chart creation every review cycle.

### 7. Scalability Problems

A risk register with 10 entries works fine in Excel. At 50+ risks with linked assets, controls, and treatment plans, spreadsheets become unmanageable.

## What Auditors Actually Look For

During an ISO 27001 certification or surveillance audit, auditors assess:

| Audit Requirement | Spreadsheet | Dedicated Tool |
|---|---|---|
| Complete risk register with scoring | ⚠️ Partial | ✅ Full |
| Audit trail of changes | ❌ None | ✅ Automatic |
| Risk-to-control mapping | ⚠️ Manual text | ✅ Linked data |
| Evidence of periodic review | ❌ No tracking | ✅ Review workflow |
| Treatment plan progress | ⚠️ Manual updates | ✅ Live tracking |
| Management reports | ❌ Manual charts | ✅ Auto-generated |

> Auditors don't just want to see *that* you have a risk register - they want to see that it's actively managed, reviewed, and traceable.

## The Modern Alternative: Purpose-Built Risk Management

The best ISO 27001 risk management tools provide:

### Structured Risk Register
Every risk has inherent and residual scoring, linked assets, threat/vulnerability pairs, and a clear owner. No more free-text fields that mean different things to different people.

### Automatic Audit Trail
Every create, update, and delete action is logged with the user, timestamp, and details. This alone can save hours of audit preparation.

### Integrated Asset Management
Your [asset inventory](/asset-inventory) feeds directly into risk assessments. When an asset's criticality changes, related risks are immediately visible.

### Control Mapping
Link controls to risks and track their effectiveness. The [Statement of Applicability](/soa-generator) stays in sync with your risk treatment decisions.

### Treatment Planning
Assign treatment actions, set due dates, track completion percentage, and maintain evidence notes - all connected to the parent risk.

### Visual Dashboards
Real-time heat maps, risk distribution charts, and KPIs that are always audit-ready. No more last-minute scrambling before management reviews.

### Customizable Risk Framework
Define your own likelihood and impact scales, configure a 5×5 risk matrix with custom colors and labels, and set risk appetite thresholds - all within the [Risk Copilot settings](/risk-management).

## How to Migrate from Spreadsheets

If you're currently using spreadsheets, here's a practical migration path:

1. **Export your current risk data** - Clean up your spreadsheet and ensure consistent formatting
2. **Define your risk methodology** - Set up your likelihood/impact scales and risk matrix in a dedicated tool
3. **Import risks** - Transfer your risk register entries with their current scoring
4. **Link assets and controls** - Establish relationships that spreadsheets couldn't maintain
5. **Set up review cycles** - Configure periodic reassessment reminders
6. **Run a parallel period** - Maintain both systems for one review cycle to ensure nothing is lost

> Start with a [free demo of Risk Copilot](/risk-management) to see how your risk register would look in a structured, audit-ready format.

## Real Cost Comparison

Consider the hidden costs of spreadsheet-based risk management:

- **Time**: 4-8 hours per month maintaining, formatting, and reconciling
- **Audit prep**: 10-20 hours before each audit gathering evidence and creating reports
- **Errors**: Undetected formula mistakes leading to incorrect risk prioritization
- **Compliance gaps**: Missing review deadlines, incomplete audit trails

A [purpose-built tool](/risk-management) eliminates these costs while improving your security posture.

## Frequently Asked Questions

### Can I start with spreadsheets and migrate later?

Yes, and many organizations do. But the longer you wait, the more data you'll need to migrate and restructure. Starting with the right tool from day one saves significant effort.

### Do auditors specifically require a dedicated tool?

No - ISO 27001 doesn't mandate specific tools. However, auditors will assess the *effectiveness* of your risk management process. A well-maintained tool demonstrates maturity and makes evidence collection straightforward.

### What about free tools?

Free tools can work for very small organizations. But as your ISMS grows, you'll need features like audit trails, role-based access, and automated reporting that free tools typically lack.

## Key Takeaway

Spreadsheets are a starting point, not a destination. As your ISMS matures and your risk landscape grows, the limitations of spreadsheet-based risk management become increasingly costly - in time, in audit findings, and in security gaps.

Moving to a structured, purpose-built [risk management platform](/risk-management) is one of the highest-impact improvements you can make to your ISO 27001 program.

---

**Ready to upgrade your risk management?** [Try Risk Copilot free](/risk-management) - no spreadsheets required.
`
      },
      fr: {
        title: "Comment gérer les risques ISO 27001 sans tableurs",
        metaTitle: "Gestion des risques ISO 27001 sans tableurs | Meilleures alternatives en 2025",
        metaDescription: "Découvrez pourquoi les tableurs échouent pour la gestion des risques ISO 27001 et comment des outils dédiés comme Risk Copilot simplifient registres, plans de traitement et pistes d'audit.",
        excerpt: "Les tableurs n'ont jamais été conçus pour la gestion des risques ISO 27001. Découvrez les coûts cachés d'Excel et une alternative plus intelligente, prête pour l'audit.",
        content: `
## Pourquoi la plupart des entreprises utilisent encore des tableurs pour les risques ISO 27001

Soyons honnêtes - lorsque la plupart des organisations commencent leur parcours ISO 27001, le registre des risques atterrit dans un tableur. C'est familier, gratuit et flexible. Mais ce qui commence comme une solution rapide devient rapidement l'un des plus grands obstacles au maintien d'un SMSI conforme.

Si vous avez déjà passé des heures à réconcilier des conflits de versions dans un fichier Excel partagé, ou cherché frénétiquement à produire une piste d'audit avant un audit de certification, vous connaissez déjà la douleur.

Ce guide explore exactement où les tableurs échouent pour la gestion des risques ISO 27001 - et quoi utiliser à la place.

## Les 7 problèmes critiques de la gestion des risques par tableur

### 1. Aucune piste d'audit

La clause 7.5 de l'ISO 27001 exige que les informations documentées soient contrôlées. Les tableurs n'offrent aucun historique de modifications intégré, aucun suivi au niveau utilisateur et aucun journal horodaté. Lors d'un audit de certification, vous aurez du mal à prouver *qui* a changé *quoi* et *quand*.

Un [outil de gestion des risques](/risk-management) dédié enregistre automatiquement chaque action - chaque risque créé, mis à jour, examiné ou clôturé - avec horodatage et attribution utilisateur.

### 2. Chaos du contrôle de version

Plusieurs personnes modifiant le même tableur entraîne des versions conflictuelles, des données écrasées et du travail perdu.

### 3. Aucune cartographie des relations

L'ISO 27001 exige de lier les risques à :
- **Les actifs** (Clause A.8)
- **Les menaces et vulnérabilités** (Clause 6.1.2)
- **Les contrôles** (Annexe A / Déclaration d'applicabilité)
- **Les plans de traitement** (Clause 6.1.3)

Dans un tableur, ces relations ne sont que du texte dans une cellule. Avec [Risk Copilot](/risk-management), chaque risque est lié aux actifs, menaces, vulnérabilités, contrôles et traitements en données structurées.

### 4. Évaluation manuelle des risques

Calculer le risque inhérent, le risque résiduel et l'efficacité des contrôles manuellement est fastidieux et sujet aux erreurs.

### 5. Aucun workflow de révision intégré

La clause 8.2 de l'ISO 27001 exige une réévaluation périodique des risques. Dans un tableur, il n'y a aucun moyen d'imposer des cycles de révision.

### 6. Le reporting est manuel et chronophage

Les revues de direction (Clause 9.3) nécessitent des données synthétisées : matrices de chaleur, analyses de tendances, progression des traitements et KPIs.

### 7. Problèmes de scalabilité

Un registre de risques avec 10 entrées fonctionne bien dans Excel. À plus de 50 risques avec des actifs, contrôles et plans de traitement liés, les tableurs deviennent ingérables.

## Ce que les auditeurs recherchent réellement

| Exigence d'audit | Tableur | Outil dédié |
|---|---|---|
| Registre des risques complet | ⚠️ Partiel | ✅ Complet |
| Piste d'audit des modifications | ❌ Aucune | ✅ Automatique |
| Cartographie risque-contrôle | ⚠️ Texte manuel | ✅ Données liées |
| Preuve de révision périodique | ❌ Pas de suivi | ✅ Workflow |
| Progression du plan de traitement | ⚠️ Mises à jour manuelles | ✅ Suivi en direct |
| Rapports de gestion | ❌ Graphiques manuels | ✅ Auto-générés |

> Les auditeurs ne veulent pas simplement voir *que* vous avez un registre des risques - ils veulent voir qu'il est activement géré, révisé et traçable.

## L'alternative moderne : la gestion des risques dédiée

Les meilleurs outils offrent un registre structuré, une piste d'audit automatique, une [gestion des actifs](/asset-inventory) intégrée, une cartographie des contrôles, des plans de traitement et des tableaux de bord visuels.

## Comment migrer depuis les tableurs

1. **Exportez vos données actuelles** - Nettoyez votre tableur
2. **Définissez votre méthodologie** - Configurez vos échelles dans un outil dédié
3. **Importez les risques** - Transférez vos entrées avec leur évaluation
4. **Liez actifs et contrôles** - Établissez les relations
5. **Configurez les cycles de révision**
6. **Période parallèle** - Maintenez les deux systèmes pendant un cycle

> Commencez avec une [démo gratuite de Risk Copilot](/risk-management) pour voir à quoi ressemblerait votre registre dans un format structuré.

## Point clé

Les tableurs sont un point de départ, pas une destination. Passer à une [plateforme de gestion des risques](/risk-management) dédiée est l'une des améliorations à plus fort impact pour votre programme ISO 27001.

---

**Prêt à améliorer votre gestion des risques ?** [Essayez Risk Copilot gratuitement](/risk-management) - sans tableurs.
`
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-03-08",
    updatedDate: "2026-03-08",
    category: "Risk Management",
    tags: ["ISO 27001", "risk management", "risk register", "spreadsheet alternative", "ISMS", "audit trail", "compliance"],
    readTime: 10,
    featured: true,
  },
  {
    slug: "iso-27001-implementation-guide-2025",
    translations: {
      en: {
        title: "Complete ISO 27001 Implementation Guide for 2025",
        metaTitle: "ISO 27001 Implementation Guide 2025 | Step-by-Step Certification Process",
        metaDescription: "Learn how to implement ISO 27001 in 2025 with our comprehensive guide. Covers gap analysis, risk assessment, ISMS setup, and certification audit preparation.",
        excerpt: "A comprehensive step-by-step guide to implementing ISO 27001 in your organization, from initial planning to successful certification.",
        content: `
## What is ISO 27001?

ISO 27001 is the international standard for information security management systems (ISMS). It provides a systematic approach to managing sensitive company information, ensuring it remains secure through a risk management process.

## Why Implement ISO 27001 in 2025?

With cyber threats becoming more sophisticated and regulations like GDPR, CCPA, and NIS2 becoming stricter, ISO 27001 certification demonstrates your commitment to information security to customers, partners, and regulators.

### Key Benefits:
- **Regulatory Compliance**: Meet requirements for GDPR, HIPAA, SOC 2, and industry-specific regulations
- **Competitive Advantage**: Win contracts that require ISO 27001 certification
- **Risk Reduction**: Systematically identify and mitigate security risks
- **Customer Trust**: Demonstrate your commitment to protecting sensitive data

## Step 1: Initial Gap Analysis

Before starting implementation, assess your current security posture against ISO 27001 requirements. Our [Gap Analysis Tool](/iso-27001-gap-analysis) helps you identify areas needing improvement.

### Key Areas to Assess:
1. Current security policies and procedures
2. [Access control](/controls/a-5-15-access-control) mechanisms
3. Physical and environmental security
4. [Incident management](/controls/a-5-24-information-security-incident-management-planning-and-preparation) processes
5. [Business continuity](/controls/a-5-30-ict-readiness-for-business-continuity) planning

## Step 2: Define the ISMS Scope

Determine which parts of your organization will be covered by the ISMS. Consider:
- Business units and locations
- Information assets
- Processes and technologies
- Third-party relationships

## Step 3: Risk Assessment

Conduct a thorough risk assessment to identify threats to your information assets. Use our [Risk Assessment Tool](/risk-assessment) to create a comprehensive risk register.

### Risk Assessment Process:
1. Identify information assets
2. Identify threats and vulnerabilities
3. Assess likelihood and impact
4. Calculate risk levels
5. Determine risk treatment options

## Step 4: Statement of Applicability (SoA)

Document which of the 93 Annex A controls apply to your organization. Our [SoA Generator](/soa-generator) streamlines this process by guiding you through each control.

## Step 5: Implement Controls

Based on your risk assessment and SoA, implement the necessary security controls:
- **Organizational Controls**: Policies, procedures, and responsibilities
- **People Controls**: [Security awareness](/controls/a-6-3-information-security-awareness-education-and-training), training, and HR security
- **Physical Controls**: Secure areas, equipment protection
- **Technological Controls**: Access control, cryptography, network security

## Step 6: Document Your ISMS

Create and maintain documentation including:
- [Information Security Policy](/controls/a-5-1-policies-for-information-security)
- Risk Assessment Methodology
- Statement of Applicability
- Risk Treatment Plan
- Procedures and work instructions

Check our [Documents Library](/documents) for policy templates.

## Step 7: Internal Audit

Conduct internal audits to verify ISMS effectiveness. Use our [Internal Audit Checklist](/internal-audit) to ensure comprehensive coverage.

## Step 8: Management Review

Senior management must review the ISMS regularly to ensure its continuing suitability, adequacy, and effectiveness.

## Step 9: Certification Audit

The certification process involves:
1. **Stage 1 Audit**: Documentation review
2. **Stage 2 Audit**: Implementation verification
3. **Certification Decision**: Certification body issues certificate
4. **Surveillance Audits**: Annual verification audits

## Implementation Timeline

Typical implementation takes 6-12 months depending on organization size and complexity. Use our [Implementation Roadmap](/implementation-roadmap) to plan your timeline.

## Conclusion

ISO 27001 implementation requires commitment but delivers significant benefits. Start with our free tools to assess your readiness and build your implementation plan.
        `
      },
      fr: {
        title: "Guide complet de mise en œuvre ISO 27001 pour 2025",
        metaTitle: "Guide de mise en œuvre ISO 27001 2025 | Processus de certification étape par étape",
        metaDescription: "Apprenez à mettre en œuvre la norme ISO 27001 en 2025 avec notre guide complet. Couvre l'analyse des écarts, l'évaluation des risques, la mise en place du SMSI et la préparation à l'audit de certification.",
        excerpt: "Un guide complet étape par étape pour mettre en œuvre la norme ISO 27001 dans votre organisation, de la planification initiale à la certification réussie.",
        content: `
## Qu'est-ce que l'ISO 27001 ?

L'ISO 27001 est la norme internationale pour les systèmes de management de la sécurité de l'information (SMSI). Elle fournit une approche systématique pour gérer les informations sensibles de l'entreprise, garantissant leur sécurité grâce à un processus de gestion des risques.

## Pourquoi mettre en œuvre l'ISO 27001 en 2025 ?

Avec des cybermenaces de plus en plus sophistiquées et des réglementations comme le RGPD, le CCPA et NIS2 devenant plus strictes, la certification ISO 27001 démontre votre engagement envers la sécurité de l'information auprès des clients, partenaires et régulateurs.

### Avantages clés :
- **Conformité réglementaire** : Répondre aux exigences du RGPD, HIPAA, SOC 2 et des réglementations sectorielles
- **Avantage concurrentiel** : Remporter des contrats qui exigent la certification ISO 27001
- **Réduction des risques** : Identifier et atténuer systématiquement les risques de sécurité
- **Confiance des clients** : Démontrer votre engagement à protéger les données sensibles

## Étape 1 : Analyse initiale des écarts

Avant de commencer la mise en œuvre, évaluez votre posture de sécurité actuelle par rapport aux exigences de l'ISO 27001. Notre [Outil d'analyse des écarts](/tools/gap-analysis) vous aide à identifier les domaines nécessitant des améliorations.

### Domaines clés à évaluer :
1. Politiques et procédures de sécurité actuelles
2. Mécanismes de contrôle d'accès
3. Sécurité physique et environnementale
4. Processus de gestion des incidents
5. Planification de la continuité des activités

## Étape 2 : Définir le périmètre du SMSI

Déterminez quelles parties de votre organisation seront couvertes par le SMSI. Considérez :
- Les unités commerciales et les sites
- Les actifs informationnels
- Les processus et technologies
- Les relations avec les tiers

## Étape 3 : Évaluation des risques

Effectuez une évaluation approfondie des risques pour identifier les menaces pesant sur vos actifs informationnels. Utilisez notre [Outil d'évaluation des risques](/risk-assessment) pour créer un registre des risques complet.

### Processus d'évaluation des risques :
1. Identifier les actifs informationnels
2. Identifier les menaces et vulnérabilités
3. Évaluer la probabilité et l'impact
4. Calculer les niveaux de risque
5. Déterminer les options de traitement des risques

## Étape 4 : Déclaration d'applicabilité (DdA)

Documentez lesquels des 93 contrôles de l'Annexe A s'appliquent à votre organisation. Notre [Générateur de DdA](/soa-generator) simplifie ce processus en vous guidant à travers chaque contrôle.

## Étape 5 : Mettre en œuvre les contrôles

Sur la base de votre évaluation des risques et de votre DdA, mettez en œuvre les contrôles de sécurité nécessaires :
- **Contrôles organisationnels** : Politiques, procédures et responsabilités
- **Contrôles liés aux personnes** : Sensibilisation à la sécurité, formation et sécurité RH
- **Contrôles physiques** : Zones sécurisées, protection des équipements
- **Contrôles technologiques** : Contrôle d'accès, cryptographie, sécurité réseau

## Étape 6 : Documenter votre SMSI

Créez et maintenez la documentation incluant :
- Politique de sécurité de l'information
- Méthodologie d'évaluation des risques
- Déclaration d'applicabilité
- Plan de traitement des risques
- Procédures et instructions de travail

Consultez notre [Bibliothèque de documents](/documents) pour les modèles de politiques.

## Étape 7 : Audit interne

Effectuez des audits internes pour vérifier l'efficacité du SMSI. Utilisez notre [Liste de contrôle d'audit interne](/internal-audit) pour assurer une couverture complète.

## Étape 8 : Revue de direction

La direction doit examiner régulièrement le SMSI pour s'assurer de sa pertinence, de son adéquation et de son efficacité continues.

## Étape 9 : Audit de certification

Le processus de certification comprend :
1. **Audit de phase 1** : Examen de la documentation
2. **Audit de phase 2** : Vérification de la mise en œuvre
3. **Décision de certification** : L'organisme de certification délivre le certificat
4. **Audits de surveillance** : Audits de vérification annuels

## Calendrier de mise en œuvre

La mise en œuvre typique prend 6 à 12 mois selon la taille et la complexité de l'organisation. Utilisez notre [Feuille de route de mise en œuvre](/implementation-roadmap) pour planifier votre calendrier.

## Conclusion

La mise en œuvre de l'ISO 27001 nécessite un engagement mais offre des avantages significatifs. Commencez avec nos outils gratuits pour évaluer votre préparation et construire votre plan de mise en œuvre.
        `
      }
    },
    author: "ISO27001KIT",
    publishDate: "2025-06-15",
    updatedDate: "2025-06-15",
    category: "Implementation",
    tags: ["ISO 27001", "ISMS", "Implementation", "Certification", "Information Security"],
    readTime: 12,
    featured: true
  },
  {
    slug: "iso-27001-risk-assessment-methodology",
    translations: {
      en: {
        title: "ISO 27001 Risk Assessment: Complete Methodology Guide",
        metaTitle: "ISO 27001 Risk Assessment Methodology | Free Risk Assessment Template",
        metaDescription: "Master ISO 27001 risk assessment with our comprehensive guide. Learn risk identification, analysis, evaluation, and treatment with practical examples and free templates.",
        excerpt: "Learn how to conduct an effective ISO 27001 risk assessment using industry best practices and proven methodologies.",
        content: `
## Understanding ISO 27001 Risk Assessment

Risk assessment is the cornerstone of ISO 27001 implementation. It helps organizations identify, analyze, and evaluate information security risks to make informed decisions about risk treatment.

## Why Risk Assessment Matters

Without a proper risk assessment, you cannot:
- Prioritize security investments
- Select appropriate controls
- Justify security decisions to management
- Demonstrate due diligence to auditors

## The Risk Assessment Process

### Step 1: Establish Context

Define the scope, criteria, and methodology for your risk assessment:
- **Risk Criteria**: Define acceptable risk levels
- **Impact Criteria**: Define impact scales (financial, operational, reputational)
- **Likelihood Criteria**: Define probability scales

### Step 2: Asset Identification

Identify all information assets within scope:
- **Data Assets**: Customer data, financial records, intellectual property
- **Software Assets**: Applications, databases, operating systems
- **Hardware Assets**: Servers, workstations, network devices
- **People Assets**: Employees, contractors, third parties
- **Process Assets**: Business processes that handle information

### Step 3: Threat Identification

Identify potential threats to each asset:
- **Natural Threats**: Fire, flood, earthquake
- **Human Threats**: Hackers, disgruntled employees, social engineering
- **Technical Threats**: System failures, malware, software bugs
- **Environmental Threats**: Power outages, HVAC failures

### Step 4: Vulnerability Assessment

Identify vulnerabilities that threats could exploit:
- Weak [access controls](/controls/a-5-15-access-control)
- Unpatched systems
- Lack of [encryption](/controls/a-8-24-use-of-cryptography)
- Poor [security awareness](/controls/a-6-3-information-security-awareness-education-and-training)
- Inadequate [backup](/controls/a-8-13-information-backup) procedures

### Step 5: Risk Analysis

Calculate risk levels using the formula:

**Risk = Likelihood × Impact**

Use a 5×5 matrix to categorize risks:
- **Very Low (1-4)**: Accept the risk
- **Low (5-8)**: Monitor and review
- **Medium (9-12)**: Implement controls
- **High (15-16)**: Prioritize treatment
- **Critical (20-25)**: Immediate action required

### Step 6: Risk Evaluation

Compare calculated risks against your risk criteria to determine:
- Which risks are acceptable
- Which risks require treatment
- Priority order for risk treatment

### Step 7: Risk Treatment

Select appropriate treatment options:
- **Avoid**: Eliminate the activity causing the risk
- **Transfer**: Share risk through insurance or contracts
- **Mitigate**: Implement controls to reduce likelihood or impact
- **Accept**: Acknowledge and monitor acceptable risks

## Practical Example

**Asset**: Customer Database
**Threat**: SQL Injection Attack
**Vulnerability**: Input validation weaknesses
**Likelihood**: 4 (Likely)
**Impact**: 5 (Critical - data breach)
**Risk Level**: 20 (Critical)

**Treatment**: Implement parameterized queries, WAF, regular [security testing](/controls/a-8-29-security-testing-in-development-and-acceptance)

## Common Mistakes to Avoid

1. **Too Generic**: Assess specific assets, not broad categories
2. **Ignoring Likelihood**: Don't treat all threats equally
3. **No Documentation**: Document methodology and decisions
4. **One-Time Exercise**: Risk assessment must be ongoing
5. **Siloed Approach**: Involve stakeholders across the organization

## Tools and Templates

Use our [Risk Assessment Tool](/risk-assessment) to:
- Create a comprehensive risk register
- Calculate risk levels automatically
- Generate treatment plans
- Export to Excel for stakeholders

## Conclusion

A well-executed risk assessment provides the foundation for your entire ISMS. Take the time to do it properly, and revisit it regularly as your business and threat landscape evolve.
        `
      },
      fr: {
        title: "Évaluation des risques ISO 27001 : Guide méthodologique complet",
        metaTitle: "Méthodologie d'évaluation des risques ISO 27001 | Modèle gratuit d'évaluation des risques",
        metaDescription: "Maîtrisez l'évaluation des risques ISO 27001 avec notre guide complet. Apprenez l'identification, l'analyse, l'évaluation et le traitement des risques avec des exemples pratiques et des modèles gratuits.",
        excerpt: "Apprenez à mener une évaluation des risques ISO 27001 efficace en utilisant les meilleures pratiques de l'industrie et des méthodologies éprouvées.",
        content: `
## Comprendre l'évaluation des risques ISO 27001

L'évaluation des risques est la pierre angulaire de la mise en œuvre de l'ISO 27001. Elle aide les organisations à identifier, analyser et évaluer les risques de sécurité de l'information pour prendre des décisions éclairées sur le traitement des risques.

## Pourquoi l'évaluation des risques est importante

Sans une évaluation des risques appropriée, vous ne pouvez pas :
- Prioriser les investissements en sécurité
- Sélectionner les contrôles appropriés
- Justifier les décisions de sécurité auprès de la direction
- Démontrer la diligence raisonnable aux auditeurs

## Le processus d'évaluation des risques

### Étape 1 : Établir le contexte

Définissez la portée, les critères et la méthodologie pour votre évaluation des risques :
- **Critères de risque** : Définir les niveaux de risque acceptables
- **Critères d'impact** : Définir les échelles d'impact (financier, opérationnel, réputationnel)
- **Critères de probabilité** : Définir les échelles de probabilité

### Étape 2 : Identification des actifs

Identifiez tous les actifs informationnels dans le périmètre :
- **Actifs de données** : Données clients, dossiers financiers, propriété intellectuelle
- **Actifs logiciels** : Applications, bases de données, systèmes d'exploitation
- **Actifs matériels** : Serveurs, postes de travail, équipements réseau
- **Actifs humains** : Employés, sous-traitants, tiers
- **Actifs de processus** : Processus métier qui traitent l'information

### Étape 3 : Identification des menaces

Identifiez les menaces potentielles pour chaque actif :
- **Menaces naturelles** : Incendie, inondation, tremblement de terre
- **Menaces humaines** : Pirates, employés mécontents, ingénierie sociale
- **Menaces techniques** : Défaillances système, malware, bugs logiciels
- **Menaces environnementales** : Coupures de courant, pannes de climatisation

### Étape 4 : Évaluation des vulnérabilités

Identifiez les vulnérabilités que les menaces pourraient exploiter :
- Contrôles d'accès faibles
- Systèmes non corrigés
- Manque de chiffrement
- Faible sensibilisation à la sécurité
- Procédures de sauvegarde inadéquates

### Étape 5 : Analyse des risques

Calculez les niveaux de risque en utilisant la formule :

**Risque = Probabilité × Impact**

Utilisez une matrice 5×5 pour catégoriser les risques :
- **Très faible (1-4)** : Accepter le risque
- **Faible (5-8)** : Surveiller et examiner
- **Moyen (9-12)** : Mettre en œuvre des contrôles
- **Élevé (15-16)** : Prioriser le traitement
- **Critique (20-25)** : Action immédiate requise

### Étape 6 : Évaluation des risques

Comparez les risques calculés avec vos critères de risque pour déterminer :
- Quels risques sont acceptables
- Quels risques nécessitent un traitement
- L'ordre de priorité pour le traitement des risques

### Étape 7 : Traitement des risques

Sélectionnez les options de traitement appropriées :
- **Éviter** : Éliminer l'activité causant le risque
- **Transférer** : Partager le risque par l'assurance ou des contrats
- **Atténuer** : Mettre en œuvre des contrôles pour réduire la probabilité ou l'impact
- **Accepter** : Reconnaître et surveiller les risques acceptables

## Exemple pratique

**Actif** : Base de données clients
**Menace** : Attaque par injection SQL
**Vulnérabilité** : Faiblesses de validation des entrées
**Probabilité** : 4 (Probable)
**Impact** : 5 (Critique - violation de données)
**Niveau de risque** : 20 (Critique)

**Traitement** : Implémenter des requêtes paramétrées, WAF, tests de sécurité réguliers

## Erreurs courantes à éviter

1. **Trop générique** : Évaluez des actifs spécifiques, pas des catégories larges
2. **Ignorer la probabilité** : Ne traitez pas toutes les menaces de manière égale
3. **Pas de documentation** : Documentez la méthodologie et les décisions
4. **Exercice ponctuel** : L'évaluation des risques doit être continue
5. **Approche cloisonnée** : Impliquez les parties prenantes dans toute l'organisation

## Outils et modèles

Utilisez notre [Outil d'évaluation des risques](/risk-assessment) pour :
- Créer un registre des risques complet
- Calculer automatiquement les niveaux de risque
- Générer des plans de traitement
- Exporter vers Excel pour les parties prenantes

## Conclusion

Une évaluation des risques bien exécutée fournit la base de tout votre SMSI. Prenez le temps de le faire correctement et revisitez-le régulièrement à mesure que votre entreprise et le paysage des menaces évoluent.
        `
      }
    },
    author: "ISO27001KIT",
    publishDate: "2025-06-10",
    updatedDate: "2025-06-10",
    category: "Risk Management",
    tags: ["Risk Assessment", "ISO 27001", "Risk Management", "Information Security", "ISMS"],
    readTime: 10,
    featured: true
  },
  {
    slug: "iso-27001-annex-a-controls-explained",
    translations: {
      en: {
        title: "All 93 ISO 27001:2022 Annex A Controls Explained",
        metaTitle: "All 93 ISO 27001:2022 Annex A Controls Explained - With Implementation Guidance",
        metaDescription: "Complete breakdown of all 93 Annex A controls in ISO 27001:2022. Organized by category with practical implementation tips for each control.",
        excerpt: "A detailed breakdown of all 93 Annex A controls in ISO 27001:2022, organized by category with practical implementation guidance.",
        content: `
## Introduction to Annex A Controls

ISO 27001:2022 includes 93 controls in Annex A, organized into four categories. These controls provide a comprehensive framework for information security management.

## The Four Control Categories

### 1. Organizational Controls (37 Controls)

These controls focus on policies, procedures, and organizational structures:

**Key Controls Include:**
- **[A.5.1](/controls/a-5-1-policies-for-information-security) Policies for Information Security**: Establish and maintain security policies
- **[A.5.2](/controls/a-5-2-information-security-roles-and-responsibilities) Information Security Roles**: Define security responsibilities
- **[A.5.7](/controls/a-5-7-threat-intelligence) Threat Intelligence**: Collect and analyze threat information
- **[A.5.15](/controls/a-5-15-access-control) Access Control**: Manage access based on business requirements
- **[A.5.23](/controls/a-5-23-information-security-for-use-of-cloud-services) Cloud Security**: Address cloud-specific security requirements
- **[A.5.30](/controls/a-5-30-ict-readiness-for-business-continuity) ICT Readiness**: Ensure business continuity planning

### 2. People Controls (8 Controls)

Controls addressing human aspects of security:

**Key Controls Include:**
- **A.6.1 Screening**: Pre-employment verification
- **A.6.2 Terms of Employment**: Security in employment contracts
- **A.6.3 Security Awareness**: Training and education
- **A.6.4 Disciplinary Process**: Handling security violations
- **A.6.5 After Employment**: Exit procedures
- **A.6.7 Remote Working**: Secure remote work practices

### 3. Physical Controls (14 Controls)

Controls protecting physical assets and environments:

**Key Controls Include:**
- **A.7.1 Physical Security Perimeters**: Secure boundaries
- **A.7.2 Physical Entry**: Access control to facilities
- **A.7.4 Physical Security Monitoring**: Surveillance and detection
- **A.7.8 Equipment Siting**: Secure equipment placement
- **A.7.10 Storage Media**: Protecting removable media
- **A.7.14 Secure Disposal**: Equipment and media disposal

### 4. Technological Controls (34 Controls)

Controls for technical security measures:

**Key Controls Include:**
- **A.8.1 User Endpoints**: Securing user devices
- **A.8.5 Secure Authentication**: Strong authentication mechanisms
- **A.8.9 Configuration Management**: Secure system configurations
- **A.8.12 Data Leakage Prevention**: Preventing data exfiltration
- **A.8.16 Monitoring Activities**: Security monitoring and logging
- **A.8.24 Cryptography**: Encryption and key management
- **A.8.28 Secure Coding**: Secure software development

## New Controls in ISO 27001:2022

The 2022 revision introduced 11 new controls:

1. **A.5.7** Threat Intelligence
2. **A.5.23** Information Security for Cloud Services
3. **A.5.30** ICT Readiness for Business Continuity
4. **A.7.4** Physical Security Monitoring
5. **A.8.9** Configuration Management
6. **A.8.10** Information Deletion
7. **A.8.11** Data Masking
8. **A.8.12** Data Leakage Prevention
9. **A.8.16** Monitoring Activities
10. **A.8.23** Web Filtering
11. **A.8.28** Secure Coding

## Creating Your Statement of Applicability

Not all controls apply to every organization. Use our [SoA Generator](/soa-generator) to:
- Review each control systematically
- Document applicability decisions
- Justify exclusions
- Track implementation status

## Implementation Priority

Focus on controls that address your highest risks first. Use your [risk assessment](/risk-assessment) results to prioritize implementation.

## Conclusion

Understanding Annex A controls is essential for ISO 27001 implementation. Use our tools to systematically work through each control and build a comprehensive ISMS.
        `
      },
      fr: {
        title: "Les 93 contrôles de l'Annexe A de l'ISO 27001:2022 expliqués",
        metaTitle: "Contrôles de l'Annexe A ISO 27001:2022 | Guide complet des 93 contrôles",
        metaDescription: "Guide complet des 93 contrôles de l'Annexe A de l'ISO 27001:2022. Comprenez les contrôles organisationnels, humains, physiques et technologiques avec des conseils de mise en œuvre.",
        excerpt: "Une analyse détaillée des 93 contrôles de l'Annexe A dans l'ISO 27001:2022, organisés par catégorie avec des conseils pratiques de mise en œuvre.",
        content: `
## Introduction aux contrôles de l'Annexe A

L'ISO 27001:2022 comprend 93 contrôles dans l'Annexe A, organisés en quatre catégories. Ces contrôles fournissent un cadre complet pour la gestion de la sécurité de l'information.

## Les quatre catégories de contrôles

### 1. Contrôles organisationnels (37 contrôles)

Ces contrôles se concentrent sur les politiques, procédures et structures organisationnelles :

**Contrôles clés :**
- **A.5.1 Politiques de sécurité de l'information** : Établir et maintenir des politiques de sécurité
- **A.5.2 Rôles de sécurité de l'information** : Définir les responsabilités de sécurité
- **A.5.7 Veille sur les menaces** : Collecter et analyser les informations sur les menaces
- **A.5.15 Contrôle d'accès** : Gérer l'accès selon les exigences métier
- **A.5.23 Sécurité du cloud** : Traiter les exigences de sécurité spécifiques au cloud
- **A.5.30 Préparation TIC** : Assurer la planification de la continuité des activités

### 2. Contrôles liés aux personnes (8 contrôles)

Contrôles traitant des aspects humains de la sécurité :

**Contrôles clés :**
- **A.6.1 Sélection** : Vérification avant l'embauche
- **A.6.2 Conditions d'emploi** : Sécurité dans les contrats de travail
- **A.6.3 Sensibilisation à la sécurité** : Formation et éducation
- **A.6.4 Processus disciplinaire** : Traitement des violations de sécurité
- **A.6.5 Après l'emploi** : Procédures de départ
- **A.6.7 Télétravail** : Pratiques de travail à distance sécurisées

### 3. Contrôles physiques (14 contrôles)

Contrôles protégeant les actifs physiques et les environnements :

**Contrôles clés :**
- **A.7.1 Périmètres de sécurité physique** : Limites sécurisées
- **A.7.2 Entrée physique** : Contrôle d'accès aux installations
- **A.7.4 Surveillance de la sécurité physique** : Surveillance et détection
- **A.7.8 Emplacement des équipements** : Placement sécurisé des équipements
- **A.7.10 Supports de stockage** : Protection des supports amovibles
- **A.7.14 Mise au rebut sécurisée** : Élimination des équipements et supports

### 4. Contrôles technologiques (34 contrôles)

Contrôles pour les mesures de sécurité techniques :

**Contrôles clés :**
- **A.8.1 Terminaux utilisateurs** : Sécurisation des appareils utilisateurs
- **A.8.5 Authentification sécurisée** : Mécanismes d'authentification forte
- **A.8.9 Gestion des configurations** : Configurations système sécurisées
- **A.8.12 Prévention des fuites de données** : Prévention de l'exfiltration de données
- **A.8.16 Activités de surveillance** : Surveillance et journalisation de sécurité
- **A.8.24 Cryptographie** : Chiffrement et gestion des clés
- **A.8.28 Codage sécurisé** : Développement logiciel sécurisé

## Nouveaux contrôles dans l'ISO 27001:2022

La révision 2022 a introduit 11 nouveaux contrôles :

1. **A.5.7** Veille sur les menaces
2. **A.5.23** Sécurité de l'information pour les services cloud
3. **A.5.30** Préparation TIC pour la continuité des activités
4. **A.7.4** Surveillance de la sécurité physique
5. **A.8.9** Gestion des configurations
6. **A.8.10** Suppression des informations
7. **A.8.11** Masquage des données
8. **A.8.12** Prévention des fuites de données
9. **A.8.16** Activités de surveillance
10. **A.8.23** Filtrage web
11. **A.8.28** Codage sécurisé

## Créer votre déclaration d'applicabilité

Tous les contrôles ne s'appliquent pas à toutes les organisations. Utilisez notre [Générateur de DdA](/soa-generator) pour :
- Examiner systématiquement chaque contrôle
- Documenter les décisions d'applicabilité
- Justifier les exclusions
- Suivre l'état de mise en œuvre

## Priorité de mise en œuvre

Concentrez-vous d'abord sur les contrôles qui traitent vos risques les plus élevés. Utilisez les résultats de votre [évaluation des risques](/risk-assessment) pour prioriser la mise en œuvre.

## Conclusion

Comprendre les contrôles de l'Annexe A est essentiel pour la mise en œuvre de l'ISO 27001. Utilisez nos outils pour travailler systématiquement sur chaque contrôle et construire un SMSI complet.
        `
      }
    },
    author: "ISO27001KIT",
    publishDate: "2025-06-05",
    updatedDate: "2025-06-05",
    category: "Controls",
    tags: ["Annex A", "ISO 27001", "Security Controls", "ISO 27001:2022", "ISMS"],
    readTime: 15,
    featured: false
  },
  {
    slug: "iso-27001-gap-analysis-guide",
    translations: {
      en: {
        title: "How to Conduct an ISO 27001 Gap Analysis",
        metaTitle: "ISO 27001 Gap Analysis: Free Checklist, Template & Step-by-Step Guide",
        metaDescription: "Complete guide to conducting an ISO 27001 gap analysis. Includes a free gap analysis checklist covering all clauses and 93 Annex A controls, plus a downloadable template and report format.",
        excerpt: "A practical guide to conducting an ISO 27001 gap analysis to identify where your organization stands and what needs improvement.",
        content: `
## What is a Gap Analysis?

A gap analysis compares your current security practices against ISO 27001 requirements to identify areas needing improvement. It's typically the first step in an ISO 27001 implementation project.

## Why Conduct a Gap Analysis?

- **Understand Your Starting Point**: Know where you are before planning where to go
- **Estimate Resources**: Identify the scope of work required
- **Prioritize Efforts**: Focus on the most critical gaps first
- **Build Business Case**: Justify investment to management
- **Set Realistic Timelines**: Plan implementation phases accurately

## Gap Analysis Methodology

### Phase 1: Preparation

**Define Scope**
- Which business units are included?
- What locations are covered?
- Which systems and processes are in scope?

**Gather Documentation**
- Existing security policies
- Procedures and work instructions
- Previous audit reports
- Risk assessments
- Incident logs

**Assemble the Team**
- IT Security personnel
- Business process owners
- Compliance officers
- Department representatives

### Phase 2: Assessment

**Clause-by-Clause Review**

Assess compliance with ISO 27001 main clauses:

1. **Clause 4: Context of the Organization**
   - Understanding the organization
   - Interested parties expectations
   - ISMS scope definition

2. **Clause 5: Leadership**
   - Management commitment
   - Security policy
   - Roles and responsibilities

3. **Clause 6: Planning**
   - Risk assessment process
   - Security objectives
   - [Change management](/controls/a-8-32-change-management)

4. **Clause 7: Support**
   - Resources allocation
   - Competence and awareness
   - Documentation

5. **Clause 8: Operation**
   - Operational planning
   - Risk assessment execution
   - Risk treatment

6. **Clause 9: Performance Evaluation**
   - Monitoring and measurement
   - Internal audit
   - Management review

7. **Clause 10: Improvement**
   - Nonconformity handling
   - Continual improvement

**Control Assessment**

Review each Annex A control:
- Is it implemented?
- Is it documented?
- Is it effective?
- Is evidence available?

### Phase 3: Analysis

**Rate Each Gap**

Use a maturity scale:
- **0 - Non-existent**: No process in place
- **1 - Initial**: Ad-hoc, uncontrolled
- **2 - Developing**: Basic process exists
- **3 - Defined**: Documented and standardized
- **4 - Managed**: Measured and monitored
- **5 - Optimized**: Continuously improved

**Prioritize Gaps**

Consider:
- Risk level (from risk assessment)
- Ease of implementation
- Resource requirements
- Dependencies
- Regulatory requirements

### Phase 4: Reporting

Create a gap analysis report including:
- Executive summary
- Methodology description
- Detailed findings by clause/control
- Maturity ratings
- Prioritized recommendations
- Estimated effort and timeline

## Using Our Gap Analysis Tool

Our [Gap Analysis Tool](/tools/gap-analysis) helps you:
- Systematically assess each control
- Document current status and evidence
- Identify priority areas
- Generate action plans
- Export comprehensive reports

## Common Gaps Found

1. **Documentation**: Missing or outdated policies
2. **Risk Assessment**: Informal or incomplete
3. **[Access Control](/controls/a-5-15-access-control)**: Weak password policies
4. **Awareness Training**: Infrequent or ineffective
5. **[Incident Management](/controls/a-5-24-information-security-incident-management-planning-and-preparation)**: No formal process
6. **[Business Continuity](/controls/a-5-30-ict-readiness-for-business-continuity)**: Untested plans
7. **[Supplier Security](/controls/a-5-19-information-security-in-supplier-relationships)**: Inadequate oversight
8. **Monitoring**: Limited logging and review

## Next Steps After Gap Analysis

1. Present findings to management
2. Develop remediation roadmap
3. Secure budget and resources
4. Begin implementation
5. Regular progress reviews

## Conclusion

A thorough gap analysis sets the foundation for successful ISO 27001 implementation. Use our free tools to conduct your assessment and build your improvement plan.
        `
      },
      fr: {
        title: "Comment mener une analyse des écarts ISO 27001",
        metaTitle: "Guide d'analyse des écarts ISO 27001 | Modèle et liste de contrôle gratuits",
        metaDescription: "Apprenez à mener efficacement une analyse des écarts ISO 27001. Identifiez les lacunes de conformité, priorisez les améliorations et planifiez votre mise en œuvre avec nos outils gratuits.",
        excerpt: "Un guide pratique pour mener une analyse des écarts ISO 27001 afin d'identifier où se situe votre organisation et ce qui doit être amélioré.",
        content: `
## Qu'est-ce qu'une analyse des écarts ?

Une analyse des écarts compare vos pratiques de sécurité actuelles aux exigences de l'ISO 27001 pour identifier les domaines nécessitant des améliorations. C'est généralement la première étape d'un projet de mise en œuvre ISO 27001.

## Pourquoi mener une analyse des écarts ?

- **Comprendre votre point de départ** : Savoir où vous êtes avant de planifier où aller
- **Estimer les ressources** : Identifier l'étendue du travail requis
- **Prioriser les efforts** : Se concentrer d'abord sur les écarts les plus critiques
- **Construire le cas métier** : Justifier l'investissement auprès de la direction
- **Définir des délais réalistes** : Planifier avec précision les phases de mise en œuvre

## Méthodologie d'analyse des écarts

### Phase 1 : Préparation

**Définir le périmètre**
- Quelles unités commerciales sont incluses ?
- Quels sites sont couverts ?
- Quels systèmes et processus sont dans le périmètre ?

**Rassembler la documentation**
- Politiques de sécurité existantes
- Procédures et instructions de travail
- Rapports d'audit précédents
- Évaluations des risques
- Journaux d'incidents

**Constituer l'équipe**
- Personnel de sécurité informatique
- Propriétaires de processus métier
- Responsables de la conformité
- Représentants des départements

### Phase 2 : Évaluation

**Examen clause par clause**

Évaluez la conformité avec les principales clauses de l'ISO 27001 :

1. **Clause 4 : Contexte de l'organisation**
   - Compréhension de l'organisation
   - Attentes des parties intéressées
   - Définition du périmètre du SMSI

2. **Clause 5 : Leadership**
   - Engagement de la direction
   - Politique de sécurité
   - Rôles et responsabilités

3. **Clause 6 : Planification**
   - Processus d'évaluation des risques
   - Objectifs de sécurité
   - Gestion du changement

4. **Clause 7 : Support**
   - Allocation des ressources
   - Compétence et sensibilisation
   - Documentation

5. **Clause 8 : Exploitation**
   - Planification opérationnelle
   - Exécution de l'évaluation des risques
   - Traitement des risques

6. **Clause 9 : Évaluation des performances**
   - Surveillance et mesure
   - Audit interne
   - Revue de direction

7. **Clause 10 : Amélioration**
   - Traitement des non-conformités
   - Amélioration continue

**Évaluation des contrôles**

Examinez chaque contrôle de l'Annexe A :
- Est-il mis en œuvre ?
- Est-il documenté ?
- Est-il efficace ?
- Des preuves sont-elles disponibles ?

### Phase 3 : Analyse

**Noter chaque écart**

Utilisez une échelle de maturité :
- **0 - Inexistant** : Aucun processus en place
- **1 - Initial** : Ad-hoc, non contrôlé
- **2 - En développement** : Un processus de base existe
- **3 - Défini** : Documenté et standardisé
- **4 - Géré** : Mesuré et surveillé
- **5 - Optimisé** : Amélioré en continu

**Prioriser les écarts**

Considérez :
- Niveau de risque (d'après l'évaluation des risques)
- Facilité de mise en œuvre
- Besoins en ressources
- Dépendances
- Exigences réglementaires

### Phase 4 : Rapport

Créez un rapport d'analyse des écarts incluant :
- Résumé exécutif
- Description de la méthodologie
- Résultats détaillés par clause/contrôle
- Notes de maturité
- Recommandations priorisées
- Effort estimé et calendrier

## Utiliser notre outil d'analyse des écarts

Notre [Outil d'analyse des écarts](/tools/gap-analysis) vous aide à :
- Évaluer systématiquement chaque contrôle
- Documenter l'état actuel et les preuves
- Identifier les domaines prioritaires
- Générer des plans d'action
- Exporter des rapports complets

## Écarts couramment trouvés

1. **Documentation** : Politiques manquantes ou obsolètes
2. **Évaluation des risques** : Informelle ou incomplète
3. **Contrôle d'accès** : Politiques de mots de passe faibles
4. **Formation de sensibilisation** : Peu fréquente ou inefficace
5. **Gestion des incidents** : Pas de processus formel
6. **Continuité des activités** : Plans non testés
7. **Sécurité des fournisseurs** : Surveillance inadéquate
8. **Surveillance** : Journalisation et examen limités

## Prochaines étapes après l'analyse des écarts

1. Présenter les résultats à la direction
2. Développer une feuille de route de remédiation
3. Sécuriser le budget et les ressources
4. Commencer la mise en œuvre
5. Revues régulières des progrès

## Conclusion

Une analyse des écarts approfondie établit les bases d'une mise en œuvre ISO 27001 réussie. Utilisez nos outils gratuits pour mener votre évaluation et construire votre plan d'amélioration.
        `
      }
    },
    author: "ISO27001KIT",
    publishDate: "2025-05-28",
    updatedDate: "2025-05-28",
    category: "Implementation",
    tags: ["Gap Analysis", "ISO 27001", "Compliance", "Assessment", "ISMS"],
    readTime: 11,
    featured: false
  },
  {
    slug: "iso-27001-internal-audit-checklist",
    translations: {
      en: {
        title: "Complete ISO 27001 Internal Audit Checklist",
        metaTitle: "ISO 27001 Internal Audit Checklist 2025 | Free Audit Template",
        metaDescription: "Comprehensive ISO 27001 internal audit checklist covering all clauses and Annex A controls. Prepare for certification with our free audit template and guidance.",
        excerpt: "Everything you need to conduct effective ISO 27001 internal audits, including checklists, techniques, and best practices.",
        content: `
## The Importance of Internal Audits

Internal audits are a mandatory ISO 27001 requirement (Clause 9.2) and serve multiple purposes:
- Verify ISMS effectiveness
- Identify improvement opportunities
- Prepare for certification audits
- Demonstrate due diligence
- Drive continual improvement

## Audit Planning

### Establish an Audit Program

Plan audits to cover:
- All ISMS processes annually
- High-risk areas more frequently
- Recent changes or incidents
- Previous nonconformities

### Auditor Selection

Auditors must be:
- Competent in audit techniques
- Knowledgeable about ISO 27001
- Objective and impartial
- Independent from areas being audited

### Audit Preparation

Before the audit:
- Review previous audit reports
- Examine relevant documentation
- Prepare audit checklist
- Schedule interviews
- Notify auditees

## Internal Audit Checklist

### Clause 4: Context of the Organization

**4.1 Understanding the Organization**
- [ ] Are internal and external issues documented?
- [ ] Is the business context regularly reviewed?

**4.2 Interested Parties**
- [ ] Are interested parties identified?
- [ ] Are their requirements documented?

**4.3 ISMS Scope**
- [ ] Is the scope clearly defined?
- [ ] Are exclusions justified?

**4.4 ISMS**
- [ ] Is the ISMS established and maintained?
- [ ] Are processes and interactions defined?

### Clause 5: Leadership

**5.1 Leadership and Commitment**
- [ ] Does top management demonstrate commitment?
- [ ] Are resources adequate?

**5.2 Policy**
- [ ] Is the security policy appropriate?
- [ ] Is it communicated and available?

**5.3 Roles and Responsibilities**
- [ ] Are roles clearly defined?
- [ ] Are responsibilities understood?

### Clause 6: Planning

**6.1 Risk Assessment**
- [ ] Is the risk assessment methodology defined?
- [ ] Are risks systematically identified and assessed?
- [ ] Is the risk treatment plan implemented?

**6.2 Objectives**
- [ ] Are security objectives established?
- [ ] Are they measurable and monitored?

**6.3 Planning of Changes**
- [ ] Are ISMS changes planned?
- [ ] Are impacts assessed?

### Clause 7: Support

**7.1 Resources**
- [ ] Are sufficient resources provided?

**7.2 Competence**
- [ ] Is competence defined for security roles?
- [ ] Is training provided and recorded?

**7.3 Awareness**
- [ ] Are personnel aware of the policy?
- [ ] Do they understand their responsibilities?

**7.4 Communication**
- [ ] Is communication planned?
- [ ] Are internal and external communications managed?

**7.5 Documented Information**
- [ ] Is required documentation maintained?
- [ ] Is document control effective?

### Clause 8: Operation

**8.1 Operational Planning**
- [ ] Are processes planned and controlled?
- [ ] Are criteria established?

**8.2 Risk Assessment**
- [ ] Are risk assessments performed at intervals?
- [ ] Are results documented?

**8.3 Risk Treatment**
- [ ] Is the risk treatment plan implemented?
- [ ] Are results documented?

### Clause 9: Performance Evaluation

**9.1 Monitoring**
- [ ] Is the ISMS monitored effectively?
- [ ] Are methods defined?

**9.2 Internal Audit**
- [ ] Is the audit program planned?
- [ ] Are audits conducted objectively?

**9.3 Management Review**
- [ ] Are reviews conducted at planned intervals?
- [ ] Are required inputs considered?

### Clause 10: Improvement

**10.1 Nonconformity**
- [ ] Are nonconformities addressed?
- [ ] Is root cause analysis performed?

**10.2 Continual Improvement**
- [ ] Is the ISMS continually improved?
- [ ] Are opportunities identified?

## Audit Techniques

### Document Review
- Policy completeness
- Procedure adequacy
- Record accuracy

### Interviews
- Process understanding
- Awareness levels
- Practical application

### Observation
- [Physical security](/controls/a-7-1-physical-security-perimeters)
- Working practices
- Technical controls

### Testing
- [Access control](/controls/a-5-15-access-control) verification
- [Backup](/controls/a-8-13-information-backup) restoration
- [Incident response](/controls/a-5-26-response-to-information-security-incidents)

## Reporting Findings

**Nonconformities**
- Major: Absence of or significant failure
- Minor: Partial implementation or isolated instance

**Observations**
- Improvement opportunities
- Best practices
- Potential risks

## Use Our Internal Audit Tool

Our [Internal Audit Checklist Tool](/internal-audit) provides:
- Comprehensive control checklists
- Customizable audit questions
- Finding documentation
- Report generation
- Progress tracking

## Conclusion

Regular internal audits are essential for maintaining an effective ISMS. Use our tools and this guidance to conduct thorough, value-adding audits.
        `
      },
      fr: {
        title: "Liste de contrôle complète d'audit interne ISO 27001",
        metaTitle: "Liste de contrôle d'audit interne ISO 27001 2025 | Modèle d'audit gratuit",
        metaDescription: "Liste de contrôle complète d'audit interne ISO 27001 couvrant toutes les clauses et contrôles de l'Annexe A. Préparez-vous à la certification avec notre modèle d'audit gratuit et nos conseils.",
        excerpt: "Tout ce dont vous avez besoin pour mener des audits internes ISO 27001 efficaces, y compris des listes de contrôle, des techniques et des meilleures pratiques.",
        content: `
## L'importance des audits internes

Les audits internes sont une exigence obligatoire de l'ISO 27001 (Clause 9.2) et servent plusieurs objectifs :
- Vérifier l'efficacité du SMSI
- Identifier les opportunités d'amélioration
- Préparer les audits de certification
- Démontrer la diligence raisonnable
- Conduire l'amélioration continue

## Planification de l'audit

### Établir un programme d'audit

Planifiez les audits pour couvrir :
- Tous les processus du SMSI annuellement
- Les domaines à haut risque plus fréquemment
- Les changements ou incidents récents
- Les non-conformités précédentes

### Sélection des auditeurs

Les auditeurs doivent être :
- Compétents dans les techniques d'audit
- Connaisseurs de l'ISO 27001
- Objectifs et impartiaux
- Indépendants des domaines audités

### Préparation de l'audit

Avant l'audit :
- Examiner les rapports d'audit précédents
- Examiner la documentation pertinente
- Préparer la liste de contrôle d'audit
- Planifier les entretiens
- Notifier les audités

## Liste de contrôle d'audit interne

### Clause 4 : Contexte de l'organisation

**4.1 Compréhension de l'organisation**
- [ ] Les enjeux internes et externes sont-ils documentés ?
- [ ] Le contexte métier est-il régulièrement revu ?

**4.2 Parties intéressées**
- [ ] Les parties intéressées sont-elles identifiées ?
- [ ] Leurs exigences sont-elles documentées ?

**4.3 Périmètre du SMSI**
- [ ] Le périmètre est-il clairement défini ?
- [ ] Les exclusions sont-elles justifiées ?

**4.4 SMSI**
- [ ] Le SMSI est-il établi et maintenu ?
- [ ] Les processus et interactions sont-ils définis ?

### Clause 5 : Leadership

**5.1 Leadership et engagement**
- [ ] La direction démontre-t-elle son engagement ?
- [ ] Les ressources sont-elles adéquates ?

**5.2 Politique**
- [ ] La politique de sécurité est-elle appropriée ?
- [ ] Est-elle communiquée et disponible ?

**5.3 Rôles et responsabilités**
- [ ] Les rôles sont-ils clairement définis ?
- [ ] Les responsabilités sont-elles comprises ?

### Clause 6 : Planification

**6.1 Évaluation des risques**
- [ ] La méthodologie d'évaluation des risques est-elle définie ?
- [ ] Les risques sont-ils systématiquement identifiés et évalués ?
- [ ] Le plan de traitement des risques est-il mis en œuvre ?

**6.2 Objectifs**
- [ ] Les objectifs de sécurité sont-ils établis ?
- [ ] Sont-ils mesurables et surveillés ?

**6.3 Planification des changements**
- [ ] Les changements du SMSI sont-ils planifiés ?
- [ ] Les impacts sont-ils évalués ?

### Clause 7 : Support

**7.1 Ressources**
- [ ] Des ressources suffisantes sont-elles fournies ?

**7.2 Compétence**
- [ ] La compétence est-elle définie pour les rôles de sécurité ?
- [ ] La formation est-elle fournie et enregistrée ?

**7.3 Sensibilisation**
- [ ] Le personnel est-il conscient de la politique ?
- [ ] Comprend-il ses responsabilités ?

**7.4 Communication**
- [ ] La communication est-elle planifiée ?
- [ ] Les communications internes et externes sont-elles gérées ?

**7.5 Informations documentées**
- [ ] La documentation requise est-elle maintenue ?
- [ ] Le contrôle des documents est-il efficace ?

### Clause 8 : Exploitation

**8.1 Planification opérationnelle**
- [ ] Les processus sont-ils planifiés et contrôlés ?
- [ ] Les critères sont-ils établis ?

**8.2 Évaluation des risques**
- [ ] Les évaluations des risques sont-elles effectuées à intervalles réguliers ?
- [ ] Les résultats sont-ils documentés ?

**8.3 Traitement des risques**
- [ ] Le plan de traitement des risques est-il mis en œuvre ?
- [ ] Les résultats sont-ils documentés ?

### Clause 9 : Évaluation des performances

**9.1 Surveillance**
- [ ] Le SMSI est-il surveillé efficacement ?
- [ ] Les méthodes sont-elles définies ?

**9.2 Audit interne**
- [ ] Le programme d'audit est-il planifié ?
- [ ] Les audits sont-ils menés de manière objective ?

**9.3 Revue de direction**
- [ ] Les revues sont-elles menées à intervalles planifiés ?
- [ ] Les éléments d'entrée requis sont-ils considérés ?

### Clause 10 : Amélioration

**10.1 Non-conformité**
- [ ] Les non-conformités sont-elles traitées ?
- [ ] L'analyse des causes profondes est-elle effectuée ?

**10.2 Amélioration continue**
- [ ] Le SMSI est-il continuellement amélioré ?
- [ ] Les opportunités sont-elles identifiées ?

## Techniques d'audit

### Revue documentaire
- Complétude des politiques
- Adéquation des procédures
- Exactitude des enregistrements

### Entretiens
- Compréhension des processus
- Niveaux de sensibilisation
- Application pratique

### Observation
- Sécurité physique
- Pratiques de travail
- Contrôles techniques

### Tests
- Vérification du contrôle d'accès
- Restauration des sauvegardes
- Réponse aux incidents

## Rapporter les constats

**Non-conformités**
- Majeure : Absence ou défaillance significative
- Mineure : Mise en œuvre partielle ou instance isolée

**Observations**
- Opportunités d'amélioration
- Meilleures pratiques
- Risques potentiels

## Utiliser notre outil d'audit interne

Notre [Outil de liste de contrôle d'audit interne](/internal-audit) fournit :
- Listes de contrôle complètes
- Questions d'audit personnalisables
- Documentation des constats
- Génération de rapports
- Suivi des progrès

## Conclusion

Des audits internes réguliers sont essentiels pour maintenir un SMSI efficace. Utilisez nos outils et ces conseils pour mener des audits approfondis et à valeur ajoutée.
        `
      }
    },
    author: "ISO27001KIT",
    publishDate: "2025-05-22",
    updatedDate: "2025-05-22",
    category: "Audit",
    tags: ["Internal Audit", "ISO 27001", "Audit Checklist", "Compliance", "Certification"],
    readTime: 13,
    featured: false
  },
  {
    slug: "iso-27001-vs-soc-2-comparison",
    translations: {
      en: {
        title: "ISO 27001 vs SOC 2: Which Framework is Right for You?",
        metaTitle: "ISO 27001 vs SOC 2: Which Security Framework Should You Choose in 2025?",
        metaDescription: "Side-by-side comparison of ISO 27001 and SOC 2. Understand the key differences, costs, timelines, and which framework fits your business.",
        excerpt: "A detailed comparison of ISO 27001 and SOC 2 to help you choose the right security framework for your organization.",
        content: `
## Introduction

Both ISO 27001 and SOC 2 are leading security frameworks, but they serve different purposes and audiences. Understanding their differences helps you make the right choice.

## Quick Comparison

| Aspect | ISO 27001 | SOC 2 |
|--------|-----------|-------|
| Origin | International (ISO) | United States (AICPA) |
| Scope | Global recognition | Primarily North America |
| Focus | ISMS management | Trust Services Criteria |
| Certification | Yes (3-year certificate) | No (audit report) |
| Prescriptive | Framework-based | Criteria-based |
| Cost | Generally lower | Can be higher |

## ISO 27001 Overview

### What It Is
An international standard specifying requirements for an Information Security Management System (ISMS).

### Key Characteristics
- **Systematic Approach**: Comprehensive management framework
- **Risk-Based**: Security controls based on risk assessment
- **Certification**: Formal certification by accredited bodies
- **Global Recognition**: Accepted worldwide
- **93 Controls**: Annex A provides control framework

### Best For
- Organizations with international operations
- Companies seeking formal certification
- Enterprises wanting comprehensive security framework
- Regulatory environments requiring ISO standards

## SOC 2 Overview

### What It Is
An audit framework developed by AICPA based on Trust Services Criteria.

### Key Characteristics
- **Trust Services Criteria**: Security, Availability, Processing Integrity, Confidentiality, Privacy
- **Type I or Type II**: Point-in-time or period of time reports
- **Service Organizations**: Designed for service providers
- **Audit Report**: Attestation report, not certification
- **Flexible Scope**: Choose which criteria apply

### Best For
- SaaS and cloud service providers
- Companies serving US customers
- Organizations needing quick attestation
- Service organizations with specific trust requirements

## Key Differences

### 1. Geographic Recognition
**ISO 27001**: Globally recognized standard
**SOC 2**: Primarily recognized in North America

### 2. Certification vs. Attestation
**ISO 27001**: Formal certification valid for 3 years with surveillance audits
**SOC 2**: Audit report (attestation) requiring annual renewal

### 3. Control Framework
**ISO 27001**: 93 Annex A controls to evaluate
**SOC 2**: Trust Services Criteria with flexible controls

### 4. Audit Process
**ISO 27001**: Stage 1 (documentation) + Stage 2 (implementation)
**SOC 2**: Type I (design) or Type II (design + operating effectiveness)

### 5. Cost Considerations
**ISO 27001**: Typically $15,000-$50,000 for certification
**SOC 2**: Typically $30,000-$100,000+ for Type II

## Can You Do Both?

Yes! Many organizations pursue both frameworks because:
- Different customers require different evidence
- Controls overlap significantly (60-70%)
- Combined approach provides comprehensive coverage
- Some industries require both

### Integrated Approach
1. Start with ISO 27001 for comprehensive framework
2. Map controls to SOC 2 Trust Services Criteria
3. Conduct integrated audits where possible
4. Maintain single set of evidence

## Decision Framework

**Choose ISO 27001 if:**
- Operating internationally
- Need formal certification
- Want comprehensive management system
- Long-term security program
- Regulatory requirements specify ISO

**Choose SOC 2 if:**
- US-focused customer base
- SaaS or cloud service provider
- Customers specifically request SOC 2
- Need quick attestation
- Prefer flexible scope

**Choose Both if:**
- Diverse customer requirements
- Enterprise customers
- High security expectations
- Competitive differentiation

## Getting Started

For ISO 27001 implementation, use our free tools:
- [Gap Analysis](/tools/gap-analysis) - Assess your current state
- [Risk Assessment](/risk-assessment) - Identify and evaluate risks
- [SoA Generator](/soa-generator) - Document control applicability
- [Implementation Roadmap](/implementation-roadmap) - Plan your project

## Conclusion

Both frameworks provide valuable security assurance. Your choice depends on customer requirements, geographic focus, and organizational goals. Many successful organizations implement both for maximum market coverage.
        `
      },
      fr: {
        title: "ISO 27001 vs SOC 2 : Quel référentiel est fait pour vous ?",
        metaTitle: "Comparaison ISO 27001 vs SOC 2 | Différences clés et lequel choisir",
        metaDescription: "Comparez les référentiels ISO 27001 et SOC 2. Comprenez les différences clés en termes de portée, d'exigences, de processus de certification et lequel est le meilleur pour votre organisation.",
        excerpt: "Une comparaison détaillée de l'ISO 27001 et du SOC 2 pour vous aider à choisir le bon référentiel de sécurité pour votre organisation.",
        content: `
## Introduction

L'ISO 27001 et le SOC 2 sont tous deux des référentiels de sécurité de premier plan, mais ils servent des objectifs et des publics différents. Comprendre leurs différences vous aide à faire le bon choix.

## Comparaison rapide

| Aspect | ISO 27001 | SOC 2 |
|--------|-----------|-------|
| Origine | International (ISO) | États-Unis (AICPA) |
| Portée | Reconnaissance mondiale | Principalement Amérique du Nord |
| Focus | Gestion du SMSI | Critères de services de confiance |
| Certification | Oui (certificat de 3 ans) | Non (rapport d'audit) |
| Prescriptif | Basé sur un cadre | Basé sur des critères |
| Coût | Généralement inférieur | Peut être plus élevé |

## Aperçu de l'ISO 27001

### Ce que c'est
Une norme internationale spécifiant les exigences pour un Système de Management de la Sécurité de l'Information (SMSI).

### Caractéristiques clés
- **Approche systématique** : Cadre de gestion complet
- **Basé sur les risques** : Contrôles de sécurité basés sur l'évaluation des risques
- **Certification** : Certification formelle par des organismes accrédités
- **Reconnaissance mondiale** : Acceptée dans le monde entier
- **93 contrôles** : L'Annexe A fournit le cadre de contrôle

### Idéal pour
- Les organisations avec des opérations internationales
- Les entreprises recherchant une certification formelle
- Les entreprises voulant un cadre de sécurité complet
- Les environnements réglementaires exigeant des normes ISO

## Aperçu du SOC 2

### Ce que c'est
Un cadre d'audit développé par l'AICPA basé sur les critères de services de confiance.

### Caractéristiques clés
- **Critères de services de confiance** : Sécurité, Disponibilité, Intégrité du traitement, Confidentialité, Vie privée
- **Type I ou Type II** : Rapports ponctuels ou sur une période
- **Organisations de services** : Conçu pour les prestataires de services
- **Rapport d'audit** : Rapport d'attestation, pas de certification
- **Portée flexible** : Choisissez quels critères s'appliquent

### Idéal pour
- Les fournisseurs SaaS et cloud
- Les entreprises servant des clients américains
- Les organisations ayant besoin d'une attestation rapide
- Les organisations de services avec des exigences de confiance spécifiques

## Différences clés

### 1. Reconnaissance géographique
**ISO 27001** : Norme reconnue mondialement
**SOC 2** : Principalement reconnu en Amérique du Nord

### 2. Certification vs. Attestation
**ISO 27001** : Certification formelle valable 3 ans avec audits de surveillance
**SOC 2** : Rapport d'audit (attestation) nécessitant un renouvellement annuel

### 3. Cadre de contrôle
**ISO 27001** : 93 contrôles de l'Annexe A à évaluer
**SOC 2** : Critères de services de confiance avec contrôles flexibles

### 4. Processus d'audit
**ISO 27001** : Phase 1 (documentation) + Phase 2 (mise en œuvre)
**SOC 2** : Type I (conception) ou Type II (conception + efficacité opérationnelle)

### 5. Considérations de coût
**ISO 27001** : Généralement 15 000 à 50 000 $ pour la certification
**SOC 2** : Généralement 30 000 à 100 000 $+ pour le Type II

## Peut-on faire les deux ?

Oui ! De nombreuses organisations poursuivent les deux référentiels car :
- Différents clients exigent différentes preuves
- Les contrôles se chevauchent significativement (60-70%)
- L'approche combinée offre une couverture complète
- Certaines industries exigent les deux

### Approche intégrée
1. Commencez par l'ISO 27001 pour un cadre complet
2. Mappez les contrôles aux critères de services de confiance SOC 2
3. Menez des audits intégrés si possible
4. Maintenez un ensemble unique de preuves

## Cadre de décision

**Choisissez l'ISO 27001 si :**
- Opérations internationales
- Besoin d'une certification formelle
- Souhait d'un système de gestion complet
- Programme de sécurité à long terme
- Exigences réglementaires spécifiant l'ISO

**Choisissez le SOC 2 si :**
- Base de clients américaine
- Fournisseur SaaS ou cloud
- Les clients demandent spécifiquement le SOC 2
- Besoin d'une attestation rapide
- Préférence pour une portée flexible

**Choisissez les deux si :**
- Exigences clients diverses
- Clients entreprises
- Attentes de sécurité élevées
- Différenciation concurrentielle

## Pour commencer

Pour la mise en œuvre de l'ISO 27001, utilisez nos outils gratuits :
- [Analyse des écarts](/tools/gap-analysis) - Évaluez votre état actuel
- [Évaluation des risques](/risk-assessment) - Identifiez et évaluez les risques
- [Générateur de DdA](/soa-generator) - Documentez l'applicabilité des contrôles
- [Feuille de route de mise en œuvre](/implementation-roadmap) - Planifiez votre projet

## Conclusion

Les deux référentiels fournissent une assurance de sécurité précieuse. Votre choix dépend des exigences des clients, du focus géographique et des objectifs organisationnels. De nombreuses organisations prospères mettent en œuvre les deux pour une couverture maximale du marché.
        `
      }
    },
    author: "ISO27001KIT",
    publishDate: "2025-05-18",
    updatedDate: "2025-05-18",
    category: "Compliance",
    tags: ["ISO 27001", "SOC 2", "Compliance", "Framework Comparison", "Information Security"],
    readTime: 9,
    featured: false
  },
  {
    slug: "building-security-awareness-program",
    translations: {
      en: {
        title: "Building an Effective Security Awareness Program for ISO 27001",
        metaTitle: "Security Awareness Program Guide | ISO 27001 Training Requirements",
        metaDescription: "Learn how to build an effective security awareness program that meets ISO 27001 requirements. Includes training topics, delivery methods, and measurement strategies.",
        excerpt: "Create a security awareness program that engages employees and satisfies ISO 27001 requirements for competence and awareness.",
        content: `
## Why Security Awareness Matters

People are often called the weakest link in security, but with proper training, they become your strongest defense. ISO 27001 requires organizations to ensure personnel are aware of security policies and their responsibilities.

## ISO 27001 Requirements

### Clause 7.2: Competence
- Determine necessary competence
- Ensure competence through education, training, or experience
- Take actions to acquire necessary competence
- Retain evidence of competence

### Clause 7.3: Awareness
Personnel must be aware of:
- The [information security policy](/controls/a-5-1-policies-for-information-security)
- Their contribution to ISMS effectiveness
- Benefits of improved security performance
- Implications of not conforming to requirements

### Control A.6.3: Information Security Awareness, Education and Training
All personnel must receive appropriate awareness training relevant to their job function.

## Building Your Program

### Step 1: Assess Training Needs

**By Role:**
- All employees: Basic [security awareness](/controls/a-6-3-information-security-awareness-education-and-training)
- IT staff: Technical security training
- Developers: [Secure coding](/controls/a-8-28-secure-coding) practices
- Managers: Risk management and compliance
- Executives: Strategic security leadership

**By Risk:**
- Identify high-risk behaviors
- Target phishing susceptibility
- Address common security mistakes

### Step 2: Define Learning Objectives

Clear objectives for each audience:
- Recognize phishing attempts
- Handle sensitive data correctly
- Report security incidents
- Follow [access control](/controls/a-5-15-access-control) procedures
- Understand password requirements

### Step 3: Develop Content

**Core Topics:**
1. Information security policy overview
2. Password security and authentication
3. Phishing and social engineering
4. Safe internet and email usage
5. [Data classification](/controls/a-5-12-classification-of-information) and handling
6. [Physical security](/controls/a-7-1-physical-security-perimeters) awareness
7. Mobile device security
8. Remote working security
9. Incident reporting
10. Regulatory requirements

**Delivery Methods:**
- Online e-learning modules
- In-person workshops
- Video content
- Interactive simulations
- Gamification elements
- Regular communications
- Posters and visual aids

### Step 4: Implement Training

**Onboarding:**
- New employee orientation
- Role-specific training
- Policy acknowledgment

**Ongoing:**
- Annual refresher training
- Quarterly updates
- Ad-hoc awareness campaigns
- Simulated phishing tests

### Step 5: Measure Effectiveness

**Metrics to Track:**
- Training completion rates
- Quiz and assessment scores
- Phishing simulation click rates
- Security incident trends
- Policy violation rates
- Help desk security queries

**Continuous Improvement:**
- Analyze metric trends
- Gather employee feedback
- Update content regularly
- Address emerging threats
- Benchmark against industry

## Phishing Simulation Program

### Purpose
Test and improve employee resilience to phishing attacks.

### Implementation:
1. Start with baseline assessment
2. Send realistic simulations monthly
3. Provide immediate feedback
4. Offer targeted training for clickers
5. Track improvement over time

### Best Practices:
- Vary difficulty levels
- Use current threat intelligence
- Don't punish, educate
- Celebrate improvements
- Report to management

## Making Training Engaging

**Techniques:**
- Real-world examples and case studies
- Interactive scenarios
- Gamification and rewards
- Short, focused modules (microlearning)
- Mobile-friendly content
- Personal relevance

**Avoid:**
- Death by PowerPoint
- Overly technical jargon
- One-size-fits-all approach
- Annual-only training
- Punishment-focused culture

## Documentation Requirements

Maintain records of:
- Training materials and versions
- Attendance and completion records
- Assessment results
- Competence evaluations
- Program updates and changes

## Budget Considerations

**Options by Budget:**
- **Low**: Free resources, internal development
- **Medium**: Commercial e-learning platforms
- **High**: Custom content, advanced simulations

## Conclusion

An effective security awareness program is essential for ISO 27001 compliance and real security improvement. Focus on engaging content, continuous reinforcement, and measurable outcomes.

Visit our [Documents](/documents) section for policy templates to support your awareness program.
        `
      },
      fr: {
        title: "Construire un programme efficace de sensibilisation à la sécurité pour l'ISO 27001",
        metaTitle: "Guide du programme de sensibilisation à la sécurité | Exigences de formation ISO 27001",
        metaDescription: "Apprenez à construire un programme efficace de sensibilisation à la sécurité qui répond aux exigences de l'ISO 27001. Inclut les sujets de formation, les méthodes de diffusion et les stratégies de mesure.",
        excerpt: "Créez un programme de sensibilisation à la sécurité qui engage les employés et satisfait les exigences de compétence et de sensibilisation de l'ISO 27001.",
        content: `
## Pourquoi la sensibilisation à la sécurité est importante

Les personnes sont souvent appelées le maillon faible de la sécurité, mais avec une formation appropriée, elles deviennent votre meilleure défense. L'ISO 27001 exige que les organisations s'assurent que le personnel est conscient des politiques de sécurité et de ses responsabilités.

## Exigences de l'ISO 27001

### Clause 7.2 : Compétence
- Déterminer la compétence nécessaire
- Assurer la compétence par l'éducation, la formation ou l'expérience
- Prendre des mesures pour acquérir la compétence nécessaire
- Conserver les preuves de compétence

### Clause 7.3 : Sensibilisation
Le personnel doit être conscient de :
- La politique de sécurité de l'information
- Sa contribution à l'efficacité du SMSI
- Les avantages d'une meilleure performance en matière de sécurité
- Les implications du non-respect des exigences

### Contrôle A.6.3 : Sensibilisation, éducation et formation à la sécurité de l'information
Tout le personnel doit recevoir une formation de sensibilisation appropriée et pertinente pour sa fonction.

## Construire votre programme

### Étape 1 : Évaluer les besoins de formation

**Par rôle :**
- Tous les employés : Sensibilisation de base à la sécurité
- Personnel informatique : Formation technique en sécurité
- Développeurs : Pratiques de codage sécurisé
- Managers : Gestion des risques et conformité
- Dirigeants : Leadership stratégique en sécurité

**Par risque :**
- Identifier les comportements à haut risque
- Cibler la susceptibilité au phishing
- Traiter les erreurs de sécurité courantes

### Étape 2 : Définir les objectifs d'apprentissage

Objectifs clairs pour chaque public :
- Reconnaître les tentatives de phishing
- Manipuler correctement les données sensibles
- Signaler les incidents de sécurité
- Suivre les procédures de contrôle d'accès
- Comprendre les exigences de mot de passe

### Étape 3 : Développer le contenu

**Sujets principaux :**
1. Aperçu de la politique de sécurité de l'information
2. Sécurité des mots de passe et authentification
3. Phishing et ingénierie sociale
4. Utilisation sécurisée d'Internet et des e-mails
5. Classification et manipulation des données
6. Sensibilisation à la sécurité physique
7. Sécurité des appareils mobiles
8. Sécurité du travail à distance
9. Signalement des incidents
10. Exigences réglementaires

**Méthodes de diffusion :**
- Modules d'e-learning en ligne
- Ateliers en personne
- Contenu vidéo
- Simulations interactives
- Éléments de gamification
- Communications régulières
- Affiches et aides visuelles

### Étape 4 : Mettre en œuvre la formation

**Intégration :**
- Orientation des nouveaux employés
- Formation spécifique au rôle
- Reconnaissance de la politique

**Continu :**
- Formation de rappel annuelle
- Mises à jour trimestrielles
- Campagnes de sensibilisation ad-hoc
- Tests de phishing simulés

### Étape 5 : Mesurer l'efficacité

**Métriques à suivre :**
- Taux d'achèvement de la formation
- Scores des quiz et évaluations
- Taux de clics des simulations de phishing
- Tendances des incidents de sécurité
- Taux de violation des politiques
- Requêtes de sécurité au support

**Amélioration continue :**
- Analyser les tendances des métriques
- Recueillir les retours des employés
- Mettre à jour régulièrement le contenu
- Traiter les menaces émergentes
- Se comparer à l'industrie

## Programme de simulation de phishing

### Objectif
Tester et améliorer la résilience des employés aux attaques de phishing.

### Mise en œuvre :
1. Commencer par une évaluation de base
2. Envoyer des simulations réalistes mensuellement
3. Fournir un retour immédiat
4. Offrir une formation ciblée pour les cliqueurs
5. Suivre l'amélioration dans le temps

### Meilleures pratiques :
- Varier les niveaux de difficulté
- Utiliser les renseignements sur les menaces actuelles
- Ne pas punir, éduquer
- Célébrer les améliorations
- Rapporter à la direction

## Rendre la formation engageante

**Techniques :**
- Exemples et études de cas réels
- Scénarios interactifs
- Gamification et récompenses
- Modules courts et ciblés (microlearning)
- Contenu adapté aux mobiles
- Pertinence personnelle

**À éviter :**
- Mort par PowerPoint
- Jargon trop technique
- Approche unique pour tous
- Formation annuelle uniquement
- Culture axée sur la punition

## Exigences de documentation

Maintenir des enregistrements de :
- Matériels de formation et versions
- Présence et dossiers d'achèvement
- Résultats des évaluations
- Évaluations des compétences
- Mises à jour et changements du programme

## Considérations budgétaires

**Options par budget :**
- **Faible** : Ressources gratuites, développement interne
- **Moyen** : Plateformes d'e-learning commerciales
- **Élevé** : Contenu personnalisé, simulations avancées

## Conclusion

Un programme efficace de sensibilisation à la sécurité est essentiel pour la conformité ISO 27001 et l'amélioration réelle de la sécurité. Concentrez-vous sur un contenu engageant, un renforcement continu et des résultats mesurables.

Visitez notre section [Documents](/documents) pour les modèles de politiques pour soutenir votre programme de sensibilisation.
        `
      }
    },
    author: "ISO27001KIT",
    publishDate: "2025-05-12",
    updatedDate: "2025-05-12",
    category: "Training",
    tags: ["Security Awareness", "Training", "ISO 27001", "Phishing", "Employee Security"],
    readTime: 10,
    featured: false
  },
  {
    slug: "iso-27001-certification-cost-guide",
    translations: {
      en: {
        title: "ISO 27001 Certification Cost: Complete 2025 Breakdown",
        metaTitle: "ISO 27001 Certification Cost: Complete 2025 Breakdown",
        metaDescription: "How much does ISO 27001 certification really cost? Full breakdown of consultant fees, audit costs, tooling, and hidden expenses for small and large organizations.",
        excerpt: "A realistic breakdown of ISO 27001 certification costs to help you budget effectively for your implementation project.",
        content: `
## Understanding ISO 27001 Costs

ISO 27001 certification involves multiple cost components. Understanding these helps you budget accurately and avoid surprises.

## Cost Categories

### 1. Implementation Costs

**Internal Resources:**
- Project manager: 20-50% of time for 6-12 months
- IT security staff: 30-60% of time during implementation
- Business unit representatives: Participation in assessments and reviews
- Estimated cost: $30,000-$100,000+ (based on salaries)

**External Consulting:**
- Readiness assessment: $3,000-$10,000
- Full implementation support: $20,000-$75,000
- Gap analysis only: $5,000-$15,000
- Estimated cost: $10,000-$75,000

**Technology and Tools:**
- Security tools and software: $5,000-$50,000
- GRC platform: $10,000-$50,000/year
- Training platforms: $2,000-$10,000/year
- Estimated cost: $10,000-$100,000

### 2. Certification Audit Costs

**Audit Fees (Certification Body):**
- Stage 1 audit: $3,000-$8,000
- Stage 2 audit: $8,000-$25,000
- Total initial certification: $12,000-$35,000

**Factors Affecting Audit Cost:**
- Number of employees in scope
- Number of locations
- Complexity of operations
- Industry sector
- Certification body chosen

### 3. Ongoing Maintenance Costs

**Annual Surveillance Audits:**
- Year 1 surveillance: $4,000-$12,000
- Year 2 surveillance: $4,000-$12,000

**Recertification (Every 3 Years):**
- Full recertification audit: $10,000-$30,000

**Continuous Improvement:**
- Internal audit resources
- Training and awareness
- Tool subscriptions
- Process improvements

## Cost by Organization Size

### Small Business (10-50 employees)
| Cost Category | Estimated Range |
|---------------|-----------------|
| Implementation | $15,000-$40,000 |
| Consulting | $10,000-$25,000 |
| Technology | $5,000-$15,000 |
| Certification Audit | $8,000-$15,000 |
| **Total First Year** | **$38,000-$95,000** |
| Annual Maintenance | $10,000-$25,000 |

### Medium Business (50-250 employees)
| Cost Category | Estimated Range |
|---------------|-----------------|
| Implementation | $40,000-$100,000 |
| Consulting | $20,000-$50,000 |
| Technology | $15,000-$50,000 |
| Certification Audit | $15,000-$35,000 |
| **Total First Year** | **$90,000-$235,000** |
| Annual Maintenance | $25,000-$60,000 |

### Large Enterprise (250+ employees)
| Cost Category | Estimated Range |
|---------------|-----------------|
| Implementation | $100,000-$300,000+ |
| Consulting | $50,000-$150,000 |
| Technology | $50,000-$200,000 |
| Certification Audit | $30,000-$75,000 |
| **Total First Year** | **$230,000-$725,000+** |
| Annual Maintenance | $60,000-$150,000 |

## Cost Optimization Strategies

### 1. Use Free Tools
Our free [ISO 27001 tools](/tools/gap-analysis) reduce consulting needs:
- Gap Analysis Tool
- Risk Assessment Tool
- SoA Generator
- Internal Audit Checklist

### 2. Leverage Existing Resources
- Use existing policies as starting points
- Repurpose compliance documentation
- Utilize internal expertise

### 3. Phased Implementation
- Start with critical areas
- Expand scope gradually
- Spread costs over time

### 4. Compare Certification Bodies
- Get multiple quotes
- Consider regional bodies
- Evaluate experience in your sector

### 5. Build Internal Capability
- Train internal auditors
- Develop in-house expertise
- Reduce ongoing consulting needs

## ROI Considerations

**Direct Benefits:**
- Win contracts requiring ISO 27001
- Reduce security incidents
- Lower insurance premiums
- Avoid regulatory fines

**Indirect Benefits:**
- Improved security posture
- Enhanced customer trust
- Competitive differentiation
- Operational efficiency

## Hidden Costs to Consider

1. **Employee time** away from primary duties
2. **Remediation costs** for identified gaps
3. **Technology upgrades** needed for compliance
4. **Ongoing training** and awareness programs
5. **Documentation maintenance** and updates

## Budget Template

Use this structure for your budget:

**Year 1 (Implementation + Certification):**
- Internal labor: $XX
- Consulting: $XX
- Technology: $XX
- Training: $XX
- Certification audit: $XX
- Contingency (15-20%): $XX

**Ongoing Annual:**
- Surveillance audits: $XX
- Internal audits: $XX
- Training refresh: $XX
- Tool subscriptions: $XX
- Continuous improvement: $XX

## Conclusion

ISO 27001 certification is an investment that pays off through improved security, customer trust, and competitive advantage. Use our free tools to reduce implementation costs and build a sustainable security program.

Start with our [Gap Analysis](/tools/gap-analysis) to understand your current state and estimate your implementation scope.
        `
      },
      fr: {
        title: "Coût de la certification ISO 27001 : Analyse complète 2025",
        metaTitle: "Coût de la certification ISO 27001 2025 | Guide de planification budgétaire",
        metaDescription: "Comprenez le véritable coût de la certification ISO 27001 en 2025. Couvre les coûts de mise en œuvre, de conseil, d'audits et de maintenance continue avec des conseils d'optimisation budgétaire.",
        excerpt: "Une analyse réaliste des coûts de certification ISO 27001 pour vous aider à budgétiser efficacement votre projet de mise en œuvre.",
        content: `
## Comprendre les coûts de l'ISO 27001

La certification ISO 27001 implique plusieurs composantes de coût. Les comprendre vous aide à budgétiser avec précision et à éviter les surprises.

## Catégories de coûts

### 1. Coûts de mise en œuvre

**Ressources internes :**
- Chef de projet : 20-50% du temps pendant 6-12 mois
- Personnel de sécurité informatique : 30-60% du temps pendant la mise en œuvre
- Représentants des unités commerciales : Participation aux évaluations et revues
- Coût estimé : 30 000-100 000 $+ (basé sur les salaires)

**Conseil externe :**
- Évaluation de préparation : 3 000-10 000 $
- Support complet de mise en œuvre : 20 000-75 000 $
- Analyse des écarts uniquement : 5 000-15 000 $
- Coût estimé : 10 000-75 000 $

**Technologie et outils :**
- Outils et logiciels de sécurité : 5 000-50 000 $
- Plateforme GRC : 10 000-50 000 $/an
- Plateformes de formation : 2 000-10 000 $/an
- Coût estimé : 10 000-100 000 $

### 2. Coûts d'audit de certification

**Frais d'audit (Organisme de certification) :**
- Audit de phase 1 : 3 000-8 000 $
- Audit de phase 2 : 8 000-25 000 $
- Certification initiale totale : 12 000-35 000 $

**Facteurs affectant le coût de l'audit :**
- Nombre d'employés dans le périmètre
- Nombre de sites
- Complexité des opérations
- Secteur d'activité
- Organisme de certification choisi

### 3. Coûts de maintenance continue

**Audits de surveillance annuels :**
- Surveillance année 1 : 4 000-12 000 $
- Surveillance année 2 : 4 000-12 000 $

**Recertification (tous les 3 ans) :**
- Audit de recertification complet : 10 000-30 000 $

**Amélioration continue :**
- Ressources d'audit interne
- Formation et sensibilisation
- Abonnements aux outils
- Améliorations des processus

## Coût par taille d'organisation

### Petite entreprise (10-50 employés)
| Catégorie de coût | Fourchette estimée |
|-------------------|-------------------|
| Mise en œuvre | 15 000-40 000 $ |
| Conseil | 10 000-25 000 $ |
| Technologie | 5 000-15 000 $ |
| Audit de certification | 8 000-15 000 $ |
| **Total première année** | **38 000-95 000 $** |
| Maintenance annuelle | 10 000-25 000 $ |

### Entreprise moyenne (50-250 employés)
| Catégorie de coût | Fourchette estimée |
|-------------------|-------------------|
| Mise en œuvre | 40 000-100 000 $ |
| Conseil | 20 000-50 000 $ |
| Technologie | 15 000-50 000 $ |
| Audit de certification | 15 000-35 000 $ |
| **Total première année** | **90 000-235 000 $** |
| Maintenance annuelle | 25 000-60 000 $ |

### Grande entreprise (250+ employés)
| Catégorie de coût | Fourchette estimée |
|-------------------|-------------------|
| Mise en œuvre | 100 000-300 000 $+ |
| Conseil | 50 000-150 000 $ |
| Technologie | 50 000-200 000 $ |
| Audit de certification | 30 000-75 000 $ |
| **Total première année** | **230 000-725 000 $+** |
| Maintenance annuelle | 60 000-150 000 $ |

## Stratégies d'optimisation des coûts

### 1. Utiliser des outils gratuits
Nos [outils ISO 27001](/tools/gap-analysis) gratuits réduisent les besoins en conseil :
- Outil d'analyse des écarts
- Outil d'évaluation des risques
- Générateur de DdA
- Liste de contrôle d'audit interne

### 2. Exploiter les ressources existantes
- Utiliser les politiques existantes comme points de départ
- Réutiliser la documentation de conformité
- Utiliser l'expertise interne

### 3. Mise en œuvre par phases
- Commencer par les domaines critiques
- Élargir progressivement le périmètre
- Étaler les coûts dans le temps

### 4. Comparer les organismes de certification
- Obtenir plusieurs devis
- Considérer les organismes régionaux
- Évaluer l'expérience dans votre secteur

### 5. Développer les capacités internes
- Former des auditeurs internes
- Développer l'expertise interne
- Réduire les besoins en conseil continu

## Considérations sur le ROI

**Avantages directs :**
- Gagner des contrats exigeant l'ISO 27001
- Réduire les incidents de sécurité
- Baisser les primes d'assurance
- Éviter les amendes réglementaires

**Avantages indirects :**
- Posture de sécurité améliorée
- Confiance des clients renforcée
- Différenciation concurrentielle
- Efficacité opérationnelle

## Coûts cachés à considérer

1. **Temps des employés** détourné des tâches principales
2. **Coûts de remédiation** pour les écarts identifiés
3. **Mises à niveau technologiques** nécessaires pour la conformité
4. **Formation continue** et programmes de sensibilisation
5. **Maintenance de la documentation** et mises à jour

## Modèle de budget

Utilisez cette structure pour votre budget :

**Année 1 (Mise en œuvre + Certification) :**
- Main d'œuvre interne : XX $
- Conseil : XX $
- Technologie : XX $
- Formation : XX $
- Audit de certification : XX $
- Contingence (15-20%) : XX $

**Annuel continu :**
- Audits de surveillance : XX $
- Audits internes : XX $
- Rafraîchissement de formation : XX $
- Abonnements aux outils : XX $
- Amélioration continue : XX $

## Conclusion

La certification ISO 27001 est un investissement qui rapporte grâce à une sécurité améliorée, la confiance des clients et un avantage concurrentiel. Utilisez nos outils gratuits pour réduire les coûts de mise en œuvre et construire un programme de sécurité durable.

Commencez par notre [Analyse des écarts](/tools/gap-analysis) pour comprendre votre état actuel et estimer la portée de votre mise en œuvre.
        `
      }
    },
    author: "ISO27001KIT",
    publishDate: "2025-05-05",
    updatedDate: "2025-05-05",
    category: "Planning",
    tags: ["ISO 27001", "Certification Cost", "Budget", "Implementation", "ROI"],
    readTime: 11,
    featured: false
  },
  {
    slug: "iso-27001-checklist-complete-requirements",
    translations: {
      en: {
        title: "ISO 27001 Checklist: Complete Requirements for Certification",
        metaTitle: "ISO 27001 Checklist 2025 | Complete Certification Requirements List",
        metaDescription: "Free ISO 27001 checklist covering all mandatory requirements, Annex A controls, documentation, and audit preparation steps for successful certification.",
        excerpt: "A comprehensive ISO 27001 checklist covering every requirement you need to meet for successful ISMS implementation and certification.",
        content: `
## Why You Need an ISO 27001 Checklist

Achieving ISO 27001 certification involves hundreds of requirements across documentation, controls, processes, and governance. A structured checklist ensures nothing falls through the cracks during implementation and audit preparation.

## Mandatory Documentation Checklist

The following documents are explicitly required by ISO 27001:

### Clause 4 – Context of the Organization
- [ ] Scope of the ISMS (Clause 4.3)
- [ ] List of interested parties and their requirements (Clause 4.2)
- [ ] Internal and external issues affecting the ISMS (Clause 4.1)

### Clause 5 – Leadership
- [ ] [Information Security Policy](/controls/a-5-1-policies-for-information-security) (Clause 5.2)
- [ ] Roles, responsibilities, and authorities (Clause 5.3)
- [ ] Evidence of management commitment (Clause 5.1)

### Clause 6 – Planning
- [ ] Risk assessment methodology (Clause 6.1.2)
- [ ] Risk assessment results (Clause 6.1.2)
- [ ] Risk treatment plan (Clause 6.1.3)
- [ ] Statement of Applicability (Clause 6.1.3 d)
- [ ] Information security objectives (Clause 6.2)

### Clause 7 – Support
- [ ] Evidence of competence (Clause 7.2)
- [ ] [Security awareness](/controls/a-6-3-information-security-awareness-education-and-training) program records (Clause 7.3)
- [ ] Document control procedure (Clause 7.5)

### Clause 8 – Operation
- [ ] Operational planning and control records (Clause 8.1)
- [ ] Risk assessment results (Clause 8.2)
- [ ] Risk treatment results (Clause 8.3)

### Clause 9 – Performance Evaluation
- [ ] Monitoring and measurement results (Clause 9.1)
- [ ] Internal audit program and results (Clause 9.2)
- [ ] Management review minutes (Clause 9.3)

### Clause 10 – Improvement
- [ ] Nonconformity and corrective action records (Clause 10.1)
- [ ] Continual improvement evidence (Clause 10.2)

## Annex A Controls Checklist

ISO 27001:2022 contains 93 controls across four themes. Use our [Annex A Selector Tool](/annex-a-selector) to determine which apply to your organization.

### Organizational Controls (A.5) – 37 Controls
- [ ] Information security policies (A.5.1)
- [ ] Information security roles ([A.5.2](/controls/a-5-2-information-security-roles-and-responsibilities))
- [ ] [Segregation of duties](/controls/a-5-3-segregation-of-duties) (A.5.3)
- [ ] [Management responsibilities](/controls/a-5-4-management-responsibilities) (A.5.4)
- [ ] [Threat intelligence](/controls/a-5-7-threat-intelligence) (A.5.7)
- [ ] Information security in project management (A.5.8)
- [ ] Inventory of information assets (A.5.9)
- [ ] Acceptable use of assets (A.5.10)

### People Controls (A.6) – 8 Controls
- [ ] Screening (A.6.1)
- [ ] Terms and conditions of employment (A.6.2)
- [ ] Information security awareness and training (A.6.3)
- [ ] Disciplinary process (A.6.4)
- [ ] Responsibilities after termination (A.6.5)
- [ ] Remote working (A.6.7)

### Physical Controls (A.7) – 14 Controls
- [ ] Physical security perimeters (A.7.1)
- [ ] Physical entry controls (A.7.2)
- [ ] Securing offices and facilities (A.7.3)
- [ ] Clear desk and clear screen (A.7.7)
- [ ] Equipment siting and protection (A.7.8)

### Technological Controls (A.8) – 34 Controls
- [ ] User endpoint devices (A.8.1)
- [ ] Privileged access rights (A.8.2)
- [ ] Information access restriction (A.8.3)
- [ ] Secure authentication (A.8.5)
- [ ] Malware protection (A.8.7)
- [ ] Management of technical vulnerabilities (A.8.8)
- [ ] Information backup (A.8.13)
- [ ] Logging (A.8.15)
- [ ] Network security (A.8.20)
- [ ] Secure coding (A.8.28)

## Pre-Audit Readiness Checklist

Before your certification audit, verify these critical items:

### Stage 1 Audit Preparation
- [ ] All mandatory documents are approved and distributed
- [ ] Statement of Applicability is complete and justified
- [ ] Risk assessment is documented and current
- [ ] Internal audit has been conducted
- [ ] Management review has been performed
- [ ] Corrective actions from internal audit are closed

### Stage 2 Audit Preparation
- [ ] All controls in the SoA are implemented
- [ ] Staff are aware of security policies
- [ ] Evidence of control effectiveness exists
- [ ] Incident management process is tested
- [ ] Business continuity plans are tested
- [ ] Supplier security requirements are documented

## Using Our Free Tools

Streamline your checklist completion with our tools:
- [Gap Analysis Tool](/tools/gap-analysis) – Assess your current compliance status
- [SoA Generator](/soa-generator) – Build your Statement of Applicability
- [Risk Assessment Tool](/risk-assessment) – Create your risk register
- [Internal Audit Checklist](/internal-audit) – Prepare for internal audits
- [Implementation Roadmap](/implementation-roadmap) – Plan your timeline

## Common Gaps Found During Audits

Based on industry data, the most common nonconformities are:
1. **Incomplete risk assessments** – Not covering all assets or threats
2. **Missing evidence of awareness training** – No records of employee training
3. **Outdated documents** – Policies not reviewed or updated annually
4. **Weak access control** – No formal access provisioning process
5. **No incident response testing** – Plans exist but are never tested

## Conclusion

Use this checklist as your roadmap to ISO 27001 certification. Start with our [Gap Analysis](/tools/gap-analysis) to identify your current position, then work through each section systematically.
        `
      },
      fr: {
        title: "Checklist ISO 27001 : Exigences complètes pour la certification",
        metaTitle: "Checklist ISO 27001 2025 | Liste complète des exigences de certification",
        metaDescription: "Checklist ISO 27001 gratuite couvrant toutes les exigences obligatoires, les contrôles de l'Annexe A, la documentation et les étapes de préparation à l'audit.",
        excerpt: "Une checklist ISO 27001 complète couvrant toutes les exigences à respecter pour une mise en œuvre réussie du SMSI et la certification.",
        content: `
## Pourquoi vous avez besoin d'une checklist ISO 27001

L'obtention de la certification ISO 27001 implique des centaines d'exigences en matière de documentation, de contrôles, de processus et de gouvernance. Une checklist structurée garantit que rien n'est oublié lors de la mise en œuvre et de la préparation à l'audit.

## Checklist de documentation obligatoire

Les documents suivants sont explicitement requis par l'ISO 27001 :

### Clause 4 – Contexte de l'organisation
- [ ] Périmètre du SMSI (Clause 4.3)
- [ ] Liste des parties intéressées et leurs exigences (Clause 4.2)
- [ ] Enjeux internes et externes affectant le SMSI (Clause 4.1)

### Clause 5 – Leadership
- [ ] Politique de sécurité de l'information (Clause 5.2)
- [ ] Rôles, responsabilités et autorités (Clause 5.3)
- [ ] Preuves de l'engagement de la direction (Clause 5.1)

### Clause 6 – Planification
- [ ] Méthodologie d'évaluation des risques (Clause 6.1.2)
- [ ] Résultats de l'évaluation des risques (Clause 6.1.2)
- [ ] Plan de traitement des risques (Clause 6.1.3)
- [ ] Déclaration d'applicabilité (Clause 6.1.3 d)
- [ ] Objectifs de sécurité de l'information (Clause 6.2)

### Clause 7 – Support
- [ ] Preuves de compétence (Clause 7.2)
- [ ] Enregistrements du programme de sensibilisation (Clause 7.3)
- [ ] Procédure de contrôle des documents (Clause 7.5)

### Clause 8 – Fonctionnement
- [ ] Enregistrements de planification opérationnelle (Clause 8.1)
- [ ] Résultats de l'évaluation des risques (Clause 8.2)
- [ ] Résultats du traitement des risques (Clause 8.3)

### Clause 9 – Évaluation des performances
- [ ] Résultats de surveillance et de mesure (Clause 9.1)
- [ ] Programme et résultats d'audit interne (Clause 9.2)
- [ ] Procès-verbaux de revue de direction (Clause 9.3)

### Clause 10 – Amélioration
- [ ] Enregistrements de non-conformité et actions correctives (Clause 10.1)
- [ ] Preuves d'amélioration continue (Clause 10.2)

## Checklist des contrôles de l'Annexe A

L'ISO 27001:2022 contient 93 contrôles répartis en quatre thèmes. Utilisez notre [Outil de sélection Annexe A](/annex-a-selector) pour déterminer ceux qui s'appliquent à votre organisation.

### Contrôles organisationnels (A.5) – 37 contrôles
- [ ] Politiques de sécurité de l'information (A.5.1)
- [ ] Rôles de sécurité de l'information (A.5.2)
- [ ] Séparation des tâches (A.5.3)
- [ ] Responsabilités de la direction (A.5.4)
- [ ] Renseignement sur les menaces (A.5.7)
- [ ] Inventaire des actifs informationnels (A.5.9)
- [ ] Utilisation acceptable des actifs (A.5.10)

### Contrôles liés aux personnes (A.6) – 8 contrôles
- [ ] Vérification des antécédents (A.6.1)
- [ ] Conditions d'emploi (A.6.2)
- [ ] Sensibilisation et formation (A.6.3)
- [ ] Processus disciplinaire (A.6.4)
- [ ] Travail à distance (A.6.7)

### Contrôles physiques (A.7) – 14 contrôles
- [ ] Périmètres de sécurité physique (A.7.1)
- [ ] Contrôles d'entrée physique (A.7.2)
- [ ] Bureau propre et écran verrouillé (A.7.7)

### Contrôles technologiques (A.8) – 34 contrôles
- [ ] Terminaux utilisateur (A.8.1)
- [ ] Droits d'accès privilégiés (A.8.2)
- [ ] Authentification sécurisée (A.8.5)
- [ ] Protection contre les malwares (A.8.7)
- [ ] Gestion des vulnérabilités (A.8.8)
- [ ] Sauvegarde (A.8.13)
- [ ] Journalisation (A.8.15)
- [ ] Sécurité réseau (A.8.20)

## Checklist de préparation à l'audit

### Préparation audit de phase 1
- [ ] Tous les documents obligatoires sont approuvés
- [ ] La DdA est complète et justifiée
- [ ] L'évaluation des risques est documentée et à jour
- [ ] L'audit interne a été réalisé
- [ ] La revue de direction a été effectuée
- [ ] Les actions correctives sont clôturées

### Préparation audit de phase 2
- [ ] Tous les contrôles de la DdA sont mis en œuvre
- [ ] Le personnel connaît les politiques de sécurité
- [ ] Des preuves d'efficacité des contrôles existent
- [ ] Le processus de gestion des incidents est testé
- [ ] Les plans de continuité sont testés

## Conclusion

Utilisez cette checklist comme feuille de route vers la certification ISO 27001. Commencez par notre [Analyse des écarts](/tools/gap-analysis) pour identifier votre position actuelle.
        `
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-03-01",
    updatedDate: "2026-03-01",
    category: "Implementation",
    tags: ["ISO 27001", "Checklist", "Certification", "Requirements", "Compliance", "Annex A"],
    readTime: 14,
    featured: true
  },
  {
    slug: "isms-policy-template-guide",
    translations: {
      en: {
        title: "ISMS Policy Templates: How to Write ISO 27001 Security Policies",
        metaTitle: "ISMS Policy Templates | Free ISO 27001 Security Policy Guide & Downloads",
        metaDescription: "Learn how to write ISMS policies for ISO 27001 with free templates. Covers information security policy, access control, risk management, and 30+ policy templates.",
        excerpt: "Everything you need to write effective ISMS policies for ISO 27001, with free downloadable templates and practical writing guidelines.",
        content: `
## What Are ISMS Policies?

ISMS policies are formal documents that define your organization's approach to managing information security. They translate ISO 27001 requirements into actionable rules, responsibilities, and procedures that employees must follow.

## Why Policies Matter for ISO 27001

Policies serve three critical purposes:
1. **Compliance**: ISO 27001 explicitly requires several documented policies
2. **Communication**: They tell employees what is expected of them
3. **Evidence**: Auditors need documented policies to verify your ISMS

## Mandatory vs. Recommended Policies

### Mandatory Policies (Required by ISO 27001)
These policies are explicitly required by the standard:
- **[Information Security Policy](/controls/a-5-1-policies-for-information-security)** (Clause 5.2) – The overarching policy signed by top management
- **Risk Assessment & Treatment Policy** (Clause 6.1) – How you identify and handle risks
- **[Access Control](/controls/a-5-15-access-control) Policy** (A.5.15, [A.8.2](/controls/a-8-2-privileged-access-rights)-3) – Who gets access to what
- **Document Control Policy** (Clause 7.5) – How documents are managed and versioned

### Highly Recommended Policies
While not strictly mandatory, auditors expect these:
- [Acceptable Use](/controls/a-5-10-acceptable-use-of-information-and-other-associated-assets) Policy
- Password Policy
- [Backup](/controls/a-8-13-information-backup) Policy
- [Incident Management](/controls/a-5-24-information-security-incident-management-planning-and-preparation) Policy
- Business Continuity Policy
- Change Management Policy
- Network Security Policy
- Cryptography Policy
- Physical Security Policy
- Supplier Security Policy
- Secure Development Policy
- Data Classification Policy
- Clear Desk & Clear Screen Policy
- BYOD / Mobile Device Policy
- Remote Working Policy

## How to Write an Effective ISMS Policy

### Structure Template

Every policy should follow this structure:

**1. Header**
- Policy title
- Version number and date
- Author and approver
- Classification level
- Review date

**2. Purpose**
State why this policy exists in 2-3 sentences.

**3. Scope**
Define who and what the policy applies to.

**4. Policy Statements**
The core rules and requirements. Be specific and actionable:
- ❌ "Users should use strong passwords"
- ✅ "Passwords must be at least 12 characters, containing uppercase, lowercase, numbers, and special characters"

**5. Roles & Responsibilities**
Define who is responsible for what:
- CISO / Information Security Manager
- IT Department
- Department Managers
- All Employees

**6. Compliance & Enforcement**
Describe consequences of non-compliance and how compliance is monitored.

**7. Review & Revision**
How often the policy is reviewed (at least annually) and what triggers a revision.

### Writing Tips

1. **Use clear, simple language** – Avoid jargon where possible
2. **Be specific** – Measurable requirements are easier to audit
3. **Align with risk assessment** – Policies should address identified risks
4. **Keep it practical** – Policies nobody can follow are useless
5. **Version control** – Track all changes with dates and approvers

## Policy-by-Policy Guide

### Information Security Policy
The top-level policy that establishes management's direction for information security. It should:
- State commitment to information security
- Define security objectives
- Reference the ISMS scope
- Assign overall responsibility
- Commit to continual improvement

### Access Control Policy
Defines rules for granting, reviewing, and revoking access:
- Need-to-know and least privilege principles
- User registration and de-registration process
- Privileged access management
- Access review frequency (quarterly recommended)
- Multi-factor authentication requirements

### Incident Management Policy
Covers how security incidents are handled:
- Incident classification levels
- Reporting procedures and timelines
- Response and escalation procedures
- Evidence preservation requirements
- Post-incident review process
- Notification obligations (regulatory, customer)

### Business Continuity Policy
Ensures critical operations continue during disruptions:
- Business impact analysis requirements
- Recovery time and point objectives (RTO/RPO)
- Continuity plan testing frequency
- Crisis communication procedures

Use our [RTO/RPO Helper](/rto-rpo-bia-helper) to calculate recovery objectives.

## Free Policy Templates

Download our complete set of 40+ policy templates from the [Documents Library](/documents):
- All templates follow the structure above
- Customizable for your organization
- Available in PDF (free) and editable Word format
- Covers all Annex A control areas

## Common Policy Mistakes

1. **Copy-paste without customizing** – Generic policies fail audits
2. **Too long and complex** – Aim for 3-8 pages per policy
3. **No review schedule** – Policies must be reviewed at least annually
4. **Missing approval signatures** – Policies need formal approval
5. **Not communicated** – Staff must be aware of relevant policies

## Conclusion

Well-written ISMS policies are the backbone of your ISO 27001 compliance. Start with the mandatory policies, then expand based on your risk assessment findings. Use our [free templates](/documents) as a starting point and customize them for your organization.
        `
      },
      fr: {
        title: "Modèles de politiques SMSI : Comment rédiger les politiques de sécurité ISO 27001",
        metaTitle: "Modèles de politiques SMSI | Guide et téléchargements gratuits ISO 27001",
        metaDescription: "Apprenez à rédiger des politiques SMSI pour l'ISO 27001 avec des modèles gratuits. Couvre la politique de sécurité, le contrôle d'accès, la gestion des risques et plus de 30 modèles.",
        excerpt: "Tout ce dont vous avez besoin pour rédiger des politiques SMSI efficaces pour l'ISO 27001, avec des modèles téléchargeables gratuits et des directives pratiques.",
        content: `
## Que sont les politiques SMSI ?

Les politiques SMSI sont des documents formels qui définissent l'approche de votre organisation en matière de gestion de la sécurité de l'information. Elles traduisent les exigences de l'ISO 27001 en règles, responsabilités et procédures applicables.

## Pourquoi les politiques sont importantes

Les politiques servent trois objectifs critiques :
1. **Conformité** : L'ISO 27001 exige explicitement plusieurs politiques documentées
2. **Communication** : Elles indiquent aux employés ce qui est attendu d'eux
3. **Preuve** : Les auditeurs ont besoin de politiques documentées pour vérifier votre SMSI

## Politiques obligatoires vs. recommandées

### Politiques obligatoires
- **Politique de sécurité de l'information** (Clause 5.2)
- **Politique d'évaluation et de traitement des risques** (Clause 6.1)
- **Politique de contrôle d'accès** (A.5.15, A.8.2-3)
- **Politique de contrôle des documents** (Clause 7.5)

### Politiques hautement recommandées
- Politique d'utilisation acceptable
- Politique de mots de passe
- Politique de sauvegarde
- Politique de gestion des incidents
- Politique de continuité des activités
- Politique de gestion des changements
- Politique de sécurité réseau
- Politique de cryptographie
- Politique de sécurité physique
- Politique de sécurité des fournisseurs
- Politique de développement sécurisé
- Politique de classification des données
- Politique de bureau propre
- Politique BYOD / appareils mobiles
- Politique de travail à distance

## Comment rédiger une politique SMSI efficace

### Structure type

**1. En-tête** : Titre, version, date, auteur, approbateur, classification

**2. Objectif** : Pourquoi cette politique existe (2-3 phrases)

**3. Périmètre** : À qui et à quoi la politique s'applique

**4. Énoncés de politique** : Les règles et exigences principales. Soyez précis :
- ❌ « Les utilisateurs doivent utiliser des mots de passe forts »
- ✅ « Les mots de passe doivent comporter au moins 12 caractères avec majuscules, minuscules, chiffres et caractères spéciaux »

**5. Rôles et responsabilités** : Qui est responsable de quoi

**6. Conformité et application** : Conséquences du non-respect

**7. Révision** : Fréquence de révision (au moins annuellement)

### Conseils de rédaction

1. **Utilisez un langage clair** – Évitez le jargon
2. **Soyez spécifique** – Les exigences mesurables sont plus faciles à auditer
3. **Alignez avec l'évaluation des risques** – Les politiques doivent répondre aux risques identifiés
4. **Restez pratique** – Des politiques impossibles à suivre sont inutiles
5. **Contrôle de version** – Suivez tous les changements avec dates et approbateurs

## Guide politique par politique

### Politique de sécurité de l'information
La politique de haut niveau qui établit la direction de la direction. Elle doit :
- Énoncer l'engagement envers la sécurité
- Définir les objectifs de sécurité
- Référencer le périmètre du SMSI
- Assigner la responsabilité globale

### Politique de contrôle d'accès
- Principes du besoin d'en connaître et du moindre privilège
- Processus d'enregistrement et de désinscription des utilisateurs
- Gestion des accès privilégiés
- Fréquence de revue des accès
- Exigences d'authentification multifacteur

### Politique de gestion des incidents
- Niveaux de classification des incidents
- Procédures et délais de signalement
- Procédures de réponse et d'escalade
- Exigences de préservation des preuves

## Modèles gratuits

Téléchargez notre ensemble complet de plus de 40 modèles de politiques depuis la [Bibliothèque de documents](/documents) :
- Tous les modèles suivent la structure ci-dessus
- Personnalisables pour votre organisation
- Disponibles en PDF (gratuit) et en format Word modifiable

## Conclusion

Des politiques SMSI bien rédigées sont l'épine dorsale de votre conformité ISO 27001. Commencez par les politiques obligatoires, puis développez en fonction de votre évaluation des risques. Utilisez nos [modèles gratuits](/documents) comme point de départ.
        `
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-03-01",
    updatedDate: "2026-03-01",
    category: "Documentation",
    tags: ["ISMS", "Policy Template", "ISO 27001", "Information Security Policy", "Documentation", "Templates"],
    readTime: 13,
    featured: false
  },
  {
    slug: "iso-27001-audit-preparation-guide",
    translations: {
      en: {
        title: "ISO 27001 Audit Preparation: How to Pass Your Certification Audit",
        metaTitle: "ISO 27001 Audit Preparation Guide | How to Pass Certification Audit 2025",
        metaDescription: "Complete guide to preparing for your ISO 27001 certification audit. Learn what auditors look for, common findings, Stage 1 & 2 preparation, and how to avoid nonconformities.",
        excerpt: "Learn exactly how to prepare for your ISO 27001 certification audit, what auditors look for, and how to avoid the most common nonconformities.",
        content: `
## Understanding the ISO 27001 Certification Audit

The ISO 27001 certification audit is conducted by an accredited certification body and consists of two stages. Understanding what to expect at each stage is crucial for a successful outcome.

## Stage 1 Audit: Documentation Review

The Stage 1 audit (also called the "desktop review") focuses on your ISMS documentation. The auditor verifies that:

### What Auditors Check
- ISMS scope is clearly defined
- [Information Security Policy](/controls/a-5-1-policies-for-information-security) is approved by top management
- Risk assessment methodology is documented
- Risk assessment has been conducted
- Statement of Applicability (SoA) is complete
- Risk treatment plan exists
- Internal audit has been performed
- Management review has been conducted
- Security objectives are defined and measurable

### How to Prepare
1. **Organize your document library** – Use a logical folder structure with version control
2. **Ensure all mandatory documents exist** – See our [ISO 27001 checklist](/blog/iso-27001-checklist-complete-requirements)
3. **Verify document approval** – Every policy needs a signature and date
4. **Check cross-references** – Documents should reference each other consistently
5. **Prepare a document matrix** – Map each ISO 27001 clause to your documents

### Common Stage 1 Findings
- Missing or incomplete SoA justifications
- Risk assessment not covering all assets in scope
- No evidence of management review
- Internal audit not covering all clauses
- Security objectives not measurable

## Stage 2 Audit: Implementation Verification

Stage 2 typically happens 1-4 weeks after Stage 1. The auditor verifies that your documented ISMS is actually implemented and effective.

### What Auditors Do
- **Interview staff** at various levels about [security awareness](/controls/a-6-3-information-security-awareness-education-and-training)
- **Review evidence** of control implementation
- **Test controls** by requesting demonstrations
- **Sample records** to verify ongoing operations
- **Walk through processes** like [incident management](/controls/a-5-24-information-security-incident-management-planning-and-preparation) and access provisioning

### Key Areas of Focus

#### People & Awareness
Auditors will speak with random employees to verify:
- They know the Information Security Policy exists
- They understand their security responsibilities
- They know how to report security incidents
- They have received security awareness training

**Preparation**: Ensure all staff have completed awareness training and can answer basic questions.

#### Access Control
- How are [access rights](/controls/a-5-18-access-rights) granted and revoked?
- Is there a formal access provisioning process?
- Are access rights reviewed periodically?
- Is [privileged access](/controls/a-8-2-privileged-access-rights) separately managed?
- Is [multi-factor authentication](/controls/a-8-5-secure-authentication) in use?

**Preparation**: Have evidence of access reviews, provisioning requests, and revocation records.

#### Incident Management
- How are incidents reported?
- Show evidence of recent incident handling
- Is there a severity classification system?
- Are lessons learned documented?

**Preparation**: Even if no real incidents occurred, have the process documented and tested via tabletop exercises.

#### Change Management
- How are changes to systems and infrastructure managed?
- Is there a change approval process?
- Are changes tested before deployment?
- Are emergency changes handled differently?

#### Supplier Management
- How are suppliers assessed for security?
- Are security requirements in contracts?
- Is there ongoing supplier monitoring?

Use our [Supplier Risk Manager](/supplier-risk-manager) to maintain supplier assessments.

## Handling Audit Findings

### Types of Findings
- **Major Nonconformity**: A significant failure to meet a requirement. Must be resolved before certification.
- **Minor Nonconformity**: A minor gap that doesn't undermine the ISMS. Must be resolved within a set timeframe (usually 90 days).
- **Observation**: A recommendation for improvement. No action required but noted.
- **Opportunity for Improvement**: Suggestions that could enhance the ISMS.

### Response Strategy
1. **Don't argue** – Acknowledge the finding professionally
2. **Ask for clarification** – Ensure you understand exactly what's expected
3. **Provide root cause analysis** – Show you understand why the gap exists
4. **Present a corrective action plan** – With specific actions, owners, and dates
5. **Implement promptly** – Don't wait until the deadline

## 30-Day Audit Preparation Timeline

### Week 1: Documentation Review
- Review all mandatory documents for completeness
- Update any outdated policies (anything over 12 months old)
- Verify the SoA is current and justified
- Ensure risk assessment is up to date

### Week 2: Evidence Collection
- Gather evidence of control implementation
- Collect training records and awareness materials
- Prepare access review records
- Compile incident logs and response records

### Week 3: Internal Readiness Check
- Conduct a pre-audit self-assessment using our [Internal Audit Checklist](/internal-audit)
- Brief key staff on the audit process
- Prepare an audit schedule with room bookings
- Identify escorts for the auditor

### Week 4: Final Preparations
- Address any gaps found in the self-assessment
- Conduct refresher awareness sessions
- Prepare opening and closing meeting presentations
- Ensure all documentation is accessible and organized

## Tips from Experienced Auditors

1. **Be honest** – Auditors appreciate transparency over perfection
2. **Have evidence ready** – Don't make auditors wait while you search for documents
3. **Assign an audit liaison** – One person to coordinate logistics and requests
4. **Prepare your staff** – They should know what to expect but not be coached on answers
5. **Show continuous improvement** – Evidence of learning and adapting impresses auditors
6. **Don't over-document** – Quality over quantity; don't create documents just for the audit

## Post-Audit: Surveillance and Recertification

After certification:
- **Surveillance audits** occur annually (covering part of the ISMS)
- **Recertification audit** happens every 3 years (full scope)
- **Continual improvement** evidence is expected at every audit

## Conclusion

Successful audit preparation is about demonstrating that your ISMS is a living, working system – not just a set of documents. Start preparing at least 30 days before your audit using this guide and our free [audit preparation tools](/tools).
        `
      },
      fr: {
        title: "Préparation à l'audit ISO 27001 : Comment réussir votre audit de certification",
        metaTitle: "Guide de préparation audit ISO 27001 | Comment réussir l'audit de certification 2025",
        metaDescription: "Guide complet pour préparer votre audit de certification ISO 27001. Découvrez ce que les auditeurs recherchent, les constats courants et comment éviter les non-conformités.",
        excerpt: "Apprenez exactement comment préparer votre audit de certification ISO 27001, ce que les auditeurs recherchent et comment éviter les non-conformités les plus courantes.",
        content: `
## Comprendre l'audit de certification ISO 27001

L'audit de certification ISO 27001 est réalisé par un organisme de certification accrédité et se compose de deux phases. Comprendre ce qui est attendu à chaque phase est crucial.

## Audit de phase 1 : Revue documentaire

L'audit de phase 1 se concentre sur la documentation de votre SMSI. L'auditeur vérifie que :

### Ce que les auditeurs vérifient
- Le périmètre du SMSI est clairement défini
- La politique de sécurité est approuvée par la direction
- La méthodologie d'évaluation des risques est documentée
- L'évaluation des risques a été menée
- La déclaration d'applicabilité (DdA) est complète
- Un plan de traitement des risques existe
- L'audit interne a été réalisé
- La revue de direction a été effectuée

### Comment se préparer
1. **Organisez votre bibliothèque documentaire** avec un contrôle de version
2. **Vérifiez que tous les documents obligatoires existent**
3. **Vérifiez l'approbation des documents** – Chaque politique nécessite une signature
4. **Vérifiez les références croisées** entre documents
5. **Préparez une matrice documentaire**

### Constats courants de phase 1
- Justifications manquantes dans la DdA
- Évaluation des risques ne couvrant pas tous les actifs
- Pas de preuve de revue de direction
- Audit interne ne couvrant pas toutes les clauses

## Audit de phase 2 : Vérification de la mise en œuvre

La phase 2 se déroule 1 à 4 semaines après la phase 1. L'auditeur vérifie que votre SMSI documenté est réellement mis en œuvre.

### Ce que font les auditeurs
- **Interroger le personnel** sur la sensibilisation à la sécurité
- **Examiner les preuves** de mise en œuvre des contrôles
- **Tester les contrôles** en demandant des démonstrations
- **Échantillonner les enregistrements**
- **Parcourir les processus** comme la gestion des incidents

### Domaines clés

#### Personnel et sensibilisation
Les auditeurs parleront avec des employés pour vérifier qu'ils :
- Connaissent l'existence de la politique de sécurité
- Comprennent leurs responsabilités
- Savent comment signaler les incidents
- Ont reçu une formation de sensibilisation

#### Contrôle d'accès
- Comment les droits d'accès sont-ils accordés et révoqués ?
- Existe-t-il un processus formel de provisionnement ?
- Les droits sont-ils révisés périodiquement ?
- L'authentification multifacteur est-elle utilisée ?

#### Gestion des incidents
- Comment les incidents sont-ils signalés ?
- Preuves de traitement récent d'incidents
- Système de classification de la gravité
- Documentation des leçons apprises

#### Gestion des changements
- Comment les changements sont-ils gérés ?
- Existe-t-il un processus d'approbation ?
- Les changements sont-ils testés avant déploiement ?

## Gérer les constats d'audit

### Types de constats
- **Non-conformité majeure** : Doit être résolue avant la certification
- **Non-conformité mineure** : Doit être résolue dans un délai fixé (90 jours)
- **Observation** : Recommandation d'amélioration
- **Opportunité d'amélioration** : Suggestions pour améliorer le SMSI

### Stratégie de réponse
1. **Ne discutez pas** – Reconnaissez le constat professionnellement
2. **Demandez des clarifications**
3. **Fournissez une analyse des causes profondes**
4. **Présentez un plan d'action corrective**
5. **Mettez en œuvre rapidement**

## Calendrier de préparation sur 30 jours

### Semaine 1 : Revue documentaire
- Vérifier tous les documents obligatoires
- Mettre à jour les politiques obsolètes
- Vérifier que la DdA est actuelle

### Semaine 2 : Collecte de preuves
- Rassembler les preuves de mise en œuvre
- Collecter les registres de formation
- Préparer les registres de revue d'accès

### Semaine 3 : Vérification de préparation
- Réaliser une auto-évaluation avec notre [Checklist d'audit interne](/internal-audit)
- Briefer le personnel clé
- Préparer le planning d'audit

### Semaine 4 : Préparations finales
- Combler les lacunes identifiées
- Sessions de sensibilisation de rappel
- Préparer les présentations d'ouverture et de clôture

## Conseils d'auditeurs expérimentés

1. **Soyez honnête** – Les auditeurs apprécient la transparence
2. **Ayez les preuves prêtes** – Ne faites pas attendre les auditeurs
3. **Désignez un interlocuteur** pour coordonner la logistique
4. **Préparez votre personnel** sans les coaching sur les réponses
5. **Montrez l'amélioration continue**
6. **Ne sur-documentez pas** – La qualité prime sur la quantité

## Conclusion

Une préparation réussie à l'audit consiste à démontrer que votre SMSI est un système vivant et fonctionnel. Commencez la préparation au moins 30 jours avant avec ce guide et nos [outils de préparation](/tools).
        `
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-03-01",
    updatedDate: "2026-03-01",
    category: "Audit",
    tags: ["ISO 27001", "Audit Preparation", "Certification Audit", "Nonconformity", "Stage 1 Audit", "Stage 2 Audit"],
    readTime: 16,
    featured: true
  },
  {
    slug: "iso-27001-toolkits-compared-2026",
    translations: {
      en: {
        title: "Top 7 ISO 27001 Toolkits Compared - Free and Paid Options for 2026",
        metaTitle: "Top 7 ISO 27001 Toolkits Compared - Free and Paid Options for 2026",
        metaDescription: "Compare the best ISO 27001 toolkits for 2026 including ISO27001KIT, CertiKit, Advisera, IT Governance, ISMS.online, High Table, and Sprinto. Pricing, features, and honest pros and cons.",
        excerpt: "We compare seven popular ISO 27001 toolkits side by side - pricing, features, templates, and tools - so you can pick the right one for your organization's size and budget.",
        content: `
## Why You Need an ISO 27001 Toolkit

Implementing ISO 27001 from scratch is a massive undertaking. Between writing 30+ mandatory documents, building a risk register, conducting gap analyses, and preparing for certification audits, most organizations spend 6 to 18 months on the process. An **ISO 27001 toolkit** compresses that timeline by giving you pre-built templates, structured tools, and guided workflows instead of starting from a blank page.

But not every toolkit is created equal. Some focus purely on document templates. Others bundle software platforms with consulting services. Prices range from free tiers to five-figure annual subscriptions. This guide breaks down seven of the most widely used options for 2026 so you can make an informed decision.

## What to Look For in an ISO 27001 Toolkit

Before comparing specific products, here are the criteria that matter most:

- **Template coverage** - Does the toolkit include all mandatory ISO 27001:2022 documents, or just a subset?
- **Editability** - Are templates provided in editable formats (Word, Excel) or locked PDFs?
- **Tools and automation** - Does it include interactive tools for gap analysis, risk assessment, SoA generation, and audit checklists?
- **Risk management** - Is there a built-in risk register and treatment tracking system?
- **Pricing model** - One-time purchase, monthly subscription, or annual contract?
- **Free tier** - Can you evaluate the product meaningfully before paying?
- **Updates** - Does the toolkit stay current with standard revisions?

## 1. ISO27001KIT

**Website:** [iso27001kit.com](https://iso27001kit.com)

ISO27001KIT is a comprehensive platform that combines 46+ editable Word templates with 11 AI-powered implementation tools and a full risk management module called Risk Copilot.

**Templates:** 46+ documents covering every mandatory ISO 27001:2022 policy, procedure, and record. All delivered in clean, editable Word format with no watermarks. A dedicated Risk Register Template is included as a standalone Excel file.

**Tools:** The platform includes free interactive tools for Statement of Applicability generation, gap analysis, risk assessment, internal audit checklists, implementation roadmaps, ISMS scope building, [asset inventory](/controls/a-5-9-inventory-of-information-and-other-associated-assets), Annex A control selection, supplier risk management, and RTO/RPO business impact analysis.

**Risk Management:** Risk Copilot is a full-featured risk management module with a risk register, control library, treatment tracking, threat and vulnerability libraries, business process mapping, audit trail, and exportable reports. It is included in the Pro subscription.

**Pricing:**
- Free tier: All 11 tools with limited exports, one-page watermarked document previews
- Document Pack: $99 one-time for all 46+ Word templates
- Pro Monthly: $149/month for full access including Risk Copilot
- Pro Yearly: $1,499/year

**Pros:**
- Generous free tier with fully functional tools
- AI-powered tools that generate results instantly
- One-time purchase option for templates (no recurring fees)
- Risk Copilot provides enterprise-grade risk management at a fraction of competitor pricing
- Modern, clean interface

**Cons:**
- Newer entrant compared to established players
- No built-in consulting services
- Risk Copilot requires Pro subscription

## 2. CertiKit

**Website:** certikit.com

CertiKit has been in the ISO toolkit space for over a decade, primarily known for its document template packs.

**Templates:** 100+ templates covering ISO 27001, ISO 22301, ISO 9001, and other standards. Documents are delivered in Word format with guidance notes embedded.

**Tools:** Limited interactive tooling. CertiKit is primarily a document-focused product.

**Risk Management:** Basic risk assessment templates in Excel format. No dedicated software module.

**Pricing:** Starts around $900 for the ISO 27001 toolkit. Annual updates are extra.

**Pros:**
- Long track record and established reputation
- Extensive template library across multiple standards
- Detailed guidance notes within each template

**Cons:**
- Expensive compared to newer alternatives
- No interactive tools or automation
- No integrated risk management software
- Templates can feel verbose and require significant customization

## 3. Advisera (27001Academy)

**Website:** advisera.com

Advisera offers the "27001Academy" brand with document toolkits, online courses, and consulting services for ISO 27001 implementation.

**Templates:** 50+ document templates with step-by-step implementation guidance. Available in Word format.

**Tools:** Online courses and webinars rather than interactive software tools. Some Excel-based tools included.

**Risk Management:** Excel-based risk assessment methodology.

**Pricing:** Document toolkit starts around $400-$800 depending on the package. Courses and consulting are priced separately.

**Pros:**
- Strong educational content and training materials
- Well-known in the compliance community
- Detailed implementation guides alongside templates

**Cons:**
- No interactive software tools
- Pricing adds up quickly when bundling courses and templates
- Risk management is entirely spreadsheet-based
- No free tier for tools

## 4. IT Governance

**Website:** itgovernance.co.uk

IT Governance is a UK-based company offering a broad range of compliance products including documentation toolkits, software, training, and consultancy.

**Templates:** Comprehensive document sets covering ISO 27001 and related standards. Delivered in Word format.

**Tools:** Offers a separate software platform (vsRisk) for risk assessment, sold as a standalone product.

**Risk Management:** vsRisk is a dedicated risk assessment tool with asset-based risk methodology. Sold separately from the documentation toolkit.

**Pricing:** Documentation toolkit starts around $500-$1,200. vsRisk is priced separately from approximately $1,500/year. Consulting engagements are quoted individually.

**Pros:**
- Full-service provider covering documents, software, training, and consulting
- vsRisk is a mature risk assessment tool
- Strong presence in the UK and European markets

**Cons:**
- Expensive when purchasing multiple products
- Software and documents sold separately
- Interface can feel dated
- Pricing is not always transparent on the website

## 5. ISMS.online

**Website:** isms.online

ISMS.online is a cloud-based platform designed to manage the entire ISMS lifecycle including documentation, risk management, supplier management, and audit preparation.

**Templates:** Pre-built policies and procedures within the platform. Editing happens inside the application rather than in Word.

**Tools:** Integrated modules for risk assessment, statement of applicability, asset management, supplier management, and audit management.

**Risk Management:** Built-in risk management with heat maps, treatment plans, and control mapping.

**Pricing:** Subscription-based starting from approximately $8,000-$15,000 per year depending on organization size.

**Pros:**
- All-in-one platform with strong workflow automation
- Built-in collaboration and approval workflows
- Continuous compliance monitoring
- Strong integrations with other business systems

**Cons:**
- Significantly more expensive than template-based solutions
- Documents are managed within the platform, not as standalone Word files
- Overkill for small organizations
- Long-term subscription commitment required

## 6. High Table

**Website:** hightable.io

High Table is a compliance automation platform covering ISO 27001, SOC 2, and other frameworks with a focus on technology companies.

**Templates:** Policy templates generated within the platform based on questionnaire responses.

**Tools:** Automated evidence collection, control monitoring, and audit preparation workflows.

**Risk Management:** Integrated risk assessment module with automated scoring.

**Pricing:** Subscription-based, typically $5,000-$20,000 per year depending on features and company size.

**Pros:**
- Strong automation for evidence collection
- Multi-framework support
- Good fit for SaaS and technology companies
- Modern user interface

**Cons:**
- Very expensive for small businesses
- Less flexibility in document customization
- Focused on tech companies, may not suit all industries
- Templates are platform-locked

## 7. Sprinto

**Website:** sprinto.com

Sprinto is a compliance automation platform popular with startups and mid-size technology companies, covering ISO 27001, SOC 2, GDPR, and HIPAA.

**Templates:** Auto-generated policies based on your infrastructure and responses to platform questionnaires.

**Tools:** Automated evidence collection via integrations with cloud providers, HR systems, and developer tools. Continuous monitoring dashboards.

**Risk Management:** Built-in risk assessment with automated control testing.

**Pricing:** Subscription-based starting from approximately $8,000-$16,000 per year.

**Pros:**
- Deep integrations with cloud infrastructure (AWS, GCP, Azure)
- Automated evidence collection reduces manual effort
- Continuous monitoring for compliance drift
- Strong onboarding support

**Cons:**
- High price point excludes smaller organizations
- Less suitable for non-technology companies
- Limited customization of generated documents
- Platform dependency - policies live inside Sprinto

## Comparison Summary

| Feature | ISO27001KIT | CertiKit | Advisera | IT Governance | ISMS.online | High Table | Sprinto |
|---|---|---|---|---|---|---|---|
| Templates | 46+ Word | 100+ Word | 50+ Word | Word | In-platform | In-platform | Auto-generated |
| Interactive Tools | 11 free tools | None | Excel-based | vsRisk (separate) | Integrated | Integrated | Integrated |
| Risk Management | Risk Copilot | Excel | Excel | vsRisk | Built-in | Built-in | Built-in |
| Free Tier | Yes (generous) | No | Limited | No | No | No | No |
| Starting Price | $99 one-time | ~$900 | ~$400 | ~$500 | ~$8,000/yr | ~$5,000/yr | ~$8,000/yr |
| Best For | SMBs and consultants | Document-heavy orgs | Self-learners | UK enterprises | Large enterprises | Tech companies | Startups |

## Which ISO 27001 Toolkit Should You Choose?

The right choice depends on your organization's size, budget, and technical maturity:

- **If you want the best value:** [ISO27001KIT](/) offers the widest free tier, a $99 one-time document pack, and affordable Audit Room subscription with full risk management. It is the only option that combines AI-powered tools with editable Word templates at this price point.

- **If you only need documents:** CertiKit and Advisera are established players with comprehensive template libraries, though at significantly higher price points.

- **If you need enterprise-grade automation:** ISMS.online, High Table, and Sprinto provide deep automation and integrations, but at annual costs that start in the thousands.

- **If you want a free ISO 27001 toolkit download:** ISO27001KIT is the only platform offering 11 fully functional implementation tools at no cost, with free watermarked document previews to evaluate before purchasing.

No matter which toolkit you choose, the most important thing is to start. An imperfect ISMS that you build and improve is always better than a perfect plan that never gets implemented. Try our [free tools](/tools) to see how quickly you can make progress.
        `
      },
      fr: {
        title: "Top 7 des Toolkits ISO 27001 Compares - Options Gratuites et Payantes pour 2026",
        metaTitle: "Top 7 des Toolkits ISO 27001 Compares pour 2026",
        metaDescription: "Comparaison des meilleurs toolkits ISO 27001 pour 2026 incluant ISO27001KIT, CertiKit, Advisera, IT Governance, ISMS.online, High Table et Sprinto.",
        excerpt: "Nous comparons sept toolkits ISO 27001 populaires - prix, fonctionnalites, modeles et outils - pour vous aider a choisir le bon pour votre organisation.",
        content: `
## Pourquoi avez-vous besoin d'un toolkit ISO 27001 ?

La mise en oeuvre de l'ISO 27001 est un projet complexe. Un toolkit adapte peut reduire considerablement le temps et les efforts necessaires. Consultez la version anglaise de cet article pour une comparaison detaillee de sept options populaires pour 2026.
        `
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-04-09",
    updatedDate: "2026-04-09",
    category: "Implementation",
    tags: ["ISO 27001", "Toolkit", "Comparison", "Templates", "Risk Management", "Free Download"],
    readTime: 18,
    featured: false
  },
  {
    slug: "iso-27001-backup-policy-template",
    translations: {
      en: {
        title: "ISO 27001 Backup Policy Template - Free Download + Writing Guide",
        metaTitle: "ISO 27001 Backup Policy Template - Free Download + Writing Guide",
        metaDescription: "Download a free ISO 27001 backup policy template. Covers Annex A control A.8.13, backup scheduling, retention, testing, and everything auditors expect to see.",
        excerpt: "A well-written backup policy is essential for ISO 27001 compliance. This guide walks you through what to include, how to align with Annex A control A.8.13, and provides a ready-to-use template structure.",
        content: `
## Why Every ISO 27001 ISMS Needs a Backup Policy

Data loss is one of the most common and damaging information security incidents. Whether caused by ransomware, hardware failure, human error, or natural disaster, the inability to restore critical data can halt operations, trigger regulatory penalties, and destroy customer trust.

ISO 27001:2022 explicitly addresses this through **Annex A control [A.8.13](/controls/a-8-13-information-backup) - Information Backup**. This control requires organizations to maintain and regularly test backup copies of information, software, and system images in accordance with an agreed backup policy.

A backup policy is not optional for ISO 27001 certification. Auditors will ask to see it, review its contents, and verify that your organization follows it in practice. This guide explains exactly what your **ISO 27001 backup policy template** must contain and how to write one that satisfies both auditors and operational needs.

## What Is Annex A Control A.8.13?

Control A.8.13 falls under the Technological Controls theme in ISO 27001:2022. Its full title is "Information Backup" and it states:

> Backup copies of information, software, and systems shall be maintained and regularly tested in accordance with the agreed topic-specific policy on backup.

The key phrase is "topic-specific policy." ISO 27001 expects you to have a dedicated backup policy - not just a line item in a broader IT policy. This policy must define what gets backed up, how often, where backups are stored, how long they are retained, who is responsible, and how restoration is tested.

## What Your ISO 27001 Backup Policy Must Include

An effective **ISO 27001 backup policy example** covers the following sections:

### 1. Purpose and Scope

Define the objective of the policy (to protect organizational information assets through systematic backup procedures) and its scope (which systems, data, and locations are covered). Be specific. A vague scope like "all IT systems" is difficult to audit and enforce.

**Example scope statement:** "This policy applies to all production databases, file servers, email systems, and application configurations hosted in the organization's primary data center and cloud environments (AWS eu-west-1 region)."

### 2. Roles and Responsibilities

Assign clear ownership:

- **Information Security Manager** - Policy owner, responsible for review and updates
- **IT Operations Team** - Responsible for executing backup procedures and monitoring
- **System Owners** - Define backup requirements for their systems
- **All Employees** - Responsible for storing work data in designated backup-eligible locations

### 3. Backup Classification and Scheduling

Define backup tiers based on data criticality. A common approach:

| Tier | Data Type | Backup Frequency | Retention Period | Method |
|---|---|---|---|---|
| Tier 1 - Critical | Production databases, financial records | Every 4 hours (incremental), daily (full) | 90 days | Automated, encrypted |
| Tier 2 - Important | Email, file shares, application configs | Daily (incremental), weekly (full) | 30 days | Automated |
| Tier 3 - Standard | Development environments, logs | Weekly (full) | 14 days | Automated |
| Tier 4 - Archive | Compliance records, audit logs | Monthly (full) | 7 years | Encrypted, offsite |

Your backup scheduling should align with the Recovery Point Objectives (RPO) defined in your business impact analysis. If your RPO for a critical database is 4 hours, your backup frequency must be at least every 4 hours.

### 4. Backup Storage and Location

Define where backups are stored and how they are protected:

- **Primary backup storage** - Separate physical or logical infrastructure from production systems
- **Offsite/geographic redundancy** - At least one copy stored in a different geographic location
- **Cloud backup** - If using cloud storage, specify the provider, region, [encryption](/controls/a-8-24-use-of-cryptography) requirements, and [access controls](/controls/a-5-15-access-control)
- **Air-gapped copies** - For critical data, maintain at least one copy that is disconnected from the network to protect against ransomware

The classic **3-2-1 backup rule** remains a solid foundation: maintain at least 3 copies of data, on 2 different media types, with 1 copy stored offsite.

### 5. Encryption and Access Controls

All backup data must be encrypted both in transit and at rest. Your policy should specify:

- Encryption algorithm and minimum key length (e.g., AES-256)
- Key management procedures (who holds encryption keys, how they are rotated)
- Access controls for backup systems (role-based, principle of [least privilege](/controls/a-8-2-privileged-access-rights))
- Authentication requirements for backup administration
- Logging of all access to backup systems

### 6. Backup Testing and Restoration Procedures

This is where many organizations fall short - and where auditors pay close attention. Your policy must define:

- **Testing frequency** - How often restoration tests are performed (at minimum quarterly for critical systems, annually for all systems)
- **Test types** - Full restoration tests, partial file recovery tests, and disaster recovery scenario tests
- **Documentation** - Test results must be recorded, including time to restore, data integrity verification, and any issues encountered
- **Responsibility** - Who conducts tests and who reviews results
- **Success criteria** - Define what constitutes a successful backup test (e.g., data integrity verified, restoration completed within RTO, all records accessible)

**Example test record format:**

| Test Date | System | Test Type | Backup Date Used | Time to Restore | Data Integrity | Result | Tester |
|---|---|---|---|---|---|---|---|
| 2026-03-15 | Production DB | Full restoration | 2026-03-14 | 2h 15m | Verified | Pass | J. Smith |

### 7. Monitoring and Alerting

Define how backup jobs are monitored:

- Automated monitoring of all backup jobs with alerting on failures
- Daily review of backup status reports by IT Operations
- Escalation procedures for failed backups (who gets notified, response time expectations)
- Dashboard or reporting requirements for management visibility

### 8. Retention and Disposal

Specify how long backups are retained and how they are securely disposed of when no longer needed:

- Retention periods aligned with legal, regulatory, and contractual requirements
- Secure deletion procedures for expired backups (including verification that data is unrecoverable)
- Special retention requirements for data subject to legal hold

### 9. Policy Review and Maintenance

- Review frequency (at least annually or after significant changes)
- Trigger events for out-of-cycle reviews (new systems, major incidents, regulatory changes)
- Approval authority for policy changes
- Version control and change history

## Common Mistakes to Avoid

1. **No testing schedule** - Having backups without testing them is almost as bad as having no backups at all. Auditors will specifically ask for test records.

2. **Ignoring cloud workloads** - Many organizations back up on-premises systems diligently but neglect SaaS applications and cloud-native data stores.

3. **Vague retention periods** - "Backups are retained as long as needed" is not auditable. Define specific periods for each data tier.

4. **No encryption** - Unencrypted backups stored offsite create a significant data breach risk. This is a common audit finding.

5. **Single point of failure** - Storing all backups on the same infrastructure as production defeats the purpose. Ensure geographic and logical separation.

6. **Forgetting the policy review** - A backup policy dated three years ago with no evidence of review suggests the ISMS is not being maintained.

## How A.8.13 Connects to Other Controls

Your backup policy does not exist in isolation. It directly supports several other ISO 27001 controls:

- **[A.5.29](/controls/a-5-29-information-security-during-disruption) - Information security during disruption** - Backups are a key component of continuity planning
- **[A.5.30](/controls/a-5-30-ict-readiness-for-business-continuity) - ICT readiness for business continuity** - Backup capabilities must align with business continuity requirements
- **A.8.10 - Information deletion** - Backup retention must align with your data deletion policy
- **A.8.11 - Data masking** - Consider whether backups of sensitive data need masking
- **A.8.24 - Use of cryptography** - Backup encryption requirements fall under your cryptography policy

## Template Structure Summary

Your **ISO 27001 backup policy template** should follow this structure:

1. Document Control (version, owner, approval date, next review)
2. Purpose and Scope
3. Roles and Responsibilities
4. Backup Classification and Scheduling
5. Backup Storage Requirements
6. Encryption and Access Controls
7. Testing and Restoration Procedures
8. Monitoring and Alerting
9. Retention and Disposal
10. Exceptions Process
11. Policy Review Schedule
12. Appendix: System Backup Register

## Get Your Complete Backup Policy Template

Writing a backup policy from scratch takes hours of research and multiple review cycles. Our [ISO 27001 Document Pack](/documents) includes a professionally written backup policy template as part of 40+ editable Word policies, procedures and templates, plus 2 Excel registers, for a one-time payment of $99. Every template is aligned with ISO 27001:2022 and ready for auditor review.

[Browse all templates](/documents) or [get the complete Document Pack](/iso-27001-templates) to accelerate your implementation.
        `
      },
      fr: {
        title: "Modele de Politique de Sauvegarde ISO 27001 - Telechargement Gratuit + Guide",
        metaTitle: "Modele de Politique de Sauvegarde ISO 27001",
        metaDescription: "Telechargez un modele gratuit de politique de sauvegarde ISO 27001. Couvre le controle A.8.13, la planification, la retention et les tests.",
        excerpt: "Une politique de sauvegarde bien redigee est essentielle pour la conformite ISO 27001. Ce guide vous explique quoi inclure et comment s'aligner avec le controle A.8.13.",
        content: `
## Pourquoi chaque SMSI ISO 27001 a besoin d'une politique de sauvegarde

La perte de donnees est l'un des incidents de securite les plus courants. L'ISO 27001:2022 traite ce sujet via le controle A.8.13. Consultez la version anglaise pour le guide complet et le modele.
        `
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-04-09",
    updatedDate: "2026-04-09",
    category: "Templates",
    tags: ["ISO 27001", "Backup Policy", "A.8.13", "Policy Template", "Data Protection", "Business Continuity"],
    readTime: 16,
    featured: false
  },
  {
    slug: "iso-27001-gap-analysis-checklist-xls",
    translations: {
      en: {
        title: "ISO 27001 Gap Analysis Checklist - Complete XLS Template + Guide",
        metaTitle: "ISO 27001 Gap Analysis Checklist - Complete XLS Template + Guide",
        metaDescription: "Download a complete ISO 27001 gap analysis checklist in XLS format. Covers all clauses 4-10 and 93 Annex A controls with scoring methodology and remediation planning guidance.",
        excerpt: "A structured gap analysis checklist is the fastest way to assess your ISO 27001 readiness. This guide covers every clause and control you need to check, plus a scoring methodology and free tool alternative.",
        content: `
## What Is an ISO 27001 Gap Analysis Checklist?

An **ISO 27001 gap analysis checklist** is a structured document that lists every requirement of the ISO 27001:2022 standard and allows you to assess your organization's current compliance status against each one. It is the most practical tool for identifying where your information security management system (ISMS) falls short of what the standard demands.

Unlike a generic compliance questionnaire, a proper **ISO 27001 gap analysis checklist XLS** template is organized to mirror the exact structure of the standard - mandatory clauses 4 through 10 followed by all 93 Annex A controls. Each item includes space for a compliance rating, evidence references, gap descriptions, and remediation actions.

The purpose is straightforward: before you can build a plan to achieve certification, you need to know exactly where you stand today. A gap analysis checklist gives you that baseline.

## Why Use a Checklist Format?

Many organizations attempt gap analysis using informal methods - a series of meetings, a few interviews, or a high-level review of existing policies. These approaches consistently produce incomplete results because they lack structure and repeatability.

A checklist format provides several advantages:

- **Completeness** - Every requirement is explicitly listed so nothing gets overlooked
- **Consistency** - Different assessors can use the same checklist and produce comparable results
- **Traceability** - Each assessment item links to a specific clause or control, making it easy to trace findings back to the standard
- **Progress tracking** - You can reassess using the same checklist months later to measure improvement
- **Audit readiness** - Auditors expect documented evidence of your baseline assessment. A structured checklist meets that expectation

## What Your ISO 27001 Gap Analysis Checklist Must Cover

### Mandatory Clauses 4-10

The first section of your checklist should cover the seven mandatory clauses of ISO 27001:2022. These define the management system requirements that every certified organization must satisfy:

**Clause 4 - Context of the Organization**
- Have you identified external and internal issues relevant to your ISMS?
- Have you identified interested parties and their requirements?
- Have you defined the scope of your ISMS?
- Is your ISMS established, implemented, maintained, and continually improved?

**Clause 5 - Leadership**
- Has top management demonstrated leadership and commitment?
- Is there an [information security policy](/controls/a-5-1-policies-for-information-security) that is appropriate to the organization's purpose?
- Are organizational roles, responsibilities, and authorities defined and communicated?

**Clause 6 - Planning**
- Have you addressed risks and opportunities related to the ISMS?
- Have you conducted an information security risk assessment?
- Have you produced a risk treatment plan?
- Are information security objectives established and documented?

**Clause 7 - Support**
- Are adequate resources determined and provided?
- Is staff competence assessed, and are training records maintained?
- Is there an awareness program for information security?
- Are communication requirements defined (what, when, with whom, how)?
- Is documented information controlled and maintained?

**Clause 8 - Operation**
- Are operational processes planned, implemented, and controlled?
- Is the risk assessment performed at planned intervals?
- Is the risk treatment plan implemented?

**Clause 9 - Performance Evaluation**
- Is monitoring, measurement, analysis, and evaluation performed?
- Are internal audits conducted at planned intervals?
- Does management review the ISMS at planned intervals?

**Clause 10 - Improvement**
- Are nonconformities identified and corrective actions taken?
- Is the ISMS continually improved?

### All 93 Annex A Controls

The second section covers every Annex A control, organized by the four themes introduced in the 2022 revision:

**Organizational Controls (A.5) - 37 Controls**
These cover policies, roles, [threat intelligence](/controls/a-5-7-threat-intelligence), asset management, [access control](/controls/a-5-15-access-control), supplier relationships, [incident management](/controls/a-5-24-information-security-incident-management-planning-and-preparation), [business continuity](/controls/a-5-30-ict-readiness-for-business-continuity), and compliance. Key controls that auditors focus on include:
- A.5.1 - Policies for information security
- [A.5.9](/controls/a-5-9-inventory-of-information-and-other-associated-assets) - Inventory of information and other associated assets
- A.5.23 - Information security for use of cloud services
- A.5.29 - Information security during disruption

**People Controls (A.6) - 8 Controls**
These address screening, employment terms, awareness training, disciplinary processes, and responsibilities after termination.

**Physical Controls (A.7) - 14 Controls**
These cover physical security perimeters, entry controls, securing offices, monitoring, protecting against environmental threats, and equipment security.

**Technological Controls (A.8) - 34 Controls**
These address endpoint security, access rights, malware protection, backup, logging, network security, secure development, and data protection. Key controls include:
- A.8.7 - Protection against malware
- A.8.8 - Management of technical vulnerabilities
- A.8.13 - Information backup
- A.8.15 - Logging
- A.8.24 - Use of cryptography

## Scoring Methodology

Each checklist item should be rated using a consistent compliance scale. A four-level scale works well for most organizations:

| Rating | Score | Description |
|---|---|---|
| Fully Implemented | 3 | Requirement is completely met with documented evidence |
| Partially Implemented | 2 | Requirement is partially met but gaps remain |
| Not Implemented | 1 | Requirement is not met - significant work required |
| Not Applicable | N/A | Requirement does not apply to the ISMS scope |

With this scoring, you can calculate an overall compliance percentage:

**Compliance % = (Sum of scores / Maximum possible score) x 100**

For example, if you assess 120 applicable items and score 240 out of a possible 360, your compliance is 67%. This gives management a clear, quantifiable picture of the organization's readiness.

### Priority Classification

Beyond the compliance score, each gap should be classified by priority to guide remediation planning:

- **Critical** - Not Implemented items that directly affect core ISMS requirements or high-risk controls
- **High** - Not Implemented items for important controls or Partially Implemented items for core requirements
- **Medium** - Partially Implemented items where the gap is well understood and bounded
- **Low** - Minor documentation gaps or control refinements

## How to Use the Checklist Effectively

### Step 1: Define the Assessment Scope

Before starting, confirm exactly what is in scope. Your gap analysis should cover the same boundaries as your intended ISMS scope. If you are certifying only a specific business unit or service, the checklist should assess controls within that boundary.

### Step 2: Gather Evidence Before Assessing

Collect existing policies, procedures, records, system configurations, and other documentation before sitting down with the checklist. Trying to assess and gather evidence simultaneously is inefficient and produces inaccurate results.

### Step 3: Involve the Right People

The person completing the checklist should have access to process owners, IT administrators, HR, facilities management, and senior leadership. Many controls span multiple departments, so a single assessor working in isolation will miss gaps.

### Step 4: Be Honest About Status

The purpose of a gap analysis is to identify gaps - not to make the organization look good on paper. Overrating compliance status at this stage creates problems later when auditors find the reality does not match the documentation.

### Step 5: Document Everything

For each checklist item, record:
- The compliance rating
- Evidence reviewed (with specific document references)
- A description of any gap
- The proposed remediation action
- An owner for the remediation
- A target completion date

### Step 6: Generate the Gap Assessment Report

Compile your findings into a **gap analysis ISO 27001** report that includes:
- Executive summary with overall compliance percentage
- Breakdown by clause and Annex A theme
- List of all gaps sorted by priority
- Remediation roadmap with owners and timelines
- Resource and budget estimates

## ISO 27001 Gap Analysis Template Formats

The most common format for an **ISO 27001 gap analysis template** is an Excel workbook with multiple tabs:

- **Tab 1: Assessment Summary** - Organization details, scope, assessor, date, overall score
- **Tab 2: Clause Assessment** - Clauses 4-10 with individual ratings
- **Tab 3: Annex A Assessment** - All 93 controls with ratings
- **Tab 4: Gap Register** - Consolidated list of all identified gaps with priority, owner, and deadline
- **Tab 5: Remediation Plan** - Action items derived from the gap register

An **ISO 27001 gap analysis checklist XLS** in this format is immediately useful for both internal planning and external audit preparation.

## What to Do After Completing the Checklist

Your completed gap analysis is not a one-time artifact - it is the foundation for your entire implementation project:

1. **Build your remediation roadmap** - Sort gaps by priority and create a phased plan
2. **Update your risk register** - Each gap may represent an unmitigated risk
3. **Allocate resources** - Use the gap count and priority distribution to justify budget requests
4. **Assign ownership** - Every remediation action needs a named owner and deadline
5. **Schedule re-assessment** - Plan to re-run the checklist in 3-6 months to measure progress
6. **Present to management** - Use the compliance percentage and gap summary for management review

## Try Our Free ISO 27001 Gap Analysis Tool

If you prefer an interactive approach over spreadsheets, our [free ISO 27001 gap analysis tool](/tools/gap-analysis) lets you assess every clause and all 93 Annex A controls online. It calculates your compliance score instantly, generates priority-based remediation recommendations, and exports your results in Excel, PDF, or HTML format - all without signup or payment.

The tool follows the same methodology described in this guide, making it the fastest way to produce an **ISO 27001 gap analysis checklist** and **gap assessment report** for your organization.

[Start your free gap analysis now](/tools/gap-analysis)
        `
      },
      fr: {
        title: "Checklist d'Analyse des Ecarts ISO 27001 - Modele XLS Complet + Guide",
        metaTitle: "Checklist d'Analyse des Ecarts ISO 27001 - Modele XLS + Guide",
        metaDescription: "Telechargez une checklist complete d'analyse des ecarts ISO 27001 au format XLS. Couvre toutes les clauses et les 93 controles de l'Annexe A.",
        excerpt: "Une checklist structuree d'analyse des ecarts est le moyen le plus rapide d'evaluer votre pret pour l'ISO 27001. Ce guide couvre chaque clause et controle.",
        content: `
## Qu'est-ce qu'une checklist d'analyse des ecarts ISO 27001 ?

Une checklist d'analyse des ecarts ISO 27001 est un document structure qui liste chaque exigence de la norme. Consultez la version anglaise pour le guide complet avec la methodologie de notation et le modele.
        `
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-04-09",
    updatedDate: "2026-04-09",
    category: "Implementation",
    tags: ["ISO 27001", "Gap Analysis", "Checklist", "Template", "XLS", "Annex A", "Compliance"],
    readTime: 17,
    featured: false
  },
  {
    slug: "iso-27001-mandatory-documents-list",
    translations: {
      en: {
        title: "ISO 27001 Mandatory Documents - Complete List for 2026",
        metaTitle: "ISO 27001 Mandatory Documents - Complete List for 2026",
        metaDescription: "Complete list of all ISO 27001 mandatory documents and records required for certification in 2026. Covers every clause and Annex A requirement with practical guidance.",
        excerpt: "ISO 27001:2022 requires a specific set of mandatory documents and records for certification. This guide lists every required document, explains what each must contain, and highlights common mistakes that cause audit findings.",
        content: `
## Understanding ISO 27001 Document Requirements

One of the most common questions from organizations starting their ISO 27001 journey is: "What documents do we actually need?" The answer is more nuanced than many expect. ISO 27001:2022 explicitly requires certain documents (referred to as "documented information"), while other documents are strongly recommended but technically optional.

Getting your **ISO 27001 mandatory documents** right is critical. Missing a required document is an automatic nonconformity in a certification audit. Having poorly written documents that do not meet the standard's requirements is equally problematic.

This guide provides the complete **ISO 27001 mandatory documents list** for 2026, covering every document explicitly required by clauses 4 through 10 and the applicable Annex A controls.

## What Does "Mandatory" Mean in ISO 27001?

ISO 27001 uses the term "documented information" to refer to any document, record, policy, procedure, or evidence that the standard requires. There are two categories:

1. **Documents that must be maintained** - These are policies, procedures, and plans that define how your ISMS operates. They must exist, be current, and be accessible to relevant personnel.

2. **Records that must be retained** - These are evidence of activities performed, such as risk assessment results, audit reports, management review minutes, and training records. They must be retained for a defined period as proof that your ISMS is operational.

## Mandatory Documents Required by ISO 27001 Clauses

### Clause 4: Context of the Organization

**4.3 - Scope of the ISMS (Mandatory)**
You must have a documented scope statement that defines the boundaries and applicability of your ISMS. The scope must consider:
- External and internal issues (from 4.1)
- Requirements of interested parties (from 4.2)
- Interfaces and dependencies with activities performed by other organizations

**Common mistakes:**
- Scope is too vague ("all company activities")
- Scope does not address interfaces with external parties
- Scope does not mention physical locations or services covered

### Clause 5: Leadership

**5.2 - [Information Security Policy](/controls/a-5-1-policies-for-information-security) (Mandatory)**
Top management must establish an information security policy that:
- Is appropriate to the purpose of the organization
- Includes information security objectives or provides a framework for setting them
- Includes a commitment to satisfy applicable requirements
- Includes a commitment to continual improvement

The policy must be documented, communicated within the organization, and available to interested parties as appropriate.

**Common mistakes:**
- Policy is generic and not tailored to the organization
- Policy does not include measurable objectives or a framework for setting them
- Policy is not signed or approved by top management
- Policy has no review date

**5.3 - Organizational Roles, Responsibilities, and Authorities (Mandatory)**
Documented assignment of information security responsibilities. This can be in a RACI matrix, organizational chart, or role descriptions.

### Clause 6: Planning

**6.1.2 - Information Security Risk Assessment Process (Mandatory)**
A documented risk assessment methodology that defines:
- Risk acceptance criteria
- Criteria for performing risk assessments
- How risks are identified, analyzed, and evaluated
- How the process ensures consistent, valid, and comparable results

**6.1.3 - Risk Treatment Plan (Mandatory)**
A documented plan showing how each identified risk will be treated (mitigate, accept, transfer, or avoid) along with the controls selected and justification for their selection.

**6.1.3d - Statement of Applicability (Mandatory)**
The Statement of Applicability (SoA) is one of the most important **ISO 27001 documents**. It must list:
- All controls from Annex A
- Whether each control is applicable or not
- Justification for inclusion or exclusion
- Implementation status

**6.2 - Information Security Objectives (Mandatory)**
Documented objectives that are measurable, consistent with the security policy, and communicated to relevant personnel.

### Clause 7: Support

**7.2 - Evidence of Competence (Mandatory Record)**
Records demonstrating that personnel performing work affecting information security have the necessary competence. This includes training records, certifications, qualifications, and experience documentation.

**7.5 - Documented Information Control (Mandatory)**
You need a documented approach to creating, updating, and controlling documented information. This is essentially your document management procedure.

### Clause 8: Operation

**8.1 - Operational Planning and Control (Mandatory)**
Documentation of operational processes and their planned outcomes. This includes any processes that have been outsourced.

**8.2 - Results of Information Security Risk Assessment (Mandatory Record)**
Retained records of each risk assessment performed, including the methodology used, risks identified, risk analysis results, and risk evaluation outcomes.

**8.3 - Results of Information Security Risk Treatment (Mandatory Record)**
Retained records showing how risk treatment decisions were made and implemented.

### Clause 9: Performance Evaluation

**9.1 - Monitoring, Measurement, Analysis, and Evaluation Results (Mandatory Record)**
Records of what was monitored and measured, when, by whom, and the results.

**9.2 - Internal Audit Program and Results (Mandatory Record)**
- A documented internal audit program (schedule, scope, frequency)
- Audit reports with findings and conclusions
- Evidence that audit results were reported to relevant management

**9.3 - Management Review Results (Mandatory Record)**
Minutes or records of management review meetings showing that all required inputs were considered and decisions/actions were documented.

### Clause 10: Improvement

**10.1 - Nonconformity and Corrective Action Records (Mandatory Record)**
Records of:
- The nature of nonconformities
- Actions taken in response
- Results of corrective actions

## Mandatory Documents Required by Annex A Controls

While Annex A controls are selected based on your risk assessment and SoA, several controls explicitly require documented policies or procedures when they are applicable:

**A.5.1 - Policies for Information Security**
A set of information security policies, approved by management, published, and communicated.

**[A.5.10](/controls/a-5-10-acceptable-use-of-information-and-other-associated-assets) - Acceptable Use of Information and Associated Assets**
Rules for the acceptable use of information and assets must be identified, documented, and implemented.

**[A.5.13](/controls/a-5-13-labelling-of-information) - Labelling of Information**
Procedures for information labelling in accordance with the classification scheme.

**[A.5.23](/controls/a-5-23-information-security-for-use-of-cloud-services) - Information Security for Use of Cloud Services**
Processes for acquisition, use, management, and exit from cloud services.

**[A.5.29](/controls/a-5-29-information-security-during-disruption) - Information Security During Disruption**
Plans for maintaining information security during adverse situations.

**[A.5.37](/controls/a-5-37-documented-operating-procedures) - Documented Operating Procedures**
Operating procedures for information processing facilities must be documented and made available.

**A.6.1 - Screening**
Background verification procedures for all candidates.

**A.6.2 - Terms and Conditions of Employment**
Employment contracts stating information security responsibilities.

**A.6.3 - Information Security Awareness, Education, and Training**
A documented awareness and training program.

**A.8.1 - User Endpoint Devices**
A topic-specific policy for managing endpoint devices.

**A.8.9 - Configuration Management**
Documented configurations for hardware, software, services, and networks.

**A.8.13 - Information Backup**
A topic-specific backup policy (see our [backup policy template guide](/blog/iso-27001-backup-policy-template)).

**A.8.15 - Logging**
A topic-specific policy on logging.

**A.8.25 - Secure Development Lifecycle**
Rules for the secure development of software and systems.

## The Complete ISO 27001 Mandatory Documents List

Here is a consolidated checklist of all **ISO 27001 required documents**:

| # | Document | Clause/Control | Type |
|---|---|---|---|
| 1 | ISMS Scope | 4.3 | Document |
| 2 | Information Security Policy | 5.2 | Document |
| 3 | Roles and Responsibilities | 5.3 | Document |
| 4 | Risk Assessment Methodology | 6.1.2 | Document |
| 5 | Risk Treatment Plan | 6.1.3 | Document |
| 6 | Statement of Applicability | 6.1.3d | Document |
| 7 | Information Security Objectives | 6.2 | Document |
| 8 | Competence Evidence | 7.2 | Record |
| 9 | Document Control Procedure | 7.5 | Document |
| 10 | Risk Assessment Results | 8.2 | Record |
| 11 | Risk Treatment Results | 8.3 | Record |
| 12 | Monitoring and Measurement Results | 9.1 | Record |
| 13 | Internal Audit Program | 9.2 | Document |
| 14 | Internal Audit Reports | 9.2 | Record |
| 15 | Management Review Minutes | 9.3 | Record |
| 16 | Nonconformity and Corrective Action Records | 10.1 | Record |
| 17 | Acceptable Use Policy | A.5.10 | Document |
| 18 | Access Control Policy | A.5.15 | Document |
| 19 | Operating Procedures | A.5.37 | Document |
| 20 | Backup Policy | A.8.13 | Document |
| 21 | Logging Policy | A.8.15 | Document |
| 22 | Secure Development Policy | A.8.25 | Document |

This list covers the minimum. Most organizations also maintain additional policies for topics like incident management, supplier security, change management, and business continuity.

## Common Mistakes That Cause Audit Findings

1. **Missing the Statement of Applicability** - The SoA is the single most important document. Without it, you cannot pass a Stage 1 audit. Use our [free SoA generator](/soa-generator) to create one quickly.

2. **No version control** - Every document must have a version number, approval date, review date, and document owner. Auditors check this immediately.

3. **Policies without evidence of implementation** - A well-written policy that nobody follows is worse than no policy at all. Auditors verify implementation through interviews and record reviews.

4. **Incomplete risk assessment records** - Your risk assessment must cover all assets in scope and use the methodology defined in your documented process. Partial assessments are flagged.

5. **No management review minutes** - Management review is mandatory at planned intervals. Missing minutes means missing evidence of leadership engagement.

6. **Training records without competence proof** - Attending a training session is not the same as demonstrating competence. Records should show how competence was evaluated.

7. **Outdated documents** - If your policy says "reviewed annually" but the last review was two years ago, that is a nonconformity.

8. **Inconsistent documents** - If your risk assessment methodology document describes a 5x5 matrix but your risk register uses a 3x3 matrix, auditors will flag the inconsistency.

## How Many Documents Do You Actually Need?

The mandatory minimum is approximately 20-25 documents and records. However, a well-structured ISMS for a medium-sized organization typically includes 35-50 documents when you add recommended policies and procedures for all applicable Annex A controls.

Our [ISO 27001 Document Pack](/iso-27001-templates) includes 46+ professionally written documents covering every mandatory and recommended document listed above. All templates are in editable Word format, aligned with ISO 27001:2022, and ready for auditor review.

## Get All Your ISO 27001 Documents in One Pack

Instead of writing each document from scratch, start with professionally written templates that you can customize to your organization. Our Document Pack covers every **ISO 27001 mandatory document** plus recommended policies for all applicable Annex A controls.

[Browse all documents](/documents) | [Get the complete Document Pack - $99](/iso-27001-templates)
        `
      },
      fr: {
        title: "Documents Obligatoires ISO 27001 - Liste Complete pour 2026",
        metaTitle: "Documents Obligatoires ISO 27001 - Liste Complete pour 2026",
        metaDescription: "Liste complete de tous les documents obligatoires ISO 27001 requis pour la certification en 2026. Couvre chaque clause et exigence de l'Annexe A.",
        excerpt: "L'ISO 27001:2022 exige un ensemble specifique de documents obligatoires pour la certification. Ce guide liste chaque document requis et explique ce que chacun doit contenir.",
        content: `
## Comprendre les exigences documentaires de l'ISO 27001

L'une des questions les plus courantes concerne les documents necessaires. L'ISO 27001:2022 exige explicitement certains documents. Consultez la version anglaise pour la liste complete et les conseils pratiques.
        `
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-04-09",
    updatedDate: "2026-04-09",
    category: "Implementation",
    tags: ["ISO 27001", "Mandatory Documents", "Required Documents", "Certification", "Documentation", "Annex A"],
    readTime: 18,
    featured: false
  },
  {
    slug: "iso-27001-compliance-guide",
    translations: {
      en: {
        title: "ISO 27001 Compliance - What It Means and How to Achieve It in 2026",
        metaTitle: "ISO 27001 Compliance - What It Means and How to Achieve It in 2026",
        metaDescription: "Learn what ISO 27001 compliance means, how it differs from certification, the steps to achieve compliance, and common mistakes to avoid. Complete 2026 guide.",
        excerpt: "ISO 27001 compliance is more than just getting a certificate on the wall. This guide breaks down what compliance actually means, how it differs from certification, the practical steps to get there, and the mistakes that derail most organizations.",
        content: `
## What Does ISO 27001 Compliance Actually Mean?

ISO 27001 compliance means your organization has implemented an Information Security Management System (ISMS) that meets the requirements defined in the ISO/IEC 27001:2022 standard. This includes establishing policies, conducting risk assessments, implementing controls, and maintaining continuous improvement processes that protect the confidentiality, integrity, and availability of your information assets.

But here is the critical distinction most people miss: compliance is not the same as certification. You can be fully compliant with ISO 27001 without ever engaging a certification body. Compliance means you follow the standard's requirements. Certification means an accredited third-party auditor has verified your compliance and issued a formal certificate.

Many organizations choose to pursue compliance first and certification later. Others maintain compliance without ever seeking formal certification, particularly when their clients or regulators accept self-declared conformity. Both approaches are valid, and the right choice depends on your business context, client requirements, and industry expectations.

The standard itself is structured around a Plan-Do-Check-Act (PDCA) cycle. This means compliance is never a one-time achievement. It requires ongoing management attention, regular reviews, and continuous improvement. An organization that implements ISO 27001 in January and ignores it by June is not compliant, regardless of what documentation exists.

### The Scope of Compliance

ISO 27001 compliance covers the entire lifecycle of information security management. It begins with understanding your organization's context (Clause 4), moves through leadership commitment (Clause 5), planning (Clause 6), support and resources (Clause 7), operational implementation (Clause 8), performance evaluation (Clause 9), and continuous improvement (Clause 10).

Each of these clauses contains specific requirements that must be addressed. You cannot pick and choose which clauses to follow - all mandatory clauses (4 through 10) must be implemented for compliance. The only flexibility is in Annex A, where you select applicable controls based on your risk assessment and justify any exclusions in your Statement of Applicability.

## Compliance vs Certification - Understanding the Difference

The distinction between compliance and certification is fundamental, yet frequently confused. Let us break it down clearly.

**Compliance** means your ISMS meets the requirements of ISO 27001. You have documented policies, conducted risk assessments, implemented appropriate controls, and established processes for monitoring and improvement. Compliance is something you declare and maintain internally.

**Certification** means an accredited certification body (like BSI, Bureau Veritas, or TUV) has audited your ISMS and confirmed that it meets ISO 27001 requirements. Certification involves a Stage 1 audit (documentation review) and Stage 2 audit (implementation verification), followed by annual surveillance audits and a full recertification audit every three years.

### When Compliance Without Certification Is Enough

Some scenarios where compliance alone may suffice:

- Your clients accept self-declared conformity statements
- Your industry does not mandate third-party certification
- You are using ISO 27001 as an internal framework to improve security
- You are preparing for certification but are not ready for the audit yet
- Budget constraints make certification impractical in the short term

### When Certification Is Necessary

Certification becomes necessary when:

- Client contracts explicitly require ISO 27001 certification
- Your industry has regulatory requirements for certified security management
- You are competing for enterprise deals where certification is a differentiator
- You need to demonstrate compliance to multiple stakeholders efficiently
- Insurance providers offer better terms for certified organizations

The bottom line: compliance is the substance, certification is the verification. You need compliance regardless of whether you pursue certification. But certification without genuine compliance is both impossible and pointless.

## Steps to Achieve ISO 27001 Compliance

Achieving ISO 27001 compliance is a structured process. While every organization's journey is unique, the following steps provide a proven framework that works across industries and company sizes.

### Step 1: Secure Management Commitment

ISO 27001 Clause 5 requires top management to demonstrate leadership and commitment to the ISMS. This is not a checkbox exercise. Without genuine management support, your ISMS will lack the resources, authority, and organizational priority it needs to succeed.

Management commitment means allocating budget for tools and training, appointing an ISMS manager or team, communicating the importance of information security across the organization, and participating in management review meetings. If leadership treats ISO 27001 as purely an IT project, compliance will fail.

### Step 2: Define the ISMS Scope

Your ISMS scope (Clause 4.3) defines the boundaries of your information security management system. It specifies which parts of your organization, which locations, which systems, and which processes are covered.

A common mistake is making the scope too broad initially. Start with a manageable scope - perhaps a single business unit, product line, or location - and expand later. The scope must be documented and should consider internal and external issues (Clause 4.1) and the needs of interested parties (Clause 4.2).

Use a tool like the [ISMS Scope Builder](/tools/isms-scope-builder) to structure your scope definition and ensure it meets the standard's requirements.

### Step 3: Conduct a Risk Assessment

Risk assessment (Clause 6.1.2) is the engine that drives your entire ISMS. It determines which risks you face, how significant they are, and what controls you need to implement.

Your risk assessment methodology must be documented and repeatable. It should include criteria for risk acceptance, methods for risk identification, a consistent approach to analyzing likelihood and impact, and a process for evaluating and prioritizing risks.

The [Gap Analysis Tool](/tools/gap-analysis) helps you identify where your current security posture falls short of ISO 27001 requirements, providing a clear picture of what needs to change.

### Step 4: Implement Controls and the Statement of Applicability

Based on your risk assessment, you select controls from Annex A (or other sources) to treat identified risks. The Statement of Applicability (SoA) documents which of the 93 Annex A controls are applicable to your organization and which are excluded, with justification for each exclusion.

The SoA is one of the most important documents in your ISMS. Auditors will scrutinize it closely to ensure your control selection aligns with your risk assessment results.

### Step 5: Develop Documentation

ISO 27001 requires specific documented information, including:

- [Information security policy](/controls/a-5-1-policies-for-information-security) (Clause 5.2)
- Risk assessment methodology and results (Clause 6.1.2)
- Statement of Applicability (Clause 6.1.3)
- Risk treatment plan (Clause 6.1.3)
- Information security objectives (Clause 6.2)
- Evidence of competence (Clause 7.2)
- Operational planning and control documentation (Clause 8.1)
- Risk assessment results (Clause 8.2)
- Risk treatment results (Clause 8.3)
- Monitoring and measurement results (Clause 9.1)
- Internal audit program and results (Clause 9.2)
- Management review results (Clause 9.3)
- Nonconformities and corrective actions (Clause 10.1)

The [ISO 27001 Document Pack](/documents) provides professionally written templates for all mandatory documents, saving months of drafting time.

### Step 6: Train Your People

Clause 7.2 and 7.3 require that people performing work under your ISMS are competent and aware of the information security policy, their contribution to ISMS effectiveness, and the implications of not conforming to ISMS requirements.

Training should cover general [security awareness](/controls/a-6-3-information-security-awareness-education-and-training) for all employees and role-specific training for people with ISMS responsibilities. Document all training activities and maintain records of competence.

### Step 7: Operate and Monitor

Once your ISMS is operational, Clause 8 requires you to plan, implement, and control the processes needed to meet information security requirements. This includes conducting risk assessments at planned intervals or when significant changes occur.

Clause 9.1 requires you to monitor, measure, analyze, and evaluate your ISMS performance. Define what needs to be monitored, the methods for monitoring, when monitoring should be performed, and who is responsible.

### Step 8: Conduct Internal Audits

Internal audits (Clause 9.2) verify that your ISMS conforms to both the standard's requirements and your own organizational requirements. Audits should cover the entire ISMS over a planned cycle and should be conducted by people who are objective and impartial.

Use the [Internal Audit Checklist](/tools/internal-audit) to ensure comprehensive coverage of all ISO 27001 requirements during your internal audit program.

### Step 9: Management Review

Clause 9.3 requires top management to review the ISMS at planned intervals to ensure its continuing suitability, adequacy, and effectiveness. Management reviews should consider the status of actions from previous reviews, changes in external and internal issues, feedback on ISMS performance, and opportunities for improvement.

### Step 10: Continuous Improvement

Clause 10 requires you to continually improve the suitability, adequacy, and effectiveness of your ISMS. This means addressing nonconformities through corrective action and proactively identifying opportunities for improvement.

## Common Mistakes That Derail ISO 27001 Compliance

After working with dozens of organizations on their ISO 27001 journey, these are the mistakes we see most frequently.

### Treating It as a One-Time Project

ISO 27001 compliance is not a project with a start and end date. It is an ongoing management system. Organizations that treat implementation as a one-time effort inevitably find their ISMS deteriorating within months. Build compliance into your regular business operations from the start.

### Overcomplicating the Risk Assessment

Some organizations spend months debating risk assessment methodologies, creating overly complex scoring systems, and analyzing risks at a granular level that adds no value. Keep your risk assessment practical and repeatable. A simple 5x5 matrix with clear criteria is more effective than an elaborate system nobody understands.

### Ignoring the Human Factor

The most sophisticated technical controls are worthless if your people do not follow them. Security awareness training is not optional - it is a requirement of the standard. Invest in making your policies understandable and your training engaging.

### Writing Policies That Nobody Reads

Creating a 50-page information security policy that sits on a shared drive, unread, does not constitute compliance. Your policies should be clear, concise, and accessible. They should reflect what your organization actually does, not what you think auditors want to hear.

### Skipping the Gap Analysis

Jumping straight into implementation without understanding your current state leads to wasted effort and missed requirements. A thorough [gap analysis](/tools/gap-analysis) identifies exactly where you stand and what needs to change, allowing you to prioritize effectively.

### Underestimating Documentation Requirements

ISO 27001 requires documented information at every stage. Organizations that leave documentation until the end scramble to create records retroactively, which auditors can easily spot. Document as you go, not after the fact.

### Not Allocating Sufficient Resources

ISO 27001 implementation requires dedicated time, budget, and personnel. Organizations that try to achieve compliance as a side project alongside regular duties rarely succeed. Even small organizations should allocate at least one person with significant dedicated time to manage the ISMS.

## How ISO27001KIT Accelerates Your Compliance Journey

Achieving ISO 27001 compliance does not have to take years or cost a fortune. The right tools dramatically reduce the time and effort required.

The [ISO 27001 Document Pack](/documents) provides all mandatory documentation templates, professionally written and ready to customize. Instead of spending months drafting policies from scratch, you can have audit-ready documents within days.

Our free [Gap Analysis Tool](/tools/gap-analysis) gives you an immediate picture of where you stand against ISO 27001 requirements. It identifies gaps, prioritizes actions, and generates exportable reports for management review.

For organizations that need ongoing risk management, [Risk Copilot](/risk-copilot/demo) provides a complete risk register, treatment tracking, control mapping, and audit trail - everything Clause 6.1 and Clause 8 require, without spreadsheets.

The combination of proper documentation, structured gap analysis, and continuous risk management covers the core requirements of ISO 27001 compliance and puts you on the fastest path to certification if you choose to pursue it.

## Conclusion

ISO 27001 compliance is achievable for organizations of any size. It requires genuine commitment from management, a structured approach to risk assessment, appropriate controls, and continuous improvement. The standard is designed to be flexible enough to work for a 10-person startup or a 10,000-person enterprise.

Start with a gap analysis to understand where you stand. Build your documentation systematically. Implement controls based on your actual risks, not a generic checklist. Train your people. Monitor your performance. And remember that compliance is a journey, not a destination.

The organizations that succeed with ISO 27001 are those that treat it as a business enabler rather than a burden. When implemented well, ISO 27001 reduces security incidents, builds customer trust, opens new markets, and creates a culture of security awareness that protects your most valuable assets.
`
      },
      fr: {
        title: "ISO 27001 Compliance - What It Means and How to Achieve It in 2026",
        metaTitle: "ISO 27001 Compliance - What It Means and How to Achieve It in 2026",
        metaDescription: "Learn what ISO 27001 compliance means, how it differs from certification, the steps to achieve compliance, and common mistakes to avoid. Complete 2026 guide.",
        excerpt: "ISO 27001 compliance is more than just getting a certificate on the wall. This guide breaks down what compliance actually means, how it differs from certification, the practical steps to get there, and the mistakes that derail most organizations.",
        content: ``
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-04-15",
    updatedDate: "2026-04-15",
    category: "Implementation",
    tags: ["iso 27001 compliance", "iso 27001 requirements", "isms", "certification", "gap analysis"],
    readTime: 12,
    featured: false
  },
  {
    slug: "iso-27001-software-tools-guide",
    translations: {
      en: {
        title: "ISO 27001 Software - Best Tools for Implementation and Risk Management in 2026",
        metaTitle: "ISO 27001 Software - Best Tools for Implementation and Risk Management in 2026",
        metaDescription: "Compare the best ISO 27001 software for implementation and risk management. Review free and paid tools, GRC platforms, and purpose-built solutions for 2026.",
        excerpt: "Choosing the right ISO 27001 software can cut your implementation timeline in half. This guide covers every category of tool available, compares the leading options, and explains why purpose-built solutions outperform generic GRC platforms.",
        content: `
## What Is ISO 27001 Software?

ISO 27001 software is any tool designed to help organizations implement, maintain, and improve their Information Security Management System in alignment with the ISO/IEC 27001:2022 standard. This category spans everything from simple document templates to comprehensive GRC (Governance, Risk, and Compliance) platforms.

The right software eliminates the manual, error-prone processes that make ISO 27001 implementation painful. Instead of managing risks in spreadsheets, tracking controls in email threads, and storing policies across scattered shared drives, ISO 27001 software centralizes everything into a structured, auditable system.

The market for ISO 27001 software has evolved significantly. Early tools were essentially document management systems with an ISO 27001 label. Modern solutions provide interactive risk assessments, automated control mapping, real-time compliance dashboards, and integrated audit management - all designed around the specific workflows that ISO 27001 requires.

For organizations choosing between options, the key question is not whether to use software, but which type of software matches your maturity level, team size, and budget.

## Categories of ISO 27001 Software

The ISO 27001 software landscape breaks down into several distinct categories, each serving different needs and organizational stages.

### 1. Document Template Packs

Document template packs provide pre-written policy templates, procedure documents, and record forms that cover ISO 27001's mandatory documentation requirements. They give you a starting point for all the written material your ISMS needs.

**Best for:** Organizations starting their ISO 27001 journey who need to establish documentation quickly. Smaller teams that want professional-quality documents without hiring consultants.

**Limitations:** Templates alone do not provide ongoing management capability. They solve the documentation problem but leave you managing risk assessments, audits, and control tracking through other means.

The [ISO 27001 Document Pack](/documents) provides over 40 professionally written templates covering every mandatory document, from the [information security policy](/controls/a-5-1-policies-for-information-security) to the Statement of Applicability. Each template includes guidance notes explaining what to customize and why.

### 2. Risk Assessment and Management Tools

Risk management tools handle the core engine of ISO 27001: identifying, assessing, treating, and monitoring information security risks. They replace the spreadsheets that most organizations start with and provide structure, automation, and audit trails.

**Best for:** Organizations that have outgrown spreadsheet-based risk management. Teams managing more than 20 risks or those that need proper audit trails for risk decisions.

**Key features to look for:** Customizable risk matrices, treatment plan tracking, control-to-risk mapping, automatic residual risk calculation, and exportable risk reports.

### 3. GRC Platforms

GRC (Governance, Risk, and Compliance) platforms are enterprise-grade solutions that cover ISO 27001 alongside other frameworks like SOC 2, GDPR, HIPAA, and PCI DSS. They provide the broadest functionality but come with the highest complexity and cost.

**Best for:** Large organizations managing multiple compliance frameworks simultaneously. Companies with dedicated compliance teams and substantial budgets.

**Limitations:** GRC platforms are often over-engineered for organizations that only need ISO 27001. The learning curve is steep, implementation takes months, and annual licensing costs typically start at $15,000 to $50,000 or more. Many features go unused by teams focused solely on ISO 27001.

### 4. Gap Analysis Tools

Gap analysis tools assess your current security posture against ISO 27001 requirements and identify areas that need improvement. They provide a structured checklist approach that ensures no requirement is overlooked.

**Best for:** Organizations at the beginning of their ISO 27001 journey or preparing for surveillance audits. Teams that need to quickly understand their compliance status.

### 5. Audit Management Tools

Audit management tools support internal audit planning, execution, and reporting. They help you manage audit schedules, track findings, assign corrective actions, and maintain the audit evidence that certification bodies expect.

**Best for:** Organizations with mature ISMS processes that conduct regular internal audits. Teams preparing for external certification audits.

### 6. Integrated Implementation Platforms

Integrated platforms combine multiple categories into a single solution specifically designed for ISO 27001. They provide document management, risk assessment, control tracking, audit management, and compliance dashboards in one unified system.

**Best for:** Organizations that want a single tool to manage their entire ISMS without integrating multiple point solutions.

## Comparing Popular ISO 27001 Software Options

The market offers dozens of options across these categories. Here is an honest comparison of the major approaches.

### Enterprise GRC Platforms

**Examples:** ServiceNow GRC, OneTrust, Archer, MetricStream

**Pros:**
- Comprehensive feature sets covering multiple frameworks
- Strong integration capabilities with enterprise systems
- Advanced reporting and analytics
- Established vendor reputation

**Cons:**
- Annual costs typically $30,000 to $100,000+
- Implementation timelines of 3 to 12 months
- Require dedicated administrators
- Many features irrelevant for ISO 27001-only needs
- Steep learning curve for smaller teams

**Verdict:** Overkill for most organizations pursuing ISO 27001 alone. Make sense only if you are managing five or more compliance frameworks with a dedicated team.

### Mid-Market Compliance Platforms

**Examples:** Vanta, Drata, Sprinto, Secureframe

**Pros:**
- Faster implementation than enterprise GRC
- Pre-built ISO 27001 frameworks
- Automated evidence collection from [cloud services](/controls/a-5-23-information-security-for-use-of-cloud-services)
- Modern user interfaces

**Cons:**
- Annual costs typically $10,000 to $30,000
- Primarily designed for SOC 2, with ISO 27001 as an add-on
- Limited customization of risk methodologies
- May not cover all ISO 27001 clauses in depth
- Evidence collection focuses on cloud/SaaS environments

**Verdict:** Good for cloud-native companies that also need SOC 2. The ISO 27001 coverage can be shallow compared to purpose-built tools.

### Purpose-Built ISO 27001 Tools

**Examples:** ISO27001KIT, Advisera, isoTracker

**Pros:**
- Designed specifically for ISO 27001 workflows
- Typically more affordable than GRC platforms
- Faster to implement and learn
- Deep coverage of ISO 27001-specific requirements
- Better value for organizations focused on a single standard

**Cons:**
- May not cover other compliance frameworks
- Smaller vendor size compared to enterprise players
- Feature sets vary significantly between vendors

**Verdict:** The best choice for most organizations whose primary goal is ISO 27001 compliance or certification. Purpose-built tools understand the standard's nuances better than generic platforms.

### Spreadsheet-Based Approaches

**Pros:**
- Zero or minimal cost
- Familiar interface
- Complete flexibility

**Cons:**
- No audit trail
- Version control problems
- Manual calculations prone to error
- No automated reporting
- Painful to scale beyond 20-30 risks
- Not impressive to auditors

**Verdict:** Acceptable only for the very earliest stages of ISO 27001 exploration. Move to proper tooling before serious implementation begins.

## Why ISO27001KIT Tools Are Built Different

Most ISO 27001 software takes a top-down approach: build a massive platform and bolt on ISO 27001 as one of many frameworks. ISO27001KIT takes the opposite approach - every tool is designed from the ground up for ISO 27001 practitioners.

### Free Implementation Tools

The [Tools page](/tools) provides 11 free interactive tools that cover the core activities of ISO 27001 implementation:

- **Gap Analysis Tool** - Assess your compliance status against every clause and Annex A control
- **Risk Assessment Tool** - Identify and evaluate information security risks with a structured methodology
- **SoA Generator** - Build your Statement of Applicability with all 93 Annex A controls
- **Internal Audit Checklist** - Plan and conduct internal audits against ISO 27001 requirements
- **Implementation Roadmap** - Create a phased implementation plan with timeline and milestones
- **ISMS Scope Builder** - Define and document your ISMS scope
- **[Asset Inventory](/controls/a-5-9-inventory-of-information-and-other-associated-assets)** - Catalog information assets with classification and ownership
- **Annex A Control Selector** - Choose applicable controls based on your risk profile
- **Supplier Risk Manager** - Assess and track third-party security risks
- **RTO/RPO BIA Helper** - Define recovery objectives for [business continuity](/controls/a-5-30-ict-readiness-for-business-continuity)

Each tool generates exportable outputs in PDF, Excel, or Word format that you can use directly in your ISMS documentation.

### Risk Copilot - Purpose-Built Risk Management

For organizations that need ongoing, professional-grade risk management, [Risk Copilot](/risk-copilot/demo) provides everything Clause 6.1 and Clause 8 require:

**Risk Register.** A complete risk register with customizable risk IDs, categories, threat and vulnerability mapping, inherent and residual risk scoring, and treatment tracking. Every risk links to specific assets, controls, and treatment plans.

**Customizable Methodology.** Define your own likelihood and impact scales, risk matrix, and risk appetite thresholds. Use a 3x3, 4x4, or 5x5 matrix. Set scoring formulas that match your organization's approach.

**Control Management.** Track all security controls with status, effectiveness ratings, testing schedules, and evidence notes. Link controls directly to the risks they mitigate.

**Asset Management.** Maintain your information asset inventory with confidentiality, integrity, and availability classifications. Link assets to business processes and risks.

**Threat and Vulnerability Libraries.** Pre-built libraries of common threats and vulnerabilities based on ISO 27005, with the ability to add custom entries.

**Audit Trail.** Every change to every record is logged automatically, providing the evidence trail that auditors expect.

**Professional Exports.** Generate ISO 27001-formatted Excel workbooks and Word documents ready for management review or audit submission.

**Business Process Registry.** Map risks and assets to business processes, supporting the context of the organization analysis required by Clause 4.

The key difference is that Risk Copilot is not a generic risk tool with an ISO 27001 skin. Every feature, every field, every workflow is designed around what ISO 27001 actually requires. There are no unnecessary features cluttering the interface, and no missing features that force you back to spreadsheets.

## How to Choose the Right ISO 27001 Software

Choosing the right tool depends on where you are in your ISO 27001 journey and what your organization needs.

### Just Starting Out

If you are beginning your ISO 27001 journey, you need two things: documentation templates and a gap analysis. Start with the [ISO 27001 Document Pack](/documents) and the free [Gap Analysis Tool](/tools/gap-analysis). These give you the foundation without any commitment to expensive platforms.

### Ready for Structured Risk Management

Once you have completed your initial gap analysis and started implementing controls, you need proper risk management tooling. This is where Risk Copilot fits perfectly - it provides professional risk management without the complexity or cost of enterprise GRC platforms.

### Managing Multiple Frameworks

If you need to manage ISO 27001 alongside SOC 2, GDPR, or other frameworks, consider a mid-market compliance platform. But be realistic about whether you truly need multi-framework support or whether ISO 27001 is your primary focus.

### Enterprise Requirements

Large organizations with dedicated compliance teams and requirements for managing multiple frameworks across business units may benefit from enterprise GRC platforms. But even in these cases, purpose-built ISO 27001 tools often provide better day-to-day usability.

## The Total Cost of ISO 27001 Software

Software costs vary dramatically across categories:

| Category | Annual Cost Range | Implementation Time |
|---|---|---|
| Document Templates | $99 to $500 (one-time) | Days |
| Free Tools (ISO27001KIT) | $0 | Immediate |
| Risk Copilot Pro | $149/month | Hours |
| Mid-Market Platforms | $10,000 to $30,000/year | 1 to 3 months |
| Enterprise GRC | $30,000 to $100,000+/year | 3 to 12 months |

The hidden cost is not the software license - it is the time your team spends learning and maintaining the tool. A simpler, purpose-built tool that your team actually uses consistently delivers more value than an expensive platform that collects dust.

## Conclusion

The best ISO 27001 software is the one your team will actually use consistently. For most organizations, that means purpose-built tools designed specifically for ISO 27001, not bloated GRC platforms that try to do everything.

Start with the basics: professional documentation and a structured gap analysis. Add risk management tooling when you are ready for continuous risk monitoring. And choose tools that understand the specific workflows and requirements of ISO 27001, not generic platforms that treat it as just another checkbox.

The [ISO27001KIT tools](/tools) and [Risk Copilot](/risk-copilot/demo) are built by practitioners, for practitioners. Every feature exists because ISO 27001 requires it, and nothing is included just to pad a feature list. That is the difference between software built for compliance and software built for ISO 27001.
`
      },
      fr: {
        title: "ISO 27001 Software - Best Tools for Implementation and Risk Management in 2026",
        metaTitle: "ISO 27001 Software - Best Tools for Implementation and Risk Management in 2026",
        metaDescription: "Compare the best ISO 27001 software for implementation and risk management. Review free and paid tools, GRC platforms, and purpose-built solutions for 2026.",
        excerpt: "Choosing the right ISO 27001 software can cut your implementation timeline in half. This guide covers every category of tool available, compares the leading options, and explains why purpose-built solutions outperform generic GRC platforms.",
        content: ``
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-04-15",
    updatedDate: "2026-04-15",
    category: "Tools",
    tags: ["iso 27001 software", "iso 27001 risk management software", "isms tools", "grc", "risk copilot"],
    readTime: 13,
    featured: false
  },
  {
    slug: "iso-27001-requirements-complete-checklist",
    translations: {
      en: {
        title: "ISO 27001 Requirements - Complete Checklist of What You Need",
        metaTitle: "ISO 27001 Requirements - Complete Checklist of What You Need",
        metaDescription: "Complete ISO 27001 requirements checklist covering every clause from 4 to 10, mandatory documents, Annex A controls, and common gaps organizations miss.",
        excerpt: "ISO 27001 has specific, non-negotiable requirements across seven mandatory clauses and 93 Annex A controls. This complete checklist covers every requirement you need to meet, the documents you must produce, and the gaps that catch most organizations off guard.",
        content: `
## Understanding ISO 27001 Requirements Structure

ISO 27001:2022 is structured into two main parts: the mandatory clauses (4 through 10) that define the management system requirements, and Annex A that provides a reference set of 93 information security controls organized into four themes.

Every requirement in Clauses 4 through 10 is mandatory. You cannot exclude or skip any of them. Annex A controls, however, are selected based on your risk assessment results - you choose which controls are applicable and document your justification for excluding any that are not.

This distinction is crucial. Auditors will verify that every clause requirement is fully implemented. For Annex A, they will check that your selection is justified by your risk assessment and documented in your Statement of Applicability.

Let us walk through every requirement, clause by clause.

## Clause 4: Context of the Organization

Clause 4 establishes the foundation of your ISMS by requiring you to understand your organization and its environment.

### 4.1 Understanding the Organization and Its Context

**Requirement:** Determine external and internal issues relevant to your purpose that affect your ability to achieve the intended outcomes of your ISMS.

**What this means in practice:**
- Identify external factors: regulatory requirements, market conditions, technology trends, threat landscape, contractual obligations
- Identify internal factors: organizational culture, governance structure, existing capabilities, resource constraints, strategic objectives
- Document these issues and review them regularly

**Common gap:** Organizations list generic factors without connecting them to information security. Your context analysis should specifically address how each factor impacts your ISMS.

### 4.2 Understanding the Needs and Expectations of Interested Parties

**Requirement:** Determine the interested parties relevant to your ISMS and their requirements.

**What this means in practice:**
- Identify stakeholders: customers, regulators, employees, shareholders, suppliers, partners
- Determine what each stakeholder expects regarding information security
- Identify which requirements will be addressed through your ISMS

**Common gap:** Failing to identify regulatory requirements specific to your industry or geography. GDPR, NIS2, and sector-specific regulations all create ISMS requirements.

### 4.3 Determining the Scope of the ISMS

**Requirement:** Determine the boundaries and applicability of the ISMS to establish its scope, considering clauses 4.1 and 4.2.

**What this means in practice:**
- Define which business units, locations, systems, and processes are covered
- Document the scope clearly and make it available to interested parties
- Consider interfaces and dependencies with activities performed by other organizations

**Common gap:** Scope statements that are too vague ("all IT systems") or too broad (the entire organization when only one division handles sensitive data). Use the [ISMS Scope Builder](/tools/isms-scope-builder) to create a precise, auditable scope.

### 4.4 Information Security Management System

**Requirement:** Establish, implement, maintain, and continually improve an ISMS.

**What this means in practice:** This is the overarching requirement that everything else supports. Your ISMS must be a living system, not a collection of static documents.

## Clause 5: Leadership

### 5.1 Leadership and Commitment

**Requirement:** Top management shall demonstrate leadership and commitment by ensuring the [information security policy](/controls/a-5-1-policies-for-information-security) and objectives are established, integrating ISMS requirements into business processes, ensuring resources are available, and directing and supporting people to contribute to ISMS effectiveness.

**Common gap:** Leadership signs off on the policy but does not actively participate in management reviews or resource allocation decisions.

### 5.2 Policy

**Requirement:** Establish an information security policy that is appropriate to the purpose of the organization, includes a commitment to satisfy applicable requirements, and includes a commitment to continual improvement.

**Mandatory document:** Information Security Policy

**Common gap:** Policies that are too generic or too detailed. The top-level policy should set direction and principles. Detailed procedures belong in supporting documents.

### 5.3 Organizational Roles, Responsibilities, and Authorities

**Requirement:** Ensure that responsibilities and authorities for roles relevant to information security are assigned and communicated.

**Common gap:** Unclear ownership of ISMS processes. Every element of your ISMS needs a named owner with defined responsibilities.

## Clause 6: Planning

### 6.1 Actions to Address Risks and Opportunities

**6.1.1** When planning for the ISMS, consider the issues from 4.1 and requirements from 4.2 to determine risks and opportunities that need to be addressed.

**6.1.2 Information Security Risk Assessment:** Define and apply a risk assessment process that establishes risk acceptance criteria, ensures repeated assessments produce consistent and comparable results, identifies risks to the confidentiality, integrity, and availability of information, identifies risk owners, and analyzes and evaluates risks.

**Mandatory documents:** Risk assessment methodology, Risk assessment results

**6.1.3 Information Security Risk Treatment:** Define and apply a risk treatment process to select appropriate treatment options, determine all controls necessary, compare with Annex A, produce a Statement of Applicability, and formulate a risk treatment plan.

**Mandatory documents:** Statement of Applicability, Risk treatment plan

**Common gap:** Risk assessments that do not follow the documented methodology, or SoAs that do not align with risk assessment results. The [Gap Analysis Tool](/tools/gap-analysis) checks these connections systematically.

### 6.2 Information Security Objectives and Planning to Achieve Them

**Requirement:** Establish information security objectives at relevant functions and levels. Objectives must be consistent with the policy, measurable, take into account applicable requirements and risk assessment results, be monitored, communicated, and updated as appropriate.

**Common gap:** Objectives that are not measurable. "Improve [security awareness](/controls/a-6-3-information-security-awareness-education-and-training)" is not measurable. "Achieve 95% completion rate on annual security awareness training" is.

### 6.3 Planning of Changes

**Requirement:** When changes to the ISMS are needed, they shall be carried out in a planned manner.

## Clause 7: Support

### 7.1 Resources

**Requirement:** Determine and provide the resources needed for the ISMS.

### 7.2 Competence

**Requirement:** Determine the necessary competence of persons doing work that affects information security performance, ensure they are competent based on education, training, or experience, and retain documented evidence of competence.

**Mandatory document:** Evidence of competence (training records)

### 7.3 Awareness

**Requirement:** Persons doing work shall be aware of the information security policy, their contribution to the ISMS effectiveness, and the implications of not conforming.

### 7.4 Communication

**Requirement:** Determine the need for internal and external communications relevant to the ISMS, including what to communicate, when, with whom, and how.

### 7.5 Documented Information

**Requirement:** The ISMS shall include documented information required by the standard and determined by the organization as necessary for ISMS effectiveness. Control the creation, updating, and management of documented information.

**Common gap:** No document control process. Documents without version numbers, review dates, or approval status fail this requirement.

## Clause 8: Operation

### 8.1 Operational Planning and Control

**Requirement:** Plan, implement, and control the processes needed to meet ISMS requirements and to implement the actions determined in Clause 6.

### 8.2 Information Security Risk Assessment

**Requirement:** Perform information security risk assessments at planned intervals or when significant changes are proposed or occur.

**Mandatory document:** Risk assessment results (updated)

### 8.3 Information Security Risk Treatment

**Requirement:** Implement the risk treatment plan and retain documented information on the results.

**Mandatory document:** Risk treatment results

## Clause 9: Performance Evaluation

### 9.1 Monitoring, Measurement, Analysis, and Evaluation

**Requirement:** Determine what needs to be monitored and measured, the methods, when monitoring shall be performed, who monitors, when results are analyzed, and who analyzes.

**Mandatory document:** Monitoring and measurement results

### 9.2 Internal Audit

**Requirement:** Conduct internal audits at planned intervals to provide information on whether the ISMS conforms to the organization's own requirements and the requirements of ISO 27001, and is effectively implemented and maintained.

**Mandatory documents:** Internal audit program, Internal audit results

Use the [Internal Audit Checklist](/tools/internal-audit) to ensure your audits cover every requirement comprehensively.

### 9.3 Management Review

**Requirement:** Top management shall review the ISMS at planned intervals, considering the status of actions from previous reviews, changes in issues and requirements, feedback on ISMS performance (including nonconformities, monitoring results, audit results, and fulfilment of objectives), feedback from interested parties, results of risk assessment and treatment plan status, and opportunities for improvement.

**Mandatory document:** Management review results

## Clause 10: Improvement

### 10.1 Continual Improvement

**Requirement:** Continually improve the suitability, adequacy, and effectiveness of the ISMS.

### 10.2 Nonconformity and Corrective Action

**Requirement:** When a nonconformity occurs, react to it, evaluate the need for action to eliminate causes, implement corrective action, review the effectiveness of corrective action, and make changes to the ISMS if necessary.

**Mandatory document:** Nonconformities and corrective actions records

## Complete Mandatory Documents Checklist

Here is the definitive list of documented information required by ISO 27001:2022:

1. ISMS Scope (4.3)
2. Information Security Policy (5.2)
3. Risk Assessment Methodology (6.1.2)
4. Risk Assessment Results (6.1.2, 8.2)
5. Statement of Applicability (6.1.3)
6. Risk Treatment Plan (6.1.3)
7. Risk Treatment Results (8.3)
8. Information Security Objectives (6.2)
9. Evidence of Competence (7.2)
10. Documented Information Control (7.5)
11. Operational Planning and Control (8.1)
12. Monitoring and Measurement Results (9.1)
13. Internal Audit Program (9.2)
14. Internal Audit Results (9.2)
15. Management Review Results (9.3)
16. Nonconformities and Corrective Actions (10.2)

The [ISO 27001 Document Pack](/documents) includes templates for every one of these documents, professionally written and ready for customization.

## Annex A Control Requirements

Annex A of ISO 27001:2022 contains 93 controls organized into four themes:

### A.5 Organizational Controls (37 controls)

These cover policies, roles, responsibilities, asset management, [access control](/controls/a-5-15-access-control), supplier relationships, and [information security in project management](/controls/a-5-8-information-security-in-project-management). Key controls include:

- A.5.1 Policies for information security
- [A.5.2](/controls/a-5-2-information-security-roles-and-responsibilities) Information security roles and responsibilities
- [A.5.9](/controls/a-5-9-inventory-of-information-and-other-associated-assets) Inventory of information and other associated assets
- A.5.10 Acceptable use of information and other associated assets
- A.5.19 Information security in supplier relationships
- A.5.23 Information security for use of cloud services
- A.5.29 Information security during disruption
- A.5.34 Privacy and protection of personal identifiable information

### A.6 People Controls (8 controls)

These address screening, terms of employment, awareness training, disciplinary processes, and responsibilities after termination:

- A.6.1 Screening
- A.6.2 Terms and conditions of employment
- A.6.3 Information security awareness, education, and training
- A.6.5 Responsibilities after termination or change of employment

### A.7 Physical Controls (14 controls)

These cover physical security perimeters, entry controls, securing offices, monitoring, and protecting against environmental threats:

- A.7.1 Physical security perimeters
- A.7.2 Physical entry
- A.7.4 Physical security monitoring
- A.7.8 Equipment siting and protection
- A.7.10 Storage media

### A.8 Technological Controls (34 controls)

These address endpoint devices, access rights, authentication, malware protection, backups, logging, network security, and secure development:

- A.8.1 User endpoint devices
- A.8.2 Privileged access rights
- A.8.3 Information access restriction
- A.8.5 Secure authentication
- A.8.7 Protection against malware
- A.8.8 Management of technical vulnerabilities
- A.8.9 Configuration management
- A.8.13 Information backup
- A.8.15 Logging
- A.8.16 Monitoring activities
- A.8.20 Network security
- A.8.25 Secure development life cycle
- A.8.28 Secure coding

Remember: not every Annex A control will apply to your organization. Your risk assessment determines which controls are necessary, and your Statement of Applicability documents the selection with justification.

## Common Gaps Organizations Miss

After reviewing hundreds of ISMS implementations, these are the requirements most frequently missed or inadequately addressed:

**Clause 4.1 and 4.2 context analysis** - Often treated as a formality rather than a genuine analysis that drives ISMS decisions.

**Clause 6.2 measurable objectives** - Organizations set vague goals instead of specific, measurable targets with timelines and responsible parties.

**Clause 7.2 competence evidence** - Training records are incomplete or missing entirely.

**Clause 9.1 monitoring and measurement** - No defined metrics or measurement methods for ISMS performance.

**Clause 9.3 management review** - Reviews that cover some but not all required inputs, or lack documented outputs and decisions.

**Clause 10.2 corrective action** - Nonconformities are fixed but root causes are not analyzed, leading to recurring issues.

**Annex A.5.23 cloud services** - With increasing cloud adoption, this new control in the 2022 version is frequently overlooked.

**Annex A.8.28 secure coding** - Another new control that organizations with development teams often miss.

The [Gap Analysis Tool](/tools/gap-analysis) systematically checks every requirement and highlights exactly where your gaps are, ensuring nothing falls through the cracks.

## Conclusion

ISO 27001 requirements are comprehensive but logical. Every clause builds on the previous one, creating a systematic approach to managing information security. The standard does not prescribe how to implement these requirements - it defines what outcomes you need to achieve.

Success comes from treating each requirement as a practical business activity, not a documentation exercise. Your risk assessment should genuinely inform your control selection. Your internal audits should genuinely check effectiveness. Your management reviews should genuinely drive improvement.

Start with the [Gap Analysis Tool](/tools/gap-analysis) to understand your current status against every requirement. Use the [ISO 27001 Document Pack](/documents) to establish your documentation foundation. And build a sustainable ISMS that delivers real security improvements, not just audit-ready paperwork.
`
      },
      fr: {
        title: "ISO 27001 Requirements - Complete Checklist of What You Need",
        metaTitle: "ISO 27001 Requirements - Complete Checklist of What You Need",
        metaDescription: "Complete ISO 27001 requirements checklist covering every clause from 4 to 10, mandatory documents, Annex A controls, and common gaps organizations miss.",
        excerpt: "ISO 27001 has specific, non-negotiable requirements across seven mandatory clauses and 93 Annex A controls. This complete checklist covers every requirement you need to meet, the documents you must produce, and the gaps that catch most organizations off guard.",
        content: ``
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-04-15",
    updatedDate: "2026-04-15",
    category: "Implementation",
    tags: ["iso 27001 requirements", "iso 27001 checklist", "annex a controls", "mandatory documents", "compliance"],
    readTime: 14,
    featured: false
  },
  {
    slug: "soc-2-vs-iso-27001-comparison-2026",
    translations: {
      en: {
        title: "SOC 2 vs ISO 27001 - Which Should You Choose in 2026 and Can You Do Both?",
        metaTitle: "SOC 2 vs ISO 27001 - Which Should You Choose in 2026 and Can You Do Both?",
        metaDescription: "SOC 2 vs ISO 27001 compared in depth. Understand key differences, which industries prefer each, cost and timeline comparison, framework overlaps, and whether pursuing both makes sense.",
        excerpt: "SOC 2 and ISO 27001 are the two most requested security frameworks, but they serve different purposes and audiences. This guide breaks down exactly how they differ, which one your organization should prioritize, and whether pursuing both is worth the effort.",
        content: `
## SOC 2 and ISO 27001 - Different Frameworks, Different Origins

SOC 2 and ISO 27001 both aim to demonstrate that an organization manages information security effectively. But they come from different worlds, serve different audiences, and take fundamentally different approaches to achieving that goal.

**ISO 27001** is an international standard published by the International Organization for Standardization (ISO) and the International Electrotechnical Commission (IEC). It specifies requirements for establishing, implementing, maintaining, and continually improving an Information Security Management System. It is recognized globally and used across every industry.

**SOC 2** is an auditing framework developed by the American Institute of Certified Public Accountants (AICPA). It evaluates an organization's controls relevant to security, availability, processing integrity, confidentiality, and privacy based on the Trust Services Criteria. It is primarily recognized in North America.

Understanding these origins matters because they shape how each framework works in practice, who accepts them, and what your organization needs to do to achieve compliance.

## Key Differences Between SOC 2 and ISO 27001

### Approach: Prescriptive vs Flexible

**ISO 27001** takes a risk-based approach. It tells you what outcomes you need to achieve (manage risks, implement controls, maintain continuous improvement) but gives you significant flexibility in how you achieve them. Annex A provides 93 reference controls, but you select only those relevant to your risks.

**SOC 2** is criteria-based. The Trust Services Criteria define specific conditions your controls must meet. While you choose which Trust Services Categories to include (beyond the mandatory Security category), the criteria within each category are fixed.

This difference means ISO 27001 requires more upfront work to design your approach but provides greater flexibility. SOC 2 provides more structure but less room to tailor the framework to your specific context.

### Scope: Management System vs Point-in-Time Controls

**ISO 27001** certifies a management system - the entire lifecycle of how you manage information security, including governance, risk assessment, control implementation, monitoring, and continuous improvement. Certification covers a three-year cycle with annual surveillance audits.

**SOC 2** evaluates specific controls at a point in time (Type I) or over a period (Type II, typically 6 to 12 months). It produces a report on the design and operating effectiveness of your controls. SOC 2 reports are valid for 12 months and must be renewed annually.

### Recognition: Global vs Regional

**ISO 27001** is recognized and accepted worldwide. Organizations in Europe, Asia, the Middle East, Africa, and Latin America all understand and request ISO 27001 certification. It is the default security framework for international business.

**SOC 2** is primarily recognized in North America, particularly in the United States and Canada. While awareness is growing globally, many European and Asian organizations do not accept SOC 2 as equivalent to ISO 27001.

If your customers are primarily in North America, SOC 2 may be sufficient. If you do business internationally or plan to expand globally, ISO 27001 is essential.

### Audit and Certification

**ISO 27001** certification is performed by accredited certification bodies (like BSI, Bureau Veritas, or TUV). The certification is binary - you either meet the requirements or you do not. The certificate is publicly verifiable and valid for three years with surveillance audits.

**SOC 2** audits are performed by licensed CPA firms. The output is a detailed report describing your controls and the auditor's opinion on their design and effectiveness. SOC 2 reports are not pass/fail - they can contain qualified opinions or exceptions. Reports are typically shared directly with customers under [NDA](/controls/a-6-6-confidentiality-or-non-disclosure-agreements).

### Output and Transparency

**ISO 27001** produces a certificate that you can display publicly. Customers can verify the certificate's validity with the certification body. However, the detailed audit findings are confidential between you and the auditor.

**SOC 2** produces a detailed report (50 to 100+ pages) that describes your system, your controls, and the auditor's test results. This report provides customers with much more detail about your actual security practices. However, it is typically shared only under NDA.

## Which Industries Prefer Which Framework?

### Industries That Prefer ISO 27001

- **Financial services** (globally, except US-focused firms)
- **Healthcare** (particularly in Europe and Asia)
- **Government contracting** (especially outside the US)
- **Manufacturing** (especially automotive and aerospace)
- **Telecommunications**
- **Any organization with European or Asian clients**
- **Defense and critical infrastructure**

### Industries That Prefer SOC 2

- **SaaS companies** (particularly selling to US enterprises)
- **Cloud service providers** (US market)
- **FinTech** (US-focused)
- **Professional services** (US market)
- **Data centers and hosting** (US market)

### Industries That Often Require Both

- **Global SaaS companies** serving clients in both North America and Europe
- **Financial technology** companies with international operations
- **Cloud infrastructure providers** competing globally
- **Managed service providers** with diverse client bases

## Cost and Timeline Comparison

### ISO 27001 Costs

| Cost Component | Typical Range |
|---|---|
| Implementation (internal effort) | $10,000 to $50,000 in staff time |
| Consulting support (optional) | $5,000 to $30,000 |
| Documentation (e.g., Document Pack) | $99 to $500 |
| Software tools | $0 to $10,000/year |
| Certification audit (Stage 1 + 2) | $5,000 to $25,000 |
| Annual surveillance audits | $3,000 to $10,000 |
| Recertification (every 3 years) | $5,000 to $20,000 |

**Total first-year cost:** $20,000 to $100,000+ depending on organization size and complexity.

**Timeline:** 3 to 12 months from kickoff to certification, depending on existing maturity and scope.

### SOC 2 Costs

| Cost Component | Typical Range |
|---|---|
| Implementation (internal effort) | $10,000 to $40,000 in staff time |
| Compliance platform (usually required) | $10,000 to $30,000/year |
| Consulting support (optional) | $5,000 to $20,000 |
| SOC 2 Type I audit | $10,000 to $30,000 |
| SOC 2 Type II audit | $15,000 to $50,000 |
| Annual renewal audit | $15,000 to $50,000 |

**Total first-year cost:** $30,000 to $120,000+ depending on scope and auditor.

**Timeline:** 2 to 6 months for Type I, plus an additional 6 to 12 month observation period for Type II.

### Cost Comparison Summary

ISO 27001 is generally less expensive on an ongoing basis because the three-year certification cycle with annual surveillance audits costs less than annual SOC 2 Type II renewals. However, the initial implementation effort for ISO 27001 can be greater because of the management system requirements.

SOC 2 can have higher ongoing costs due to annual Type II audits and the near-mandatory requirement for compliance automation platforms.

For organizations using [ISO27001KIT tools](/tools) and the [Document Pack](/documents), the implementation cost of ISO 27001 is significantly reduced because the documentation and gap analysis are already handled.

## Framework Overlaps - Where SOC 2 and ISO 27001 Align

Despite their differences, SOC 2 and ISO 27001 share significant common ground. Organizations pursuing both can leverage this overlap to reduce duplicate effort.

### Risk Management

Both frameworks require a structured approach to identifying and managing risks. ISO 27001 Clause 6.1 and SOC 2 CC3 (Risk Assessment) both demand documented risk assessment processes, identified risks with assigned owners, risk treatment or mitigation plans, and regular risk reviews.

An organization with a mature ISO 27001 risk register and treatment plan has already satisfied most of the SOC 2 risk assessment requirements.

### Access Control

ISO 27001 Annex A.8 (Technological Controls) and SOC 2 CC6 (Logical and Physical [Access Controls](/controls/a-5-15-access-control)) both cover user authentication and authorization, [privileged access](/controls/a-8-2-privileged-access-rights) management, [access reviews](/controls/a-5-18-access-rights) and deprovisioning, and [multi-factor authentication](/controls/a-8-5-secure-authentication).

Controls implemented for one framework directly support the other.

### Change Management

Both frameworks require controlled [change management](/controls/a-8-32-change-management) processes. ISO 27001 Clause 8.1 and SOC 2 CC8 (Change Management) both expect documented change procedures, approval workflows, testing before deployment, and rollback capabilities.

### Monitoring and Logging

ISO 27001 Annex A.8.15-16 and SOC 2 CC7 (System Operations) both require security event logging, monitoring and alerting, incident detection capabilities, and log retention and protection.

### Vendor Management

ISO 27001 Annex A.5.19-22 and SOC 2 CC9 (Risk Mitigation) both address third-party risk assessment, contractual security requirements, ongoing monitoring of vendor compliance, and supply chain security.

### Incident Management

Both frameworks require documented incident response processes, defined roles and responsibilities, communication procedures, and post-incident analysis.

The overlap is substantial - typically 60% to 70% of controls implemented for ISO 27001 directly satisfy SOC 2 requirements, and vice versa.

## Can You Pursue Both? Should You?

### When Pursuing Both Makes Sense

Pursuing both SOC 2 and ISO 27001 makes sense when:

- Your customer base spans both North America and international markets
- Enterprise clients in different regions require different frameworks
- You want maximum credibility in competitive markets
- Your industry expects both (e.g., global SaaS, cloud infrastructure)
- You have the resources to manage dual compliance

### The Optimal Approach - Start with ISO 27001

If you plan to pursue both frameworks, start with ISO 27001. Here is why:

**ISO 27001 is more comprehensive.** The management system approach covers governance, risk management, and continuous improvement in a way that provides a solid foundation for SOC 2. Going from ISO 27001 to SOC 2 is easier than the reverse.

**ISO 27001 controls map well to SOC 2 criteria.** An organization with a mature ISMS can typically achieve SOC 2 compliance with 30% to 40% additional effort, primarily in areas like system description, testing procedures, and AICPA-specific documentation.

**ISO 27001 certification is valid for three years.** This gives you a stable base to layer SOC 2 on top of without the pressure of annual recertification.

### The Incremental Approach

1. **Month 1 to 6:** Implement ISO 27001 using the [Gap Analysis Tool](/tools/gap-analysis) and [Document Pack](/documents)
2. **Month 6 to 9:** Achieve ISO 27001 certification
3. **Month 9 to 12:** Map existing controls to SOC 2 Trust Services Criteria
4. **Month 12 to 15:** Fill gaps and pursue SOC 2 Type I
5. **Month 15 to 24:** Complete SOC 2 Type II observation period

This approach leverages the overlap between frameworks and avoids duplicating effort.

### When to Choose Only One

**Choose ISO 27001 only if:**
- Your clients are primarily outside North America
- Your industry recognizes ISO 27001 as the gold standard
- Budget constraints limit you to one framework
- You want a globally recognized certification

**Choose SOC 2 only if:**
- All your clients are in North America
- Your industry specifically requests SOC 2 reports
- You need to demonstrate compliance quickly (SOC 2 Type I is faster)
- Your clients want detailed control descriptions (SOC 2 reports provide this)

## Common Misconceptions

### "SOC 2 is easier than ISO 27001"

Not necessarily. SOC 2 Type II requires 6 to 12 months of continuous control operation with evidence. ISO 27001 requires a management system but allows more flexibility in implementation. The difficulty depends on your starting point, not the framework itself.

### "ISO 27001 is just about documentation"

This misconception persists but is completely wrong. ISO 27001 requires a functioning management system with actual controls, real risk management, and genuine continuous improvement. Documentation supports the system - it does not replace it.

### "You cannot start ISO 27001 without a consultant"

Many organizations achieve ISO 27001 certification without consultants by using structured tools and templates. The [ISO27001KIT tools](/tools) are specifically designed to guide organizations through implementation without requiring external consulting.

### "SOC 2 Type I is sufficient for most clients"

Increasingly, enterprise clients require SOC 2 Type II reports. Type I only demonstrates that controls are designed properly at a point in time. Type II demonstrates that controls operate effectively over a period. If you are going to invest in SOC 2, plan for Type II from the start.

### "The two frameworks are redundant"

They are complementary, not redundant. ISO 27001 provides the management system and governance framework. SOC 2 provides detailed, auditor-verified evidence of control effectiveness. Together, they give stakeholders both the big picture and the granular detail.

## Making Your Decision

The right choice depends on three factors:

**1. Where are your customers?** If primarily international, choose ISO 27001. If primarily North American, consider SOC 2. If both, plan for both.

**2. What do your contracts require?** Read the security clauses in your customer contracts. They often specify exactly which framework or certification is required.

**3. What can you sustain?** Compliance is not a one-time achievement. Choose the framework(s) you can maintain with your current team and budget. It is better to do one framework well than two frameworks poorly.

For most organizations starting their compliance journey, ISO 27001 provides the strongest foundation. It is globally recognized, cost-effective to maintain, and provides the management system infrastructure that makes adding SOC 2 later straightforward.

Start your ISO 27001 journey with the free [Gap Analysis Tool](/tools/gap-analysis) to understand where you stand, and use the [Document Pack](/documents) to accelerate your documentation. The sooner you build the foundation, the sooner you can demonstrate your security commitment to any audience, anywhere in the world.
`
      },
      fr: {
        title: "SOC 2 vs ISO 27001 - Which Should You Choose in 2026 and Can You Do Both?",
        metaTitle: "SOC 2 vs ISO 27001 - Which Should You Choose in 2026 and Can You Do Both?",
        metaDescription: "SOC 2 vs ISO 27001 compared in depth. Understand key differences, which industries prefer each, cost and timeline comparison, framework overlaps, and whether pursuing both makes sense.",
        excerpt: "SOC 2 and ISO 27001 are the two most requested security frameworks, but they serve different purposes and audiences. This guide breaks down exactly how they differ, which one your organization should prioritize, and whether pursuing both is worth the effort.",
        content: ``
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-04-15",
    updatedDate: "2026-04-15",
    category: "Frameworks",
    tags: ["soc 2 vs iso 27001", "iso 27001 vs soc 2", "soc 2", "compliance frameworks", "certification"],
    readTime: 15,
    featured: false
  },
  {
    slug: "iso-27001-vs-soc-2",
    translations: {
      en: {
        title: "ISO 27001 vs SOC 2: Which Should Your Startup Choose in 2026?",
        metaTitle: "ISO 27001 vs SOC 2 - Side-by-Side Comparison for Startups (2026)",
        metaDescription: "ISO 27001 vs SOC 2 compared side by side: scope, audit requirements, timeline, and cost. A practical 2026 guide for startups choosing their first security framework.",
        excerpt: "ISO 27001 and SOC 2 are the two frameworks every B2B buyer asks about. This side-by-side guide compares scope, audit requirements, timeline, and cost so your startup can pick the right one (or both) with confidence.",
        content: `
## ISO 27001 vs SOC 2 at a Glance

ISO 27001 and SOC 2 are the two security frameworks B2B buyers ask about most often. They overlap on roughly 80 percent of controls, but they exist for different reasons: ISO 27001 is an international management system standard that proves you run an Information Security Management System (ISMS); SOC 2 is an attestation report, written by a US CPA firm, that proves your controls meet the AICPA's Trust Services Criteria over a period of time.

If a prospect is asking "are you ISO 27001 vs SOC 2 certified?", the honest answer is that one is a certification and the other is an audit report. Choosing between them is mostly about who your customers are, where they sit, and how fast you need a deliverable in their hands.

## Side-by-Side Comparison

| Dimension | ISO 27001:2022 | SOC 2 |
| --- | --- | --- |
| Type | International standard, certification by accredited body | US attestation report, signed by a CPA firm |
| Geographic preference | Europe, UK, APAC, Middle East, global enterprises | United States, North American SaaS buyers |
| Scope | Whole ISMS: people, process, technology, suppliers | Systems supporting selected Trust Services Criteria |
| Core requirement | 10 management clauses + 93 Annex A controls | 5 Trust Services Criteria (Security is mandatory) |
| Validity | 3-year certificate with annual surveillance audits | Type I (point in time) or Type II (3 to 12 month window) |
| Renewal | Annual surveillance, full re-audit every 3 years | New Type II report every 12 months |
| Public proof | Certificate + scope statement | Full report shared under [NDA](/controls/a-6-6-confidentiality-or-non-disclosure-agreements), plus a public letter |

## Scope: What Each Framework Actually Covers

ISO 27001 forces you to define and document an ISMS, run a risk assessment, produce a Statement of Applicability against all 93 Annex A controls, and prove the management system itself works (internal audit, management review, corrective actions). It is a system-of-management standard before it is a controls standard.

SOC 2 is narrower and more flexible. You pick which of the five Trust Services Criteria apply (Security is required; Availability, Confidentiality, Processing Integrity, and Privacy are optional), then your auditor designs tests around the systems in scope. There is no equivalent of an SoA, no required management review, and no Annex A.

The practical difference: ISO 27001 evidence is reusable across products and customers because the ISMS covers the whole organization. SOC 2 evidence is tied to the specific systems you put in scope, so adding a new product can require a new audit window.

## Audit Requirements

An ISO 27001 audit is performed by an accredited certification body in two stages. Stage 1 reviews your documentation and readiness. Stage 2 tests whether the ISMS is implemented and effective. You pass once and hold a certificate for three years, with annual surveillance audits in years two and three.

A SOC 2 audit is performed by a licensed US CPA firm. Type I tests that controls are designed correctly on a single date. Type II tests that controls operated effectively over a window, typically 3, 6, or 12 months. Enterprise buyers almost always ask for Type II, which means you need at least three months of operating evidence before the audit window even closes.

## Timeline

For a startup with no existing program, a realistic timeline looks like this:

- **ISO 27001 first certification**: 4 to 6 months from kick-off to certificate, assuming you use templates and one focused implementer.
- **SOC 2 Type I**: 2 to 4 months. Useful as an interim deliverable while you build evidence for Type II.
- **SOC 2 Type II**: 6 to 12 months total, because the audit window itself is 3 to 12 months on top of readiness work.

Teams that are already running an ISO 27001 ISMS can typically add SOC 2 in 2 to 3 months because the policies, risk register, [access reviews](/controls/a-5-18-access-rights), and vendor management evidence already exist.

## Cost

Costs vary by region and firm, but the order of magnitude is consistent. For a startup of 10 to 50 people, budget roughly:

- **ISO 27001**: $8k to $25k for the certification body over a 3-year cycle, plus internal time or a consultant.
- **SOC 2 Type I**: $10k to $20k for the CPA firm.
- **SOC 2 Type II**: $20k to $60k per year for the CPA firm, depending on scope and TSCs.

The hidden cost in both is internal time: writing policies, running access reviews, collecting evidence, and managing the auditor. Using a [Document Pack](/documents) and a structured [risk register](/risk-management) cuts that internal cost more than negotiating the audit fee does.

## Which Should Your Startup Choose First?

- **Selling mainly into US SaaS buyers?** Start with SOC 2 Type I, then move to Type II in the next 12 months.
- **Selling into Europe, UK, APAC, or regulated industries?** Start with ISO 27001 because procurement teams ask for it by name.
- **Selling globally or chasing enterprise deals?** Plan for both. Build ISO 27001 first because its ISMS produces most of the evidence SOC 2 needs, then layer SOC 2 on top.
- **Pre-revenue but expecting a security review in the next deal?** A SOC 2 Type I or an ISO 27001 Stage 1 readiness letter unblocks most procurement conversations while you finish the full audit.

## How to Get Started Without Wasting Months

Whichever framework you pick, the fastest path is the same: define scope, run a gap analysis, fix the gaps, then call the auditor. You can start today with the free [Gap Analysis Tool](/tools/gap-analysis) to see exactly where you stand against ISO 27001:2022, and use the [Risk Assessment Tool](/tools/risk-assessment) to produce a defensible risk register that doubles as SOC 2 evidence.

If you already know you want ISO 27001, the [Document Pack](/documents) gives you every mandatory policy and the [SoA Generator](/soa-generator) produces an audit-ready Statement of Applicability in minutes.

The right framework is the one your buyers ask for. The wrong move is waiting another quarter to start.
`
      },
      fr: {
        title: "ISO 27001 vs SOC 2: Which Should Your Startup Choose in 2026?",
        metaTitle: "ISO 27001 vs SOC 2 - Side-by-Side Comparison for Startups (2026)",
        metaDescription: "ISO 27001 vs SOC 2 compared side by side: scope, audit requirements, timeline, and cost. A practical 2026 guide for startups choosing their first security framework.",
        excerpt: "ISO 27001 and SOC 2 are the two frameworks every B2B buyer asks about. This side-by-side guide compares scope, audit requirements, timeline, and cost so your startup can pick the right one (or both) with confidence.",
        content: ``
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-06-11",
    updatedDate: "2026-06-11",
    category: "Frameworks",
    tags: ["iso 27001 vs soc 2", "soc 2 vs iso 27001", "iso 27001 vs soc2", "compliance frameworks", "startups"],
    readTime: 9,
    featured: false
  },
  {
    slug: "iso-27001-backup-requirements",
    translations: {
      en: {
        title: "ISO 27001 Backup Requirements (Annex A 8.13) - Complete Guide & Checklist",
        metaTitle: "ISO 27001 Backup Requirements - Annex A 8.13 Guide & Audit Checklist",
        metaDescription: "Complete guide to ISO 27001 backup requirements under Annex A 8.13. What auditors expect, backup policy checklist, retention, testing, and free template.",
        excerpt: "ISO 27001:2022 control Annex A 8.13 requires organizations to back up information, software, and systems and test restoration regularly. This guide breaks down exactly what the standard expects, what auditors look for, and gives you a ready-to-use backup policy checklist.",
        content: `
## What Annex A 8.13 Actually Requires

Annex A 8.13 of ISO 27001:2022 is titled **[Information Backup](/controls/a-8-13-information-backup)**. The control objective is short but demanding: backup copies of information, software, and systems shall be maintained and regularly tested in accordance with the agreed topic-specific policy on backup.

In practice, that single sentence creates four obligations every certified organization must meet:

1. A documented backup policy that covers scope, frequency, retention, and responsibilities.
2. Backups that actually run on the schedule the policy defines, for every in-scope system.
3. Backups stored in a way that survives the failure they are meant to recover from (off-site, immutable, or air-gapped).
4. Regular restoration tests that prove the backups are usable, with evidence kept for the auditor.

If you can show all four to an ISO 27001 auditor with documentation and recent evidence, you pass this control. If any one is missing, expect a non-conformity.

## What Auditors Actually Look For

Annex A 8.13 is one of the most consistently tested controls in Stage 2 audits because it is concrete and easy to evidence. Auditors usually ask for the following, in this order:

- **The backup policy itself**, signed and version-controlled, identifying the data and systems in scope.
- **A backup schedule or runbook** showing what runs, when, where it is stored, and who owns it.
- **Backup job logs** for the past 30 to 90 days showing successful execution and failures with remediation.
- **Evidence of an off-site or logically separated copy** - typically a screenshot of cloud replication, immutable storage settings, or a tape rotation log.
- **At least one restoration test report** from the past 12 months, with the test date, scope, outcome, and any corrective actions.
- **[Encryption](/controls/a-8-24-use-of-cryptography) configuration** for backup data at rest and in transit, especially when backups leave the production environment.
- **Retention proof** that aligns with the legal, contractual, and business retention periods declared in the policy.

If your last restoration test is older than a year, or your policy says "monthly" but logs show gaps, the auditor will write it up. These are the two most common findings on this control.

## ISO 27001 Backup Policy Checklist

Use this checklist as the table of contents for your backup policy. Every item should appear in the document and be traceable to evidence.

- [ ] Purpose and scope (which systems, data, and locations are covered)
- [ ] Roles and responsibilities (backup owner, restore approver, reviewer)
- [ ] Classification-driven backup frequency (e.g. critical = daily, important = weekly)
- [ ] Retention periods aligned to legal and contractual requirements
- [ ] Backup storage location, including off-site or cloud replication
- [ ] Encryption requirements for backups at rest and in transit
- [ ] [Access controls](/controls/a-5-15-access-control) and least-privilege for backup systems
- [ ] Restoration testing schedule (at minimum annual, ideally quarterly for critical systems)
- [ ] Documented restoration test procedure and evidence template
- [ ] [Incident response](/controls/a-5-26-response-to-information-security-incidents) link - how backups are used during ransomware or major incidents
- [ ] Monitoring and alerting on backup job failures
- [ ] Review and approval cycle (annual minimum)
- [ ] Reference to related controls: [A.5.30](/controls/a-5-30-ict-readiness-for-business-continuity) (ICT readiness), [A.8.14](/controls/a-8-14-redundancy-of-information-processing-facilities) (redundancy), A.8.24 (cryptography)

You can download an editable backup policy and 40+ other ISO 27001 templates in the [ISO 27001 Document Pack](/documents).

## Backup Frequency by Data Classification

ISO 27001 does not prescribe specific frequencies. It expects you to make a risk-based decision and document it. A defensible starting point most auditors accept:

- **Confidential / Critical systems**: incremental backups every 4 to 24 hours, full backup weekly, retained 12 months minimum.
- **Internal / Important systems**: daily incremental, weekly full, retained 6 to 12 months.
- **Public / Low-impact systems**: weekly full, retained 90 days.

The frequency you choose must line up with your Recovery Point Objective (RPO) for each system. If your business continuity plan declares an RPO of 4 hours for a system, backing it up nightly is automatically non-conformant - you have committed in writing to losing no more than 4 hours of data. Use the free [RTO/RPO Helper](/tools/rto-rpo-helper) to set defensible objectives before you write the policy.

## Off-Site, Immutable, or Air-Gapped: What Counts?

Annex A 8.13 expects backups to survive the failure they protect against. In 2026 that means surviving ransomware, not just hardware failure. Three patterns satisfy modern auditors:

- **Off-site replication** to a different cloud region or physical site, with separate credentials.
- **Immutable storage** (object lock, WORM, or vendor immutability) preventing deletion or modification for a defined retention window.
- **Air-gapped copies** - tape, removable media, or storage logically disconnected from the production network.

At least one of the three is the bare minimum. Mature organizations use two. If your backups live in the same cloud account, same region, and same identity boundary as your production data, an auditor will flag it - and a ransomware attacker will encrypt both.

## Restoration Testing: The Item Most Organizations Fail

Running backups is easy. Proving they restore is what trips most teams up. A defensible restoration test includes:

1. A documented test plan identifying the system, dataset, and success criteria.
2. Restoration into a non-production environment (never overwrite live data).
3. Verification that the restored data is complete and usable - not just "files copied".
4. A signed test report with date, scope, outcome, anomalies, and corrective actions.
5. Retention of the report for at least one audit cycle.

At minimum, test once a year for every critical system. Quarterly is the standard for systems supporting customer-facing services. Schedule the test in your calendar the day you finish reading this article - missing this evidence is the single most common Annex A 8.13 finding.

## How Annex A 8.13 Connects to Other Controls

Backup is not a standalone control. Auditors will trace it through:

- **A.5.29 Information security during disruption** and **A.5.30 ICT readiness for business continuity** - your backup policy must support your BCP.
- **A.8.14 Redundancy of information processing facilities** - backups support recovery; redundancy supports availability.
- **A.8.24 Use of cryptography** - encryption keys for backups must be managed.
- **A.5.34 Privacy and protection of PII** - retention and deletion of PII inside backups.
- **A.5.24 to A.5.27 Incident management** - backups are part of ransomware response.

Document these links in your Statement of Applicability so the auditor sees a coherent control environment, not isolated policies. The [SoA Generator](/tools/soa-generator) builds these references automatically.

## Common Mistakes That Cause Non-Conformities

- Backup policy exists but lists systems that are no longer in scope, or omits new ones.
- No restoration test in the last 12 months.
- Backup logs show repeated failures with no ticket or remediation.
- Backups stored only in the same cloud account as production.
- Retention period in the policy does not match what is actually configured in the tool.
- No encryption on backups leaving the production boundary.
- SaaS data (Microsoft 365, Google Workspace, GitHub) excluded from scope on the assumption that the vendor handles it - vendors handle availability, not your retention obligations.

Fix these before your Stage 2 audit and Annex A 8.13 becomes one of your easiest controls to evidence.

## Next Steps

1. Compare your current backup setup against the checklist above and log any gaps.
2. Run a restoration test on your most critical system this quarter and write the report.
3. Download the editable backup policy template inside the [ISO 27001 Document Pack](/documents).
4. Map your backup decisions to your RTO/RPO targets using the free [RTO/RPO Helper](/tools/rto-rpo-helper).
5. Add backup evidence to your audit room with the [Internal Audit Checklist](/tools/internal-audit-checklist).

Get Annex A 8.13 right and you remove one of the most common non-conformities ISO 27001 auditors raise - and you make ransomware recovery a planned procedure instead of a crisis.
`
      },
      fr: {
        title: "ISO 27001 Backup Requirements (Annex A 8.13) - Complete Guide & Checklist",
        metaTitle: "ISO 27001 Backup Requirements - Annex A 8.13 Guide & Audit Checklist",
        metaDescription: "Complete guide to ISO 27001 backup requirements under Annex A 8.13. What auditors expect, backup policy checklist, retention, testing, and free template.",
        excerpt: "ISO 27001:2022 control Annex A 8.13 requires organizations to back up information, software, and systems and test restoration regularly. This guide breaks down exactly what the standard expects, what auditors look for, and gives you a ready-to-use backup policy checklist.",
        content: ``
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-06-16",
    updatedDate: "2026-06-16",
    category: "Controls",
    tags: ["iso 27001 backup requirements", "annex a 8.13", "backup policy", "iso 27001 controls", "information backup"],
    readTime: 9,
    featured: false
  },
  {
    slug: "what-is-iso-27001-compliance",
    translations: {
      en: {
        title: "What Is ISO 27001 Compliance? A Plain-English Guide for 2026",
        metaTitle: "What Is ISO 27001 Compliance? Plain-English Guide (2026)",
        metaDescription: "ISO 27001 compliance explained in plain English: what it means, who needs it, how it differs from certification, and the steps to get there. Free templates included.",
        excerpt: "ISO 27001 compliance is one of the most asked-about topics in information security, yet most explanations are either too technical or too vague. This guide breaks it down in plain English: what compliance actually means, how it differs from certification, who needs it, and what it takes to get there.",
        content: `
## What ISO 27001 Compliance Actually Means

ISO 27001 compliance means your organization operates an Information Security Management System (ISMS) that aligns with the requirements of the international standard **ISO/IEC 27001:2022**. In practical terms, you have documented how you protect information, you have controls in place to enforce those decisions, and you can produce evidence that the system is running.

Compliance is **not** the same as certification. You can be compliant - meeting every clause of the standard - without ever paying an auditor to certify you. Certification is the third-party stamp that confirms compliance to outsiders (customers, regulators, partners). Most organizations pursue compliance first and decide on certification once they know a real customer is asking for it.

The standard has two parts that matter for compliance:

- **Clauses 4 to 10** - the management-system requirements. These are mandatory for compliance.
- **Annex A** - 93 controls grouped into 4 themes (Organizational, People, Physical, Technological). You apply the ones that fit your risks and justify any exclusions in your Statement of Applicability.

If you can show both - a working management system plus a defensible Annex A selection - you are ISO 27001 compliant.

## Compliance vs Certification: The Difference That Matters

| | Compliance | Certification |
|---|---|---|
| What it is | You meet the standard | A third party confirms it |
| Who decides | You and your evidence | An accredited certification body |
| Cost | Internal time + tools (typically $0 - $5,000) | $10,000 - $50,000+ per cycle |
| Timeline | 3 to 6 months for a small org | Adds 2 to 4 months on top |
| Renewal | Continuous | 3-year cycle with annual surveillance |
| Customer value | Self-attestation | External proof |

Compliance is the foundation. Certification is the marketing asset. If your customers are not asking for a certificate yet, build the compliant ISMS first and skip the audit cost until the revenue justifies it.

## Who Actually Needs ISO 27001 Compliance?

Three groups make up nearly all real demand:

- **SaaS and tech companies selling to mid-market or enterprise.** Their security questionnaires either require ISO 27001 outright or score it highly. Without it, deals stall.
- **Outsourced service providers** (BPO, IT managed services, dev shops, payroll, hosting). Their clients pass regulatory obligations down the chain and need proof of a security program.
- **Regulated industries adjacent to security**: healthcare, finance, telecoms, defense suppliers. Even when the primary regulation is different (HIPAA, PCI, GDPR), ISO 27001 is the framework most regulators recognize as a baseline.

If none of those describe you, ISO 27001 compliance is probably premature. A lighter framework like CIS Controls or NIST CSF gives you the security improvements without the documentation overhead.

## What's Actually Required for Compliance

Strip the standard down to its real obligations and you get this short list:

1. **Defined scope.** Write down which parts of your business, systems, and locations are inside the ISMS. Use the [free ISMS Scope Builder](/tools/isms-scope-builder) to draft a defensible boundary.
2. **[Information security policy](/controls/a-5-1-policies-for-information-security).** A short top-level policy signed by leadership.
3. **Risk assessment and treatment.** Identify risks, score them, decide what to do with each one. The [Risk Assessment Tool](/tools/risk-assessment) walks you through this.
4. **Statement of Applicability (SoA).** A list of all 93 Annex A controls with applicability decisions and justifications. Generate yours with the [SoA Generator](/tools/soa-generator).
5. **Documented procedures** for the controls you applied ([access control](/controls/a-5-15-access-control), [incident response](/controls/a-5-26-response-to-information-security-incidents), [backup](/controls/a-8-13-information-backup), [supplier security](/controls/a-5-19-information-security-in-supplier-relationships), etc.). Templates are in the [Document Pack](/documents).
6. **Evidence the system runs**: internal audit, management review, corrective actions, training records, monitoring logs.
7. **Continual improvement.** The system gets reviewed and improved at planned intervals.

Hit those seven and you are compliant. Everything else (specific tools, vendors, automations) is implementation detail.

## How Long Does Compliance Take?

- **Small organization (under 50 people, low complexity)**: 2 to 4 months of focused effort.
- **Mid-size (50 to 250 people, multiple products)**: 4 to 8 months.
- **Enterprise**: 6 to 18 months, usually parallel to other compliance work.

The single biggest accelerator is using pre-built templates instead of drafting from scratch. Drafting 40+ policies in-house typically adds 6 to 10 weeks of work that an editable template pack removes in a day.

## What Auditors Actually Check

Even if you only target compliance and not certification, auditors (or customers running their own security review) look for the same things:

- Is the scope clear and current?
- Is the risk register live and updated within the last 12 months?
- Does the SoA match what you actually do?
- Are policies signed, dated, and reviewed annually?
- Do incidents get logged and reviewed?
- Are [access reviews](/controls/a-5-18-access-rights) happening on the cadence the policy promises?
- Is there evidence of an internal audit and a management review in the last year?

If you can answer yes with documents to back it up, you pass.

## Common Misconceptions About ISO 27001 Compliance

- **"You need a consultant."** No. Most SMBs achieve compliance using structured tools and a templated documentation pack. The standard does not require external help.
- **"It's just paperwork."** Wrong. The documentation supports a real operational system. Auditors test whether the controls actually run.
- **"You have to apply all 93 Annex A controls."** Also wrong. You apply the controls that fit your risks. The SoA documents which ones you excluded and why.
- **"Compliance is permanent."** It is continuous. Skip your annual review or stop updating the risk register and you fall out of compliance.

## The Fastest Path to Compliance

1. Run a gap analysis to see where you stand - the [free Gap Analysis Tool](/tools/gap-analysis) takes 15 minutes.
2. Define and document your ISMS scope.
3. Build the risk register and pick your Annex A controls in the SoA.
4. Implement the missing controls and policies using template starting points.
5. Run an internal audit and management review.
6. Decide whether to pursue certification.

Most organizations underestimate steps 1 to 3 and overestimate steps 4 to 6. The hard work is the structured thinking at the start - once scope, risks, and SoA are right, the rest is execution.

## Next Steps

If you want to move on ISO 27001 compliance now:

- Take the free [Readiness Check](/readiness-check) to see your current state in 5 minutes.
- Download the [ISO 27001 Document Pack](/documents) for 47+ editable policies.
- Use the [Implementation Roadmap](/tools/implementation-roadmap) to schedule the work over the next 90 days.

ISO 27001 compliance is achievable for any organization willing to commit a few months of structured effort. The hardest part is starting - not the standard itself.
`
      },
      fr: {
        title: "What Is ISO 27001 Compliance? A Plain-English Guide for 2026",
        metaTitle: "What Is ISO 27001 Compliance? Plain-English Guide (2026)",
        metaDescription: "ISO 27001 compliance explained in plain English: what it means, who needs it, how it differs from certification, and the steps to get there. Free templates included.",
        excerpt: "ISO 27001 compliance explained in plain English.",
        content: ``
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-06-16",
    updatedDate: "2026-06-16",
    category: "Fundamentals",
    tags: ["what is iso 27001 compliance", "iso 27001 compliance", "iso 27001 basics", "compliance vs certification", "iso 27001 explained"],
    readTime: 8,
    featured: false
  },
  {
    slug: "iso-27001-vs-cmmc",
    translations: {
      en: {
        title: "ISO 27001 vs CMMC: Differences, Overlap, and Which One You Need (2026)",
        metaTitle: "ISO 27001 vs CMMC - Differences, Overlap & Which to Pick (2026)",
        metaDescription: "ISO 27001 vs CMMC compared: scope, controls, cost, audit process, and overlap. A plain-English guide to picking the right framework for your business.",
        excerpt: "ISO 27001 and CMMC are often discussed together, especially by US defense contractors who also sell commercially. This guide breaks down the differences in scope, controls, cost, audit process, and overlap, and tells you which framework you actually need.",
        content: `
## The Short Answer

- **ISO 27001** is a global, voluntary information security management standard. Any organization can adopt it. Customers ask for it.
- **CMMC** (Cybersecurity Maturity Model Certification) is a US Department of Defense (DoD) requirement. If you sell to the DoD or sit in its supply chain, you must comply. Nobody else is required to.

If you only sell commercially, you want **ISO 27001**. If you have DoD contracts, you need **CMMC** - and likely ISO 27001 too, because the controls overlap heavily and ISO opens commercial doors that CMMC does not.

## What Each Framework Actually Is

**ISO 27001:2022** is the international standard for an Information Security Management System (ISMS). It defines management-system requirements (Clauses 4 to 10) plus 93 Annex A controls grouped into 4 themes. It is risk-based: you assess your risks, then pick the controls that treat them.

**CMMC 2.0** is a tiered certification model published by the US DoD. It is built on **NIST SP 800-171** (and 800-172 at higher levels) and applies to any company in the Defense Industrial Base (DIB) that handles Federal Contract Information (FCI) or Controlled Unclassified Information (CUI). The three levels are:

- **Level 1 (Foundational)** - 17 basic safeguarding practices, annual self-assessment. Required for any contractor that handles FCI.
- **Level 2 (Advanced)** - 110 practices from NIST 800-171, third-party assessment every 3 years. Required for contractors handling CUI.
- **Level 3 (Expert)** - Level 2 plus a subset of NIST 800-172, government-led assessment. Required for the most sensitive CUI programs.

## Side-by-Side Comparison

| | ISO 27001 | CMMC 2.0 |
|---|---|---|
| Type | Voluntary international standard | Mandatory US DoD requirement |
| Scope | Any industry, any country | US defense supply chain |
| Approach | Risk-based ISMS | Prescriptive control checklist |
| Controls | 93 Annex A controls | 17 (L1), 110 (L2), 110+ (L3) |
| Source | ISO/IEC | NIST SP 800-171 / 800-172 |
| Assessment | External certification body | Self-assessment (L1), C3PAO (L2), DoD (L3) |
| Cycle | 3-year cert, annual surveillance | 3-year cycle |
| Typical cost | $10k - $50k | $20k - $250k+ depending on level |
| Recognized by | Global customers, regulators | US DoD and primes |
| Marketing value | High globally | High inside DIB only |

## How Much They Overlap

If you already run ISO 27001, you have a significant head start on CMMC Level 2. Studies and consultant mappings consistently show **60 to 80 percent control overlap** between ISO 27001 Annex A and NIST 800-171. Areas of strong overlap:

- [Access control](/controls/a-5-15-access-control) and [identity management](/controls/a-5-16-identity-management)
- Awareness and training
- Audit and accountability / logging
- [Configuration management](/controls/a-8-9-configuration-management)
- [Incident response](/controls/a-5-26-response-to-information-security-incidents)
- Risk assessment
- System and communications protection
- Media protection

Areas where CMMC asks for more than ISO:

- Specific marking and handling of **CUI**
- US-person and citizenship requirements for some roles
- Detailed evidence formats expected by C3PAO assessors
- FIPS-validated cryptography (not just "appropriate" crypto)

Areas where ISO asks for more than CMMC:

- A formal **management system** with leadership, objectives, internal audit, and management review
- A documented **Statement of Applicability** justifying every control decision
- Continual improvement loop driven by risk

## Which One Do You Need?

- **Pure commercial SaaS or B2B services**: ISO 27001. CMMC adds cost with no commercial buyer.
- **DoD prime or subcontractor handling FCI only**: CMMC Level 1 (self-assessment). ISO 27001 is nice-to-have.
- **DoD subcontractor handling CUI**: CMMC Level 2 is mandatory. Add ISO 27001 if you also sell commercially - the marginal cost is small once 800-171 is in place.
- **Defense plus international expansion**: Both. ISO 27001 gets you into European and Asian enterprise pipelines; CMMC keeps your DoD contracts.
- **Healthcare, fintech, regulated commercial sectors**: ISO 27001 plus the sector framework (HIPAA, PCI, SOC 2). CMMC is irrelevant.

## The Smart Sequencing

If you need both, **start with ISO 27001**. Reasons:

1. ISO's management-system framing forces scope, risk, and leadership decisions that CMMC assumes you already made.
2. The Annex A control set covers most of NIST 800-171 already.
3. You can sell to commercial customers while the CMMC track plays out.
4. Once ISO is in place, layering CMMC means closing the **delta**: CUI handling, FIPS crypto, specific evidence formats. That delta project is shorter and cheaper than tackling both from scratch.

The reverse order (CMMC first, then ISO) works but tends to leave gaps in formal management-system documentation that ISO auditors then flag.

## Cost Reality

- **ISO 27001** total first-year cost for an SMB: usually **$10k - $30k** including templates, internal time, and certification. Self-attested compliance can be done for **under $5k** using structured tools.
- **CMMC Level 2** total first-year cost: typically **$50k - $250k** depending on existing maturity, scope of CUI, and assessor fees. The C3PAO assessment alone runs $20k - $100k.

The cheapest ISO 27001 starting point is a [free Gap Analysis](/tools/gap-analysis) plus a templated [Document Pack](/documents). That gets a small organization to a defensible baseline in weeks rather than months.

## Common Misconceptions

- **"ISO 27001 satisfies CMMC."** No. The overlap is high but CMMC has hard requirements (CUI marking, FIPS crypto, specific evidence formats) that ISO does not enforce.
- **"CMMC replaces ISO 27001."** No. Outside the US DoD supply chain, CMMC has no market value. ISO is the global currency.
- **"You can self-certify CMMC Level 2."** No - Level 2 requires a third-party (C3PAO) assessment. Only Level 1 is self-assessed.
- **"ISO 27001 is too generic to satisfy regulators."** Wrong. ISO 27001 is the most widely accepted baseline globally and is explicitly referenced by GDPR, UK NIS, and many sectoral regulators.

## What to Do Next

- Selling commercially? Take the [free Readiness Check](/readiness-check) and start the ISO 27001 path.
- DoD contracts coming? Get the [ISO 27001 Document Pack](/documents) first - 60 to 80 percent of the work crosses over to CMMC Level 2.
- Already certified ISO 27001 and now facing CMMC? Map your existing controls to NIST 800-171 before hiring a CMMC consultant. The gap is usually smaller than they will tell you.

ISO 27001 and CMMC are different tools for different markets. Pick based on **who pays you**, not on which framework sounds more impressive.
`
      },
      fr: {
        title: "ISO 27001 vs CMMC: Differences, Overlap, and Which One You Need (2026)",
        metaTitle: "ISO 27001 vs CMMC - Differences, Overlap & Which to Pick (2026)",
        metaDescription: "ISO 27001 vs CMMC compared: scope, controls, cost, audit process, and overlap. A plain-English guide to picking the right framework for your business.",
        excerpt: "ISO 27001 vs CMMC compared in plain English.",
        content: ``
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-06-16",
    updatedDate: "2026-06-16",
    category: "Comparisons",
    tags: ["iso 27001 vs cmmc", "cmmc vs iso 27001", "cmmc 2.0", "nist 800-171", "compliance frameworks"],
    readTime: 9,
    featured: false
  },
  {
    slug: "maintaining-iso-27001-after-certification",
    translations: {
      en: {
        title: "Maintaining ISO 27001 After Certification: The 12-Month Operating Cycle",
        metaTitle: "Maintaining ISO 27001 After Certification - 12-Month Cycle (2026)",
        metaDescription: "How to maintain ISO 27001 after certification: surveillance audits, risk reviews, internal audits, management reviews, and the annual cadence auditors expect.",
        excerpt: "Most teams treat ISO 27001 as a project that ends at certification. The standard treats it as a permanent operating cycle. This guide breaks down what you actually need to do month by month to stay certified, pass surveillance audits, and avoid the nonconformities that catch most organizations in year two.",
        content: `
## Why Maintenance Is Where Most ISMS Programs Fail

Getting certified is the easy part. Staying certified is where most ISMS programs quietly decay. Certification bodies run a **surveillance audit every year** and a full **recertification audit every three years**. They are specifically looking for whether the system you described in year one is still running in year two.

The pattern is predictable: the implementation team disbands after certification, the risk register stops being updated, internal audits get skipped, and the next surveillance audit surfaces five or six minor nonconformities at once. Fixing those under audit pressure is far more expensive than running the cycle properly.

## The 12-Month Operating Cycle

ISO 27001 expects a continuous PDCA (Plan-Do-Check-Act) loop. In practice, that translates into a 12-month calendar with a handful of fixed events:

| Cadence | Activity | Clause |
|---|---|---|
| Monthly | Risk register review, incident log review, vulnerability scan triage | 6.1, 8.2, [A.5.24](/controls/a-5-24-information-security-incident-management-planning-and-preparation), [A.8.8](/controls/a-8-8-management-of-technical-vulnerabilities) |
| Quarterly | [Access reviews](/controls/a-5-18-access-rights), supplier reviews, KPI/metric reporting | A.5.18, [A.5.19](/controls/a-5-19-information-security-in-supplier-relationships), 9.1 |
| Bi-annually | Awareness training refresh, [business continuity](/controls/a-5-30-ict-readiness-for-business-continuity) test | [A.6.3](/controls/a-6-3-information-security-awareness-education-and-training), A.5.30 |
| Annually | Full risk reassessment, internal audit, management review, policy review | 6.1, 9.2, 9.3, 7.5 |
| Continuous | Corrective actions, document control, evidence collection | 10.1, 7.5 |

If you only remember one thing: **the annual block (risk reassessment + internal audit + management review) must happen before each surveillance audit**, with documented outputs. That trio is the first thing every auditor opens.

## What Surveillance Auditors Actually Check

Year 2 and Year 3 surveillance audits are lighter than the original certification audit, but they zero in on the same evidence each time:

1. **Risk register**: updated within the last 12 months, with treatment decisions and owners.
2. **Statement of Applicability**: matches current controls, justifications still valid.
3. **Internal audit report**: covered the ISMS, identified findings, has corrective action progress.
4. **Management review minutes**: shows leadership reviewed performance, risks, incidents, and resourcing.
5. **Incident log**: incidents logged, classified, post-mortems for the significant ones.
6. **Corrective actions**: open items from the last audit closed with evidence.
7. **Training records**: awareness training completed by current staff (not just last year's hires).
8. **Access reviews**: documented evidence of user access being reviewed and pruned.
9. **Supplier evaluations**: critical suppliers reassessed.
10. **Document control**: policies reviewed and reapproved within the last year.

Miss any of these and you get a finding. Miss two or three and you get a major nonconformity, which suspends your certificate until cleared.

## The 90-Day Pre-Surveillance Checklist

The cleanest way to pass surveillance is to start preparing 90 days out:

- **T-90 days**: full internal audit covering all in-scope controls.
- **T-75 days**: open corrective actions for every internal-audit finding.
- **T-60 days**: refresh the risk assessment, update the risk register, regenerate the SoA where controls changed.
- **T-45 days**: management review meeting. Document inputs (audit results, risks, incidents, KPIs) and outputs (decisions, resource allocations).
- **T-30 days**: close out corrective actions, refresh training records, run access reviews.
- **T-7 days**: rehearse the audit. Walk the lead implementer through the evidence folder structure so they can produce documents instantly.

Most surveillance failures trace back to skipping the T-90 internal audit and trying to compress everything into the last two weeks.

## Where Tools Actually Help

The maintenance workload is where spreadsheets break down. A few specific tools cut the time dramatically:

- A **risk register that supports continuous updates** rather than annual rewrites. The [Risk Copilot](/risk-management) is built around the monthly review cadence and tracks overdue and due-soon items automatically.
- An **internal audit tool** with the Annex A control set pre-loaded so the auditor doesn't rebuild the checklist each year. See the [Internal Audit Checklist](/tools/internal-audit).
- A **document control system** that flags policies past their review date. Even a simple review-date column in a tracked register works.
- An **incident log** that captures severity, owner, root cause, and closure - not just a Slack channel scroll.

The goal is not more software. It is removing the per-cycle setup tax so the team can focus on actual review work.

## The Three-Year Recertification Audit

Every three years the certification body re-runs a full audit instead of the lighter surveillance check. The scope is broader and the document review is deeper. Two things matter most:

1. **Evidence of trend, not just snapshot**: auditors want to see three years of risk register updates, three years of management review minutes, three years of incident logs. If the records jump from year one to recertification with nothing in between, that is itself a finding.
2. **Defensible change management**: scope changes, new products, new locations, acquisitions, and major tech migrations should all show up in the risk register and SoA with rationale.

Treat recertification as the cumulative test the surveillance audits hint at. If you ran the 12-month cycle properly each year, recertification is a paperwork exercise rather than a project.

## Common Maintenance Mistakes

- **The annual risk reassessment that just re-saves last year's file.** Auditors check for actual changes - new threats, retired systems, new suppliers.
- **Internal audit run by the same person who wrote the policy.** Independence is required; either rotate auditors or use an external one.
- **Management review treated as a calendar formality.** It must have documented inputs and outputs, with leadership decisions visible.
- **Closing corrective actions without evidence.** A status of "done" with no artifact will be reopened during surveillance.
- **Stale SoA.** New SaaS tools, new offices, new product lines mean control applicability changes - update the SoA when they do, not at audit time.

## Cost of Maintenance

For a typical SMB, ongoing ISO 27001 maintenance costs roughly:

- **Internal time**: 0.25 to 0.5 FTE spread across the year.
- **Surveillance audit fee**: $3,000 to $8,000 per year.
- **Tooling**: $0 to $5,000 per year depending on what's already in the stack.
- **Recertification (every 3rd year)**: $8,000 to $20,000.

Skipping maintenance and rebuilding the system before each surveillance audit usually costs 3 to 5 times more in consultant fees plus the risk of a suspended certificate.

## The Continuous-Improvement Habit

The standard's Clause 10.1 is short but it's the one most often missed: **continual improvement**. Auditors want to see that the ISMS gets better over time. Concretely:

- KPIs or metrics that track ISMS performance (incident rate, training completion, mean time to remediate findings).
- Trend data showing improvement or, where things got worse, a documented response.
- New controls or process changes driven by lessons learned.

An ISMS that looks identical year over year is a warning sign to auditors, even if every individual control is in place.

## Putting It Together

The teams that maintain ISO 27001 cheaply share three habits:

1. A **fixed annual calendar** with the audit, risk reassessment, and management review locked in.
2. **One person owns the ISMS** day to day, even if they have other responsibilities.
3. **Tools chosen for ongoing operation**, not for the first certification push.

If you want to operationalize this:

- Use the [Risk Copilot](/risk-management) for the monthly risk and review cadence.
- Use the [Internal Audit Checklist](/tools/internal-audit) for the T-90 audit.
- Use the [Document Pack](/documents) for annual policy refreshes that don't require rewriting from scratch.

Certification gets you the badge. The 12-month cycle keeps it.
`
      },
      fr: {
        title: "Maintaining ISO 27001 After Certification: The 12-Month Operating Cycle",
        metaTitle: "Maintaining ISO 27001 After Certification - 12-Month Cycle (2026)",
        metaDescription: "How to maintain ISO 27001 after certification: surveillance audits, risk reviews, internal audits, management reviews, and the annual cadence auditors expect.",
        excerpt: "How to maintain ISO 27001 after certification.",
        content: ``
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-06-16",
    updatedDate: "2026-06-16",
    category: "Operations",
    tags: ["maintaining iso 27001", "iso 27001 surveillance audit", "iso 27001 recertification", "isms operations", "iso 27001 after certification"],
    readTime: 10,
    featured: false
  },
  {
    slug: "iso-27001-vs-nist-csf",
    translations: {
      en: {
        title: "ISO 27001 vs NIST CSF: Which Framework Should Your Startup Pick? (2026)",
        metaTitle: "ISO 27001 vs NIST CSF - Which to Pick for Your Startup (2026)",
        metaDescription: "ISO 27001 vs NIST CSF compared: scope, controls, certification, cost, and global vs US fit. A plain-English guide for startups choosing a security framework.",
        excerpt: "ISO 27001 and the NIST Cybersecurity Framework (CSF) often get pitched as alternatives. They are not. One is a certifiable management-system standard, the other is a voluntary risk-based playbook. This guide explains the real differences, the overlap, and which one your startup should actually adopt based on where your customers are.",
        content: `
## The Short Answer

- **ISO 27001** is an international, **certifiable** standard for an Information Security Management System (ISMS). Customers and procurement teams ask for the certificate.
- **NIST CSF 2.0** is a **voluntary US framework** that helps organizations describe and improve their cybersecurity posture. There is no certificate.

If you sell globally or to enterprise buyers - especially in Europe, the UK, or APAC - you want **ISO 27001**. If you are US-focused, early-stage, and just need a structured way to talk about security risk internally (or to a US federal customer), **NIST CSF** is a great starting point. Most growing startups end up using **CSF to organize the work and ISO 27001 to prove it**.

## What Each Framework Actually Is

**ISO 27001:2022** is the international standard for an ISMS. It defines management-system requirements (Clauses 4 to 10) plus 93 Annex A controls grouped into 4 themes (Organizational, People, Physical, Technological). It is risk-based: you assess your risks, justify the controls in your Statement of Applicability, and an accredited certification body audits the system every year.

**NIST CSF 2.0** is a voluntary framework published by the US National Institute of Standards and Technology. It is organized around **six functions**: Govern, Identify, Protect, Detect, Respond, and Recover. Each function contains categories and subcategories that map to specific outcomes - it tells you *what* good cybersecurity looks like without prescribing *how* to get there. There is no audit, no certificate, no Statement of Applicability.

## Side-by-Side Comparison

| | ISO 27001 | NIST CSF 2.0 |
|---|---|---|
| Type | Certifiable international standard | Voluntary US framework |
| Origin | ISO/IEC (global) | NIST (US Department of Commerce) |
| Structure | Management system + 93 controls | 6 Functions, 22 Categories, 100+ Subcategories |
| Approach | Risk-based ISMS with mandatory clauses | Outcome-based, flexible |
| Certification | Yes, by accredited body | No certificate exists |
| Audit cycle | Annual surveillance, 3-year recert | Self-assessed (no formal audit) |
| Documentation | Required (SoA, policies, records) | Recommended, not required |
| Typical cost | $10k - $30k for an SMB | $0 - $15k self-assessment |
| Market value | Global procurement currency | Strong in US, less recognized abroad |
| Best for | Selling to enterprise / regulated buyers | Organizing internal security program |

## How Much They Overlap

The two frameworks overlap heavily on what they ask you to do, even if they organize the work differently. Most ISO 27001 Annex A controls map directly onto NIST CSF subcategories, and vice versa. Areas of strong overlap:

- Asset management and inventory
- [Access control](/controls/a-5-15-access-control) and identity
- Risk assessment and treatment
- Awareness and training
- Logging, monitoring, and detection
- [Incident response](/controls/a-5-26-response-to-information-security-incidents) and recovery
- Supplier and third-party risk
- [Business continuity](/controls/a-5-30-ict-readiness-for-business-continuity)

Where they differ in **emphasis**:

- **ISO 27001** requires a formal **management system** - leadership commitment, objectives, internal audit, management review, continual improvement. NIST CSF does not.
- **ISO 27001** requires a documented **Statement of Applicability** justifying every control decision. NIST CSF uses target and current "Profiles" instead.
- **NIST CSF** has an explicit **Govern** function that pushes cybersecurity strategy, roles, and supply-chain risk up to leadership. ISO 27001 covers the same ground across Clauses 5 and 6 but less prescriptively.
- **NIST CSF** is easier to adopt incrementally and to communicate to non-technical stakeholders using its Tiers (Partial, Risk Informed, Repeatable, Adaptive).

## Which One Should Your Startup Pick?

Pick based on **who pays you** and **where you sell**, not on which framework you find more interesting:

- **Global SaaS, EU/UK customers, enterprise procurement**: ISO 27001. The certificate is the unlock. NIST CSF will not pass a serious vendor security review in Europe.
- **US-only SaaS, early-stage, no enterprise contracts yet**: Start with NIST CSF to structure the work. Move to ISO 27001 once a deal requires it.
- **Selling to US federal agencies or DoD supply chain**: NIST CSF plus the relevant NIST publication (800-53 for federal, 800-171 / CMMC for DoD). ISO 27001 is optional but well-regarded.
- **Healthcare or fintech in the US**: ISO 27001 plus the sector framework (HIPAA, PCI, SOC 2). NIST CSF is a useful internal organizing layer.
- **Multi-region scale-up**: ISO 27001 as the certifiable backbone, NIST CSF as the internal language your security team uses to plan and report.

## The Smart Sequencing

If you need both, **start with NIST CSF to scope and plan**, then **layer ISO 27001 to certify**. Reasons:

1. CSF's six functions are easier for a small team to use as a planning tool than ISO's clause structure.
2. CSF Profiles (current vs target state) double as an informal gap analysis - which feeds straight into your ISO 27001 risk treatment plan.
3. Most CSF subcategories map cleanly to ISO 27001 Annex A controls, so the work you do for CSF is not wasted.
4. Once your CSF Target Profile is operating, the ISO 27001 delta is mostly **paperwork**: the Statement of Applicability, internal audit, and management review.

The reverse order (ISO first, CSF later) also works, but tends to leave you with a heavy documentation set and a less natural internal vocabulary for security planning.

## Cost Reality

- **ISO 27001** total first-year cost for an SMB: typically **$10k - $30k** including templates, internal time, and certification fees. A lean, tool-driven approach can land closer to **$5k - $10k**.
- **NIST CSF** is free to download and use. A self-assessment using internal staff costs **$0 - $5k** in time. Hiring a consultant to run a formal CSF assessment runs **$10k - $25k** depending on scope.

The cheapest starting point that works for both frameworks is a [free Gap Analysis](/tools/gap-analysis) plus the templated [ISO 27001 Document Pack](/documents). Use the gap analysis as your CSF current-state Profile; use the document pack to close the ISO 27001 management-system requirements that CSF does not cover.

## Common Misconceptions

- **"NIST CSF is a replacement for ISO 27001."** It is not. CSF gives you outcomes and structure; ISO 27001 gives you a certifiable system buyers actually recognize.
- **"ISO 27001 is too European / not relevant in the US."** Wrong. ISO 27001 is the dominant enterprise procurement standard globally, including for US-headquartered Fortune 500 buyers.
- **"You can get certified to NIST CSF."** No. There is no NIST CSF certificate. Anyone selling one is selling a self-assessment dressed up as accreditation.
- **"CSF is only for critical infrastructure."** CSF 1.x was framed that way; CSF 2.0 (released 2024) is explicitly aimed at organizations of all sizes and sectors.

## What to Do Next

- US startup with no enterprise contracts yet? Run a [free Readiness Check](/readiness-check) framed as your initial CSF Current Profile.
- About to enter an enterprise sales cycle? Start the ISO 27001 path now - get the [Document Pack](/documents) and book your gap analysis. The certificate takes 4 to 9 months.
- Already running CSF internally? Map your CSF Target Profile to ISO 27001 Annex A. The delta is usually smaller than your auditor will tell you.

ISO 27001 and NIST CSF are not rivals. They are different tools for different jobs - and most mature security programs end up using both.
`
      },
      fr: {
        title: "ISO 27001 vs NIST CSF: Which Framework Should Your Startup Pick? (2026)",
        metaTitle: "ISO 27001 vs NIST CSF - Which to Pick for Your Startup (2026)",
        metaDescription: "ISO 27001 vs NIST CSF compared: scope, controls, certification, cost, and global vs US fit. A plain-English guide for startups choosing a security framework.",
        excerpt: "ISO 27001 vs NIST CSF compared in plain English.",
        content: ``
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-06-16",
    updatedDate: "2026-06-16",
    category: "Comparisons",
    tags: ["iso 27001 vs nist", "nist csf vs iso 27001", "iso 27001 vs nist csf", "nist cybersecurity framework", "compliance frameworks"],
    readTime: 10,
    featured: false
  },
  {
    slug: "iso-27001-corrective-vs-preventive-action",
    translations: {
      en: {
        title: "Corrective Action vs Preventive Action in ISO 27001 (Clause 10.1)",
        metaTitle: "Corrective vs Preventive Action ISO 27001 - CAPA Guide 2026",
        metaDescription: "ISO 27001 Clause 10.1 explained: difference between corrective and preventive actions, a practical CAPA workflow, and free templates auditors accept.",
        excerpt: "Corrective and preventive actions (CAPA) are how an ISO 27001 ISMS proves it actually improves. This guide explains the difference under Clause 10.1, gives you a step-by-step nonconformity workflow, and shows how to manage CAPAs without spreadsheets.",
        content: `
## Corrective vs Preventive Action: The Short Answer

- **Corrective action** fixes a nonconformity that has **already happened** and removes its **root cause** so it cannot recur. ISO 27001:2022 Clause 10.1 is the requirement.
- **Preventive action** addresses a **potential** nonconformity - something that has not happened yet but could. In ISO 27001:2022 the dedicated "preventive action" clause was absorbed into **Clause 6.1 (Actions to address risks and opportunities)**: your risk treatment plan is your preventive action system.

If you only remember one thing: corrective action is reactive (an issue occurred, fix the cause), preventive action is proactive (a risk exists, treat it before it occurs).

## What Clause 10.1 Actually Requires

ISO 27001:2022 Clause 10.1 ("Nonconformity and corrective action") requires that when a nonconformity occurs, the organization shall:

1. React to it - control it, correct it, and deal with the consequences.
2. Evaluate the need for action to eliminate the root causes so it does not recur, by reviewing the nonconformity, determining its causes, and checking whether similar nonconformities exist or could occur.
3. Implement any action needed.
4. Review the effectiveness of the corrective action taken.
5. Make changes to the ISMS if necessary.

And critically: retain documented information as evidence of the nature of the nonconformities, any subsequent actions taken, and the results of those actions.

That last point is what auditors test. No records = no corrective action in the auditor's eyes.

## Where Preventive Action Lives in ISO 27001:2022

The 2013 version had Clause 8.5.3 "Preventive action". The 2022 revision removed that clause - not because preventive action stopped mattering, but because the entire risk-based approach in Clause 6.1 **is** preventive action:

- **Clause 6.1.1** - actions to address risks and opportunities
- **Clause 6.1.2** - information security risk assessment
- **Clause 6.1.3** - information security risk treatment

When you identify a risk in your risk register and apply a treatment, you are performing preventive action under the 2022 standard. Auditors will look for the link between your risk register, your Statement of Applicability, and your operational controls.

## Corrective vs Preventive Action: Side by Side

| | Corrective Action | Preventive Action |
|---|---|---|
| **Trigger** | A nonconformity, incident, or audit finding has occurred | A risk has been identified that could lead to a nonconformity |
| **ISO 27001:2022 clause** | 10.1 | 6.1 (and the risk treatment plan) |
| **Question it answers** | Why did this happen and how do we stop it from happening again? | What could happen and how do we reduce the likelihood or impact? |
| **Typical input** | Incident report, audit finding, customer complaint, metric breach | Risk assessment, [threat intelligence](/controls/a-5-7-threat-intelligence), supplier review, control monitoring |
| **Typical output** | Root cause analysis, control change, retraining, documented review | Updated risk treatment plan, new or strengthened control, updated SoA |
| **Evidence auditors expect** | Nonconformity log entry, RCA, action owner, due date, effectiveness review | Risk register entry, treatment decision, control implementation evidence |

## A Practical CAPA Workflow for ISO 27001

This is the workflow we use inside the [Audit Room workspace](/audit-room) and that holds up under UKAS-accredited certification audits.

### Step 1 - Capture the Trigger

Every CAPA starts with a documented trigger. Acceptable sources:

- Internal audit finding
- External (certification or surveillance) audit finding
- Security incident (linked to your incident register)
- Management review action
- Risk review where the residual risk exceeds appetite
- Metric or KPI breach (for example, patching SLA missed two months in a row)
- Whistleblowing or staff feedback

Record: trigger source, date raised, who raised it, a clear statement of the nonconformity (what was required vs what was observed), and the clause or control reference.

### Step 2 - Contain and Correct

Before you investigate root cause, deal with the immediate consequence. Examples:

- Revoke the access that should not have been granted
- Restore the [backup](/controls/a-8-13-information-backup) that failed
- Patch the vulnerable system

Containment is **not** corrective action - it just stops the bleeding. Auditors expect to see this step recorded separately so it is clear you did not confuse "I fixed the symptom" with "I fixed the cause".

### Step 3 - Root Cause Analysis

Use a structured method. The two most defensible in an audit:

- **5 Whys** - ask "why" five times until you reach a systemic cause, not a person.
- **Fishbone (Ishikawa)** - categorize causes across People, Process, Technology, Environment, Measurement, Materials.

Stop when the cause is something you can actually change. "The engineer made a mistake" is rarely the root cause - "we have no peer review on production firewall changes" usually is.

### Step 4 - Decide the Action

For each root cause, decide:

- **Action description** - specific and verifiable
- **Owner** - one named person, not a team
- **Due date** - realistic but firm
- **Resource or budget impact** - flag anything needing management approval
- **Whether the ISMS itself needs to change** - new policy, new control, change to risk treatment plan, change to SoA

### Step 5 - Implement

Track to completion. Do not close the CAPA when the action is "done" - close it after Step 6.

### Step 6 - Verify Effectiveness

This is the step most organizations skip and the one auditors specifically probe. After enough time has passed for the fix to be tested in real operation (often 30, 60, or 90 days), confirm that:

- The original nonconformity has not recurred
- The control change is operating as intended
- No new nonconformity has been introduced by the fix

Record the evidence (a re-test, a sample of records, a metric trend) and the date of the effectiveness review. Only then close the CAPA.

### Step 7 - Feed Back into the ISMS

If the corrective action revealed a previously unidentified risk, add it to the risk register (Clause 6.1 - this is where preventive action gets renewed). If it revealed a weakness in a control, update your SoA and risk treatment plan.

## Common CAPA Mistakes That Fail Audits

- **Treating correction as corrective action.** Closing a CAPA the day the patch is deployed, with no root cause analysis and no effectiveness check.
- **One CAPA, many causes.** Lumping unrelated findings into a single action so the log looks shorter. Auditors will ask you to split them.
- **No owner or a team owner.** "IT Team" is not an owner. Name a person.
- **Permanent "in progress" status.** A CAPA that has been open for 18 months with no movement is its own nonconformity at the next audit.
- **No link to the risk register.** If a corrective action shows the original risk assessment missed something, the risk register has to change. Auditors check this trace.
- **No trend analysis.** Clause 9.1 expects you to monitor and measure. If five CAPAs in a row are all about [access reviews](/controls/a-5-18-access-rights), that is a systemic finding, not five separate ones.

## CAPA Documentation an Auditor Will Accept

At minimum, your nonconformity log should record:

- Unique CAPA reference
- Date raised and source
- Clause or control reference
- Description of the nonconformity (required state vs observed state)
- Immediate correction taken
- Root cause analysis method and result
- Corrective action(s), owner, and due date
- Implementation evidence
- Effectiveness review date, method, and outcome
- Date closed and closure approver

Word doc, Excel sheet, Jira project, or a purpose-built tool - the format does not matter as long as the fields are there and the evidence is retrievable.

## Run Your CAPA Workflow Inside Audit Room

The [Audit Room workspace](/audit-room) on iso27001kit.com gives you an opinionated internal audit and nonconformity workflow that follows Clauses 9.2 and 10.1 - capture findings during the audit, raise corrective actions with owners and due dates, track them to closure with an effectiveness review, and export the full log for your certification body.

If your preventive actions live in your risk register, the [Risk Copilot](/iso-27001-ai-assistant) keeps them linked to controls and your SoA so the trace an auditor expects under Clauses 6.1 and 8 is automatic, not retrofitted the week before audit.

[Open Audit Room - free preview](/audit-room) - or [start with the readiness check](/iso-27001-readiness-check) if you are not yet sure where your nonconformities are.
`
      },
      fr: {
        title: "Action corrective vs action preventive ISO 27001 (Clause 10.1)",
        metaTitle: "Action corrective vs preventive ISO 27001 - Guide CAPA",
        metaDescription: "Difference entre action corrective et preventive selon ISO 27001:2022 Clause 10.1, avec un flux de travail pratique pour gerer les non-conformites.",
        excerpt: "Les actions correctives et preventives (CAPA) sont la preuve qu'un SMSI ISO 27001 s'ameliore reellement.",
        content: `Ce contenu sera bientot disponible en francais.`
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-06-20",
    updatedDate: "2026-06-20",
    category: "Implementation",
    tags: ["iso 27001", "corrective action", "preventive action", "capa", "clause 10.1", "nonconformity"],
    readTime: 11,
    featured: false
  },
  {
    slug: "iso-27001-clause-7-5-3-control-of-documented-information",
    translations: {
      en: {
        title: "ISO 27001 Clause 7.5.3: Control of Documented Information (2026 Guide)",
        metaTitle: "ISO 27001 Clause 7.5.3 - Control of Documented Information",
        metaDescription: "ISO 27001 Clause 7.5.3 explained: distribution, access, retrieval, storage, and preservation of documented information, with auditor-ready templates.",
        excerpt: "Clause 7.5.3 is where most ISMS document control failures happen. This guide walks the exact requirements - distribution, access, retrieval, storage, and preservation - and shows the templates and workflow auditors expect.",
        content: `
## What ISO 27001 Clause 7.5.3 Actually Says

Clause 7.5.3 of ISO 27001:2022 ("Control of documented information") requires that documented information your ISMS depends on - both internally created and externally sourced - is **available where it is needed**, **adequately protected** (from loss of confidentiality, improper use, or loss of integrity), and **controlled** across five activities:

1. **Distribution, access, retrieval, and use**
2. **Storage and preservation**, including preservation of legibility
3. **Control of changes** (for example, version control)
4. **Retention and disposition**
5. **Identification and control of documents of external origin** the organization has determined are necessary

If any of those five activities is missing or inconsistent across your ISMS, you have a Clause 7.5.3 nonconformity waiting to happen at audit.

## Clause 7.5.3 in Context: 7.5.1, 7.5.2, 7.5.3

Clause 7.5 has three parts. They build on each other:

- **7.5.1 - General**: which documented information the ISMS requires (mandatory documents plus anything the organization decides it needs).
- **7.5.2 - Creating and updating**: identification (title, date, author, reference), format (language, software version, graphics), and review and approval for suitability and adequacy.
- **7.5.3 - Control of documented information**: how that approved information is then distributed, accessed, retrieved, used, stored, preserved, changed, retained, and disposed of.

Auditors typically test 7.5.3 by picking a document, asking who has access, asking for the previous version, and asking how external documents (for example, supplier contracts or regulatory texts) are kept current.

## The Five 7.5.3 Activities, in Practice

### 1. Distribution, Access, Retrieval, and Use

Document **who** can read, edit, approve, and publish each class of ISMS document, and **how** they get to it. Acceptable approaches:

- A document management system (SharePoint, Confluence, Google Drive, Notion) with permissions tied to roles
- A single source of truth folder with read-only links for end users and edit access for owners
- Versioned exports (PDF) distributed via the intranet or LMS for staff-facing documents

What auditors will probe: can a normal user retrieve the current policy in under a minute? Are draft and approved versions clearly separated?

### 2. Storage and Preservation (Including Legibility)

Documents must remain legible and recoverable for as long as you need them. That means:

- Storage redundancy (cloud sync, [backup](/controls/a-8-13-information-backup), or a documented backup schedule for on-prem files)
- Format choices that will still open in five years (PDF/A for archival records, common office formats for working documents)
- Protection from tampering for evidence records (immutable storage, audit logs, or hash records)

### 3. Control of Changes (Version Control)

Every controlled document needs a visible version, an effective date, an owner, and an approver. The cleanest pattern:

- Version number in the document header and filename
- Change log table at the front of each document showing version, date, author, and summary of change
- Approval recorded in the document management system or in the change log itself

Auditors love asking for "the previous version" - if you cannot produce it, that is a finding.

### 4. Retention and Disposition

Define **how long** each class of document is kept and **how** it is disposed of. The minimum:

- Records of certification audits and internal audits: typically the current certification cycle plus one (often 3-5 years)
- Risk assessments and treatment plans: rolling, with superseded versions kept for one full audit cycle
- Training and awareness records: per regulatory requirement, typically 2-3 years minimum
- Disposition method: secure deletion for digital, cross-cut shred for paper, with a record of the disposal where the document was sensitive

### 5. External Documents

If your ISMS depends on a document you did not create - a supplier SOC 2 report, an ISO 27002 standard copy, a regulatory text, a hosted-platform security whitepaper - it falls under 7.5.3. You need:

- A register of external documents the ISMS relies on, with version and date of issue
- A defined owner who checks for updates on a stated frequency (typically annually, more often for regulated content)
- Storage with [access control](/controls/a-5-15-access-control) so the version your team consults is the current one

## The Minimum Document Control Register Auditors Expect

| Field | Why it matters |
|---|---|
| Document ID | Stable reference even when titles change |
| Title | Human readable |
| Current version | Demonstrates version control under 7.5.3 |
| Effective date | Proves the version in use is the approved one |
| Owner | Single accountable person, not a team |
| Approver | Evidence of 7.5.2 review and approval |
| Classification | Drives access rules (public, internal, confidential, restricted) |
| Storage location | Where the controlled copy lives |
| Retention period | Drives disposition schedule |
| Next review date | Forces periodic reassessment (typically 12 months) |
| External / internal | Triggers external-document monitoring |

A single spreadsheet covering every controlled ISMS document, kept current, satisfies Clause 7.5.3 on its own. A document management system that emits the same fields automatically is better.

## Common Clause 7.5.3 Nonconformities (and How to Avoid Them)

- **Draft and approved versions in the same folder** - move drafts to a separate workspace and publish only approved PDFs.
- **Policies dated more than 12 months ago with no review record** - schedule annual reviews against the document register.
- **No version control on the risk register** - keep dated snapshots at each management review, not just a live sheet.
- **External documents (supplier SOC 2 reports, regulatory texts) not tracked** - add an "External documents" tab to your document register.
- **Retention undefined** - publish a retention schedule and reference it from each policy.
- **No evidence of disposal** - keep a disposal log for records that contained personal or commercially sensitive data.

## How the ISO27001KIT Templates Cover 7.5.3

The [ISO 27001 Document Pack](/iso-27001-templates) includes a **Document Control Procedure** and a pre-filled **Document Register** that map directly to the 7.5.3 activities - distribution, access, version control, retention, disposition, and external documents - so you can adopt the workflow rather than build it.

The [policy templates](/documents) ship with the standard header (ID, version, owner, approver, effective date, next review date, classification) and a change log, satisfying 7.5.2 creation requirements and feeding cleanly into a 7.5.3 control workflow.

If you want the controlled register, version history, and review reminders run for you, the [Risk Copilot workspace](/iso-27001-ai-assistant) keeps document, control, and risk records linked so the evidence an auditor wants under 7.5.3 is one export away.

[Browse the Document Pack](/iso-27001-templates) - or [start with a free gap analysis](/iso-27001-gap-analysis) to see which 7.5.3 evidence you are missing.
`
      },
      fr: {
        title: "ISO 27001 Clause 7.5.3 : Maitrise des informations documentees",
        metaTitle: "ISO 27001 Clause 7.5.3 - Maitrise des informations documentees",
        metaDescription: "Clause 7.5.3 ISO 27001 expliquee : distribution, acces, recuperation, stockage et conservation des informations documentees, avec des modeles prets pour l'audit.",
        excerpt: "Clause 7.5.3 est l'endroit ou la plupart des SMSI echouent en matiere de maitrise documentaire.",
        content: `Ce contenu sera bientot disponible en francais.`
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-06-28",
    updatedDate: "2026-06-28",
    category: "Implementation",
    tags: ["iso 27001", "clause 7.5.3", "documented information", "document control", "iso 27001 documentation"],
    readTime: 9,
    featured: false
  },
  {
    slug: "iso-27001-stage-1-vs-stage-2-audit",
    translations: {
      en: {
        title: "ISO 27001 Stage 1 vs Stage 2 Audit: What Auditors Check at Each Stage (2026)",
        metaTitle: "ISO 27001 Stage 1 vs Stage 2 Audit - Scope, Duration, Evidence",
        metaDescription: "ISO 27001 Stage 1 vs Stage 2 audit compared: scope, duration, auditor expectations, and the evidence you need to pass each stage on the first attempt.",
        excerpt: "The ISO 27001 certification audit is split into two stages that test very different things. This guide breaks down what auditors check at Stage 1 vs Stage 2, how long each takes, and the evidence you need to walk in with.",
        content: `
## Why ISO 27001 Certification Has Two Stages

ISO/IEC 17021-1 requires accredited certification bodies to split the initial audit into two stages. Stage 1 tests whether your ISMS is **designed** correctly on paper. Stage 2 tests whether it is **operating** in practice. Skipping straight to implementation testing would waste everyone's time when the underlying design is broken, so the standard forces a documentation review first.

Both stages are conducted by the same accredited certification body (BSI, Schellman, A-LIGN, DNV, etc.), usually by the same lead auditor. You cannot pick and choose - you must pass Stage 1 before Stage 2 is scheduled, and you must clear Stage 2 to receive the certificate.

## Stage 1 vs Stage 2 at a Glance

| Dimension | Stage 1 | Stage 2 |
|---|---|---|
| Purpose | Design review - is the ISMS documented correctly? | Implementation audit - is the ISMS actually running? |
| Format | Documentation review + short interviews | Sampling of controls, records, and interviews |
| Duration | 0.5 to 1.5 days (small SaaS), remote | 2 to 5 days (small SaaS), often on-site |
| Timing | 4 to 8 weeks before Stage 2 | Immediately after Stage 1 clearance |
| Output | Stage 1 report with findings and readiness verdict | Recommendation for certification (or not) |
| Common failure mode | Missing mandatory documents | Evidence does not match documented process |

If Stage 1 uncovers major issues, the auditor pauses the process and gives you time (typically 30 to 90 days) to remediate before Stage 2 is booked.

## What Auditors Check at Stage 1

Stage 1 is a **documentation and readiness review**. The auditor works through the mandatory clauses of ISO 27001:2022 and confirms each artifact exists, is approved, and covers the required content. Expect requests for:

- **ISMS scope statement** (clause 4.3) - covers the boundaries, interfaces, and exclusions of your ISMS
- **[Information security policy](/controls/a-5-1-policies-for-information-security)** approved by top management (clause 5.2)
- **Risk assessment methodology** with defined likelihood, impact, and acceptance criteria (clause 6.1.2)
- **Risk assessment results** - a populated risk register with owners (clause 6.1.2 c)
- **Risk treatment plan** with target dates and residual scores (clause 6.1.3 e)
- **Statement of Applicability** covering all 93 Annex A controls with justification (clause 6.1.3 d)
- **Objectives and plans to achieve them** (clause 6.2)
- **Internal audit programme and results** (clause 9.2)
- **Management review records** (clause 9.3)
- **Nonconformity and corrective action log** (clause 10.1)

The auditor also confirms you have **operated the ISMS long enough** to produce evidence. Certification bodies typically require at least three months of records, one internal audit, and one management review before they book Stage 2. Walking in on day one with pristine documentation but zero operational history is the most common cause of a delayed Stage 2.

## What Auditors Check at Stage 2

Stage 2 is the **implementation audit**. The auditor samples records and interviews staff to test whether the ISMS you documented in Stage 1 is actually running. This is where most first-time failures happen, not because controls are broken, but because evidence does not match the documented process.

Expect sampling across:

- **[Access control](/controls/a-5-15-access-control)** (A.5.15 to [A.5.18](/controls/a-5-18-access-rights), [A.8.2](/controls/a-8-2-privileged-access-rights) to [A.8.5](/controls/a-8-5-secure-authentication)) - joiner-mover-leaver tickets, quarterly access reviews, privileged access lists, MFA enrollment reports
- **Supplier management** ([A.5.19](/controls/a-5-19-information-security-in-supplier-relationships) to A.5.22) - supplier register, due diligence records, contract clauses, supplier reviews
- **Incident management** (A.5.24 to A.5.27) - incident tickets, post-incident reviews, lessons learned, notification records
- **Backup and recovery** (A.8.13, A.5.29 to A.5.30) - backup logs, restore test evidence, business continuity plan tests
- **Change management** (A.8.32) - change tickets, approval records, rollback evidence
- **Vulnerability management** (A.8.8) - scan reports, remediation SLAs, patch records
- **Logging and monitoring** (A.8.15 to A.8.16) - log samples, alert tuning records, on-call rotation
- **Secure development** (A.8.25 to A.8.28) - SDLC policy, code review evidence, static analysis reports
- **Training and awareness** (A.6.3) - completion reports, phishing simulation results
- **Physical security** (A.7.1 to A.7.14) - office access logs, clean-desk observations, equipment records (skipped or reduced for fully remote companies)

For each sampled area, the auditor picks a specific date range and asks for evidence. "Show me the access review for Q1 2026." "Show me the change ticket for the production deploy on March 14." If the record exists, is approved, and matches the documented process, the control passes. If not, you get a finding.

## Duration and Auditor Expectations

Duration is set by ISO/IEC 27006 based on **effective personnel** (roughly full-time equivalents in scope):

| Effective personnel | Stage 1 | Stage 2 |
|---|---|---|
| 1 to 15 | 0.5 day | 1.5 days |
| 16 to 25 | 1 day | 2 days |
| 26 to 45 | 1 day | 3 days |
| 46 to 65 | 1 day | 4 days |
| 66 to 85 | 1.5 days | 5 days |

Most seed to Series B SaaS companies land in the 1 to 45 range and see Stage 1 completed in a day and Stage 2 in 2 to 3 days. Larger organizations, multi-site scopes, or high-risk sectors (fintech, health) push these numbers up.

Auditors expect three things at every stage:

1. **Prompt evidence retrieval** - if a request takes more than 15 minutes to fulfill, it signals your ISMS is not actually operational
2. **Consistent stories from different people** - the CTO, the ISMS manager, and a random engineer should describe the same access-review process
3. **Traceability from risks to controls to evidence** - the Statement of Applicability, risk register, and control evidence must line up

## The Common Findings That Trip Companies Up

### Stage 1

- **Statement of Applicability missing controls** - all 93 Annex A controls must appear, even the ones you exclude with justification
- **Risk register with no residual scores** - the standard requires residual risk to be evaluated after treatment
- **Internal audit not covering the full standard** - clause 9.2 requires the internal audit programme to cover clauses 4 through 10 and all applicable Annex A controls, not just a spot check
- **Management review missing required inputs** - clause 9.3.2 lists 9 mandatory inputs (nonconformities, monitoring results, audit results, risk assessment updates, etc.). Skip one and it is a nonconformity

### Stage 2

- **Access reviews performed but not documented** - the process happens verbally in standups; there is no signed record
- **Supplier register that ignores critical vendors** - Auth0, AWS, GitHub, and Datadog must be in the register with a risk score
- **Incident log with no post-incident review** - clause A.5.27 requires learning from incidents; a ticket without a written lesson fails
- **Backup evidence but no restore test** - A.8.13 requires you to test the restore, not just prove the backup ran
- **Training records that stop at "sent invitation"** - completion evidence for every person in scope is required

## How to Prepare for Each Stage

### Preparing for Stage 1

1. Run a [gap analysis](/iso-27001-gap-analysis) against the 93 Annex A controls plus clauses 4 to 10
2. Close every "not documented" and "partial" gap - documentation is what Stage 1 tests
3. Populate the [Statement of Applicability](/tools/soa-generator) with a justification for every control
4. Build a risk register with owners, inherent scores, treatments, and residual scores
5. Run one internal audit and one management review at least six weeks before Stage 1

### Preparing for Stage 2

1. Operate the ISMS for at least three months to accumulate records
2. Sample your own evidence weekly - if you cannot find it in 10 minutes, the auditor cannot either
3. Brief every person the auditor might interview on the process, not the answer they want you to give
4. Prepare a shared evidence folder organized by Annex A control
5. Run a mock Stage 2 with a colleague or consultant - the [audit room checklist](/audit-room) mirrors what accredited auditors sample

## Between Stages: The 4 to 8 Week Window

Most certification bodies leave 4 to 8 weeks between Stage 1 and Stage 2. Use it deliberately:

- Close every Stage 1 finding, even the minor ones
- Run one more internal audit pass on controls that had thin evidence
- Complete any outstanding risk treatments due in that window
- Update the Statement of Applicability if Stage 1 revealed missing controls
- Confirm the Stage 2 scope, dates, and interviewees in writing with the auditor

A well-used gap between stages is the single biggest predictor of a clean Stage 2. A wasted gap is the single biggest predictor of a nonconformity.

## Next Steps

If you have the ISO 27001 [Document Pack](/iso-27001-templates), your Stage 1 evidence is already 80 percent built - every mandatory artifact ships pre-written and mapped to the clause. Combine it with the [free gap analysis tool](/iso-27001-gap-analysis) to score readiness, then move into the [Risk Copilot workspace](/iso-27001-ai-assistant) to build the risk register, treatments, and evidence trail Stage 2 samples.

Book Stage 1 only when the internal audit and management review are done. The extra six weeks of preparation pays back tenfold when the certificate lands on the first attempt.
`
      },
      fr: {
        title: "Audit ISO 27001 Etape 1 vs Etape 2",
        metaTitle: "Audit ISO 27001 Etape 1 vs Etape 2 - Ce que verifient les auditeurs",
        metaDescription: "Audit ISO 27001 Etape 1 vs Etape 2 : portee, duree, attentes des auditeurs et preuves necessaires pour reussir chaque etape.",
        excerpt: "L'audit de certification ISO 27001 est divise en deux etapes qui testent des choses tres differentes.",
        content: `Ce contenu sera bientot disponible en francais.`
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-07-12",
    updatedDate: "2026-07-12",
    category: "Certification",
    tags: ["iso 27001", "stage 1 audit", "stage 2 audit", "iso 27001 certification", "iso 27001 audit"],
    readTime: 11,
    featured: true
  },
  {
    slug: "iso-27001-annex-a-controls-guide",
    translations: {
      en: {
        title: "ISO 27001:2022 Annex A Controls Guide: All 93 Controls Explained (2026)",
        metaTitle: "ISO 27001 Annex A Controls Guide - All 93 Controls Explained",
        metaDescription: "Complete reference guide to the 93 ISO 27001:2022 Annex A controls across the 4 themes: Organizational, People, Physical, and Technological.",
        excerpt: "A complete reference to the 93 controls in ISO 27001:2022 Annex A, grouped by theme, with what each control requires and how to implement it.",
        content: `
## What Annex A Is (and Isn't)

Annex A of ISO 27001:2022 lists **93 information security controls** grouped into **4 themes**. It is a reference catalog - not a mandatory checklist. Clause 6.1.3 requires you to compare your risk treatment plan against Annex A and document your decisions in a **Statement of Applicability (SoA)**. Controls you exclude must have a written justification.

The 2022 revision reduced the previous 114 controls (2013 version, 14 clauses) down to **93 controls in 4 themes**, and introduced **5 attribute tags** (control type, information security properties, cybersecurity concepts, operational capabilities, security domains) to help you filter and report on the catalog.

## The 4 Themes at a Glance

| Theme | Prefix | Controls | Focus |
|---|---|---|---|
| Organizational | A.5 | 37 | Policies, roles, supplier and [incident management](/controls/a-5-24-information-security-incident-management-planning-and-preparation) |
| People | A.6 | 8 | Screening, awareness, [remote work](/controls/a-6-7-remote-working), [disciplinary process](/controls/a-6-4-disciplinary-process) |
| Physical | A.7 | 14 | Perimeters, offices, equipment, cabling, disposal |
| Technological | A.8 | 34 | [Access control](/controls/a-5-15-access-control), cryptography, logging, [secure development](/controls/a-8-25-secure-development-life-cycle) |

## A.5 Organizational Controls (37)

These controls govern how the ISMS is run day to day: policies, roles, third parties, incidents, and continuity.

- **[A.5.1](/controls/a-5-1-policies-for-information-security)** Policies for information security - top-level policy plus topic-specific policies, approved by management, reviewed at planned intervals.
- **A.5.2** Information security roles and responsibilities - documented and communicated.
- **A.5.3** Segregation of duties - conflicting duties split across people to reduce fraud and error.
- **A.5.4** Management responsibilities - management requires staff to apply information security per policy.
- **A.5.5** Contact with authorities - regulators, law enforcement, data protection authorities.
- **A.5.6** Contact with special interest groups - ISACs, professional bodies, vendor security groups.
- **A.5.7** Threat intelligence - collect and analyze information about threats.
- **A.5.8** Information security in project management - security requirements addressed in every project.
- **A.5.9** Inventory of information and other associated assets - the asset register.
- **A.5.10** Acceptable use of information and other associated assets.
- **A.5.11** Return of assets on termination or change of role.
- **A.5.12** Classification of information - by confidentiality, integrity, availability.
- **A.5.13** Labelling of information - handling matches the classification.
- **A.5.14** Information transfer - rules for transfer inside and outside the organization.
- **A.5.15** Access control - business rules for access, based on need-to-know.
- **A.5.16** Identity management - the full lifecycle of user identities.
- **A.5.17** Authentication information - passwords, keys, tokens are managed.
- **A.5.18** Access rights - provisioned, reviewed, modified, and removed in line with policy.
- **A.5.19** Information security in supplier relationships.
- **A.5.20** Addressing information security within supplier agreements.
- **A.5.21** Managing information security in the ICT supply chain.
- **A.5.22** Monitoring, review and change management of supplier services.
- **A.5.23** Information security for use of cloud services - new in 2022.
- **A.5.24** Information security incident management planning and preparation.
- **A.5.25** Assessment and decision on information security events.
- **A.5.26** Response to information security incidents.
- **A.5.27** Learning from information security incidents.
- **A.5.28** Collection of evidence.
- **A.5.29** Information security during disruption.
- **A.5.30** ICT readiness for business continuity - new in 2022.
- **A.5.31** Legal, statutory, regulatory and contractual requirements.
- **A.5.32** Intellectual property rights.
- **A.5.33** Protection of records.
- **A.5.34** Privacy and protection of PII.
- **A.5.35** Independent review of information security.
- **A.5.36** Compliance with policies, rules and standards for information security.
- **A.5.37** Documented operating procedures.

## A.6 People Controls (8)

- **A.6.1** Screening - background verification before hiring, proportionate to risk.
- **A.6.2** Terms and conditions of employment - security responsibilities in contracts.
- **A.6.3** Information security awareness, education and training.
- **A.6.4** Disciplinary process - for staff who violate security policy.
- **A.6.5** Responsibilities after termination or change of employment.
- **A.6.6** Confidentiality or non-disclosure agreements.
- **A.6.7** Remote working - new in 2022.
- **A.6.8** Information security event reporting - staff know how and when to report.

## A.7 Physical Controls (14)

- **A.7.1** Physical security perimeters.
- **A.7.2** Physical entry - controls on doors, mantraps, visitor logs.
- **A.7.3** Securing offices, rooms and facilities.
- **A.7.4** Physical security monitoring - new in 2022.
- **A.7.5** Protecting against physical and environmental threats - fire, flood, seismic.
- **A.7.6** Working in secure areas.
- **A.7.7** Clear desk and clear screen.
- **A.7.8** Equipment siting and protection.
- **A.7.9** Security of assets off-premises.
- **A.7.10** Storage media - handling across the lifecycle.
- **A.7.11** Supporting utilities - power, HVAC, telecoms.
- **A.7.12** Cabling security.
- **A.7.13** Equipment maintenance.
- **A.7.14** Secure disposal or re-use of equipment.

Fully remote SaaS companies can scope most of A.7 to the corporate office and cloud provider certifications - document the reasoning in the SoA.

## A.8 Technological Controls (34)

- **A.8.1** User endpoint devices - laptops, phones, tablets.
- **A.8.2** Privileged access rights - restricted and managed.
- **A.8.3** Information access restriction - enforced by systems.
- **A.8.4** Access to source code - controls on read and write access.
- **A.8.5** Secure authentication - MFA, credential management.
- **A.8.6** Capacity management.
- **A.8.7** Protection against malware.
- **A.8.8** Management of technical vulnerabilities - scanning, patching, remediation SLAs.
- **A.8.9** Configuration management - new in 2022, covers baselines and drift.
- **A.8.10** Information deletion - new in 2022.
- **A.8.11** Data masking - new in 2022.
- **A.8.12** Data leakage prevention - new in 2022.
- **A.8.13** Information backup - and restore testing.
- **A.8.14** Redundancy of information processing facilities.
- **A.8.15** Logging.
- **A.8.16** Monitoring activities - new in 2022, covers detection.
- **A.8.17** Clock synchronization.
- **A.8.18** Use of privileged utility programs.
- **A.8.19** Installation of software on operational systems.
- **A.8.20** Networks security.
- **A.8.21** Security of network services.
- **A.8.22** Segregation of networks.
- **A.8.23** Web filtering - new in 2022.
- **A.8.24** Use of cryptography - policy, key management.
- **A.8.25** Secure development lifecycle.
- **A.8.26** Application security requirements.
- **A.8.27** Secure system architecture and engineering principles.
- **A.8.28** Secure coding - new in 2022.
- **A.8.29** Security testing in development and acceptance.
- **A.8.30** Outsourced development.
- **A.8.31** Separation of development, test and production environments.
- **A.8.32** Change management.
- **A.8.33** Test information - protection of data used in testing.
- **A.8.34** Protection of information systems during audit testing.

## The 11 New Controls in 2022

Auditors focus on these because organizations coming from ISO 27001:2013 often have gaps:

A.5.7 Threat intelligence, A.5.23 Cloud services, A.5.30 ICT readiness for business continuity, A.7.4 Physical security monitoring, A.8.9 Configuration management, A.8.10 Information deletion, A.8.11 Data masking, A.8.12 Data leakage prevention, A.8.16 Monitoring activities, A.8.23 Web filtering, A.8.28 Secure coding.

## How to Use Annex A (Not Just Read It)

1. **Run a [gap analysis](/iso-27001-gap-analysis)** against all 93 controls to score current state.
2. **Build your risk register** and map each risk to the Annex A control(s) that treat it.
3. **Populate the [Statement of Applicability](/tools/soa-generator)** - every control is either Applicable (with implementation status) or Excluded (with justification).
4. **Attach evidence per control** - policy, procedure, ticket, log, screenshot. This is what Stage 2 auditors sample.
5. **Review annually** - clause 9.3 management review must reassess applicability at planned intervals.

## From Reference to Implementation

A control list is not an ISMS. You need policies, procedures, and records that turn each applicable control into a repeatable process. The [ISO 27001 Document Pack](/iso-27001-templates) ships pre-mapped to Annex A: every template names the controls it satisfies, so building the SoA becomes a merge exercise instead of a writing exercise. Combine it with the [Risk Copilot](/iso-27001-ai-assistant) to link risks, treatments, and Annex A controls in one workspace.
`
      },
      fr: {
        title: "Guide des controles Annexe A ISO 27001:2022",
        metaTitle: "Guide des controles Annexe A ISO 27001 - Les 93 controles",
        metaDescription: "Guide de reference des 93 controles ISO 27001:2022 Annexe A repartis en 4 themes : organisationnel, personnes, physique et technologique.",
        excerpt: "Reference complete des 93 controles de l'Annexe A d'ISO 27001:2022, par theme, avec ce qu'exige chaque controle.",
        content: `Ce contenu sera bientot disponible en francais.`
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-07-12",
    updatedDate: "2026-07-12",
    category: "Frameworks",
    tags: ["iso 27001", "annex a", "iso 27001 controls", "iso 27001:2022", "controls guide"],
    readTime: 14,
    featured: true
  },
  {
    slug: "iso-27001-dr-site-distance",
    translations: {
      en: {
        title: "ISO 27001 Disaster Recovery Site Distance: How Far Apart Should Sites Be? (2026)",
        metaTitle: "ISO 27001 DR Site Distance - How Far Should Sites Be Apart",
        metaDescription: "How far should your primary and disaster recovery sites be to satisfy ISO 27001 Annex A 5.29 and 5.30? Practical guidance, ranges, and cloud region rules.",
        excerpt: "There is no fixed number in ISO 27001, but there is a right way to justify DR site distance. Here is how to pick and document it for Annex A 5.29 and 5.30.",
        content: `
## Does ISO 27001 Set a Minimum Distance?

No. ISO 27001:2022 does not name a distance in kilometres or miles. Annex A 5.29 ([ICT readiness for business continuity](/controls/a-5-30-ict-readiness-for-business-continuity)) and Annex A 5.30 (ICT continuity) require that the organisation **plans, implements, maintains and tests** ICT continuity so that information and services stay available at agreed levels during disruption. The distance between primary and recovery sites is a **risk decision** you make and document, not a value the standard prescribes.

Auditors will not fail you for choosing 40 km over 400 km. They will fail you for choosing a distance with no threat analysis behind it.

## What "Enough Distance" Actually Means

A recovery site is far enough when a single event that takes out the primary site cannot also take out the recovery site. The threats that drive the number are:

- **Power grid** - primary and DR should sit on different substations or grid regions.
- **Telecom carriers** - different backbone providers, different last-mile fibre routes.
- **Natural hazards** - earthquake fault zones, flood plains, hurricane corridors, wildfire zones.
- **Regional outages** - the same cloud region, the same metro data-centre campus, or the same postcode all count as one site for continuity purposes.
- **Regulatory boundaries** - data residency may force you to stay inside a country or region even when a larger distance would be safer.

## Practical Ranges People Actually Use

These are common industry ranges, not ISO requirements. Use them as a starting point and adjust to your risk assessment.

| Pattern | Typical distance | When it fits |
|---|---|---|
| Metro pair (synchronous replication) | 10-50 km | RPO near zero, same country, tolerant of correlated regional events |
| Regional (asynchronous replication) | 100-500 km | Different power grid and hazard zone, seconds-to-minutes RPO |
| Cross-region (geo-redundant) | 500+ km or different country | High-impact services, regulatory redundancy, independent cloud regions |

Financial services and healthcare regulators often push the number up. FFIEC and similar guidance in the US typically expects **out-of-region** DR for critical services. In the EU, DORA requires ICT third parties to demonstrate that recovery sites are not exposed to the same risk profile as the primary.

## Cloud-Native DR: Regions Are Your Distance Units

If your workload is in AWS, Azure, or GCP, the distance conversation shifts:

- **Availability Zones** in the same region protect against a single data-centre failure but share regional risk. Do not count AZ redundancy as DR for a full-region outage.
- **Regions** are your DR unit. eu-west-1 to eu-central-1, or us-east-1 to us-west-2, gives you hundreds of kilometres and independent power, network, and staff.
- **Data residency** may block you from picking the furthest region. Document the constraint in your SoA and pick the next-best option.

For SaaS companies on Lovable, Vercel, Netlify, or similar platforms, the DR conversation is really about **your provider's** region strategy plus **your database and object-storage** replication. Include the provider's documented recovery capabilities in your evidence pack.

## How to Justify the Distance in Your ISMS

Auditors want to see the reasoning, not just the number. Put this in your ICT continuity plan (Annex A 5.30) and cross-reference it from the SoA (Annex A 5.29):

1. **Threat catalogue** - list the hazards that could affect the primary site (grid, telecom, natural, regulatory).
2. **Site profile for each candidate** - hazard exposure, grid, carrier, jurisdiction.
3. **Correlated failure analysis** - show that a single realistic event cannot hit both sites.
4. **RPO and RTO fit** - synchronous replication limits distance to ~50-100 km; async replication opens it up. Match the pattern to your recovery objectives.
5. **Test evidence** - clause 5.30 requires the plan to be **tested** at planned intervals. Keep the last failover or tabletop record with dates, participants, and results.

## Common Mistakes That Fail Stage 2

- **"Same building, different floor"** - not DR. It is HA at best.
- **Two AZs in one cloud region called "DR"** - covers zone failure, not region failure.
- **DR site inside the same flood plain or fault zone** - the map contradicts the plan.
- **No test in the last 12 months** - Annex A 5.30 explicitly requires testing.
- **Distance chosen "because the vendor recommended it"** - no risk assessment, no defensible answer.

## From Distance Decision to Audit-Ready Evidence

The distance is one line. The **justification, test record, and SoA entry** are what carry you through Stage 2. The [ISO 27001 Document Pack](/iso-27001-templates) includes an ICT continuity plan template pre-mapped to Annex A 5.29 and 5.30, with sections for site profiling, correlated-failure analysis, and test logs. Combine it with the [Risk Copilot](/iso-27001-ai-assistant) to link continuity risks to their treatments and controls in one workspace.
`
      },
      fr: {
        title: "Distance entre sites de reprise ISO 27001",
        metaTitle: "Distance site DR ISO 27001 - Combien de km entre les sites",
        metaDescription: "Quelle distance entre site principal et site de reprise pour satisfaire aux controles ISO 27001 Annexe A 5.29 et 5.30 ? Guide pratique.",
        excerpt: "ISO 27001 ne fixe pas de distance. Voici comment choisir et justifier la distance entre sites pour l'Annexe A 5.29 et 5.30.",
        content: `Ce contenu sera bientot disponible en francais.`
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-07-12",
    updatedDate: "2026-07-12",
    category: "Business Continuity",
    tags: ["iso 27001", "disaster recovery", "annex a 5.29", "annex a 5.30", "business continuity", "dr distance"],
    readTime: 9,
    featured: true
  },
  {
    slug: "corrective-action-vs-preventive-action",
    translations: {
      en: {
        title: "Corrective Action vs Preventive Action in ISO 27001 (2026 Guide)",
        metaTitle: "Corrective Action vs Preventive Action - ISO 27001 Guide",
        metaDescription: "Clear definitions and practical differences between corrective action and preventive action under ISO 27001:2022, with examples, clause references, and templates.",
        excerpt: "ISO 27001:2022 kept corrective action and dropped the old preventive action clause - but preventive thinking is still required. Here is what each term means, how they differ, and what auditors expect to see.",
        content: `
## The Short Answer

**Corrective action** deals with a problem that has already happened. You fix the immediate issue, find out why it happened, and change something so it does not happen again.

**Preventive action** deals with a problem that has not happened yet. You spot a plausible failure, weakness, or trend and act before it turns into a real nonconformity or incident.

ISO 27001:2022 keeps an explicit clause for corrective action (clause 10.2). It does **not** have a separate clause called "preventive action" - that concept was absorbed into risk-based thinking in clauses 6.1 (actions to address risks and opportunities) and 10.1 (continual improvement). Preventive action is still expected. It just lives somewhere else in the standard.

## Definitions Side by Side

| Aspect | Corrective action | Preventive action |
| --- | --- | --- |
| Trigger | A nonconformity, incident, or audit finding that already occurred | A risk, weakness, trend, or near miss that could cause a problem |
| Purpose | Eliminate the cause so it does not recur | Eliminate the cause so it does not occur in the first place |
| ISO 27001:2022 clause | 10.2 (explicit requirement) | 6.1 risk treatment and 10.1 continual improvement |
| Typical evidence | Nonconformity record, root cause analysis, action plan, effectiveness check | Risk register entry, treatment plan, monitoring metric, improvement log |
| Timing | Reactive (after the event) | Proactive (before the event) |

## What Clause 10.2 Actually Requires

When a nonconformity occurs, ISO 27001:2022 clause 10.2 requires you to:

1. **React to it** - control it and correct it, and deal with the consequences.
2. **Evaluate the need to eliminate the cause** - by reviewing the nonconformity, determining its causes, and checking whether similar nonconformities exist or could occur elsewhere.
3. **Implement any action needed.**
4. **Review the effectiveness** of the corrective action taken.
5. **Make changes to the ISMS** if necessary.

You must also retain documented information on the nature of the nonconformity, the actions taken, and the results. In practice, that means a corrective action record per finding - not a single spreadsheet cell saying "fixed".

## Correction Is Not Corrective Action

This is the distinction auditors test most often.

- **Correction**: a laptop was left unencrypted, so you encrypt it today.
- **Corrective action**: you find out that the build image had [encryption](/controls/a-8-24-use-of-cryptography) disabled by default, fix the image, and add an automated check so no future laptop ships unencrypted.

If your records only ever show the first line, an auditor will raise a finding for an ineffective corrective action process - even though the immediate issue was resolved.

## Practical Examples in an ISMS

| Situation | Correction | Corrective action | Preventive action |
| --- | --- | --- | --- |
| Leaver still had VPN access two weeks after exit | Revoke the account now | Fix the offboarding trigger between HR and IT, add a weekly orphan-account report | Extend the same joiner-mover-leaver check to contractors and third-party accounts before it fails there too |
| Internal audit found the risk register was not reviewed for 14 months | Review it now | Add a scheduled review owner, due date, and management review agenda item | Apply the same review cadence and ownership model to the SoA, policies, and supplier assessments |
| A phishing email led to credential theft | Reset credentials, contain the account | Root cause analysis, enforce phishing-resistant [MFA](/controls/a-8-5-secure-authentication) on the affected system | Roll MFA out to the remaining systems that share the same weakness |
| [Backup](/controls/a-8-13-information-backup) restore test failed | Restore from an older backup | Fix the backup job configuration and re-test | Add restore testing to every system in the same backup class |

## How to Run a Corrective Action Properly

1. **Record the nonconformity.** Describe what was observed, where, when, and which requirement or control it breaches.
2. **Apply the immediate correction.** Contain the damage. Note it separately from the corrective action.
3. **Find the root cause.** Use 5 Whys or a simple cause-and-effect analysis. Stop when you reach something you can change - a process, a control setting, a responsibility gap. "Human error" is almost never a root cause.
4. **Check for the same cause elsewhere.** Clause 10.2 explicitly asks this. If one team's [access reviews](/controls/a-5-18-access-rights) slipped, check every team.
5. **Define the action, owner, and due date.** One owner. A real date.
6. **Verify effectiveness later.** Come back after a defined period and prove the problem did not recur. Attach evidence: a log, a report, a screenshot, a re-test.
7. **Update the ISMS.** Amend the policy, procedure, risk register, or SoA if the fix changed how you work.

## How Preventive Action Shows Up in ISO 27001:2022

Because there is no clause named "preventive action", auditors look for preventive behaviour in these places instead:

- **Clause 6.1** - risks and opportunities identified and treated before they materialise.
- **Clause 8.2 and 8.3** - risk assessment and treatment performed at planned intervals, not just after incidents.
- **Clause 9.1** - monitoring and measurement that surfaces negative trends early.
- **Clause 9.3** - management review inputs covering trends in nonconformities and risk changes.
- **Clause 10.1** - continual improvement of the suitability, adequacy, and effectiveness of the ISMS.

A good signal of a mature ISMS: your improvement log contains items that were never nonconformities. They came from near misses, metrics, supplier changes, or lessons from other organisations.

## Common Mistakes Auditors Flag

- Recording only the correction and calling it a corrective action.
- Root causes written as "staff did not follow the procedure" with no analysis of why.
- No effectiveness check, or an effectiveness check performed the same day as the fix.
- Corrective actions with no owner or a permanently rolling due date.
- Never checking whether the same cause exists elsewhere in scope.
- No preventive or improvement items at all - every entry in the log is reactive.

## Documentation You Need

At minimum, keep:

- A **nonconformity and corrective action register** with: source, description, immediate correction, root cause, action, owner, due date, status, effectiveness verification, and date closed.
- A **continual improvement log** for preventive and opportunity-driven items.
- Links from both into your **risk register**, **internal audit reports**, and **management review** minutes.

Our [ISO 27001 document pack](/documents) includes a corrective action and nonconformity register aligned to clause 10.2, and the [internal audit checklist tool](/tools/internal-audit-checklist) generates findings you can feed straight into it.

## Frequently Asked Questions

**Did ISO 27001:2022 remove preventive action?**
It removed the standalone clause, not the expectation. Preventive action is now delivered through risk-based thinking in clause 6.1 and continual improvement in clause 10.1.

**Is a corrective action needed for every incident?**
Not every incident, but every incident that reveals a nonconformity with your ISMS, a control failure, or a repeatable cause should trigger one.

**How long should a corrective action stay open?**
There is no fixed limit. Set a due date proportionate to the risk and evidence progress. Long-open actions with no movement are a common audit finding.

**Who approves closure?**
Typically the ISMS manager or process owner, based on documented evidence that the action worked - not just that it was performed.

## Next Steps

Start by auditing your own register. For each of the last ten entries, ask: is this a correction or a corrective action? Is the root cause something we can actually change? Did anyone verify it worked? If more than a couple fail those tests, your clause 10.2 evidence needs work before your next audit.
`
      },
      fr: {
        title: "Action corrective et action preventive ISO 27001",
        metaTitle: "Action corrective vs action preventive - ISO 27001",
        metaDescription: "Definitions et differences pratiques entre action corrective et action preventive dans le cadre de la norme ISO 27001:2022.",
        excerpt: "ISO 27001:2022 conserve l'action corrective et integre l'action preventive dans l'approche par les risques. Voici les differences.",
        content: `Ce contenu sera bientot disponible en francais.`
      }
    },
    author: "ISO27001KIT",
    publishDate: "2026-07-26",
    updatedDate: "2026-07-26",
    category: "Compliance",
    tags: ["corrective action vs preventive action", "iso 27001 corrective action", "clause 10.2", "nonconformity", "continual improvement", "iso 27001:2022"],
    readTime: 9,
    featured: true
  },
];

// Helper function to get localized blog post
export const getLocalizedPost = (post: BlogPost, language: 'en' | 'fr'): LocalizedBlogPost => {
  const translation = post.translations[language] || post.translations.en;
  return {
    slug: post.slug,
    title: translation.title,
    metaTitle: translation.metaTitle,
    metaDescription: translation.metaDescription,
    excerpt: translation.excerpt,
    content: translation.content,
    author: post.author,
    publishDate: post.publishDate,
    updatedDate: post.updatedDate,
    category: post.category,
    tags: post.tags,
    readTime: post.readTime,
    featured: post.featured
  };
};

// Slugs consolidated into a single canonical post; hide duplicates from listings/sitemap-driving consumers.
const DEDUPED_SLUGS = new Set<string>([
  "iso-27001-vs-soc-2-comparison",
  "soc-2-vs-iso-27001-comparison-2026",
]);

const visiblePosts = (): BlogPost[] => blogPosts.filter(p => !DEDUPED_SLUGS.has(p.slug));

export const getLocalizedPosts = (language: 'en' | 'fr'): LocalizedBlogPost[] => {
  return visiblePosts().map(post => getLocalizedPost(post, language));
};

export const getPostBySlug = (slug: string, language: 'en' | 'fr' = 'en'): LocalizedBlogPost | undefined => {
  const post = blogPosts.find(post => post.slug === slug);
  return post ? getLocalizedPost(post, language) : undefined;
};

export const getFeaturedPosts = (language: 'en' | 'fr' = 'en'): LocalizedBlogPost[] => {
  return visiblePosts().filter(post => post.featured).map(post => getLocalizedPost(post, language));
};

export const getPostsByCategory = (category: string, language: 'en' | 'fr' = 'en'): LocalizedBlogPost[] => {
  return visiblePosts().filter(post => post.category === category).map(post => getLocalizedPost(post, language));
};

export const getAllCategories = (): string[] => {
  return [...new Set(visiblePosts().map(post => post.category))];
};

export const getAllTags = (): string[] => {
  const allTags = visiblePosts().flatMap(post => post.tags);
  return [...new Set(allTags)];
};
